import {
  DateTimeType,
  DeliveryInstructions,
  CmrArticle2OtherModeType,
  ECMR,
  HandlingInstructions,
  LocationParty,
  LogisticsLocation,
  LogisticsPackage,
  LogisticsSeal,
  LogisticsServiceCharge,
  LogisticsTransportEquipment,
  LogisticsTransportMovement,
  MeasureType,
  MeasurementType,
  Note,
  ReferencedDocument,
  ReferencedLogisticsTransportEquipment,
  SupplyChainConsignmentItem,
  TemperatureSettingInstructions,
  TradeAddress,
  TradeContact,
  TradeCountry,
  TradeParty,
  TransportCargo,
  TransportDangerousGoods,
  TransportEvent,
  TransportInstructions,
  TransportSettingTemperature,
  EcmrParticipant,
} from '../types/eCMR';
import fileDownload from 'js-file-download';
import { validateECMR } from './schemaValidator';
import { validateCmrDocument } from '../compliance/validateCmrDocument';
import { authService, User } from './authService';
import { safeConsoleError, safeConsoleWarn } from '../utils/safeConsole';

const compactDateTimeString = (date = new Date()): string =>
  date.toISOString().replace(/\D/g, '').substring(0, 14);
const BACKEND_XML_VALIDATION_REQUIRED_ERROR = 'Generated XML did not pass backend UNECE eCMR/CMR validation';
export type ECMRXmlSchemaProfile = 'D24A' | 'D25A';

export interface ConvertToXmlOptions {
  schemaProfile?: ECMRXmlSchemaProfile;
}

interface XmlSerializationProfileConfig {
  schemaProfile: ECMRXmlSchemaProfile;
  schemaLocation: string;
  guidelineId: string;
  includeMeasureUnitCode: boolean;
  maxTradeContactTelephoneCommunications?: number;
}

const XML_SERIALIZATION_PROFILES: Record<ECMRXmlSchemaProfile, XmlSerializationProfileConfig> = {
  D24A: {
    schemaProfile: 'D24A',
    schemaLocation: 'eCMR_100pD24A.xsd',
    guidelineId: 'urn:un:unece:uncefact:data:standard:eCMR:100::D24A',
    includeMeasureUnitCode: false,
  },
  D25A: {
    schemaProfile: 'D25A',
    schemaLocation: 'eCMR_100pD25A.xsd',
    guidelineId: 'urn:un:unece:uncefact:data:standard:eCMR:100::D25A',
    includeMeasureUnitCode: true,
    maxTradeContactTelephoneCommunications: 1,
  },
};

const DEFAULT_XML_SCHEMA_PROFILE: ECMRXmlSchemaProfile = 'D25A';

const D25A_ONLY_DOCUMENT_CODES = new Set([
  '471', '472', '473', '474', '475', '476', '477', '478',
  '479', '504', '505', '506', '507', '661', '939', '948',
]);
const D25A_ONLY_LOCATION_FUNCTION_CODES = new Set([
  '317', '318', '319', '320', '321', '322', '323', '324', '325',
]);
const D25A_ONLY_PARTY_ROLE_CODES = new Set([
  'DHF', 'DHG', 'DHH', 'DHI', 'DHJ', 'DHK', 'DHL', 'DHM',
]);
const D24A_ONLY_LANGUAGE_CODES = new Set(['BH', 'MO', 'bh', 'mo']);

const xmlSerializationProfile = (options?: ConvertToXmlOptions): XmlSerializationProfileConfig =>
  XML_SERIALIZATION_PROFILES[options?.schemaProfile ?? DEFAULT_XML_SCHEMA_PROFILE];

// Template para un eCMR vacío
export const emptyECMR: ECMR = {
  ExchangedDocumentContext: {
    GuidelineSpecifiedDocumentContextParameter: [
      {
        ID: 'urn:un:unece:uncefact:data:standard:eCMR:100::D25A',
      }
    ]
  },
  ExchangedDocument: {
    ID: '',
    TypeCode: '730', // Código para CMR
    IssueDateTime: {
      DateTimeString: compactDateTimeString(),
    },
    Remarks: [],
    IncludedNote: [],
    IssueLogisticsLocation: {
      Name: '',
    },
    IssuerTradeParty: {
      ID: [''],
      Name: [''],
      DefinedTradeContact: []
    }
  },
  SpecifiedSupplyChainConsignment: {
    ID: '',
    ConsignorAssignedID: '',
    CarrierAssignedID: '',
    GrossWeightMeasure: [],
    TransshipmentPermissionIndicator: false,
    ConsignorTradeParty: {
      ID: [''],
      Name: [''],
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      },
      DefinedTradeContact: []
    },
    ConsigneeTradeParty: {
      ID: [''],
      Name: [''],
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      },
      DefinedTradeContact: []
    },
    CarrierTradeParty: {
      ID: [''],
      Name: [''],
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      },
      DefinedTradeContact: []
    },
    CarrierAcceptanceLogisticsLocation: {
      Name: '',
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      }
    },
    ConsigneeReceiptLogisticsLocation: {
      Name: '',
      PostalTradeAddress: {
        PostcodeCode: '',
        StreetName: [''],
        CityName: [''],
        CountryID: ''
      }
    },
    IncludedSupplyChainConsignmentItem: [],
    UtilizedLogisticsTransportEquipment: [],
    MainCarriageLogisticsTransportMovement: []
  }
};

const escapeXml = (value: string | number | boolean): string =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

const attributesToString = (attributes: Record<string, string | undefined> = {}): string => {
  const serializedAttributes = Object.entries(attributes)
    .filter(([, value]) => isPresent(value))
    .map(([name, value]) => ` ${name}="${escapeXml(value as string)}"`);

  return serializedAttributes.join('');
};

type XmlPrefix = 'ram' | 'rsm' | 'udt' | 'qdt';

const qualifiedName = (name: string, prefix: XmlPrefix = 'ram'): string =>
  name.includes(':') ? name : `${prefix}:${name}`;

const element = (
  name: string,
  value: string | number | boolean | undefined | null,
  attributes?: Record<string, string | undefined>,
  prefix: XmlPrefix = 'ram'
): string => {
  const tagName = qualifiedName(name, prefix);

  return isPresent(value)
    ? `<${tagName}${attributesToString(attributes)}>${escapeXml(value as string | number | boolean)}</${tagName}>`
    : '';
};

const wrap = (name: string, content: string, prefix: XmlPrefix = 'ram'): string => {
  const tagName = qualifiedName(name, prefix);

  return content ? `<${tagName}>${content}</${tagName}>` : '';
};

const serializeStringArray = (name: string, values?: string[]): string =>
  values?.map((value) => element(name, value)).join('') ?? '';

const primaryAndAdditionalValues = (
  primary: string | undefined,
  additional: string[] | undefined
): string[] => [primary, ...(additional ?? [])].filter(isPresent) as string[];

const assertD24ACompatibleCodes = (
  values: Array<string | undefined>,
  profile: XmlSerializationProfileConfig,
  d25aOnlyCodes: ReadonlySet<string>,
  message: string
): void => {
  if (
    profile.schemaProfile === 'D24A' &&
    values.some((value) => isPresent(value) && d25aOnlyCodes.has(String(value).trim()))
  ) {
    throw new Error(message);
  }
};

const assertD25ACompatibleCodes = (
  values: Array<string | undefined>,
  profile: XmlSerializationProfileConfig,
  d24aOnlyCodes: ReadonlySet<string>,
  message: string
): void => {
  if (
    profile.schemaProfile === 'D25A' &&
    values.some((value) => isPresent(value) && d24aOnlyCodes.has(String(value).trim()))
  ) {
    throw new Error(message);
  }
};

const serializeDateTime = (name: string, dateTime?: DateTimeType): string =>
  wrap(name, element('DateTimeString', dateTime?.DateTimeString, undefined, 'udt'));

const serializeFormattedDateTime = (name: string, dateTime?: DateTimeType): string =>
  wrap(name, element('DateTimeString', dateTime?.DateTimeString, undefined, 'qdt'));

const serializeIndicator = (name: string, value?: boolean): string =>
  wrap(name, element('Indicator', value, undefined, 'udt'));

const defaultMeasureUnitCode = (name: string): string | undefined => {
  if (name === 'GrossWeightMeasure') {
    return 'KGM';
  }
  if (name === 'GrossVolumeMeasure') {
    return 'MTQ';
  }
  return undefined;
};

const serializeMeasure = (
  name: string,
  measure: MeasureType | undefined,
  profile: XmlSerializationProfileConfig
): string =>
  measure
    ? element(
        name,
        measure.value,
        profile.includeMeasureUnitCode
          ? { unitCode: measure.unitCode || defaultMeasureUnitCode(name) }
          : undefined
      )
    : '';

const serializeUnqualifiedMeasure = (
  name: string,
  measure: string | MeasureType | undefined
): string => {
  if (typeof measure === 'string') {
    return element(name, measure);
  }
  return measure ? element(name, measure.value, { unitCode: measure.unitCode }) : '';
};

const serializeAmount = (name: string, amount?: { value: string; currencyID?: string }): string =>
  amount ? element(name, amount.value, { currencyID: amount.currencyID }) : '';

const serializeTransportInstructions = (name: string, instruction: TransportInstructions): string =>
  wrap(
    name,
    [
      element('Description', instruction.Description),
      element('DescriptionCode', instruction.DescriptionCode),
    ].join('')
  );

const assertD25ASingleStringValue = (
  values: string[] | undefined,
  profile: XmlSerializationProfileConfig,
  message: string
): void => {
  if (profile.schemaProfile === 'D25A' && (values?.filter(isPresent).length ?? 0) > 1) {
    throw new Error(message);
  }
};

const serializeDeliveryInstructions = (
  instructions: DeliveryInstructions,
  profile: XmlSerializationProfileConfig
): string => {
  assertD25ASingleStringValue(
    instructions.Description,
    profile,
    'D25A permits at most one description per delivery instruction'
  );
  return wrap(
    'DeliveryInstructions',
    [
      serializeStringArray('Description', instructions.Description),
      element('DescriptionCode', instructions.DescriptionCode),
    ].join('')
  );
};

const serializeAddress = (
  address: TradeAddress | undefined,
  profile: XmlSerializationProfileConfig
): string => {
  const isD25A = profile.schemaProfile === 'D25A';
  const addressPresent = Boolean(
    address &&
      [
        ...(address.ID ?? []),
        address.PostcodeCode,
        ...(address.StreetName ?? []),
        ...(address.CityName ?? []),
        address.CountryID,
        ...(address.CountryName ?? []),
        ...(address.CountrySubDivisionName ?? []),
      ].some(isPresent)
  );

  if (isD25A && addressPresent) {
    if ((address?.StreetName?.length ?? 0) > 1) {
      throw new Error('D25A permits at most one street name per trade address');
    }
    if ((address?.CountryName?.length ?? 0) > 1) {
      throw new Error('D25A permits at most one country name per trade address');
    }
    if ((address?.CountrySubDivisionName?.length ?? 0) > 1) {
      throw new Error('D25A permits at most one country subdivision name per trade address');
    }
    if (!isPresent(address?.CountryID)) {
      throw new Error('D25A requires CountryID for every emitted postal trade address');
    }
  }

  return wrap(
    'PostalTradeAddress',
    [
      serializeStringArray('ID', address?.ID),
      element('PostcodeCode', address?.PostcodeCode),
      serializeStringArray('StreetName', address?.StreetName),
      serializeStringArray('CityName', address?.CityName),
      element('CountryID', address?.CountryID),
      serializeStringArray('CountryName', address?.CountryName),
      serializeStringArray('CountrySubDivisionName', address?.CountrySubDivisionName),
    ].join('')
  );
};

const serializeCommunication = (
  name: string,
  communication?: { URIID?: string; CompleteNumber?: string }
): string =>
  wrap(name, [element('URIID', communication?.URIID), element('CompleteNumber', communication?.CompleteNumber)].join(''));

const serializeNamedContact = (
  name: string,
  contact: TradeContact,
  profile: XmlSerializationProfileConfig
): string => {
  const telephoneCommunications = contact.TelephoneUniversalCommunication ?? [];
  if (
    profile.maxTradeContactTelephoneCommunications !== undefined &&
    telephoneCommunications.length > profile.maxTradeContactTelephoneCommunications
  ) {
    throw new Error('D25A permits at most one telephone communication per trade contact');
  }

  return wrap(
    name,
    [
      serializeStringArray('PersonName', contact.PersonName),
      telephoneCommunications
        .map((communication) =>
          serializeCommunication('TelephoneUniversalCommunication', communication)
        )
        .join(''),
      serializeCommunication(
        'MobileTelephoneUniversalCommunication',
        contact.MobileTelephoneUniversalCommunication
      ),
      serializeCommunication(
        'EmailURIUniversalCommunication',
        contact.EmailURIUniversalCommunication
      ),
      contact.SpecifiedNote
        ?.map((note) => serializeNamedNote('SpecifiedNote', note, profile))
        .join('') ?? '',
    ].join('')
  );
};

const serializeContact = (
  contact: TradeContact,
  profile: XmlSerializationProfileConfig
): string => serializeNamedContact('DefinedTradeContact', contact, profile);

const serializeTradeParty = (
  name: string,
  party: TradeParty | undefined,
  profile: XmlSerializationProfileConfig
): string => {
  const isD25A = profile.schemaProfile === 'D25A';
  if (isD25A && (party?.ID?.length ?? 0) > 1) {
    throw new Error('D25A permits at most one ID per trade party');
  }
  if (isD25A && (party?.DefinedTradeContact?.length ?? 0) > 1) {
    throw new Error('D25A permits at most one defined trade contact per trade party');
  }
  assertD24ACompatibleCodes(
    party?.RoleCode ?? [],
    profile,
    D25A_ONLY_PARTY_ROLE_CODES,
    'D24A does not permit D25A-only party role codes'
  );
  assertD25ACompatibleCodes(
    party?.LanguageCode ?? [],
    profile,
    D24A_ONLY_LANGUAGE_CODES,
    'D25A does not permit language codes removed from its controlled ISO list'
  );

  return wrap(
    name,
    [
      serializeStringArray('ID', party?.ID),
      serializeStringArray('Name', party?.Name),
      serializeStringArray('RoleCode', party?.RoleCode),
      serializeStringArray('LanguageCode', party?.LanguageCode),
      party?.DefinedTradeContact?.map((contact) => serializeContact(contact, profile)).join('') ?? '',
      serializeAddress(party?.PostalTradeAddress, profile),
      party?.SpecifiedTaxRegistration?.map((registration) =>
        wrap('SpecifiedTaxRegistration', element('ID', registration.ID))
      ).join('') ?? '',
      party?.SpecifiedGovernmentRegistration?.map((registration) =>
        wrap(
          'SpecifiedGovernmentRegistration',
          [
            element('ID', registration.ID),
            element('TypeCode', registration.TypeCode),
            element('CountryID', registration.CountryID),
          ].join('')
        )
      ).join('') ?? '',
    ].join('')
  );
};

const serializeLocationParty = (
  party: LocationParty,
  profile: XmlSerializationProfileConfig
): string =>
  wrap(
    'ServicingSpecifiedLocationParty',
    party.DefinedTradeContact?.map((contact) => serializeContact(contact, profile)).join('') ?? ''
  );

const serializeLocation = (
  name: string,
  location: LogisticsLocation | undefined,
  profile: XmlSerializationProfileConfig
): string => {
  const names = primaryAndAdditionalValues(location?.Name, location?.AdditionalName);
  const descriptions = primaryAndAdditionalValues(
    location?.Description,
    location?.AdditionalDescription
  );
  if (profile.schemaProfile === 'D25A' && names.length > 1) {
    throw new Error('D25A permits at most one name per logistics location');
  }
  const servicingParties = location?.ServicingSpecifiedLocationParty
    ?.map((party) => serializeLocationParty(party, profile))
    .filter(Boolean) ?? [];
  if (profile.schemaProfile === 'D25A' && servicingParties.length > 1) {
    throw new Error('D25A permits at most one servicing party per logistics location');
  }
  assertD24ACompatibleCodes(
    [location?.TypeCode],
    profile,
    D25A_ONLY_LOCATION_FUNCTION_CODES,
    'D24A does not permit D25A-only location function codes'
  );

  return wrap(
    name,
    [
      element('ID', location?.ID),
      serializeStringArray('Name', names),
      element('TypeCode', location?.TypeCode),
      serializeStringArray('Description', descriptions),
      element('CountryID', location?.CountryID),
      location?.PhysicalGeographicalCoordinate
        ? wrap(
            'PhysicalGeographicalCoordinate',
            [
              serializeUnqualifiedMeasure(
                'AltitudeMeasure',
                location.PhysicalGeographicalCoordinate.AltitudeMeasure
              ),
              serializeUnqualifiedMeasure(
                'LatitudeMeasure',
                location.PhysicalGeographicalCoordinate.LatitudeMeasure
              ),
              serializeUnqualifiedMeasure(
                'LongitudeMeasure',
                location.PhysicalGeographicalCoordinate.LongitudeMeasure
              ),
              serializeIndicator(
                'LatitudeDirectionIndicator',
                location.PhysicalGeographicalCoordinate.LatitudeDirectionIndicator
              ),
              serializeIndicator(
                'LongitudeDirectionIndicator',
                location.PhysicalGeographicalCoordinate.LongitudeDirectionIndicator
              ),
              element('SystemID', location.PhysicalGeographicalCoordinate.SystemID),
              element('ID', location.PhysicalGeographicalCoordinate.ID),
              element(
                'AltimetricSystemID',
                location.PhysicalGeographicalCoordinate.AltimetricSystemID
              ),
            ].join('')
          )
        : '',
      serializeAddress(location?.PostalTradeAddress, profile),
      servicingParties.join(''),
    ].join('')
  );
};

const assertNoteCardinality = (
  note: Note,
  profile: XmlSerializationProfileConfig
): void => {
  if (profile.schemaProfile !== 'D25A') {
    return;
  }
  if ((note.ContentCode?.filter(isPresent).length ?? 0) > 1) {
    throw new Error('D25A permits at most one content code per note');
  }
  if ((note.Content?.filter(isPresent).length ?? 0) > 1) {
    throw new Error('D25A permits at most one content value per note');
  }
};

function serializeNamedNote(
  name: string,
  note: Note,
  profile: XmlSerializationProfileConfig = XML_SERIALIZATION_PROFILES.D25A
): string {
  assertNoteCardinality(note, profile);
  return wrap(
    name,
    [
      element('Subject', note.Subject),
      serializeStringArray('ContentCode', note.ContentCode),
      serializeStringArray('Content', note.Content),
      serializeStringArray('SubjectCode', note.SubjectCode),
    ].join('')
  );
}

function serializeNote(
  note: Note,
  profile: XmlSerializationProfileConfig = XML_SERIALIZATION_PROFILES.D25A
): string {
  return serializeNamedNote('IncludedNote', note, profile);
}

const OPTIONAL_APPLICABILITY_NOTE_SUBJECT_CODE = 'OECMR_CMR_6_2_APPLICABILITY';
const OPTIONAL_APPLICABILITY_KEYS = [
  'transshipmentProhibition',
  'senderPaidCharges',
  'cashOnDelivery',
  'declaredValueOrSpecialInterest',
  'insuranceInstructions',
  'agreedTimeLimit',
  'documentsHandedToCarrier',
] as const;

const serializeOptionalApplicabilityNote = (
  consignment?: ECMR['SpecifiedSupplyChainConsignment']
): string => {
  const applicability = consignment?.CmrOptionalParticularsApplicable;
  if (!applicability || !OPTIONAL_APPLICABILITY_KEYS.some((key) => applicability[key] === true)) {
    return '';
  }

  const sanitizedApplicability = OPTIONAL_APPLICABILITY_KEYS.reduce<Record<string, boolean>>(
    (accumulator, key) => ({ ...accumulator, [key]: applicability[key] === true }),
    {}
  );

  return serializeNote({
    SubjectCode: [OPTIONAL_APPLICABILITY_NOTE_SUBJECT_CODE],
    Content: [JSON.stringify(sanitizedApplicability)],
  });
};

const parseOptionalApplicabilityNote = (notes: Note[]): ECMR['SpecifiedSupplyChainConsignment']['CmrOptionalParticularsApplicable'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(OPTIONAL_APPLICABILITY_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    const applicability = OPTIONAL_APPLICABILITY_KEYS.reduce<Record<string, boolean>>((accumulator, key) => {
      if (parsed[key] === true) {
        accumulator[key] = true;
      }
      return accumulator;
    }, {});

    return Object.keys(applicability).length
      ? (applicability as ECMR['SpecifiedSupplyChainConsignment']['CmrOptionalParticularsApplicable'])
      : undefined;
  } catch {
    return undefined;
  }
};

const CMR_ARTICLE_2_MULTIMODAL_NOTE_SUBJECT_CODE = 'OECMR_CMR_ARTICLE_2_MULTIMODAL_CARRIAGE';
const CMR_ARTICLE_2_OTHER_MODE_TYPES = ['sea', 'rail', 'inland_waterways', 'air'] as const;

const isCmrArticle2OtherModeType = (value: unknown): value is CmrArticle2OtherModeType =>
  typeof value === 'string' &&
  (CMR_ARTICLE_2_OTHER_MODE_TYPES as readonly string[]).includes(value);

const serializeCmrArticle2MultimodalCarriageNote = (
  consignment?: ECMR['SpecifiedSupplyChainConsignment']
): string => {
  const declaration = consignment?.CmrArticle2MultimodalCarriage;
  if (declaration?.vehicleCarriedByOtherMode !== true) {
    return '';
  }

  const sanitizedDeclaration: {
    vehicleCarriedByOtherMode: true;
    goodsUnloadedFromVehicle: boolean;
    article14ImpedimentBasis: boolean;
    otherModeType?: CmrArticle2OtherModeType;
  } = {
    vehicleCarriedByOtherMode: true,
    goodsUnloadedFromVehicle: declaration.goodsUnloadedFromVehicle === true,
    article14ImpedimentBasis: declaration.article14ImpedimentBasis === true,
  };

  if (isCmrArticle2OtherModeType(declaration.otherModeType)) {
    sanitizedDeclaration.otherModeType = declaration.otherModeType;
  }

  return serializeNote({
    SubjectCode: [CMR_ARTICLE_2_MULTIMODAL_NOTE_SUBJECT_CODE],
    Content: [JSON.stringify(sanitizedDeclaration)],
  });
};

const parseCmrArticle2MultimodalCarriageNote = (
  notes: Note[]
): ECMR['SpecifiedSupplyChainConsignment']['CmrArticle2MultimodalCarriage'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(CMR_ARTICLE_2_MULTIMODAL_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return note ? { vehicleCarriedByOtherMode: true } : undefined;
  }

  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    if (parsed.vehicleCarriedByOtherMode !== true) {
      return undefined;
    }

    return {
      vehicleCarriedByOtherMode: true,
      ...(isCmrArticle2OtherModeType(parsed.otherModeType)
        ? { otherModeType: parsed.otherModeType }
        : {}),
      goodsUnloadedFromVehicle: parsed.goodsUnloadedFromVehicle === true,
      article14ImpedimentBasis: parsed.article14ImpedimentBasis === true,
    };
  } catch {
    return { vehicleCarriedByOtherMode: true };
  }
};

const PROCEDURE_ACCEPTANCE_NOTE_SUBJECT_CODE = 'OECMR_PROCEDURE_ACCEPTANCE_EVIDENCE';
const AMENDMENT_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_AMENDMENT_EVIDENCE';
const SIGNATURE_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_SIGNATURE_EVIDENCE';
const CARRIER_RESERVATION_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_CARRIER_RESERVATION_EVIDENCE';
const PROOF_OF_DELIVERY_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_PROOF_OF_DELIVERY_EVIDENCE';
const SUPPLEMENTARY_DOCUMENT_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_SUPPLEMENTARY_DOCUMENT_EVIDENCE';
const SUPPLEMENTARY_DOCUMENT_REFERENCE_ID_PREFIX = 'OECMR-SUPPLEMENTARY-DOCUMENT-';
const SUPPLEMENTARY_DOCUMENT_LINK_REFERENCE_PATTERN = /^[a-f0-9]{32}$/;
const RETENTION_GOVERNANCE_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_RETENTION_GOVERNANCE_EVIDENCE';
const AUDIT_REVIEW_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_AUDIT_REVIEW_EVIDENCE';
const TASK_STATE_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_TASK_STATE_EVIDENCE';
const IMMUTABLE_ARCHIVE_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_IMMUTABLE_ARCHIVE_EVIDENCE';
const SOURCE_RECORD_OPERATIONAL_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_SOURCE_RECORD_OPERATIONAL_EVIDENCE';
const DOWNLOAD_COMPLETION_EVIDENCE_NOTE_SUBJECT_CODE = 'OECMR_DOWNLOAD_COMPLETION_EVIDENCE';

const publicAuditEventTypes = new Set<string>([
  'document.amendment.applied',
  'document.amendment.conflict_rejected',
  'document.amendment.requested',
  'document.amendment_acknowledgement.conflict_rejected',
  'document.article18_2_claimant_rebuttal.conflict_rejected',
  'document.article18_2_claimant_rebuttal.recorded',
  'document.attachment.direct_upload.confirmed',
  'document.attachment.download_url.created',
  'document.attachment.upload_intent.created',
  'document.attachment.upload_rejected',
  'document.attachment.uploaded',
  'document.attachments.listed',
  'document.authority_review.recorded',
  'document.carriage_accounting.conflict_rejected',
  'document.carriage_accounting.recorded',
  'document.carriage_impediment.conflict_rejected',
  'document.carriage_impediment.recorded',
  'document.carrier_liability_incident.conflict_rejected',
  'document.carrier_liability_incident.recorded',
  'document.carrier_reservation.conflict_rejected',
  'document.carrier_reservation.created',
  'document.carrier_reservation_acknowledgement.conflict_rejected',
  'document.carrier_reservation_acknowledgement.recorded',
  'document.carrier_takeover_check.conflict_rejected',
  'document.carrier_takeover_check.recorded',
  'document.carrier_takeover_check.requested',
  'document.carrier_takeover_check_request.conflict_rejected',
  'document.claim_reservation.conflict_rejected',
  'document.claim_reservation.recorded',
  'document.cmr_business_validation.failed',
  'document.cod_delivery_omission.conflict_rejected',
  'document.cod_delivery_omission.recorded',
  'document.compensation_assessment.conflict_rejected',
  'document.compensation_assessment.recorded',
  'document.compensation_interest.conflict_rejected',
  'document.compensation_interest.recorded',
  'document.consignee_delivery_request.conflict_rejected',
  'document.consignee_delivery_request.recorded',
  'document.consignment_note_copy_access.conflict_rejected',
  'document.consignment_note_copy_access.recorded',
  'document.created',
  'document.dangerous_goods_notice.conflict_rejected',
  'document.dangerous_goods_notice.recorded',
  'document.delivery.confirmed',
  'document.delivery_confirmation.conflict_rejected',
  'document.delivery_reservation_acknowledgement.conflict_rejected',
  'document.delivery_reservation_acknowledgement.recorded',
  'document.delivery_timing_assessment.conflict_rejected',
  'document.delivery_timing_assessment.recorded',
  'document.disposal_execution_notice.conflict_rejected',
  'document.disposal_execution_notice.recorded',
  'document.disposal_instruction.conflict_rejected',
  'document.disposal_instruction.recorded',
  'document.dispute_procedure.conflict_rejected',
  'document.dispute_procedure.recorded',
  'document.ecmr_protocol_validation.failed',
  'document.ecmr_receipt_access.requested',
  'document.ecmr_receipt_access.conflict_rejected',
  'document.ecmr_receipt_access.recorded',
  'document.ecmr_receipt_access_request.conflict_rejected',
  'document.evidence_download.completed',
  'document.evidence_manifest.archived',
  'document.evidence_manifest.exported',
  'document.evidence_transfer_audit.correlated',
  'document.exception_review_task.status_updated',
  'document.final_xml.archived',
  'document.final_xml.retrieved',
  'document.integrity.compromised',
  'document.legal_hold.applied',
  'document.legal_hold.released',
  'document.liability_limitation.conflict_rejected',
  'document.liability_limitation.recorded',
  'document.listed',
  'document.multimodal_carriage.conflict_rejected',
  'document.multimodal_carriage.recorded',
  'document.procedure_set.accepted',
  'document.prohibited_clause.conflict_rejected',
  'document.prohibited_clause.recorded',
  'document.replacement.conflict_rejected',
  'document.replacement.recorded',
  'document.retention_policy.applied',
  'document.sender_particulars_responsibility.conflict_rejected',
  'document.sender_particulars_responsibility.recorded',
  'document.signature.conflict_rejected',
  'document.signed',
  'document.successive_carrier.conflict_rejected',
  'document.successive_carrier.recorded',
  'document.tombstoned',
  'document.viewed',
  'document.xml.validated',
  'document.xml.validation.failed',
]);

const publicAuditActorRoles = new Set<string>([
  'owner',
  'consignor',
  'carrier',
  'consignee',
  'authority',
  'delegate',
]);

const auditReviewCategoryPatterns = [
  { category: 'integrity-tamper', patterns: ['integrity.compromised', 'tamper'] },
  { category: 'validation-failure', patterns: ['validation.failed', 'validation_failure', 'cmr_business_validation'] },
  { category: 'rejected-upload-action', patterns: ['upload_rejected', 'action_rejected', 'rejected'] },
  { category: 'download-failure', patterns: ['download.failed', 'download_failure'] },
] as const;
const auditReviewCategories = new Set([
  'operational-review',
  'validation-review',
  ...auditReviewCategoryPatterns.map((candidate) => candidate.category),
]);

const classifyAuditReviewCategory = (eventType?: string): string => {
  const normalized = eventType?.toLowerCase() ?? '';
  return auditReviewCategoryPatterns.find((candidate) =>
    candidate.patterns.some((pattern) => normalized.includes(pattern))
  )?.category ?? 'operational-review';
};

const publicAuditReviewCategory = (category: unknown, eventType?: string): string =>
  typeof category === 'string' && auditReviewCategories.has(category)
    ? category
    : classifyAuditReviewCategory(eventType);

const signatureSlots = [
  'ConsignorDigitalSignature',
  'CarrierDigitalSignature',
  'ConsigneeDigitalSignature',
] as const;

const signatureSlotPublicRoles: Record<(typeof signatureSlots)[number], 'consignor' | 'carrier' | 'consignee'> = {
  ConsignorDigitalSignature: 'consignor',
  CarrierDigitalSignature: 'carrier',
  ConsigneeDigitalSignature: 'consignee',
};

const signatureSlotDisplayRoles: Record<(typeof signatureSlots)[number], 'Consignor' | 'Carrier' | 'Consignee'> = {
  ConsignorDigitalSignature: 'Consignor',
  CarrierDigitalSignature: 'Carrier',
  ConsigneeDigitalSignature: 'Consignee',
};

const signatureSlotForPublicRole = (role: unknown): (typeof signatureSlots)[number] | undefined => {
  if (typeof role !== 'string') {
    return undefined;
  }
  const normalizedRole = role.trim().toLowerCase();
  return signatureSlots.find((slot) => signatureSlotPublicRoles[slot] === normalizedRole);
};

const uniqueNonEmptyStrings = (values: Array<unknown> = []): string[] =>
  Array.from(new Set(values.filter((value): value is string => typeof value === 'string' && value.length > 0)));

const ecmrProtocolArticle5CategoryIds = [
  'issue_and_note_delivery',
  'integrity_assurance',
  'entitlement_demonstration',
  'delivery_confirmation',
  'supplementing_and_amending',
  'replacement_by_other_means',
] as const;

const safeArticle5CategoryCoverage = (values?: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const provided = new Set(values.filter((value): value is string => typeof value === 'string'));
  return ecmrProtocolArticle5CategoryIds.filter((categoryId) => provided.has(categoryId));
};
const hasInvalidArticle5CategoryCoverage = (values?: unknown): boolean => {
  if (!Array.isArray(values)) {
    return true;
  }

  const article5CategoryCoverage = safeArticle5CategoryCoverage(values);
  return (
    article5CategoryCoverage.length !== values.length ||
    article5CategoryCoverage.length !== ecmrProtocolArticle5CategoryIds.length
  );
};

const serializeProcedureAcceptanceNote = (acceptances?: ECMR['procedureAcceptances']): string => {
  const safeAcceptances = acceptances
    ?.map((acceptance) => {
      const article5CategoryCoverage = safeArticle5CategoryCoverage(acceptance.article5CategoryCoverage);
      const acceptedByRoles = Array.isArray(acceptance.acceptedByRoles)
        ? acceptance.acceptedByRoles
          .map((role) => safeManifestLabel(role, documentTaskRoles))
          .filter((role): role is string => Boolean(role) && role !== 'other')
        : undefined;
      return {
        ...(acceptance.procedureSetReferencePresent === true || isPresent(acceptance.procedureSetId)
          ? { procedureSetReferencePresent: true }
          : {}),
        ...(typeof acceptance.procedureSetVersion === 'string'
          ? { procedureSetVersion: safeManifestVersionLabel(acceptance.procedureSetVersion) }
          : {}),
        ...(article5CategoryCoverage.length > 0 ? { article5CategoryCoverage } : {}),
        accepted: acceptance.accepted,
        ...(acceptance.acceptedAtPresent === true || isPresent(acceptance.acceptedAt)
          ? { acceptedAtPresent: true }
          : {}),
        ...(acceptedByRoles ? { acceptedByRoles } : {}),
        ...(typeof acceptance.contractParticipantReferencePresent === 'boolean'
          ? { contractParticipantReferencePresent: acceptance.contractParticipantReferencePresent }
          : {}),
      };
    })
    .filter((acceptance) =>
      Object.values(acceptance).some((value) =>
        Array.isArray(value) ? value.length > 0 : isPresent(value)
      )
    );

  if (!safeAcceptances?.length) {
    return '';
  }

  return serializeNote({
    SubjectCode: [PROCEDURE_ACCEPTANCE_NOTE_SUBJECT_CODE],
    Content: [JSON.stringify({ procedureAcceptances: safeAcceptances })],
  });
};

const parseProcedureAcceptanceNote = (notes: Note[]): ECMR['procedureAcceptances'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(PROCEDURE_ACCEPTANCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { procedureAcceptances?: Array<unknown> };
    const acceptances = parsed.procedureAcceptances
      ?.flatMap((acceptance) => {
        if (!acceptance || typeof acceptance !== 'object') {
          return [];
        }

        const acceptanceRecord = acceptance as Record<string, unknown>;
        if (hasInvalidArticle5CategoryCoverage(acceptanceRecord.article5CategoryCoverage)) {
          return [];
        }

        const article5CategoryCoverage = safeArticle5CategoryCoverage(acceptanceRecord.article5CategoryCoverage);
        const acceptedByRoles = Array.isArray(acceptanceRecord.acceptedByRoles)
          ? acceptanceRecord.acceptedByRoles
            .map((role) => safeManifestLabel(role, documentTaskRoles))
            .filter((role): role is string => Boolean(role) && role !== 'other')
          : undefined;
        const procedureSetVersion = safeManifestVersionLabel(acceptanceRecord.procedureSetVersion);
        return [{
          ...(acceptanceRecord.procedureSetReferencePresent === true || typeof acceptanceRecord.procedureSetId === 'string'
            ? { procedureSetReferencePresent: true }
            : {}),
          ...(procedureSetVersion ? { procedureSetVersion } : {}),
          ...(article5CategoryCoverage.length > 0 ? { article5CategoryCoverage } : {}),
          ...(typeof acceptanceRecord.accepted === 'boolean' ? { accepted: acceptanceRecord.accepted } : {}),
          ...(acceptanceRecord.acceptedAtPresent === true || typeof acceptanceRecord.acceptedAt === 'string'
            ? { acceptedAt: 'present' }
            : {}),
          ...(acceptedByRoles ? { acceptedByRoles } : {}),
          ...(typeof acceptanceRecord.contractParticipantReferencePresent === 'boolean'
            ? { contractParticipantReferencePresent: acceptanceRecord.contractParticipantReferencePresent }
            : {}),
        }];
      })
      .filter((acceptance) => Object.keys(acceptance).length > 0);

    return acceptances?.length ? acceptances : undefined;
  } catch {
    return undefined;
  }
};
const procedureAcceptanceNoteHasInvalidArticle5Categories = (note: Note): boolean => {
  if (!note.SubjectCode?.includes(PROCEDURE_ACCEPTANCE_NOTE_SUBJECT_CODE)) {
    return false;
  }

  const content = note.Content?.[0];
  if (!content) {
    return false;
  }

  try {
    const parsed = JSON.parse(content) as { procedureAcceptances?: Array<unknown> };
    return parsed.procedureAcceptances?.some((acceptance) => {
      if (!acceptance || typeof acceptance !== 'object') {
        return false;
      }

      return hasInvalidArticle5CategoryCoverage(
        (acceptance as Record<string, unknown>).article5CategoryCoverage
      );
    }) ?? false;
  } catch {
    return false;
  }
};

type XmlAmendmentEvidenceReference = NonNullable<ECMR['amendmentRequests']>[number];

const amendmentAcknowledgements = (amendment: XmlAmendmentEvidenceReference): unknown[] => {
  if (Array.isArray(amendment.acknowledgementList)) {
    return amendment.acknowledgementList;
  }

  const legacyAcknowledgements = (amendment as { acknowledgements?: unknown }).acknowledgements;
  return Array.isArray(legacyAcknowledgements) ? legacyAcknowledgements : [];
};

const amendmentProposedChangeFieldCount = (amendment: XmlAmendmentEvidenceReference): number => {
  const safeFieldCount = safePublicManifestCount(amendment.proposedChangeFieldCount);
  if (safeFieldCount !== undefined) {
    return safeFieldCount;
  }

  return amendment.proposedChanges && typeof amendment.proposedChanges === 'object'
    ? Object.keys(amendment.proposedChanges).length
    : 0;
};

const serializeAmendmentEvidenceNote = (amendments?: ECMR['amendmentRequests']): string => {
  if (!amendments?.length) {
    return '';
  }

  const amendmentReferences = amendments.map((amendment) => {
    const appliedVersionNumber = (amendment as { appliedVersionNumber?: unknown }).appliedVersionNumber;
    const normalizedAppliedVersionNumber = safePublicManifestVersionNumber(appliedVersionNumber);
    const parentVersionNumber = safePublicManifestVersionNumber(amendment.parentVersionNumber);
    const acknowledgementCount =
      safePublicManifestCount(amendment.acknowledgementCount) ??
      safePublicManifestCount(amendmentAcknowledgements(amendment).length) ??
      0;
    const rawStatus = typeof amendment.status === 'string' && amendment.status.length > 0
      ? amendment.status
      : amendment.appliedVersionReferencePresent === true
        ? 'applied'
        : 'pending';
    const status = safeManifestLabel(rawStatus, amendmentStatuses);
    const authorizationBasis = strictAmendmentAuthorizationBasis(amendment.authorizationBasis);

    return {
      status,
      parentVersionReferencePresent: amendment.parentVersionReferencePresent === true,
      appliedVersionReferencePresent: amendment.appliedVersionReferencePresent === true,
      proposedChangeFieldCount: amendmentProposedChangeFieldCount(amendment),
      acknowledgementCount,
      ...(authorizationBasis
        ? { authorizationBasis }
        : {}),
      ...(typeof amendment.authorizationEvidenceReferencePresent === 'boolean'
        ? { authorizationEvidenceReferencePresent: amendment.authorizationEvidenceReferencePresent }
        : {}),
      ...(typeof amendment.proposedXmlDigestAvailable === 'boolean'
        ? { proposedXmlDigestAvailable: amendment.proposedXmlDigestAvailable }
        : {}),
      ...(amendment.legalDetermination === false
        ? { legalDetermination: false }
        : {}),
      ...(parentVersionNumber !== undefined
        ? { parentVersionNumber }
        : {}),
      ...(normalizedAppliedVersionNumber !== undefined
        ? { appliedVersionNumber: normalizedAppliedVersionNumber }
        : {}),
      ...(isPresent(amendment.appliedAt) ? { appliedAtPresent: true } : {}),
    };
  });

  return serializeNote({
    SubjectCode: [AMENDMENT_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        amendments: amendmentReferences,
      }),
    ],
  });
};

const parseAmendmentEvidenceNote = (notes: Note[]): ECMR['amendmentRequests'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(AMENDMENT_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { amendments?: Array<Record<string, unknown>> };
    if (!Array.isArray(parsed.amendments) || !parsed.amendments.length) {
      return undefined;
    }

    return parsed.amendments.map((amendment) => {
      const acknowledgementCount =
        safePublicManifestCount(amendment.acknowledgementCount) ?? 0;
      const status = safeManifestLabel(amendment.status, amendmentStatuses);
      const appliedAtPresent =
        amendment.appliedAtPresent === true || (typeof amendment.appliedAt === 'string' && amendment.appliedAt.length > 0);
      const proposedChangeFieldCount = safePublicManifestCount(amendment.proposedChangeFieldCount) ?? 0;
      const parentVersionNumber = safePublicManifestVersionNumber(amendment.parentVersionNumber);
      const appliedVersionNumber = safePublicManifestVersionNumber(amendment.appliedVersionNumber);
      const authorizationBasis = strictAmendmentAuthorizationBasis(amendment.authorizationBasis);

      return {
        amendmentId: '',
        ...(status ? { status } : {}),
        parentVersionReferencePresent: amendment.parentVersionReferencePresent === true,
        appliedVersionReferencePresent: amendment.appliedVersionReferencePresent === true,
        proposedChangeFieldCount,
        acknowledgementCount,
        ...(authorizationBasis
          ? { authorizationBasis }
          : {}),
        ...(typeof amendment.authorizationEvidenceReferencePresent === 'boolean'
          ? { authorizationEvidenceReferencePresent: amendment.authorizationEvidenceReferencePresent }
          : {}),
        ...(typeof amendment.proposedXmlDigestAvailable === 'boolean'
          ? { proposedXmlDigestAvailable: amendment.proposedXmlDigestAvailable }
          : {}),
        ...(amendment.legalDetermination === false
          ? { legalDetermination: false }
          : {}),
        ...(parentVersionNumber !== undefined
          ? { parentVersionNumber }
          : {}),
        ...(appliedVersionNumber !== undefined
          ? { appliedVersionNumber }
          : {}),
        ...(appliedAtPresent ? { appliedAt: 'present' } : {}),
        ...(acknowledgementCount > 0
          ? {
              acknowledgementList: Array.from({ length: acknowledgementCount }, () => ({
                acknowledged: true,
              })),
            }
          : {}),
      };
    });
  } catch {
    return undefined;
  }
};

const serializeSignatureEvidenceNote = (document?: ECMR['ExchangedDocument']): string => {
  const signatures = signatureSlots
    .flatMap((slot) => {
      const signature = document?.[slot];
      if (!signature) {
        return [];
      }
      return [{
        signatoryPartyRole: signatureSlotPublicRoles[slot],
        ...(isPresent(signature.SigningTime) ? { signingTimePresent: true } : {}),
        ...(signature.SignatureMethod === 'manual-capture'
          ? { signatureMethod: signature.SignatureMethod }
          : {}),
        certificateAvailable: Boolean(signature.SignatureCertificate),
      }];
    })
    .filter((signature) =>
      Object.entries(signature).some(([key, value]) =>
        key === 'certificateAvailable' ? false : isPresent(value)
      )
    );

  if (!signatures.length) {
    return '';
  }

  return serializeNote({
    SubjectCode: [SIGNATURE_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [JSON.stringify({ signatures })],
  });
};

const parseSignatureEvidenceNote = (notes: Note[]): Partial<NonNullable<ECMR['ExchangedDocument']>> => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(SIGNATURE_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return {};
  }

  try {
    const parsed = JSON.parse(content) as { signatures?: Array<Record<string, unknown>> };
    return parsed.signatures?.reduce<Partial<NonNullable<ECMR['ExchangedDocument']>>>((accumulator, signature) => {
      const slot = signatureSlotForPublicRole(signature.signatoryPartyRole);
      if (!slot) {
        return accumulator;
      }

      accumulator[slot] = {
        SignatoryPartyRole: signatureSlotDisplayRoles[slot],
        SigningTime: signature.signingTimePresent === true || typeof signature.signingTime === 'string'
          ? 'present'
          : '',
        ...(signature.signatureMethod === 'manual-capture'
          ? { SignatureMethod: signature.signatureMethod }
          : {}),
        SignatureValue: '',
      };
      return accumulator;
    }, {}) ?? {};
  } catch {
    return {};
  }
};

const serializeCarrierReservationEvidenceNote = (reservations?: ECMR['carrierReservations']): string => {
  if (!reservations?.length) {
    return '';
  }

  const categories = uniqueNonEmptyStrings(
    reservations.map((reservation) => safeManifestLabel(reservation.category, carrierReservationCategories))
  );
  const statuses = uniqueNonEmptyStrings(
    reservations.map((reservation) => safeManifestLabel(reservation.statusAtReservation, documentLifecycleStatuses))
  );
  const roles = uniqueNonEmptyStrings(
    reservations
      .flatMap((reservation) => reservation.createdByRoles ?? [])
      .map((role) => safeManifestLabel(role, documentTaskRoles))
  ).filter((role) => role !== 'other');

  return serializeNote({
    SubjectCode: [CARRIER_RESERVATION_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        carrierReservations: {
          count: reservations.length,
          categories,
          statuses,
          createdAtPresent: reservations.some((reservation) => isPresent(reservation.createdAt)),
          documentVersionReferenceAvailable: reservations.some((reservation) =>
            reservation.documentVersionReferenceAvailable === true ||
            reservation.documentVersionReferencePresent === true ||
            isPresent(reservation.documentVersionId) ||
            isPresent(reservation.documentVersionNumber)
          ),
          roles,
        },
      }),
    ],
  });
};

const parseCarrierReservationEvidenceNote = (notes: Note[]): ECMR['carrierReservations'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(CARRIER_RESERVATION_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { carrierReservations?: Record<string, unknown> };
    const evidence = parsed.carrierReservations;
    if (!evidence) {
      return undefined;
    }

    const count = safePublicManifestCount(evidence?.count) ?? 0;
    if (!count) {
      return undefined;
    }

    const categories = Array.isArray(evidence.categories)
      ? uniqueNonEmptyStrings(evidence.categories.map((value) =>
          safeManifestLabel(value, carrierReservationCategories)
        ))
      : [];
    const statuses = Array.isArray(evidence.statuses)
      ? uniqueNonEmptyStrings(evidence.statuses.map((value) =>
          safeManifestLabel(value, documentLifecycleStatuses)
        ))
      : [];
    const roles = Array.isArray(evidence.roles)
      ? uniqueNonEmptyStrings(evidence.roles.map((value) => safeManifestLabel(value, documentTaskRoles)))
          .filter((role) => role !== 'other')
      : [];

    return Array.from({ length: count }, (_, index) => ({
      reservationId: '',
      category: categories[index] ?? categories[0] ?? 'other',
      grounds: '',
      createdBy: '',
      createdByRoles: roles,
      ...(evidence?.createdAtPresent === true ? { createdAt: 'present' } : {}),
      ...(evidence?.documentVersionReferenceAvailable === true
        ? { documentVersionReferenceAvailable: true }
        : {}),
      ...(statuses[index] ?? statuses[0] ? { statusAtReservation: statuses[index] ?? statuses[0] } : {}),
    }));
  } catch {
    return undefined;
  }
};

const serializeProofOfDeliveryEvidenceNote = (delivery?: ECMR['deliveryConfirmation']): string => {
  if (!delivery) {
    return '';
  }

  const status = safeManifestLabel(delivery.statusAtConfirmation, documentLifecycleStatuses);
  const confirmedByRoles = uniqueNonEmptyStrings(
    (delivery.confirmedByRoles ?? [])
      .map((role) => safeManifestLabel(role, documentTaskRoles))
      .filter((role): role is string => Boolean(role) && role !== 'other')
  );
  const deliveryReservationCategories = uniqueNonEmptyStrings(
    (delivery.deliveryReservations ?? []).map((reservation) =>
      safeManifestLabel(reservation.category, carrierReservationCategories) ?? 'other'
    )
  );
  const signatureSignerRole = safeManifestLabel(delivery.signatureEvidence?.signerRole, signatureManifestRoles);
  const signatureVerificationStatus = safeManifestLabel(
    delivery.signatureEvidence?.verificationStatus,
    signatureVerificationStatuses
  );
  const signatureMethod = safeManifestLabel(delivery.signatureEvidence?.signatureMethod, signatureManifestMethods);

  return serializeNote({
    SubjectCode: [PROOF_OF_DELIVERY_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        proofOfDelivery: {
          deliveredAtPresent: isPresent(delivery.deliveredAt),
          confirmedAtPresent: isPresent(delivery.confirmedAt),
          documentVersionReferenceAvailable:
            delivery.documentVersionReferenceAvailable === true ||
            delivery.documentVersionReferencePresent === true ||
            isPresent(delivery.documentVersionId) ||
            isPresent(delivery.documentVersionNumber),
          ...(status ? { status } : {}),
          confirmedByRoles,
          deliveryReservationCount: delivery.deliveryReservations?.length ?? 0,
          deliveryReservationCategories,
          ...(delivery.signatureEvidence
            ? {
                signature: {
                  ...(signatureSignerRole ? { signerRole: signatureSignerRole } : {}),
                  timestampPresent: isPresent(delivery.signatureEvidence.timestamp),
                  ...(signatureVerificationStatus ? { verificationStatus: signatureVerificationStatus } : {}),
                  ...(signatureMethod ? { method: signatureMethod } : {}),
                },
              }
            : {}),
        },
      }),
    ],
  });
};

const parseProofOfDeliveryEvidenceNote = (notes: Note[]): ECMR['deliveryConfirmation'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(PROOF_OF_DELIVERY_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { proofOfDelivery?: Record<string, unknown> };
    const evidence = parsed.proofOfDelivery;
    if (!evidence) {
      return undefined;
    }

    const roles = Array.isArray(evidence.confirmedByRoles)
      ? uniqueNonEmptyStrings(
          evidence.confirmedByRoles
            .map((role) => safeManifestLabel(role, documentTaskRoles))
            .filter((role): role is string => Boolean(role) && role !== 'other')
        )
      : [];
    const reservationCount = safePublicManifestCount(evidence.deliveryReservationCount) ?? 0;
    const reservationCategories = Array.isArray(evidence.deliveryReservationCategories)
      ? uniqueNonEmptyStrings(
          evidence.deliveryReservationCategories.map((category) =>
            safeManifestLabel(category, carrierReservationCategories) ?? 'other'
          )
        )
      : [];
    const signature = typeof evidence.signature === 'object' && evidence.signature
      ? evidence.signature as Record<string, unknown>
      : undefined;
    const status = safeManifestLabel(evidence.status, documentLifecycleStatuses);
    const signatureSignerRole = signature
      ? safeManifestLabel(signature.signerRole, signatureManifestRoles) ?? ''
      : '';
    const signatureMethod = signature
      ? safeManifestLabel(signature.method, signatureManifestMethods) ?? ''
      : '';
    const signatureVerificationStatus = signature
      ? safeManifestLabel(signature.verificationStatus, signatureVerificationStatuses) ?? ''
      : '';

    return {
      deliveryId: '',
      deliveredAt: evidence.deliveredAtPresent === true ? 'present' : '',
      recipientName: '',
      deliveryLocation: {},
      confirmedBy: '',
      confirmedByRoles: roles,
      ...(evidence.confirmedAtPresent === true ? { confirmedAt: 'present' } : {}),
      ...(evidence.documentVersionReferenceAvailable === true
        ? { documentVersionReferenceAvailable: true }
        : {}),
      ...(status ? { statusAtConfirmation: status } : {}),
      ...(signature
        ? {
            signatureEvidence: {
              signatureId: '',
              documentVersionId: '',
              signerUserId: '',
              signerRole: signatureSignerRole,
              signedDigest: '',
              signatureMethod,
              ...(signature.timestampPresent === true ? { timestamp: 'present' } : {}),
              verificationStatus: signatureVerificationStatus,
            },
          }
        : {}),
      deliveryReservations: Array.from({ length: reservationCount }, (_, index) => ({
        reservationId: '',
        category: reservationCategories[index] ?? reservationCategories[0] ?? 'other',
        grounds: '',
      })),
    };
  } catch {
    return undefined;
  }
};

const serializeSupplementaryDocumentEvidenceNote = (documents?: ECMR['supplementaryDocuments']): string => {
  if (!documents?.length) {
    return '';
  }

  const documentTypes = uniqueNonEmptyStrings(
    documents.map((document) =>
      safeTokenRecordType(document.documentType) ??
      (isPresent(document.documentType) ? 'other' : undefined)
    )
  );
  const mimeTypes = uniqueNonEmptyStrings(
    documents.map((document) => safeManifestMimeType(document.mimeType))
  );
  const roles = uniqueNonEmptyStrings(
    documents
      .flatMap((document) => document.uploadedByRoles ?? [])
      .map((role) => safeManifestLabel(role, documentTaskRoles))
      .filter((role): role is string => Boolean(role) && role !== 'other')
  );

  return serializeNote({
    SubjectCode: [SUPPLEMENTARY_DOCUMENT_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        supplementaryDocuments: {
          count: documents.length,
          documentTypes,
          mimeTypes,
          digestAvailable: documents.some((document) =>
            document.digestAvailable === true || isPresent(document.sha256Digest) || isPresent(document.digestAlgorithm)
          ),
          payloadStoredExternally: documents.some((document) =>
            document.payloadStoredExternally === true ||
            isPresent(document.storageBucket) ||
            isPresent(document.storageKey) ||
            isPresent(document.uploadMethod)
          ),
          documentVersionReferenceAvailable: documents.some((document) =>
            document.documentVersionReferenceAvailable === true ||
            document.documentVersionReferencePresent === true ||
            isPresent(document.documentVersionId) ||
            isPresent(document.documentVersionNumber)
          ),
          uploadedAtPresent: documents.some((document) => isPresent(document.uploadedAt)),
          ecmrLinkReferenceCount: documents.filter((document) =>
            Boolean(safeSupplementaryDocumentLinkReference(document.ecmrLinkReference))
          ).length,
          allEcmrLinkReferencesBound: documents.every((document) =>
            Boolean(safeSupplementaryDocumentLinkReference(document.ecmrLinkReference))
          ),
          roles,
        },
      }),
    ],
  });
};

const safeSupplementaryDocumentLinkReference = (value: unknown): string | undefined =>
  typeof value === 'string' && SUPPLEMENTARY_DOCUMENT_LINK_REFERENCE_PATTERN.test(value.trim())
    ? value.trim()
    : undefined;

const supplementaryDocumentLinkReferencesFromXml = (
  references: ReferencedDocument[]
): Array<string | undefined> =>
  references
    .filter((reference) =>
      typeof reference.ID === 'string' && reference.ID.startsWith(SUPPLEMENTARY_DOCUMENT_REFERENCE_ID_PREFIX)
    )
    .map((reference) => safeSupplementaryDocumentLinkReference(
      reference.ID?.slice(SUPPLEMENTARY_DOCUMENT_REFERENCE_ID_PREFIX.length)
    ));

const parseSupplementaryDocumentEvidenceNote = (
  notes: Note[],
  references: ReferencedDocument[] = []
): ECMR['supplementaryDocuments'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(SUPPLEMENTARY_DOCUMENT_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { supplementaryDocuments?: Record<string, unknown> };
    const evidence = parsed.supplementaryDocuments;
    const count = safePublicManifestCount(evidence?.count) ?? 0;
    if (!evidence || !count) {
      return undefined;
    }

    const documentTypes = Array.isArray(evidence.documentTypes)
      ? uniqueNonEmptyStrings(
          evidence.documentTypes.map((documentType) =>
            safeTokenRecordType(documentType) ??
            (isPresent(documentType) ? 'other' : undefined)
          )
        )
      : [];
    const mimeTypes = Array.isArray(evidence.mimeTypes)
      ? uniqueNonEmptyStrings(evidence.mimeTypes.map((mimeType) => safeManifestMimeType(mimeType)))
      : [];
    const roles = Array.isArray(evidence.roles)
      ? uniqueNonEmptyStrings(
          evidence.roles
            .map((role) => safeManifestLabel(role, documentTaskRoles))
            .filter((role): role is string => Boolean(role) && role !== 'other')
        )
      : [];

    const linkReferences = supplementaryDocumentLinkReferencesFromXml(references);
    return Array.from({ length: count }, (_, index) => ({
      attachmentId: '',
      ...(linkReferences[index]
        ? { ecmrLinkReference: linkReferences[index], ecmrLinkReferencePresent: true }
        : {}),
      ...(documentTypes[index] ?? documentTypes[0]
        ? { documentType: documentTypes[index] ?? documentTypes[0] }
        : {}),
      ...(mimeTypes[index] ?? mimeTypes[0] ? { mimeType: mimeTypes[index] ?? mimeTypes[0] } : {}),
      digestAvailable: evidence.digestAvailable === true,
      payloadStoredExternally: evidence.payloadStoredExternally === true,
      ...(evidence.documentVersionReferenceAvailable === true
        ? { documentVersionReferenceAvailable: true }
        : {}),
      ...(evidence.uploadedAtPresent === true ? { uploadedAt: 'present' } : {}),
      uploadedByRoles: roles,
    }));
  } catch {
    return undefined;
  }
};

type SupplementaryDocumentEvidenceReference = NonNullable<ECMR['supplementaryDocuments']>[number];

const hasSupplementaryDocumentDigest = (document: SupplementaryDocumentEvidenceReference): boolean =>
  document.digestAvailable === true || isPresent(document.sha256Digest) || isPresent(document.digestAlgorithm);

const hasSupplementaryDocumentExternalPayload = (document: SupplementaryDocumentEvidenceReference): boolean =>
  document.payloadStoredExternally === true ||
  isPresent(document.storageBucket) ||
  isPresent(document.storageKey) ||
  isPresent(document.uploadMethod);

const hasSupplementaryDocumentVersionReference = (document: SupplementaryDocumentEvidenceReference): boolean =>
  document.documentVersionReferenceAvailable === true ||
  document.documentVersionReferencePresent === true ||
  isPresent(document.documentVersionId) ||
  isPresent(document.documentVersionNumber);

const supplementaryDocumentTypeCode = (document: SupplementaryDocumentEvidenceReference): string | undefined => {
  const typeCode = typeof document.documentType === 'string' ? document.documentType.trim() : '';
  return /^\d{1,3}$/.test(typeCode) ? typeCode : undefined;
};

const supplementaryDocumentMimeCode = (document: SupplementaryDocumentEvidenceReference): string | undefined =>
  typeof document.mimeType === 'string' && document.mimeType.trim().length > 0
    ? safeManifestMimeType(document.mimeType)
    : undefined;

const isGeneratedSupplementaryReferencedDocument = (document: ReferencedDocument): boolean =>
  typeof document.ID === 'string' && document.ID.startsWith(SUPPLEMENTARY_DOCUMENT_REFERENCE_ID_PREFIX);

const buildSupplementaryReferencedDocuments = (
  documents?: ECMR['supplementaryDocuments']
): ReferencedDocument[] =>
  documents?.map((document, index) => {
    const linkReference = safeSupplementaryDocumentLinkReference(document.ecmrLinkReference);
    const remarks = [
      'Electronic supplementary document linked to this eCMR',
      ...(hasSupplementaryDocumentDigest(document) ? ['Digest available'] : []),
      ...(hasSupplementaryDocumentExternalPayload(document) ? ['External payload storage indicated'] : []),
      ...(hasSupplementaryDocumentVersionReference(document)
        ? ['Current document version reference available']
        : []),
    ];
    const typeCode = supplementaryDocumentTypeCode(document);
    const mimeCode = supplementaryDocumentMimeCode(document);

    return {
      ...(typeCode ? { TypeCode: typeCode } : {}),
      ID: `${SUPPLEMENTARY_DOCUMENT_REFERENCE_ID_PREFIX}${linkReference ?? `UNBOUND-${index + 1}`}`,
      Remarks: remarks,
      ...(mimeCode
        ? {
            AttachedSpecifiedBinaryFile: [
              {
                MIMECode: mimeCode,
              },
            ],
          }
        : {}),
    };
  }) ?? [];

const consignmentReferencedDocumentsForXml = (
  consignment?: ECMR['SpecifiedSupplyChainConsignment'],
  supplementaryDocuments?: ECMR['supplementaryDocuments']
): ReferencedDocument[] => {
  const generatedSupplementaryReferences = buildSupplementaryReferencedDocuments(supplementaryDocuments);
  const existingReferences = consignment?.AssociatedReferencedDocument ?? [];

  if (!generatedSupplementaryReferences.length) {
    return existingReferences;
  }

  return [
    ...existingReferences.filter((document) => !isGeneratedSupplementaryReferencedDocument(document)),
    ...generatedSupplementaryReferences,
  ];
};

const serializeRetentionGovernanceEvidenceNote = (
  retentionPolicy?: ECMR['retentionPolicy'],
  legalHold?: ECMR['legalHold']
): string => {
  if (!retentionPolicy && !legalHold) {
    return '';
  }

  const retentionMode = safeManifestLabel(retentionPolicy?.retentionMode, retentionModes);
  const jurisdiction = safeManifestJurisdiction(retentionPolicy?.jurisdiction);
  const status = safeManifestLabel(retentionPolicy?.status, retentionPolicyStatuses);

  return serializeNote({
    SubjectCode: [RETENTION_GOVERNANCE_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        retentionGovernance: {
          policyAvailable: Boolean(retentionPolicy),
          ...(retentionMode ? { retentionMode } : {}),
          ...(jurisdiction ? { jurisdiction } : {}),
          retainUntilPresent: isPresent(retentionPolicy?.retainUntil),
          documentVersionReferenceAvailable:
            retentionPolicy?.documentVersionReferenceAvailable === true ||
            retentionPolicy?.documentVersionReferencePresent === true ||
            isPresent(retentionPolicy?.documentVersionId),
          ...(status ? { status } : {}),
          legalHoldActive: legalHold?.active === true,
          legalHoldAppliedAtPresent: isPresent(legalHold?.appliedAt),
        },
      }),
    ],
  });
};

const parseRetentionGovernanceEvidenceNote = (
  notes: Note[]
): Pick<ECMR, 'retentionPolicy' | 'legalHold'> => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(RETENTION_GOVERNANCE_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return {};
  }

  try {
    const parsed = JSON.parse(content) as { retentionGovernance?: Record<string, unknown> };
    const evidence = parsed.retentionGovernance;
    if (!evidence) {
      return {};
    }

    const retentionMode = safeManifestLabel(evidence.retentionMode, retentionModes);
    const jurisdiction = safeManifestJurisdiction(evidence.jurisdiction);
    const status = safeManifestLabel(evidence.status, retentionPolicyStatuses);

    return {
      ...(evidence.policyAvailable === true
        ? {
            retentionPolicy: {
              ...(retentionMode ? { retentionMode } : {}),
              ...(jurisdiction ? { jurisdiction } : {}),
              ...(evidence.retainUntilPresent === true ? { retainUntil: 'present' } : {}),
              documentVersionReferenceAvailable:
                evidence.documentVersionReferenceAvailable === true,
              ...(status ? { status } : {}),
            },
          }
        : {}),
      ...(evidence.legalHoldActive === true || evidence.legalHoldAppliedAtPresent === true
        ? {
            legalHold: {
              active: evidence.legalHoldActive === true,
              ...(evidence.legalHoldAppliedAtPresent === true ? { appliedAt: 'present' } : {}),
            },
          }
        : {}),
    };
  } catch {
    return {};
  }
};

const serializeAuditReviewEvidenceNote = (auditEvents?: ECMR['auditEvents']): string => {
  if (!auditEvents?.length) {
    return '';
  }

  return serializeNote({
    SubjectCode: [AUDIT_REVIEW_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        auditReview: {
          count: auditEvents.length,
          categories: uniqueNonEmptyStrings(
            auditEvents.map((event) => publicAuditReviewCategory(event.category, event.eventType))
          ),
          createdAtPresent: auditEvents.some((event) => isPresent(event.createdAt)),
          documentVersionReferenceAvailable: auditEvents.some((event) =>
            event.documentVersionReferenceAvailable === true ||
            event.documentVersionReferencePresent === true ||
            isPresent(event.documentVersionId)
          ),
        },
      }),
    ],
  });
};

const parseAuditReviewEvidenceNote = (notes: Note[]): ECMR['auditEvents'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(AUDIT_REVIEW_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { auditReview?: Record<string, unknown> };
    const evidence = parsed.auditReview;
    const count = safePublicManifestCount(evidence?.count) ?? 0;
    if (!evidence || !count) {
      return undefined;
    }

    const categories = Array.isArray(evidence.categories)
      ? uniqueNonEmptyStrings(evidence.categories.map((category) => publicAuditReviewCategory(category)))
      : [];
    return Array.from({ length: count }, (_, index) => ({
      category: categories[index] ?? categories[0] ?? 'operational-review',
      ...(evidence.createdAtPresent === true ? { createdAt: 'present' } : {}),
      documentVersionReferenceAvailable: evidence.documentVersionReferenceAvailable === true,
    }));
  } catch {
    return undefined;
  }
};

const taskStateTypeValues = [
  'exception_review',
  'archive_readiness',
  'carrier_acceptance_review',
  'transport_lifecycle',
  'compliance_governance',
  'operational_acknowledgement',
  'signature_verification_review',
  'download_completion_review',
  'retention_governance',
  'supplementary_documents_review',
  'evidence_review',
];
const taskStateTypes = new Set(taskStateTypeValues);
const taskStateStatusValues = ['open', 'in_progress', 'completed', 'deferred'];
const taskStateStatuses = new Set(taskStateStatusValues);

const serializeTaskStateEvidenceNote = (tasks?: ECMR['documentTasks']): string => {
  if (!tasks?.length) {
    return '';
  }

  return serializeNote({
    SubjectCode: [TASK_STATE_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        taskState: {
          count: tasks.length,
          types: uniqueNonEmptyStrings(tasks.map((task) => safeManifestLabel(task.taskType, taskStateTypes))),
          statuses: uniqueNonEmptyStrings(tasks.map((task) => safeManifestLabel(task.status, taskStateStatuses))),
          assignedRoleCount: uniqueNonEmptyStrings(
            tasks
              .flatMap((task) => task.assignedRoles ?? [])
              .map((role) => safeManifestLabel(role, documentTaskRoles))
              .filter((role): role is string => Boolean(role) && role !== 'other')
          ).length,
          evidenceGapPresent: tasks.some((task) => (task.evidenceGaps?.length ?? 0) > 0),
          documentVersionReferenceAvailable: tasks.some((task) =>
            task.documentVersionReferenceAvailable === true ||
            task.documentVersionReferencePresent === true ||
            isPresent(task.documentVersionId)
          ),
        },
      }),
    ],
  });
};

const parseTaskStateEvidenceNote = (notes: Note[]): ECMR['documentTasks'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(TASK_STATE_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { taskState?: Record<string, unknown> };
    const evidence = parsed.taskState;
    const count = safePublicManifestCount(evidence?.count) ?? 0;
    if (!evidence || !count) {
      return undefined;
    }

    const types = Array.isArray(evidence.types)
      ? uniqueNonEmptyStrings(evidence.types.map((value) => safeManifestLabel(value, taskStateTypes)))
      : [];
    const statuses = Array.isArray(evidence.statuses)
      ? uniqueNonEmptyStrings(evidence.statuses.map((value) => safeManifestLabel(value, taskStateStatuses)))
      : [];
    return Array.from({ length: count }, (_, index) => ({
      taskType: types[index] ?? types[0] ?? 'evidence_review',
      ...(statuses[index] ?? statuses[0] ? { status: statuses[index] ?? statuses[0] } : {}),
      documentVersionReferenceAvailable: evidence.documentVersionReferenceAvailable === true,
      ...(evidence.evidenceGapPresent === true ? { evidenceGaps: ['present'] } : {}),
    }));
  } catch {
    return undefined;
  }
};

const sourceRecordOperationalEvidenceTypes = new Set([
  'source_record_operational_evidence',
  'xml_validation',
  'cmr_business_validation',
  'cmr_business_validation_failure',
  'ecmr_protocol_validation',
  'ecmr_protocol_validation_failure',
  'integrity_compromised',
  'integrity_tamper',
  'attachment_upload_rejection',
  'final_xml_retrieval',
  'evidence_manifest_export',
  'document_access',
  'attachment_access',
  'signature_verification',
]);

const sourceRecordOperationalEvidenceClasses = new Set([
  'source_record_operational_evidence',
  'validation',
  'security',
  'retrieval',
  'export',
  'access',
  'attachment',
  'signature',
  'upload',
  'archive',
  'operational',
  'browser_xml_note',
]);

const sourceRecordOperationalEvidenceStatuses = new Set([
  'captured',
  'succeeded',
  'failed',
  'denied',
  'verified',
  'invalid',
  'detected',
  'rejected',
  'recorded',
  'created',
]);

const safeSourceRecordOperationalEvidenceType = (value: unknown): string | undefined =>
  safeManifestLabel(value, sourceRecordOperationalEvidenceTypes);

const safeSourceRecordOperationalEvidenceClass = (value: unknown): string | undefined =>
  safeManifestLabel(value, sourceRecordOperationalEvidenceClasses);

const safeSourceRecordOperationalEvidenceStatus = (value: unknown): string | undefined =>
  safeManifestLabel(value, sourceRecordOperationalEvidenceStatuses);

const sanitizedSourceRecordOperationalEvidenceTuples = (
  evidenceReferences: NonNullable<ECMR['sourceRecordOperationalEvidence']>
): Array<{ evidenceType?: string; evidenceClass?: string; status?: string }> =>
  evidenceReferences
    .map((evidence) => ({
      ...(safeSourceRecordOperationalEvidenceType(evidence.evidenceType)
        ? { evidenceType: safeSourceRecordOperationalEvidenceType(evidence.evidenceType) }
        : {}),
      ...(safeSourceRecordOperationalEvidenceClass(evidence.evidenceClass)
        ? { evidenceClass: safeSourceRecordOperationalEvidenceClass(evidence.evidenceClass) }
        : {}),
      ...(safeSourceRecordOperationalEvidenceStatus(evidence.status)
        ? { status: safeSourceRecordOperationalEvidenceStatus(evidence.status) }
        : {}),
    }))
    .filter((evidence) => Object.keys(evidence).length > 0);

const serializeSourceRecordOperationalEvidenceNote = (
  evidenceReferences?: ECMR['sourceRecordOperationalEvidence']
): string => {
  if (!evidenceReferences?.length) {
    return '';
  }
  const sanitizedRecords = sanitizedSourceRecordOperationalEvidenceTuples(evidenceReferences);

  return serializeNote({
    SubjectCode: [SOURCE_RECORD_OPERATIONAL_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        sourceRecordOperationalEvidence: {
          count: evidenceReferences.length,
          records: sanitizedRecords,
          evidenceTypes: uniqueNonEmptyStrings(sanitizedRecords.map((evidence) => evidence.evidenceType)),
          evidenceClasses: uniqueNonEmptyStrings(sanitizedRecords.map((evidence) => evidence.evidenceClass)),
          statuses: uniqueNonEmptyStrings(sanitizedRecords.map((evidence) => evidence.status)),
          createdAtPresent: evidenceReferences.some((evidence) => isPresent(evidence.createdAt)),
          documentVersionReferenceAvailable: evidenceReferences.some((evidence) =>
            evidence.documentVersionReferenceAvailable === true ||
            evidence.documentVersionReferencePresent === true ||
            isPresent(evidence.documentVersionId) ||
            isPresent(evidence.documentVersionNumber)
          ),
          digestAvailable: evidenceReferences.some((evidence) =>
            evidence.digestAvailable === true ||
            isPresent(evidence.sha256Digest) ||
            isPresent(evidence.digestAlgorithm)
          ),
          payloadStoredExternally: evidenceReferences.some((evidence) =>
            evidence.payloadStoredExternally === true ||
            isPresent(evidence.storageBucket) ||
            isPresent(evidence.storageKey) ||
            isPresent(evidence.objectVersionId) ||
            isPresent(evidence.url) ||
            isPresent(evidence.downloadUrl)
          ),
        },
      }),
    ],
  });
};

const parseSourceRecordOperationalEvidenceNote = (
  notes: Note[]
): ECMR['sourceRecordOperationalEvidence'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(SOURCE_RECORD_OPERATIONAL_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { sourceRecordOperationalEvidence?: Record<string, unknown> };
    const evidence = parsed.sourceRecordOperationalEvidence;
    const count = safePublicManifestCount(evidence?.count) ?? 0;
    if (!evidence || !count) {
      return undefined;
    }

    const evidenceTypes = Array.isArray(evidence.evidenceTypes)
      ? uniqueNonEmptyStrings(evidence.evidenceTypes.map(safeSourceRecordOperationalEvidenceType))
      : [];
    const evidenceClasses = Array.isArray(evidence.evidenceClasses)
      ? uniqueNonEmptyStrings(evidence.evidenceClasses.map(safeSourceRecordOperationalEvidenceClass))
      : [];
    const statuses = Array.isArray(evidence.statuses)
      ? uniqueNonEmptyStrings(evidence.statuses.map(safeSourceRecordOperationalEvidenceStatus))
      : [];
    const records = Array.isArray(evidence.records)
      ? evidence.records
          .map((record) => {
            const sourceRecord = typeof record === 'object' && record ? record as Record<string, unknown> : {};
            return {
              ...(safeSourceRecordOperationalEvidenceType(sourceRecord.evidenceType)
                ? { evidenceType: safeSourceRecordOperationalEvidenceType(sourceRecord.evidenceType) }
                : {}),
              ...(safeSourceRecordOperationalEvidenceClass(sourceRecord.evidenceClass)
                ? { evidenceClass: safeSourceRecordOperationalEvidenceClass(sourceRecord.evidenceClass) }
                : {}),
              ...(safeSourceRecordOperationalEvidenceStatus(sourceRecord.status)
                ? { status: safeSourceRecordOperationalEvidenceStatus(sourceRecord.status) }
                : {}),
            };
          })
          .filter((record) => Object.keys(record).length > 0)
      : [];

    if (records.length) {
      return Array.from({ length: count }, (_, index) => ({
        evidenceType: records[index]?.evidenceType ?? 'source_record_operational_evidence',
        ...(records[index]?.evidenceClass ? { evidenceClass: records[index].evidenceClass } : {}),
        ...(records[index]?.status ? { status: records[index].status } : {}),
        ...(evidence.createdAtPresent === true ? { createdAt: 'present' } : {}),
        documentVersionReferenceAvailable: evidence.documentVersionReferenceAvailable === true,
        digestAvailable: evidence.digestAvailable === true,
        payloadStoredExternally: evidence.payloadStoredExternally === true,
      }));
    }

    return Array.from({ length: count }, (_, index) => ({
      evidenceType: evidenceTypes[index] ?? evidenceTypes[0] ?? 'source_record_operational_evidence',
      ...(evidenceClasses[index] ?? evidenceClasses[0]
        ? { evidenceClass: evidenceClasses[index] ?? evidenceClasses[0] }
        : {}),
      ...(statuses[index] ?? statuses[0] ? { status: statuses[index] ?? statuses[0] } : {}),
      ...(evidence.createdAtPresent === true ? { createdAt: 'present' } : {}),
      documentVersionReferenceAvailable: evidence.documentVersionReferenceAvailable === true,
      digestAvailable: evidence.digestAvailable === true,
      payloadStoredExternally: evidence.payloadStoredExternally === true,
    }));
  } catch {
    return undefined;
  }
};

const serializeDownloadCompletionEvidenceNote = (
  completions?: ECMR['evidenceDownloadCompletions']
): string => {
  if (!completions?.length) {
    return '';
  }

  const actorRoleCount = completions.reduce((maximum, completion) =>
    Math.max(maximum, publicOperationalRoleCount(completion.actorRoleCount, completion.actorRoles) ?? 0),
  0);
  const evidenceTypes = uniqueNonEmptyStrings(
    completions.map((completion) =>
      safeManifestLabel(completion.evidenceType, downloadCompletionEvidenceTypes) ??
      (isPresent(completion.evidenceType) ? 'other' : undefined)
    )
  );

  return serializeNote({
    SubjectCode: [DOWNLOAD_COMPLETION_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        evidenceDownloadCompletions: {
          count: completions.length,
          evidenceTypes,
          clientConfirmed: completions.some((completion) => completion.clientConfirmed === true),
          independentTransferProof: false,
          confirmedAtPresent: completions.some((completion) => isPresent(completion.confirmedAt)),
          documentVersionReferenceAvailable: completions.some((completion) =>
            completion.documentVersionReferenceAvailable === true ||
            completion.documentVersionReferencePresent === true ||
            isPresent(completion.documentVersionId) ||
            isPresent(completion.documentVersionNumber)
          ),
          ...(actorRoleCount > 0 ? { actorRoleCount } : {}),
          evidenceReferencePresent: completions.some((completion) => isPresent(completion.evidenceId)),
        },
      }),
    ],
  });
};

const parseDownloadCompletionEvidenceNote = (
  notes: Note[]
): ECMR['evidenceDownloadCompletions'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(DOWNLOAD_COMPLETION_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { evidenceDownloadCompletions?: Record<string, unknown> };
    const evidence = parsed.evidenceDownloadCompletions;
    const count = safePublicManifestCount(evidence?.count) ?? 0;
    if (!evidence || !count) {
      return undefined;
    }

    const evidenceTypes = Array.isArray(evidence.evidenceTypes)
      ? uniqueNonEmptyStrings(
          evidence.evidenceTypes.map((evidenceType) =>
            safeManifestLabel(evidenceType, downloadCompletionEvidenceTypes) ??
            (isPresent(evidenceType) ? 'other' : undefined)
          )
        )
      : [];
    const actorRoleCount = publicOperationalRoleCount(evidence.actorRoleCount, evidence.actorRoles);

    return Array.from({ length: count }, (_, index) => ({
      evidenceType: evidenceTypes[index] ?? evidenceTypes[0] ?? 'other',
      clientConfirmed: evidence.clientConfirmed === true,
      independentTransferProof: false,
      ...(evidence.confirmedAtPresent === true ? { confirmedAt: 'present' } : {}),
      documentVersionReferenceAvailable: evidence.documentVersionReferenceAvailable === true,
      ...(actorRoleCount !== undefined ? { actorRoleCount } : {}),
      evidenceReferencePresent: evidence.evidenceReferencePresent === true,
    }));
  } catch {
    return undefined;
  }
};

const immutableArchiveEvidenceTypes = new Set(['final-xml', 'evidence-manifest-xml', 'other']);

const serializeImmutableArchiveEvidenceNote = (references?: ECMR['immutableStorageReferences']): string => {
  if (!references?.length) {
    return '';
  }

  const evidenceTypes = uniqueNonEmptyStrings(
    references.map((reference) => safeManifestLabel(reference.evidenceType, immutableArchiveEvidenceTypes))
  );
  const retentionMode = safeManifestLabel(
    references.find((reference) => isPresent(reference.retentionMode))?.retentionMode,
    retentionModes
  );

  return serializeNote({
    SubjectCode: [IMMUTABLE_ARCHIVE_EVIDENCE_NOTE_SUBJECT_CODE],
    Content: [
      JSON.stringify({
        immutableArchive: {
          count: references.length,
          evidenceTypes,
          digestAvailable: references.some((reference) =>
            reference.digestAvailable === true || isPresent(reference.sha256Digest) || isPresent(reference.digestAlgorithm)
          ),
          payloadStoredExternally: references.some((reference) =>
            reference.payloadStoredExternally === true ||
            isPresent(reference.storageBucket) ||
            isPresent(reference.storageKey) ||
            isPresent(reference.objectVersionId) ||
            isPresent(reference.downloadUrl)
          ),
          documentVersionReferenceAvailable: references.some((reference) =>
            reference.documentVersionReferenceAvailable === true ||
            reference.documentVersionReferencePresent === true ||
            isPresent(reference.documentVersionId) ||
            isPresent(reference.documentVersionNumber)
          ),
          ...(retentionMode ? { retentionMode } : {}),
          retainUntilPresent: references.some((reference) => isPresent(reference.retainUntil)),
          archivedAtPresent: references.some((reference) => isPresent(reference.archivedAt)),
        },
      }),
    ],
  });
};

const parseImmutableArchiveEvidenceNote = (notes: Note[]): ECMR['immutableStorageReferences'] | undefined => {
  const note = notes.find((candidate) =>
    candidate.SubjectCode?.includes(IMMUTABLE_ARCHIVE_EVIDENCE_NOTE_SUBJECT_CODE)
  );
  const content = note?.Content?.[0];
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { immutableArchive?: Record<string, unknown> };
    const evidence = parsed.immutableArchive;
    const count = safePublicManifestCount(evidence?.count) ?? 0;
    if (!evidence || !count) {
      return undefined;
    }

    const evidenceTypes = Array.isArray(evidence.evidenceTypes)
      ? uniqueNonEmptyStrings(evidence.evidenceTypes.map((value) =>
          safeManifestLabel(value, immutableArchiveEvidenceTypes)
        ))
      : [];
    const retentionMode = safeManifestLabel(evidence.retentionMode, retentionModes);
    return Array.from({ length: count }, (_, index) => ({
      evidenceType: evidenceTypes[index] ?? evidenceTypes[0] ?? 'immutable_archive',
      digestAvailable: evidence.digestAvailable === true,
      payloadStoredExternally: evidence.payloadStoredExternally === true,
      documentVersionReferenceAvailable: evidence.documentVersionReferenceAvailable === true,
      ...(retentionMode ? { retentionMode } : {}),
      ...(evidence.retainUntilPresent === true ? { retainUntil: 'present' } : {}),
      ...(evidence.archivedAtPresent === true ? { archivedAt: 'present' } : {}),
    }));
  } catch {
    return undefined;
  }
};

const serializeReferencedDocument = (
  name: string,
  document: ReferencedDocument,
  profile: XmlSerializationProfileConfig
): string => {
  assertD24ACompatibleCodes(
    [document.TypeCode],
    profile,
    D25A_ONLY_DOCUMENT_CODES,
    'D24A does not permit D25A-only document codes'
  );
  const ids = primaryAndAdditionalValues(document.ID, document.AdditionalID);
  if (profile.schemaProfile === 'D25A' && ids.length > 1) {
    throw new Error('D25A permits at most one ID per referenced document');
  }
  const attachedFiles = document.AttachedSpecifiedBinaryFile?.map((file) => {
    const fileIds = primaryAndAdditionalValues(file.ID, file.AdditionalID);
    if (profile.schemaProfile === 'D25A' && fileIds.length > 1) {
      throw new Error('D25A permits at most one ID per specified binary file');
    }
    return wrap(
      'AttachedSpecifiedBinaryFile',
      [
        serializeStringArray('ID', fileIds),
        element('URIID', file.URIID),
        element('MIMECode', file.MIMECode),
        element('EncodingCode', file.EncodingCode),
        element('CharacterSetCode', file.CharacterSetCode),
        serializeUnqualifiedMeasure('SizeMeasure', file.SizeMeasure),
      ].join('')
    );
  }).join('') ?? '';

  return wrap(
    name,
    [
      element('TypeCode', document.TypeCode),
      serializeStringArray('ID', ids),
      document.Remarks?.map((remark) => element('Remarks', remark)).join('') ?? '',
      serializeFormattedDateTime('FormattedIssueDateTime', document.FormattedIssueDateTime),
      wrap(
        'EffectiveSpecifiedPeriod',
        [
          serializeDateTime('StartDateTime', document.EffectiveSpecifiedPeriod?.StartDateTime),
          serializeDateTime('EndDateTime', document.EffectiveSpecifiedPeriod?.EndDateTime),
        ].join('')
      ),
      attachedFiles,
    ].join('')
  );
};

const serializeAssociatedReferencedDocument = (
  document: ReferencedDocument,
  profile: XmlSerializationProfileConfig
): string => serializeReferencedDocument('AssociatedReferencedDocument', document, profile);

const serializePreviousAdministrativeReferencedDocument = (
  document: ReferencedDocument,
  profile: XmlSerializationProfileConfig
): string => serializeReferencedDocument('PreviousAdministrativeReferencedDocument', document, profile);

const serializeShippingMarks = (
  marks: NonNullable<LogisticsPackage['PhysicalLogisticsShippingMarks']>[number],
  profile: XmlSerializationProfileConfig
): string => {
  assertD25ASingleStringValue(
    marks.Marking,
    profile,
    'D25A permits at most one marking per logistics shipping marks block'
  );
  return wrap(
    'PhysicalLogisticsShippingMarks',
    [
      serializeStringArray('Marking', marks.Marking),
      marks.BarcodeLogisticsLabel
        ?.map((label) => wrap('BarcodeLogisticsLabel', element('ID', label.ID)))
        .join('') ?? '',
    ].join('')
  );
};

const serializePackage = (
  logisticsPackage: LogisticsPackage,
  profile: XmlSerializationProfileConfig
): string =>
  wrap(
    'TransportLogisticsPackage',
    [
      element('ItemQuantity', logisticsPackage.ItemQuantity),
      element('LevelCode', logisticsPackage.LevelCode),
      serializeStringArray(
        'TypeCode',
        primaryAndAdditionalValues(
          logisticsPackage.TypeCode,
          logisticsPackage.AdditionalTypeCode
        )
      ),
      serializeStringArray('Type', logisticsPackage.Type),
      element('ID', logisticsPackage.ID),
      serializeIndicator('ReturnableIndicator', logisticsPackage.ReturnableIndicator),
      logisticsPackage.PhysicalLogisticsShippingMarks
        ?.map((marks) => serializeShippingMarks(marks, profile))
        .join('') ?? '',
    ].join('')
  );

const serializeTransportCargo = (cargo?: TransportCargo): string =>
  wrap(
    'NatureIdentificationTransportCargo',
    [
      serializeStringArray('Identification', cargo?.Identification),
      element('StatisticalClassificationCode', cargo?.StatisticalClassificationCode),
    ].join('')
  );

const serializeProfileMeasure = (
  name: string,
  measure: MeasureType | undefined,
  profile: XmlSerializationProfileConfig,
  missingUnitMessage: string
): string => {
  if (!isPresent(measure?.value)) {
    return '';
  }
  if (profile.schemaProfile === 'D25A' && !isPresent(measure?.unitCode)) {
    throw new Error(missingUnitMessage);
  }
  return element(
    name,
    measure?.value,
    profile.includeMeasureUnitCode ? { unitCode: measure?.unitCode } : undefined
  );
};

const serializeMeasurement = (
  name: string,
  measurement: MeasurementType | undefined,
  profile: XmlSerializationProfileConfig
): string => {
  const measures = measurement?.ActualMeasure?.filter((measure) => isPresent(measure.value)) ?? [];
  if (profile.schemaProfile === 'D25A' && measures.length > 1) {
    throw new Error('D25A permits at most one actual measure per measurement');
  }
  return wrap(
    name,
    measures
      .map((measure) =>
        serializeProfileMeasure(
          'ActualMeasure',
          measure,
          profile,
          'D25A requires unitCode for measurement values'
        )
      )
      .join('')
  );
};

const serializeDangerousGoods = (
  dangerousGoods: TransportDangerousGoods,
  profile: XmlSerializationProfileConfig
): string => {
  assertD25ASingleStringValue(
    dangerousGoods.TechnicalName,
    profile,
    'D25A permits at most one technical name per dangerous goods block'
  );
  return wrap(
    'ApplicableTransportDangerousGoods',
    [
      element('UNDGIdentificationCode', dangerousGoods.UNDGIdentificationCode),
      element('RegulationCode', dangerousGoods.RegulationCode),
      serializeStringArray('TechnicalName', dangerousGoods.TechnicalName),
      element('UpperPartOrangeHazardPlacardID', dangerousGoods.UpperPartOrangeHazardPlacardID),
      element('LowerPartOrangeHazardPlacardID', dangerousGoods.LowerPartOrangeHazardPlacardID),
      element('PackagingDangerLevelCode', dangerousGoods.PackagingDangerLevelCode),
      element('ProperShippingName', dangerousGoods.ProperShippingName),
      dangerousGoods.TransportExpertTradeContact
        ? serializeNamedContact(
            'TransportExpertTradeContact',
            dangerousGoods.TransportExpertTradeContact,
            profile
          )
        : '',
      dangerousGoods.EmergencyTradeContact
        ? serializeNamedContact(
            'EmergencyTradeContact',
            dangerousGoods.EmergencyTradeContact,
            profile
          )
        : '',
      serializeMeasurement(
        'FlashpointTemperatureMeasurement',
        dangerousGoods.FlashpointTemperatureMeasurement,
        profile
      ),
    ].join('')
  );
};

const serializedValueCount = (
  values: Array<{ value: string }> | undefined
): number => values?.filter((value) => isPresent(value.value)).length ?? 0;

const assertConsignmentItemCardinality = (
  item: SupplyChainConsignmentItem,
  profile: XmlSerializationProfileConfig
): void => {
  if (profile.schemaProfile !== 'D25A') {
    return;
  }
  if (serializedValueCount(item.InvoiceAmount) > 1) {
    throw new Error('D25A permits at most one invoice amount per consignment item');
  }
  if ((item.GlobalID?.filter(isPresent).length ?? 0) > 1) {
    throw new Error('D25A permits at most one global ID per consignment item');
  }
};

const serializeConsignmentItem = (
  item: SupplyChainConsignmentItem,
  profile: XmlSerializationProfileConfig
): string => {
  assertConsignmentItemCardinality(item, profile);
  return wrap(
    'IncludedSupplyChainConsignmentItem',
    [
      element('SequenceNumeric', item.SequenceNumeric),
      element('TypeCode', item.TypeCode),
      item.InvoiceAmount?.map((amount) => serializeAmount('InvoiceAmount', amount)).join('') ?? '',
      serializeMeasure('GrossWeightMeasure', item.GrossWeightMeasure, profile),
      serializeMeasure('GrossVolumeMeasure', item.GrossVolumeMeasure, profile),
      element('TariffQuantity', item.TariffQuantity),
      serializeStringArray('GlobalID', item.GlobalID),
      serializeTransportCargo(item.NatureIdentificationTransportCargo),
      item.ApplicableTransportDangerousGoods
        ?.map((dangerousGoods) => serializeDangerousGoods(dangerousGoods, profile))
        .join('') ?? '',
      item.HandlingInstructions
        ?.map((instructions) => serializeHandlingInstructions(instructions, profile))
        .join('') ?? '',
      serializeTradeCountry('OriginTradeCountry', item.OriginTradeCountry),
      serializeTradeCountry('DestinationTradeCountry', item.DestinationTradeCountry),
      item.AssociatedReferencedLogisticsTransportEquipment
        ?.map(serializeReferencedLogisticsTransportEquipment)
        .join('') ?? '',
      item.AssociatedReferencedDocument
        ?.map((document) => serializeAssociatedReferencedDocument(document, profile))
        .join('') ?? '',
      item.TransportLogisticsPackage
        ?.map((logisticsPackage) => serializePackage(logisticsPackage, profile))
        .join('') ?? '',
      item.PreviousAdministrativeReferencedDocument
        ?.map((document) => serializePreviousAdministrativeReferencedDocument(document, profile))
        .join('') ?? '',
      item.ApplicableNote
        ?.map((note) => serializeNamedNote('ApplicableNote', note, profile))
        .join('') ?? '',
    ].join('')
  );
};

const assertConsignmentCardinality = (
  consignment: ECMR['SpecifiedSupplyChainConsignment'],
  profile: XmlSerializationProfileConfig
): void => {
  if (profile.schemaProfile !== 'D25A') {
    return;
  }
  if (serializedValueCount(consignment.GrossWeightMeasure) > 1) {
    throw new Error('D25A permits at most one gross weight measure per consignment');
  }
  if (serializedValueCount(consignment.GrossVolumeMeasure) > 1) {
    throw new Error('D25A permits at most one gross volume measure per consignment');
  }
  if (serializedValueCount(consignment.AssociatedInvoiceAmount) > 1) {
    throw new Error('D25A permits at most one associated invoice amount per consignment');
  }
};

const temperatureSettingInstructionsHasContent = (
  instructions: TemperatureSettingInstructions | undefined
): boolean => instructions?.Description?.some(isPresent) ?? false;

const transportSettingTemperatureHasContent = (
  temperature: TransportSettingTemperature
): boolean =>
  isPresent(temperature.MinimumValueMeasure?.value) ||
  isPresent(temperature.MaximumValueMeasure?.value) ||
  temperatureSettingInstructionsHasContent(
    temperature.InformationTemperatureSettingInstructions
  );

const serializeTemperatureMeasure = (
  name: string,
  measure: MeasureType | undefined,
  profile: XmlSerializationProfileConfig
): string =>
  serializeProfileMeasure(
    name,
    measure,
    profile,
    'D25A requires unitCode for temperature measures'
  );

const serializeTemperatureSettingInstructions = (
  instructions: TemperatureSettingInstructions | undefined,
  profile: XmlSerializationProfileConfig
): string => {
  assertD25ASingleStringValue(
    instructions?.Description,
    profile,
    'D25A permits at most one description per temperature setting instruction'
  );
  return wrap(
    'InformationTemperatureSettingInstructions',
    serializeStringArray('Description', instructions?.Description)
  );
};

const serializeNamedTransportSettingTemperature = (
  name: string,
  temperature: TransportSettingTemperature,
  profile: XmlSerializationProfileConfig
): string =>
  wrap(
    name,
    [
      serializeTemperatureMeasure('MinimumValueMeasure', temperature.MinimumValueMeasure, profile),
      serializeTemperatureMeasure('MaximumValueMeasure', temperature.MaximumValueMeasure, profile),
      serializeTemperatureSettingInstructions(
        temperature.InformationTemperatureSettingInstructions,
        profile
      ),
    ].join('')
  );

const serializeTransportSettingTemperature = (
  temperature: TransportSettingTemperature,
  profile: XmlSerializationProfileConfig
): string =>
  serializeNamedTransportSettingTemperature(
    'ApplicableTransportSettingTemperature',
    temperature,
    profile
  );

const serializeHandlingInstructions = (
  instructions: HandlingInstructions,
  profile: XmlSerializationProfileConfig
): string => {
  assertD25ASingleStringValue(
    instructions.Description,
    profile,
    'D25A permits at most one description per handling instruction'
  );
  assertD25ASingleStringValue(
    instructions.DescriptionCode,
    profile,
    'D25A permits at most one description code per handling instruction'
  );
  const temperatures = instructions.ApplicableTransportSettingTemperature?.filter(
    transportSettingTemperatureHasContent
  ) ?? [];
  if (profile.schemaProfile === 'D25A' && temperatures.length > 1) {
    throw new Error('D25A permits at most one transport setting temperature per handling instruction');
  }
  return wrap(
    'HandlingInstructions',
    [
      serializeStringArray('Description', instructions.Description),
      serializeStringArray('DescriptionCode', instructions.DescriptionCode),
      temperatures
        .map((temperature) => serializeTransportSettingTemperature(temperature, profile))
        .join(''),
    ].join('')
  );
};

const serializeLogisticsServiceCharge = (
  charge: LogisticsServiceCharge,
  profile: XmlSerializationProfileConfig
): string => {
  if (profile.schemaProfile === 'D25A' && serializedValueCount(charge.AppliedAmount) > 1) {
    throw new Error('D25A permits at most one applied amount per logistics service charge');
  }
  return wrap(
    'ApplicableLogisticsServiceCharge',
    [
      element('PaymentArrangementCode', charge.PaymentArrangementCode),
      element('CategoryCode', charge.CategoryCode),
      charge.AppliedAmount?.map((amount) => serializeAmount('AppliedAmount', amount)).join('') ?? '',
      element('PayingPartyRoleCode', charge.PayingPartyRoleCode),
    ].join('')
  );
};

const serializeTransportEvent = (
  name: string,
  event: NonNullable<ECMR['SpecifiedSupplyChainConsignment']['PickUpTransportEvent']> | undefined,
  profile: XmlSerializationProfileConfig
): string =>
  wrap(
    name,
    [
      element('ID', event?.ID),
      element('TypeCode', event?.TypeCode),
      serializeStringArray('Description', event?.Description),
      serializeDateTime('EstimatedOccurrenceDateTime', event?.EstimatedOccurrenceDateTime),
      serializeDateTime('ActualOccurrenceDateTime', event?.ActualOccurrenceDateTime),
      serializeDateTime('ScheduledOccurrenceDateTime', event?.ScheduledOccurrenceDateTime),
      event?.SpecifiedTransportInstructions?.map((instruction) =>
        wrap(
          'SpecifiedTransportInstructions',
          [
            element('Description', instruction.Description),
            element('DescriptionCode', instruction.DescriptionCode),
          ].join('')
        )
      ).join('') ?? '',
      serializeLocation('OccurrenceLogisticsLocation', event?.OccurrenceLogisticsLocation, profile),
      event?.CertifyingTradeParty
        ?.map((party) => serializeTradeParty('CertifyingTradeParty', party, profile))
        .join('') ?? '',
      event?.ApplicableNote
        ?.map((note) => serializeNamedNote('ApplicableNote', note, profile))
        .join('') ?? '',
    ].join('')
  );

const serializeLogisticsSeal = (seal: LogisticsSeal): string =>
  wrap(
    'AffixedLogisticsSeal',
    [
      element('ID', seal.ID),
      serializeStringArray('ConditionCode', seal.ConditionCode),
      element('SealingPartyRoleCode', seal.SealingPartyRoleCode),
    ].join('')
  );

const serializeTransportEquipment = (
  equipment: LogisticsTransportEquipment,
  profile: XmlSerializationProfileConfig
): string =>
  wrap(
    'UtilizedLogisticsTransportEquipment',
    [
      element('ID', equipment.ID),
      element('CategoryCode', equipment.CategoryCode),
      element('CharacteristicCode', equipment.CharacteristicCode),
      serializeIndicator('SealedIndicator', equipment.SealedIndicator),
      serializeIndicator('ReturnableIndicator', equipment.ReturnableIndicator),
      equipment.AffixedLogisticsSeal?.map(serializeLogisticsSeal).join('') ?? '',
      equipment.SettingTransportSettingTemperature
        ?.map((temperature) => serializeNamedTransportSettingTemperature(
          'SettingTransportSettingTemperature',
          temperature,
          profile
        ))
        .join('') ?? '',
      equipment.ApplicableNote
        ?.map((note) => serializeNamedNote('ApplicableNote', note, profile))
        .join('') ?? '',
    ].join('')
  );

const serializeTradeCountry = (name: string, country?: TradeCountry): string =>
  wrap(name, element('ID', country?.ID));

const serializeTradeDeliveryTerms = (
  terms?: ECMR['SpecifiedSupplyChainConsignment']['SpecifiedTradeDeliveryTerms']
): string =>
  wrap(
    'SpecifiedTradeDeliveryTerms',
    [
      element('DeliveryTypeCode', terms?.DeliveryTypeCode),
      wrap('RelevantTradeLocation', element('ID', terms?.RelevantTradeLocation?.ID)),
    ].join('')
  );

const serializeReferencedLogisticsTransportEquipment = (
  equipment: ReferencedLogisticsTransportEquipment
): string =>
  wrap(
    'AssociatedReferencedLogisticsTransportEquipment',
    [
      element('ID', equipment.ID),
      element('LoadedPackageQuantity', equipment.LoadedPackageQuantity),
    ].join('')
  );

const serializeTransportMovement = (
  movement: LogisticsTransportMovement,
  profile: XmlSerializationProfileConfig
): string => {
  const means = movement.UsedLogisticsTransportMeans;
  return wrap(
    'MainCarriageLogisticsTransportMovement',
    [
      element('StageCode', movement.StageCode),
      element('ModeCode', movement.ModeCode),
      means
        ? wrap(
            'UsedLogisticsTransportMeans',
            [
              element('TypeCode', means.TypeCode),
              serializeStringArray(
                'ID',
                primaryAndAdditionalValues(means.ID, means.AdditionalID)
              ),
              serializeTradeCountry('RegistrationTradeCountry', means.RegistrationTradeCountry),
            ].join('')
          )
        : '',
      movement.SpecifiedTransportEvent
        ?.map((event) => serializeTransportEvent('SpecifiedTransportEvent', event, profile))
        .join('') ?? '',
    ].join('')
  );
};

const serializeExchangedDocumentContext = (
  ecmr: ECMR,
  profile: XmlSerializationProfileConfig
): string => {
  const businessProcessParameters =
    ecmr.ExchangedDocumentContext?.BusinessProcessSpecifiedDocumentContextParameter
      ?.filter((parameter) => isPresent(parameter.ID)) ?? [];
  if (profile.schemaProfile === 'D25A' && businessProcessParameters.length > 1) {
    throw new Error('D25A permits at most one business process context parameter');
  }

  return wrap(
    'ExchangedDocumentContext',
    [
      element('SpecifiedTransactionID', ecmr.ExchangedDocumentContext?.SpecifiedTransactionID),
      businessProcessParameters
        .map((parameter) =>
          wrap('BusinessProcessSpecifiedDocumentContextParameter', element('ID', parameter.ID))
        )
        .join(''),
      wrap(
        'GuidelineSpecifiedDocumentContextParameter',
        element('ID', profile.guidelineId)
      ),
    ].join(''),
    'rsm'
  );
};

// Convertir objeto ECMR a XML
export const convertToXml = async (ecmr: ECMR, options?: ConvertToXmlOptions): Promise<string> => {
  const profile = xmlSerializationProfile(options);
  const exchangedDocument = ecmr.ExchangedDocument;
  const consignment = ecmr.SpecifiedSupplyChainConsignment;
  const exchangedDocumentTypeCode = exchangedDocument?.TypeCode || '730';
  assertD24ACompatibleCodes(
    [exchangedDocumentTypeCode],
    profile,
    D25A_ONLY_DOCUMENT_CODES,
    'D24A does not permit D25A-only document codes'
  );
  assertConsignmentCardinality(consignment, profile);
  const associatedReferencedDocuments = consignmentReferencedDocumentsForXml(
    consignment,
    ecmr.supplementaryDocuments
  );
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<rsm:eCMR 
  xmlns:rsm="urn:un:unece:uncefact:data:standard:eCMR:100"
  xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
  xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100"
  xmlns:qdt="urn:un:unece:uncefact:data:standard:QualifiedDataType:100"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="urn:un:unece:uncefact:data:standard:eCMR:100 ${profile.schemaLocation}">
  
  ${serializeExchangedDocumentContext(ecmr, profile)}
  
  <rsm:ExchangedDocument>
    ${element('ID', exchangedDocument?.ID)}
    ${element('TypeCode', exchangedDocumentTypeCode)}
    ${serializeDateTime('IssueDateTime', exchangedDocument?.IssueDateTime)}
    ${exchangedDocument?.Remarks?.map((remark) => element('Remarks', remark)).join('') ?? ''}
    ${exchangedDocument?.IncludedNote?.map((note) => serializeNote(note, profile)).join('') ?? ''}
    ${serializeOptionalApplicabilityNote(consignment)}
    ${serializeCmrArticle2MultimodalCarriageNote(consignment)}
    ${serializeProcedureAcceptanceNote(ecmr.procedureAcceptances)}
    ${serializeAmendmentEvidenceNote(ecmr.amendmentRequests)}
    ${serializeSignatureEvidenceNote(exchangedDocument)}
    ${serializeCarrierReservationEvidenceNote(ecmr.carrierReservations)}
    ${serializeProofOfDeliveryEvidenceNote(ecmr.deliveryConfirmation)}
    ${serializeSupplementaryDocumentEvidenceNote(ecmr.supplementaryDocuments)}
    ${serializeRetentionGovernanceEvidenceNote(ecmr.retentionPolicy, ecmr.legalHold)}
    ${serializeAuditReviewEvidenceNote(ecmr.auditEvents)}
    ${serializeTaskStateEvidenceNote(ecmr.documentTasks)}
    ${serializeSourceRecordOperationalEvidenceNote(ecmr.sourceRecordOperationalEvidence)}
    ${serializeDownloadCompletionEvidenceNote(ecmr.evidenceDownloadCompletions)}
    ${serializeImmutableArchiveEvidenceNote(ecmr.immutableStorageReferences)}
    ${serializeLocation('IssueLogisticsLocation', exchangedDocument?.IssueLogisticsLocation, profile)}
    ${serializeTradeParty('IssuerTradeParty', exchangedDocument?.IssuerTradeParty, profile)}
    ${exchangedDocument?.RecipientTradeParty?.map((party) => serializeTradeParty('RecipientTradeParty', party, profile)).join('') ?? ''}
  </rsm:ExchangedDocument>
  
  <rsm:SpecifiedSupplyChainConsignment>
    ${element('ID', consignment?.ID)}
    ${element('ConsignorAssignedID', consignment?.ConsignorAssignedID)}
    ${element('CarrierAssignedID', consignment?.CarrierAssignedID)}
    ${element('FreightForwarderAssignedID', consignment?.FreightForwarderAssignedID)}
    ${consignment?.GrossWeightMeasure?.map((measure) => serializeMeasure('GrossWeightMeasure', measure, profile)).join('') ?? ''}
    ${consignment?.GrossVolumeMeasure?.map((measure) => serializeMeasure('GrossVolumeMeasure', measure, profile)).join('') ?? ''}
    ${consignment?.AssociatedInvoiceAmount?.map((amount) => serializeAmount('AssociatedInvoiceAmount', amount)).join('') ?? ''}
    ${serializeAmount('CODAmount', consignment?.CODAmount)}
    ${element('ConsignmentItemQuantity', consignment?.ConsignmentItemQuantity)}
    ${element('CargoInsuranceInstructionsInformation', consignment?.CargoInsuranceInstructionsInformation)}
    ${serializeIndicator('TransshipmentPermissionIndicator', consignment?.TransshipmentPermissionIndicator)}
    ${serializeTradeParty('ConsignorTradeParty', consignment?.ConsignorTradeParty, profile)}
    ${serializeTradeParty('ConsigneeTradeParty', consignment?.ConsigneeTradeParty, profile)}
    ${serializeTradeParty('CarrierTradeParty', consignment?.CarrierTradeParty, profile)}
    ${consignment?.NotifiedTradeParty?.map((party) => serializeTradeParty('NotifiedTradeParty', party, profile)).join('') ?? ''}
    ${consignment?.ConnectingCarrierTradeParty?.map((party) => serializeTradeParty('ConnectingCarrierTradeParty', party, profile)).join('') ?? ''}
    ${serializeLocation('CarrierAcceptanceLogisticsLocation', consignment?.CarrierAcceptanceLogisticsLocation, profile)}
    ${serializeLocation('ConsigneeReceiptLogisticsLocation', consignment?.ConsigneeReceiptLogisticsLocation, profile)}
    ${associatedReferencedDocuments.map((document) => serializeAssociatedReferencedDocument(document, profile)).join('')}
    ${consignment?.HandlingInstructions?.map((instructions) => serializeHandlingInstructions(instructions, profile)).join('') ?? ''}
    ${consignment?.LoadingTransportInstructions?.map((instruction) => serializeTransportInstructions('LoadingTransportInstructions', instruction)).join('') ?? ''}
    ${serializeTransportEvent('DeliveryTransportEvent', consignment?.DeliveryTransportEvent, profile)}
    ${consignment?.ExaminationTransportEvent?.map((event) => serializeTransportEvent('ExaminationTransportEvent', event, profile)).join('') ?? ''}
    ${serializeTransportEvent('PickUpTransportEvent', consignment?.PickUpTransportEvent, profile)}
    ${consignment?.IncludedSupplyChainConsignmentItem?.map((item) => serializeConsignmentItem(item, profile)).join('') ?? ''}
    ${consignment?.UtilizedLogisticsTransportEquipment?.map((equipment) => serializeTransportEquipment(equipment, profile)).join('') ?? ''}
    ${consignment?.MainCarriageLogisticsTransportMovement?.map((movement) => serializeTransportMovement(movement, profile)).join('') ?? ''}
    ${serializeTradeDeliveryTerms(consignment?.SpecifiedTradeDeliveryTerms)}
    ${consignment?.ApplicableLogisticsServiceCharge?.map((charge) => serializeLogisticsServiceCharge(charge, profile)).join('') ?? ''}
    ${consignment?.TransportLogisticsPackage?.map((logisticsPackage) => serializePackage(logisticsPackage, profile)).join('') ?? ''}
    ${consignment?.ConsignorProvidedBorderClearanceTransportInstructions?.map((instruction) => serializeTransportInstructions('ConsignorProvidedBorderClearanceTransportInstructions', instruction)).join('') ?? ''}
    ${consignment?.DeliveryInstructions?.map((instructions) => serializeDeliveryInstructions(instructions, profile)).join('') ?? ''}
    ${consignment?.PreviousAdministrativeReferencedDocument?.map((document) => serializePreviousAdministrativeReferencedDocument(document, profile)).join('') ?? ''}
    ${serializeTradeParty('TransportServicesBuyerTradeParty', consignment?.TransportServicesBuyerTradeParty, profile)}
  </rsm:SpecifiedSupplyChainConsignment>
  
</rsm:eCMR>`;

  return xml;
};

// Convertir XML a objeto ECMR (using native DOM parser)
export const convertFromXml = async (xml: string): Promise<ECMR> => {
  // Validar el XML primero
  const validationResult = await validateECMR(xml);
  if (validationResult && !validationResult.valid && validationResult.errors) {
    // Si hay errores, los enviamos como advertencias pero continuamos la conversión
    safeConsoleWarn('Advertencias de validación', 'ecmr.xml.import.validation', {
      count: validationResult.errors.length,
    });
  }
  
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, 'text/xml');
  
  // Check for parsing errors
  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Invalid XML syntax');
  }
  
  return transformXmlToEcmr(xmlDoc);
};

// Transformar el DOM XML a la estructura ECMR esperada
const transformXmlToEcmr = (xmlDoc: Document): ECMR => {
  const rsmNamespace = 'urn:un:unece:uncefact:data:standard:eCMR:100';
  const ramNamespace =
    'urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100';
  const udtNamespace = 'urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100';
  const qdtNamespace = 'urn:un:unece:uncefact:data:standard:QualifiedDataType:100';

  const leafText = (element: Element | null | undefined): string =>
    element && element.children.length === 0 ? element.textContent?.trim() ?? '' : '';

  const directChildrenByLocalName = (
    root: ParentNode,
    localName: string,
    namespace: string = ramNamespace
  ): Element[] =>
    Array.from(root.children ?? []).filter(
      (element) => element.localName === localName && element.namespaceURI === namespace
    );

  const directText = (
    root: ParentNode,
    localName: string,
    namespace: string = ramNamespace
  ): string => leafText(directChildrenByLocalName(root, localName, namespace)[0]);

  const directTexts = (
    root: ParentNode,
    localName: string,
    namespace: string = ramNamespace
  ): string[] => directChildrenByLocalName(root, localName, namespace).map(leafText).filter(Boolean);

  const directChild = (
    root: ParentNode,
    localName: string,
    namespace: string = ramNamespace
  ): Element | undefined => directChildrenByLocalName(root, localName, namespace)[0];

  const rootElement =
    xmlDoc.documentElement.localName === 'eCMR' &&
    xmlDoc.documentElement.namespaceURI === rsmNamespace
      ? xmlDoc.documentElement
      : undefined;
  const exchangedDocumentContextElement = rootElement
    ? directChild(rootElement, 'ExchangedDocumentContext', rsmNamespace)
    : undefined;
  const exchangedDocumentElement = rootElement
    ? directChild(rootElement, 'ExchangedDocument', rsmNamespace)
    : undefined;
  const consignmentElement = rootElement
    ? directChild(rootElement, 'SpecifiedSupplyChainConsignment', rsmNamespace)
    : undefined;

  const parseAmountElement = (amountElement: Element | undefined): { value: string; currencyID?: string } | undefined => {
    if (!amountElement) {
      return undefined;
    }
    const value = leafText(amountElement);
    if (!value) {
      return undefined;
    }

    return {
      value,
      currencyID: amountElement.getAttribute('currencyID') || undefined,
    };
  };

  const parseDateTimeElement = (dateTimeElement: Element | undefined): DateTimeType | undefined => {
    const dateTimeString = dateTimeElement
      ? directText(dateTimeElement, 'DateTimeString', udtNamespace) ||
        directText(dateTimeElement, 'DateTimeString', qdtNamespace)
      : '';
    return dateTimeString ? { DateTimeString: dateTimeString } : undefined;
  };

  const parseMeasureElement = (measureElement: Element | undefined): MeasureType | undefined => {
    const value = leafText(measureElement);
    const unitCode = measureElement?.getAttribute('unitCode') || '';
    return value ? { value, ...(unitCode ? { unitCode } : {}) } : undefined;
  };

  const parseGeographicalMeasureElement = (
    measureElement: Element | undefined
  ): string | MeasureType | undefined => {
    const measure = parseMeasureElement(measureElement);
    if (!measure) {
      return undefined;
    }
    return measure.unitCode ? measure : measure.value;
  };

  const parseBooleanIndicatorElement = (
    indicatorElement: Element | undefined
  ): boolean | undefined => {
    const value = indicatorElement
      ? directText(indicatorElement, 'Indicator', udtNamespace).trim().toLowerCase()
      : '';
    if (value === 'true' || value === '1') {
      return true;
    }
    if (value === 'false' || value === '0') {
      return false;
    }
    return undefined;
  };

  const parseAddressElement = (addressElement: Element | undefined): TradeAddress | undefined => {
    if (!addressElement) {
      return undefined;
    }

    const address: TradeAddress = {
      ...(directTexts(addressElement, 'ID').length ? { ID: directTexts(addressElement, 'ID') } : {}),
      ...(directText(addressElement, 'PostcodeCode')
        ? { PostcodeCode: directText(addressElement, 'PostcodeCode') }
        : {}),
      ...(directTexts(addressElement, 'StreetName').length
        ? { StreetName: directTexts(addressElement, 'StreetName') }
        : {}),
      ...(directTexts(addressElement, 'CityName').length
        ? { CityName: directTexts(addressElement, 'CityName') }
        : {}),
      ...(directText(addressElement, 'CountryID')
        ? { CountryID: directText(addressElement, 'CountryID') }
        : {}),
      ...(directTexts(addressElement, 'CountryName').length
        ? { CountryName: directTexts(addressElement, 'CountryName') }
        : {}),
      ...(directTexts(addressElement, 'CountrySubDivisionName').length
        ? { CountrySubDivisionName: directTexts(addressElement, 'CountrySubDivisionName') }
        : {}),
    };

    return Object.keys(address).length ? address : undefined;
  };

  const parseCommunicationElement = (
    communicationElement: Element | undefined
  ): TradeContact['EmailURIUniversalCommunication'] | undefined => {
    if (!communicationElement) {
      return undefined;
    }

    const uriId = directText(communicationElement, 'URIID');
    const completeNumber = directText(communicationElement, 'CompleteNumber');
    return uriId || completeNumber
      ? {
          ...(uriId ? { URIID: uriId } : {}),
          ...(completeNumber ? { CompleteNumber: completeNumber } : {}),
        }
      : undefined;
  };

  const parseContactElement = (contactElement: Element): TradeContact => {
    const telephoneCommunications = directChildrenByLocalName(
      contactElement,
      'TelephoneUniversalCommunication'
    )
      .map(parseCommunicationElement)
      .filter(
        (communication): communication is NonNullable<
          TradeContact['EmailURIUniversalCommunication']
        > => Boolean(communication)
      );
    const mobileCommunication = parseCommunicationElement(
      directChild(contactElement, 'MobileTelephoneUniversalCommunication')
    );
    const emailCommunication = parseCommunicationElement(
      directChild(contactElement, 'EmailURIUniversalCommunication')
    );
    const specifiedNotes = directChildrenByLocalName(contactElement, 'SpecifiedNote').map(
      (noteElement): Note => ({
        ...(directText(noteElement, 'Subject')
          ? { Subject: directText(noteElement, 'Subject') }
          : {}),
        ContentCode: directTexts(noteElement, 'ContentCode'),
        Content: directTexts(noteElement, 'Content'),
        SubjectCode: directTexts(noteElement, 'SubjectCode'),
      })
    );

    return {
      ...(directTexts(contactElement, 'PersonName').length
        ? { PersonName: directTexts(contactElement, 'PersonName') }
        : {}),
      ...(telephoneCommunications.length
        ? { TelephoneUniversalCommunication: telephoneCommunications }
        : {}),
      ...(mobileCommunication
        ? { MobileTelephoneUniversalCommunication: mobileCommunication }
        : {}),
      ...(emailCommunication ? { EmailURIUniversalCommunication: emailCommunication } : {}),
      ...(specifiedNotes.length ? { SpecifiedNote: specifiedNotes } : {}),
    };
  };

  const parseLocationPartyElement = (
    partyElement: Element
  ): LocationParty | undefined => {
    const contacts = directChildrenByLocalName(partyElement, 'DefinedTradeContact')
      .map(parseContactElement)
      .filter((contact) => Object.keys(contact).length);
    return contacts.length ? { DefinedTradeContact: contacts } : undefined;
  };

  const parseTradePartyElement = (partyElement: Element | undefined): TradeParty | undefined => {
    if (!partyElement) {
      return undefined;
    }

    const contacts = directChildrenByLocalName(partyElement, 'DefinedTradeContact')
      .map(parseContactElement)
      .filter((contact) => Object.keys(contact).length);
    const address = parseAddressElement(directChild(partyElement, 'PostalTradeAddress'));
    const taxRegistrations = directChildrenByLocalName(partyElement, 'SpecifiedTaxRegistration')
      .map((registrationElement) => directText(registrationElement, 'ID'))
      .filter(Boolean)
      .map((ID) => ({ ID }));
    const governmentRegistrations = directChildrenByLocalName(
      partyElement,
      'SpecifiedGovernmentRegistration'
    )
      .map((registrationElement) => ({
        ...(directText(registrationElement, 'ID')
          ? { ID: directText(registrationElement, 'ID') }
          : {}),
        ...(directText(registrationElement, 'TypeCode')
          ? { TypeCode: directText(registrationElement, 'TypeCode') }
          : {}),
        ...(directText(registrationElement, 'CountryID')
          ? { CountryID: directText(registrationElement, 'CountryID') }
          : {}),
      }))
      .filter((registration) => Object.keys(registration).length);
    const party: TradeParty = {
      ...(directTexts(partyElement, 'ID').length ? { ID: directTexts(partyElement, 'ID') } : {}),
      ...(directTexts(partyElement, 'Name').length
        ? { Name: directTexts(partyElement, 'Name') }
        : {}),
      ...(directTexts(partyElement, 'RoleCode').length
        ? { RoleCode: directTexts(partyElement, 'RoleCode') }
        : {}),
      ...(directTexts(partyElement, 'LanguageCode').length
        ? { LanguageCode: directTexts(partyElement, 'LanguageCode') }
        : {}),
      ...(contacts.length ? { DefinedTradeContact: contacts } : {}),
      ...(address ? { PostalTradeAddress: address } : {}),
      ...(taxRegistrations.length ? { SpecifiedTaxRegistration: taxRegistrations } : {}),
      ...(governmentRegistrations.length
        ? { SpecifiedGovernmentRegistration: governmentRegistrations }
        : {}),
    };

    return Object.keys(party).length ? party : undefined;
  };

  const parseLocationElement = (
    locationElement: Element | undefined
  ): LogisticsLocation | undefined => {
    if (!locationElement) {
      return undefined;
    }

    const coordinateElement = directChild(locationElement, 'PhysicalGeographicalCoordinate');
    const altitudeMeasure = parseGeographicalMeasureElement(
      coordinateElement ? directChild(coordinateElement, 'AltitudeMeasure') : undefined
    );
    const latitudeMeasure = parseGeographicalMeasureElement(
      coordinateElement ? directChild(coordinateElement, 'LatitudeMeasure') : undefined
    );
    const longitudeMeasure = parseGeographicalMeasureElement(
      coordinateElement ? directChild(coordinateElement, 'LongitudeMeasure') : undefined
    );
    const latitudeDirectionIndicator = parseBooleanIndicatorElement(
      coordinateElement
        ? directChild(coordinateElement, 'LatitudeDirectionIndicator')
        : undefined
    );
    const longitudeDirectionIndicator = parseBooleanIndicatorElement(
      coordinateElement
        ? directChild(coordinateElement, 'LongitudeDirectionIndicator')
        : undefined
    );
    const coordinateSystemID = coordinateElement
      ? directText(coordinateElement, 'SystemID')
      : '';
    const coordinateID = coordinateElement ? directText(coordinateElement, 'ID') : '';
    const altimetricSystemID = coordinateElement
      ? directText(coordinateElement, 'AltimetricSystemID')
      : '';
    const address = parseAddressElement(directChild(locationElement, 'PostalTradeAddress'));
    const names = directTexts(locationElement, 'Name');
    const descriptions = directTexts(locationElement, 'Description');
    const servicingParties = directChildrenByLocalName(
      locationElement,
      'ServicingSpecifiedLocationParty'
    )
      .map(parseLocationPartyElement)
      .filter((party): party is LocationParty => Boolean(party));
    const location: LogisticsLocation = {
      ...(directText(locationElement, 'ID') ? { ID: directText(locationElement, 'ID') } : {}),
      ...(names.length ? { Name: names[0] } : {}),
      ...(names.length > 1 ? { AdditionalName: names.slice(1) } : {}),
      ...(directText(locationElement, 'TypeCode')
        ? { TypeCode: directText(locationElement, 'TypeCode') }
        : {}),
      ...(descriptions.length ? { Description: descriptions[0] } : {}),
      ...(descriptions.length > 1
        ? { AdditionalDescription: descriptions.slice(1) }
        : {}),
      ...(directText(locationElement, 'CountryID')
        ? { CountryID: directText(locationElement, 'CountryID') }
        : {}),
      ...(altitudeMeasure !== undefined ||
      latitudeMeasure !== undefined ||
      longitudeMeasure !== undefined ||
      latitudeDirectionIndicator !== undefined ||
      longitudeDirectionIndicator !== undefined ||
      coordinateSystemID ||
      coordinateID ||
      altimetricSystemID
        ? {
            PhysicalGeographicalCoordinate: {
              ...(altitudeMeasure !== undefined ? { AltitudeMeasure: altitudeMeasure } : {}),
              ...(latitudeMeasure !== undefined ? { LatitudeMeasure: latitudeMeasure } : {}),
              ...(longitudeMeasure !== undefined ? { LongitudeMeasure: longitudeMeasure } : {}),
              ...(latitudeDirectionIndicator !== undefined
                ? { LatitudeDirectionIndicator: latitudeDirectionIndicator }
                : {}),
              ...(longitudeDirectionIndicator !== undefined
                ? { LongitudeDirectionIndicator: longitudeDirectionIndicator }
                : {}),
              ...(coordinateSystemID ? { SystemID: coordinateSystemID } : {}),
              ...(coordinateID ? { ID: coordinateID } : {}),
              ...(altimetricSystemID ? { AltimetricSystemID: altimetricSystemID } : {}),
            },
          }
        : {}),
      ...(address ? { PostalTradeAddress: address } : {}),
      ...(servicingParties.length
        ? { ServicingSpecifiedLocationParty: servicingParties }
        : {}),
    };

    return Object.keys(location).length ? location : undefined;
  };

  const parseTransportInstructionElement = (instructionElement: Element): TransportInstructions => ({
    Description: directText(instructionElement, 'Description') || undefined,
    DescriptionCode: directText(instructionElement, 'DescriptionCode') || undefined,
  });

  const parseDeliveryInstructionsElement = (instructionElement: Element): DeliveryInstructions => ({
    Description: directChildrenByLocalName(instructionElement, 'Description')
      .map(leafText)
      .filter(Boolean),
    DescriptionCode: directText(instructionElement, 'DescriptionCode') || undefined,
  });

  const parseTemperatureSettingInstructionsElement = (
    instructionsElement: Element | undefined
  ): TemperatureSettingInstructions | undefined => {
    if (!instructionsElement) {
      return undefined;
    }
    const descriptions = directTexts(instructionsElement, 'Description');
    return descriptions.length ? { Description: descriptions } : undefined;
  };

  const parseTransportSettingTemperatureElement = (
    temperatureElement: Element
  ): TransportSettingTemperature => {
    const minimum = parseMeasureElement(directChild(temperatureElement, 'MinimumValueMeasure'));
    const maximum = parseMeasureElement(directChild(temperatureElement, 'MaximumValueMeasure'));
    const instructions = parseTemperatureSettingInstructionsElement(
      directChild(temperatureElement, 'InformationTemperatureSettingInstructions')
    );
    return {
      ...(minimum ? { MinimumValueMeasure: minimum } : {}),
      ...(maximum ? { MaximumValueMeasure: maximum } : {}),
      ...(instructions ? { InformationTemperatureSettingInstructions: instructions } : {}),
    };
  };

  const parseHandlingInstructionsElement = (
    instructionElement: Element
  ): HandlingInstructions => {
    const descriptionCodes = directTexts(instructionElement, 'DescriptionCode');
    const temperatures = directChildrenByLocalName(
      instructionElement,
      'ApplicableTransportSettingTemperature'
    ).map(parseTransportSettingTemperatureElement);
    return {
      Description: directTexts(instructionElement, 'Description'),
      ...(descriptionCodes.length ? { DescriptionCode: descriptionCodes } : {}),
      ...(temperatures.length
        ? { ApplicableTransportSettingTemperature: temperatures }
        : {}),
    };
  };

  const parseApplicableNoteElement = (
    noteElement: Element,
    includeEmptyArrays = false
  ): Note => {
    const contentCodes = directTexts(noteElement, 'ContentCode');
    const content = directTexts(noteElement, 'Content');
    const subjectCodes = directTexts(noteElement, 'SubjectCode');

    return {
      ...(directText(noteElement, 'Subject')
        ? { Subject: directText(noteElement, 'Subject') }
        : {}),
      ...(includeEmptyArrays || contentCodes.length ? { ContentCode: contentCodes } : {}),
      ...(includeEmptyArrays || content.length ? { Content: content } : {}),
      ...(includeEmptyArrays || subjectCodes.length ? { SubjectCode: subjectCodes } : {}),
    };
  };

  const parseShippingMarksElement = (
    marksElement: Element
  ): NonNullable<LogisticsPackage['PhysicalLogisticsShippingMarks']>[number] => {
    const labels = directChildrenByLocalName(marksElement, 'BarcodeLogisticsLabel')
      .map((labelElement) => ({ ID: directText(labelElement, 'ID') || undefined }));
    return {
      Marking: directChildrenByLocalName(marksElement, 'Marking')
        .map(leafText)
        .filter(Boolean),
      ...(labels.length ? { BarcodeLogisticsLabel: labels } : {}),
    };
  };

  const parsePackageElement = (packageElement: Element): LogisticsPackage => {
    const typeCodes = directTexts(packageElement, 'TypeCode');
    return {
      ItemQuantity: directText(packageElement, 'ItemQuantity')
        ? Number(directText(packageElement, 'ItemQuantity'))
        : undefined,
      LevelCode: directText(packageElement, 'LevelCode') || undefined,
      TypeCode: typeCodes[0] || undefined,
      AdditionalTypeCode: typeCodes.length > 1 ? typeCodes.slice(1) : undefined,
      Type: directChildrenByLocalName(packageElement, 'Type')
        .map(leafText)
        .filter(Boolean),
      ID: directText(packageElement, 'ID') || undefined,
      ReturnableIndicator: parseBooleanIndicatorElement(
        directChild(packageElement, 'ReturnableIndicator')
      ),
      PhysicalLogisticsShippingMarks: directChildrenByLocalName(
        packageElement,
        'PhysicalLogisticsShippingMarks'
      ).map(parseShippingMarksElement),
    };
  };

  const parseTransportCargoElement = (cargoElement: Element | undefined): TransportCargo | undefined => {
    if (!cargoElement) {
      return undefined;
    }

    return {
      Identification: directChildrenByLocalName(cargoElement, 'Identification')
        .map(leafText)
        .filter(Boolean),
      StatisticalClassificationCode: directText(cargoElement, 'StatisticalClassificationCode') || undefined,
    };
  };

  const parseMeasurementElement = (
    measurementElement: Element | undefined
  ): MeasurementType | undefined => {
    if (!measurementElement) {
      return undefined;
    }
    const measures = directChildrenByLocalName(measurementElement, 'ActualMeasure')
      .map(parseMeasureElement)
      .filter((measure): measure is MeasureType => Boolean(measure));
    return measures.length ? { ActualMeasure: measures } : undefined;
  };

  const parseDangerousGoodsElement = (
    dangerousGoodsElement: Element
  ): TransportDangerousGoods => {
    const flashpointTemperatureMeasurement = parseMeasurementElement(
      directChild(dangerousGoodsElement, 'FlashpointTemperatureMeasurement')
    );
    const transportExpertContactElement = directChild(
      dangerousGoodsElement,
      'TransportExpertTradeContact'
    );
    const emergencyContactElement = directChild(
      dangerousGoodsElement,
      'EmergencyTradeContact'
    );
    return {
      UNDGIdentificationCode: directText(dangerousGoodsElement, 'UNDGIdentificationCode'),
      RegulationCode: directText(dangerousGoodsElement, 'RegulationCode') || undefined,
      TechnicalName: directChildrenByLocalName(dangerousGoodsElement, 'TechnicalName')
        .map(leafText)
        .filter(Boolean),
      UpperPartOrangeHazardPlacardID:
        directText(dangerousGoodsElement, 'UpperPartOrangeHazardPlacardID') || undefined,
      LowerPartOrangeHazardPlacardID:
        directText(dangerousGoodsElement, 'LowerPartOrangeHazardPlacardID') || undefined,
      PackagingDangerLevelCode:
        directText(dangerousGoodsElement, 'PackagingDangerLevelCode') || undefined,
      ProperShippingName: directText(dangerousGoodsElement, 'ProperShippingName') || undefined,
      ...(transportExpertContactElement
        ? { TransportExpertTradeContact: parseContactElement(transportExpertContactElement) }
        : {}),
      ...(emergencyContactElement
        ? { EmergencyTradeContact: parseContactElement(emergencyContactElement) }
        : {}),
      ...(flashpointTemperatureMeasurement
        ? { FlashpointTemperatureMeasurement: flashpointTemperatureMeasurement }
        : {}),
    };
  };

  const parseTradeCountryElement = (
    countryElement: Element | undefined
  ): TradeCountry | undefined => {
    const ID = countryElement ? directText(countryElement, 'ID') : '';
    return ID ? { ID } : undefined;
  };

  const parseReferencedLogisticsTransportEquipmentElement = (
    equipmentElement: Element
  ): ReferencedLogisticsTransportEquipment => ({
    ID: directText(equipmentElement, 'ID') || undefined,
    LoadedPackageQuantity:
      directText(equipmentElement, 'LoadedPackageQuantity') || undefined,
  });

  const parseConsignmentItemElement = (itemElement: Element): SupplyChainConsignmentItem => ({
    SequenceNumeric: Number(directText(itemElement, 'SequenceNumeric') || '0'),
    TypeCode: directText(itemElement, 'TypeCode') || undefined,
    InvoiceAmount: directChildrenByLocalName(itemElement, 'InvoiceAmount')
      .map(parseAmountElement)
      .filter((amount): amount is { value: string; currencyID?: string } => Boolean(amount)),
    GrossWeightMeasure: parseMeasureElement(directChild(itemElement, 'GrossWeightMeasure')),
    GrossVolumeMeasure: parseMeasureElement(directChild(itemElement, 'GrossVolumeMeasure')),
    TariffQuantity: directText(itemElement, 'TariffQuantity') || undefined,
    GlobalID: directChildrenByLocalName(itemElement, 'GlobalID')
      .map(leafText)
      .filter(Boolean),
    NatureIdentificationTransportCargo: parseTransportCargoElement(
      directChild(itemElement, 'NatureIdentificationTransportCargo')
    ),
    ApplicableTransportDangerousGoods: directChildrenByLocalName(
      itemElement,
      'ApplicableTransportDangerousGoods'
    ).map(parseDangerousGoodsElement),
    HandlingInstructions: directChildrenByLocalName(itemElement, 'HandlingInstructions').map(
      parseHandlingInstructionsElement
    ),
    OriginTradeCountry: parseTradeCountryElement(
      directChild(itemElement, 'OriginTradeCountry')
    ),
    DestinationTradeCountry: parseTradeCountryElement(
      directChild(itemElement, 'DestinationTradeCountry')
    ),
    AssociatedReferencedLogisticsTransportEquipment: directChildrenByLocalName(
      itemElement,
      'AssociatedReferencedLogisticsTransportEquipment'
    ).map(parseReferencedLogisticsTransportEquipmentElement),
    AssociatedReferencedDocument: directChildrenByLocalName(
      itemElement,
      'AssociatedReferencedDocument'
    ).map(parseReferencedDocumentElement),
    TransportLogisticsPackage: directChildrenByLocalName(
      itemElement,
      'TransportLogisticsPackage'
    ).map(parsePackageElement),
    PreviousAdministrativeReferencedDocument: directChildrenByLocalName(
      itemElement,
      'PreviousAdministrativeReferencedDocument'
    ).map(parseReferencedDocumentElement),
    ApplicableNote: directChildrenByLocalName(itemElement, 'ApplicableNote').map((noteElement) =>
      parseApplicableNoteElement(noteElement)
    ),
  });

  const parseReferencedDocumentElement = (documentElement: Element): ReferencedDocument => {
    const formattedIssueDateTime = parseDateTimeElement(
      directChild(documentElement, 'FormattedIssueDateTime')
    );
    const remarks = directChildrenByLocalName(documentElement, 'Remarks')
      .map(leafText)
      .filter(Boolean);
    const ids = directTexts(documentElement, 'ID');
    const effectivePeriodElement = directChild(documentElement, 'EffectiveSpecifiedPeriod');
    const effectivePeriod = effectivePeriodElement
      ? {
          StartDateTime: parseDateTimeElement(
            directChild(effectivePeriodElement, 'StartDateTime')
          ),
          EndDateTime: parseDateTimeElement(
            directChild(effectivePeriodElement, 'EndDateTime')
          ),
        }
      : undefined;
    const attachedFiles = directChildrenByLocalName(documentElement, 'AttachedSpecifiedBinaryFile')
      .map((fileElement) => {
        const fileIds = directTexts(fileElement, 'ID');
        return {
          ID: fileIds[0] || undefined,
          AdditionalID: fileIds.length > 1 ? fileIds.slice(1) : undefined,
          URIID: directText(fileElement, 'URIID') || undefined,
          MIMECode: directText(fileElement, 'MIMECode') || undefined,
          EncodingCode: directText(fileElement, 'EncodingCode') || undefined,
          CharacterSetCode: directText(fileElement, 'CharacterSetCode') || undefined,
          SizeMeasure: parseMeasureElement(directChild(fileElement, 'SizeMeasure')),
        };
      })
      .filter((file) => Object.values(file).some(Boolean));

    return {
      TypeCode: directText(documentElement, 'TypeCode') || undefined,
      ID: ids[0] || undefined,
      AdditionalID: ids.length > 1 ? ids.slice(1) : undefined,
      Remarks: remarks.length ? remarks : undefined,
      FormattedIssueDateTime: formattedIssueDateTime as ReferencedDocument['FormattedIssueDateTime'],
      EffectiveSpecifiedPeriod:
        effectivePeriod?.StartDateTime || effectivePeriod?.EndDateTime
          ? effectivePeriod
          : undefined,
      AttachedSpecifiedBinaryFile: attachedFiles.length ? attachedFiles : undefined,
    };
  };

  const parseLogisticsServiceChargeElement = (chargeElement: Element): LogisticsServiceCharge => ({
    PaymentArrangementCode: directText(chargeElement, 'PaymentArrangementCode') || undefined,
    CategoryCode: directText(chargeElement, 'CategoryCode') || undefined,
    AppliedAmount: directChildrenByLocalName(chargeElement, 'AppliedAmount')
      .map(parseAmountElement)
      .filter((amount): amount is { value: string; currencyID?: string } => Boolean(amount)),
    PayingPartyRoleCode: directText(chargeElement, 'PayingPartyRoleCode') || undefined,
  });

  const parseLogisticsSealElement = (sealElement: Element): LogisticsSeal => ({
    ID: directText(sealElement, 'ID') || undefined,
    ConditionCode: directTexts(sealElement, 'ConditionCode'),
    SealingPartyRoleCode: directText(sealElement, 'SealingPartyRoleCode') || undefined,
  });

  const parseTransportEquipmentElement = (
    equipmentElement: Element
  ): LogisticsTransportEquipment => {
    const sealedIndicator = parseBooleanIndicatorElement(
      directChild(equipmentElement, 'SealedIndicator')
    );
    const returnableIndicator = parseBooleanIndicatorElement(
      directChild(equipmentElement, 'ReturnableIndicator')
    );
    const seals = directChildrenByLocalName(equipmentElement, 'AffixedLogisticsSeal')
      .map(parseLogisticsSealElement);
    const temperatures = directChildrenByLocalName(
      equipmentElement,
      'SettingTransportSettingTemperature'
    ).map(parseTransportSettingTemperatureElement);
    const notes = directChildrenByLocalName(equipmentElement, 'ApplicableNote')
      .map((noteElement) => parseApplicableNoteElement(noteElement));
    return {
      ID: directText(equipmentElement, 'ID') || undefined,
      CategoryCode: directText(equipmentElement, 'CategoryCode') || undefined,
      CharacteristicCode: directText(equipmentElement, 'CharacteristicCode') || undefined,
      ...(sealedIndicator !== undefined ? { SealedIndicator: sealedIndicator } : {}),
      ...(returnableIndicator !== undefined
        ? { ReturnableIndicator: returnableIndicator }
        : {}),
      ...(seals.length ? { AffixedLogisticsSeal: seals } : {}),
      ...(temperatures.length
        ? { SettingTransportSettingTemperature: temperatures }
        : {}),
      ...(notes.length ? { ApplicableNote: notes } : {}),
    };
  };

  const parseTransportMovementElement = (movementElement: Element): LogisticsTransportMovement => {
    const usedMeansElement = directChild(movementElement, 'UsedLogisticsTransportMeans');
    const meansIds = usedMeansElement ? directTexts(usedMeansElement, 'ID') : [];
    const registrationCountryElement = usedMeansElement
      ? directChild(usedMeansElement, 'RegistrationTradeCountry')
      : undefined;
    const specifiedEvents = directChildrenByLocalName(
      movementElement,
      'SpecifiedTransportEvent'
    )
      .map((eventElement) => parseTransportEventElement(eventElement))
      .filter((event): event is TransportEvent => Boolean(event));

    return {
      StageCode: directText(movementElement, 'StageCode') || undefined,
      ModeCode: directText(movementElement, 'ModeCode') || undefined,
      UsedLogisticsTransportMeans: usedMeansElement
        ? {
            TypeCode: directText(usedMeansElement, 'TypeCode') || undefined,
            ID: meansIds[0] || undefined,
            AdditionalID: meansIds.length > 1 ? meansIds.slice(1) : undefined,
            RegistrationTradeCountry: registrationCountryElement
              ? { ID: directText(registrationCountryElement, 'ID') || undefined }
              : undefined,
          }
        : undefined,
      SpecifiedTransportEvent: specifiedEvents.length ? specifiedEvents : undefined,
    };
  };

  const parseTransportEventElement = (eventElement: Element | undefined): TransportEvent | undefined => {
    if (!eventElement) {
      return undefined;
    }

    const description = directChildrenByLocalName(eventElement, 'Description')
      .map(leafText)
      .filter(Boolean);
    const specifiedInstructions = directChildrenByLocalName(
      eventElement,
      'SpecifiedTransportInstructions'
    ).map(parseTransportInstructionElement);
    const applicableNotes = directChildrenByLocalName(eventElement, 'ApplicableNote').map(
      (noteElement) => parseApplicableNoteElement(noteElement)
    );

    const scheduledOccurrenceDateTime = parseDateTimeElement(
      directChild(eventElement, 'ScheduledOccurrenceDateTime')
    );
    const estimatedOccurrenceDateTime = parseDateTimeElement(
      directChild(eventElement, 'EstimatedOccurrenceDateTime')
    );
    const actualOccurrenceDateTime = parseDateTimeElement(
      directChild(eventElement, 'ActualOccurrenceDateTime')
    );
    const occurrenceLocation = parseLocationElement(
      directChild(eventElement, 'OccurrenceLogisticsLocation')
    );
    const certifyingParties = directChildrenByLocalName(eventElement, 'CertifyingTradeParty')
      .map(parseTradePartyElement)
      .filter((party): party is TradeParty => Boolean(party));
    const event: TransportEvent = {
      ...(directText(eventElement, 'ID') ? { ID: directText(eventElement, 'ID') } : {}),
      ...(directText(eventElement, 'TypeCode')
        ? { TypeCode: directText(eventElement, 'TypeCode') }
        : {}),
      ...(scheduledOccurrenceDateTime ? { ScheduledOccurrenceDateTime: scheduledOccurrenceDateTime } : {}),
      ...(estimatedOccurrenceDateTime ? { EstimatedOccurrenceDateTime: estimatedOccurrenceDateTime } : {}),
      ...(actualOccurrenceDateTime ? { ActualOccurrenceDateTime: actualOccurrenceDateTime } : {}),
      ...(description.length ? { Description: description } : {}),
      ...(specifiedInstructions.length ? { SpecifiedTransportInstructions: specifiedInstructions } : {}),
      ...(occurrenceLocation ? { OccurrenceLogisticsLocation: occurrenceLocation } : {}),
      ...(certifyingParties.length ? { CertifyingTradeParty: certifyingParties } : {}),
      ...(applicableNotes.length ? { ApplicableNote: applicableNotes } : {}),
    };

    return Object.values(event).some(Boolean) ? event : undefined;
  };

  const parseIncludedNotes = (): Note[] => {
    if (!exchangedDocumentElement) {
      return [];
    }

    return directChildrenByLocalName(exchangedDocumentElement, 'IncludedNote').map((noteElement) =>
      parseApplicableNoteElement(noteElement, true)
    );
  };

  const includedNotes = parseIncludedNotes().filter(
    (note) => !procedureAcceptanceNoteHasInvalidArticle5Categories(note)
  );
  const associatedInvoiceAmounts = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'AssociatedInvoiceAmount')
        .map(parseAmountElement)
        .filter((amount): amount is { value: string; currencyID?: string } => Boolean(amount))
    : [];
  const applicableLogisticsServiceCharges = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'ApplicableLogisticsServiceCharge').map(
        parseLogisticsServiceChargeElement
      )
    : [];
  const associatedReferencedDocuments = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'AssociatedReferencedDocument').map(
        parseReferencedDocumentElement
      )
    : [];
  const previousAdministrativeReferencedDocuments = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'PreviousAdministrativeReferencedDocument').map(
        parseReferencedDocumentElement
      )
    : [];
  const consignmentItems = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'IncludedSupplyChainConsignmentItem').map(
        parseConsignmentItemElement
      )
    : [];
  const grossWeightMeasures = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'GrossWeightMeasure')
        .map(parseMeasureElement)
        .filter((measure): measure is MeasureType => Boolean(measure))
    : [];
  const grossVolumeMeasures = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'GrossVolumeMeasure')
        .map(parseMeasureElement)
        .filter((measure): measure is MeasureType => Boolean(measure))
    : [];
  const borderClearanceInstructions = consignmentElement
    ? directChildrenByLocalName(
        consignmentElement,
        'ConsignorProvidedBorderClearanceTransportInstructions'
      ).map(parseTransportInstructionElement)
    : [];
  const deliveryInstructions = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'DeliveryInstructions').map(
        parseDeliveryInstructionsElement
      )
    : [];
  const handlingInstructions = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'HandlingInstructions').map(
        parseHandlingInstructionsElement
      )
    : [];
  const loadingInstructions = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'LoadingTransportInstructions').map(
        parseTransportInstructionElement
      )
    : [];
  const transportEquipment = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'UtilizedLogisticsTransportEquipment').map(
        parseTransportEquipmentElement
      )
    : [];
  const transportPackages = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'TransportLogisticsPackage').map(
        parsePackageElement
      )
    : [];
  const mainCarriageMovements = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'MainCarriageLogisticsTransportMovement').map(
        parseTransportMovementElement
      )
    : [];
  const notifiedTradeParties = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'NotifiedTradeParty')
        .map(parseTradePartyElement)
        .filter((party): party is TradeParty => Boolean(party))
    : [];
  const connectingCarrierTradeParties = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'ConnectingCarrierTradeParty')
        .map(parseTradePartyElement)
        .filter((party): party is TradeParty => Boolean(party))
    : [];
  const examinationTransportEvents = consignmentElement
    ? directChildrenByLocalName(consignmentElement, 'ExaminationTransportEvent')
        .map(parseTransportEventElement)
        .filter((event): event is TransportEvent => Boolean(event))
    : [];
  const supplementaryEvidenceReferences = [
    ...associatedReferencedDocuments,
    ...previousAdministrativeReferencedDocuments,
    ...consignmentItems.flatMap((item) => item.AssociatedReferencedDocument ?? []),
    ...consignmentItems.flatMap(
      (item) => item.PreviousAdministrativeReferencedDocument ?? []
    ),
  ];
  const guidelineParameterElement = exchangedDocumentContextElement
    ? directChild(
        exchangedDocumentContextElement,
        'GuidelineSpecifiedDocumentContextParameter'
      )
    : undefined;
  const businessProcessParameters = exchangedDocumentContextElement
    ? directChildrenByLocalName(
        exchangedDocumentContextElement,
        'BusinessProcessSpecifiedDocumentContextParameter'
      )
        .map((parameterElement) => directText(parameterElement, 'ID'))
        .filter(Boolean)
        .map((ID) => ({ ID }))
    : [];
  const issueDateTime = parseDateTimeElement(
    exchangedDocumentElement
      ? directChild(exchangedDocumentElement, 'IssueDateTime')
      : undefined
  );
  const issueLocationElement = exchangedDocumentElement
    ? directChild(exchangedDocumentElement, 'IssueLogisticsLocation')
    : undefined;
  const issuerTradePartyElement = exchangedDocumentElement
    ? directChild(exchangedDocumentElement, 'IssuerTradeParty')
    : undefined;
  const recipientTradeParties = exchangedDocumentElement
    ? directChildrenByLocalName(exchangedDocumentElement, 'RecipientTradeParty')
        .map(parseTradePartyElement)
        .filter((party): party is TradeParty => Boolean(party))
    : [];

  // Extract basic document information
  const ecmr: ECMR = {
    ExchangedDocumentContext: {
      ...(exchangedDocumentContextElement &&
      directText(exchangedDocumentContextElement, 'SpecifiedTransactionID')
        ? {
            SpecifiedTransactionID: directText(
              exchangedDocumentContextElement,
              'SpecifiedTransactionID'
            ),
          }
        : {}),
      ...(businessProcessParameters.length
        ? { BusinessProcessSpecifiedDocumentContextParameter: businessProcessParameters }
        : {}),
      GuidelineSpecifiedDocumentContextParameter: [
        {
          ID:
            (guidelineParameterElement ? directText(guidelineParameterElement, 'ID') : '') ||
            'urn:un:unece:uncefact:data:standard:eCMR:100::D25A'
        }
      ]
    },
    ExchangedDocument: {
      ID: exchangedDocumentElement ? directText(exchangedDocumentElement, 'ID') : '',
      TypeCode: exchangedDocumentElement ? directText(exchangedDocumentElement, 'TypeCode') || '730' : '730',
      IssueDateTime: {
        DateTimeString: issueDateTime?.DateTimeString || ''
      },
      Remarks: exchangedDocumentElement
        ? directTexts(exchangedDocumentElement, 'Remarks')
        : [],
      IncludedNote: includedNotes,
      IssueLogisticsLocation: parseLocationElement(issueLocationElement),
      IssuerTradeParty: parseTradePartyElement(issuerTradePartyElement),
      RecipientTradeParty: recipientTradeParties.length ? recipientTradeParties : undefined,
      ...parseSignatureEvidenceNote(includedNotes)
    },
    SpecifiedSupplyChainConsignment: {
      ID: consignmentElement ? directText(consignmentElement, 'ID') : '',
      ConsignorAssignedID: consignmentElement ? directText(consignmentElement, 'ConsignorAssignedID') : '',
      CarrierAssignedID: consignmentElement ? directText(consignmentElement, 'CarrierAssignedID') : '',
      FreightForwarderAssignedID:
        consignmentElement
          ? directText(consignmentElement, 'FreightForwarderAssignedID') || undefined
          : undefined,
      GrossWeightMeasure: grossWeightMeasures,
      GrossVolumeMeasure: grossVolumeMeasures,
      ConsignmentItemQuantity:
        consignmentElement ? directText(consignmentElement, 'ConsignmentItemQuantity') || undefined : undefined,
      AssociatedInvoiceAmount: associatedInvoiceAmounts.length ? associatedInvoiceAmounts : undefined,
      CODAmount: consignmentElement
        ? parseAmountElement(directChild(consignmentElement, 'CODAmount'))
        : undefined,
      CargoInsuranceInstructionsInformation:
        consignmentElement ? directText(consignmentElement, 'CargoInsuranceInstructionsInformation') || undefined : undefined,
      TransshipmentPermissionIndicator:
        consignmentElement && directChild(consignmentElement, 'TransshipmentPermissionIndicator')
          ? directText(
              directChild(consignmentElement, 'TransshipmentPermissionIndicator')!,
              'Indicator',
              udtNamespace
            ) === 'true'
          : undefined,
      ConsignorTradeParty: parseTradePartyElement(
        consignmentElement ? directChild(consignmentElement, 'ConsignorTradeParty') : undefined
      ),
      ConsigneeTradeParty: parseTradePartyElement(
        consignmentElement ? directChild(consignmentElement, 'ConsigneeTradeParty') : undefined
      ),
      CarrierTradeParty: parseTradePartyElement(
        consignmentElement ? directChild(consignmentElement, 'CarrierTradeParty') : undefined
      ),
      NotifiedTradeParty: notifiedTradeParties.length ? notifiedTradeParties : undefined,
      ConnectingCarrierTradeParty: connectingCarrierTradeParties.length
        ? connectingCarrierTradeParties
        : undefined,
      CarrierAcceptanceLogisticsLocation: parseLocationElement(
        consignmentElement
          ? directChild(consignmentElement, 'CarrierAcceptanceLogisticsLocation')
          : undefined
      ),
      ConsigneeReceiptLogisticsLocation: parseLocationElement(
        consignmentElement
          ? directChild(consignmentElement, 'ConsigneeReceiptLogisticsLocation')
          : undefined
      ),
      IncludedSupplyChainConsignmentItem: consignmentItems,
      HandlingInstructions: handlingInstructions.length ? handlingInstructions : undefined,
      LoadingTransportInstructions: loadingInstructions.length ? loadingInstructions : undefined,
      ConsignorProvidedBorderClearanceTransportInstructions: borderClearanceInstructions.length
        ? borderClearanceInstructions
        : undefined,
      PreviousAdministrativeReferencedDocument: previousAdministrativeReferencedDocuments.length
        ? previousAdministrativeReferencedDocuments
        : undefined,
      PickUpTransportEvent: parseTransportEventElement(
        consignmentElement ? directChild(consignmentElement, 'PickUpTransportEvent') : undefined
      ),
      DeliveryTransportEvent: parseTransportEventElement(
        consignmentElement ? directChild(consignmentElement, 'DeliveryTransportEvent') : undefined
      ),
      ExaminationTransportEvent: examinationTransportEvents.length
        ? examinationTransportEvents
        : undefined,
      DeliveryInstructions: deliveryInstructions.length ? deliveryInstructions : undefined,
      AssociatedReferencedDocument: associatedReferencedDocuments.length ? associatedReferencedDocuments : undefined,
      ApplicableLogisticsServiceCharge: applicableLogisticsServiceCharges.length
        ? applicableLogisticsServiceCharges
        : undefined,
      UtilizedLogisticsTransportEquipment: transportEquipment,
      MainCarriageLogisticsTransportMovement: mainCarriageMovements,
      SpecifiedTradeDeliveryTerms: consignmentElement && directChild(
        consignmentElement,
        'SpecifiedTradeDeliveryTerms'
      )
        ? {
            ...(directChild(
              directChild(consignmentElement, 'SpecifiedTradeDeliveryTerms')!,
              'RelevantTradeLocation'
            )
              ? {
                  RelevantTradeLocation: {
                    ID:
                      directText(
                        directChild(
                          directChild(consignmentElement, 'SpecifiedTradeDeliveryTerms')!,
                          'RelevantTradeLocation'
                        )!,
                        'ID'
                      ) || undefined,
                  },
                }
              : {}),
            DeliveryTypeCode:
              directText(
                directChild(consignmentElement, 'SpecifiedTradeDeliveryTerms')!,
                'DeliveryTypeCode'
              ) || undefined,
          }
        : undefined,
      TransportLogisticsPackage: transportPackages.length ? transportPackages : undefined,
      TransportServicesBuyerTradeParty: parseTradePartyElement(
        consignmentElement
          ? directChild(consignmentElement, 'TransportServicesBuyerTradeParty')
          : undefined
      ),
      CmrOptionalParticularsApplicable: parseOptionalApplicabilityNote(includedNotes),
      CmrArticle2MultimodalCarriage: parseCmrArticle2MultimodalCarriageNote(includedNotes)
    },
    procedureAcceptances: parseProcedureAcceptanceNote(includedNotes),
    amendmentRequests: parseAmendmentEvidenceNote(includedNotes),
    carrierReservations: parseCarrierReservationEvidenceNote(includedNotes),
    deliveryConfirmation: parseProofOfDeliveryEvidenceNote(includedNotes),
    supplementaryDocuments: parseSupplementaryDocumentEvidenceNote(
      includedNotes,
      supplementaryEvidenceReferences
    ),
    auditEvents: parseAuditReviewEvidenceNote(includedNotes),
    documentTasks: parseTaskStateEvidenceNote(includedNotes),
    sourceRecordOperationalEvidence: parseSourceRecordOperationalEvidenceNote(includedNotes),
    evidenceDownloadCompletions: parseDownloadCompletionEvidenceNote(includedNotes),
    immutableStorageReferences: parseImmutableArchiveEvidenceNote(includedNotes),
    ...parseRetentionGovernanceEvidenceNote(includedNotes)
  };

  return ecmr;
};

// Cloud-only boundary: final XML downloads must use the backend archive API.
export const saveECMRAsXml = async (_ecmr: ECMR, _filename: string = 'ecmr.xml'): Promise<void> => {
  safeConsoleWarn(
    'Local XML download is disabled in cloud-only mode; use the backend final XML archive endpoint',
    'ecmr.xml.local_download_disabled'
  );
  throw new Error('Local XML export is disabled in cloud-only mode');
};

// Cargar XML desde un archivo
export const loadECMRFromXml = async (file: File): Promise<{ ecmr: ECMR, validationResult?: { valid: boolean, errors?: string[] } }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const xml = e.target?.result as string;
        // Validar el XML
        const validationResult = await validateECMR(xml);
        if (!validationResult.valid) {
          reject(new Error('Imported XML did not pass backend UNECE eCMR/CMR validation'));
          return;
        }
        const ecmr = await convertFromXml(xml);
        resolve({ ecmr, validationResult });
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };
    reader.readAsText(file);
  });
};

// Ejemplos para crear y manipular eCMRs
export const createExampleECMR = (): ECMR => {
  const now = new Date();
  const formattedDateTime = now.toISOString().replace(/\D/g, '').substring(0, 14);

  return {
    ExchangedDocumentContext: {
      GuidelineSpecifiedDocumentContextParameter: [
        {
          ID: 'urn:un:unece:uncefact:data:standard:eCMR:100::D25A'
        }
      ]
    },
    ExchangedDocument: {
      ID: `CMR-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`,
      TypeCode: '730',
      IssueDateTime: {
        DateTimeString: formattedDateTime
      },
      Remarks: ['Entrega urgente'],
      IncludedNote: [
        {
          SubjectCode: ['CMR_CONVENTION_STATEMENT'],
          Content: ['Carriage is subject to the CMR Convention provisions.']
        }
      ],
      IssueLogisticsLocation: {
        Name: 'Madrid'
      },
      IssuerTradeParty: {
        ID: ['ES12345678Z'],
        Name: ['Empresa Exportadora S.A.'],
        DefinedTradeContact: [
          {
            PersonName: ['Juan Pérez'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+34912345678'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'juan.perez@empresaexportadora.es'
            }
          }
        ]
      }
    },
    SpecifiedSupplyChainConsignment: {
      ID: `CONS-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}-${now.getFullYear()}`,
      ConsignorAssignedID: `CS-REF-${now.getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      CarrierAssignedID: `CAR-REF-${now.getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      GrossWeightMeasure: [
        {
          value: '1250.00'
        }
      ],
      GrossVolumeMeasure: [
        {
          value: '2.75'
        }
      ],
      AssociatedInvoiceAmount: [
        {
          value: '1250.00',
          currencyID: 'EUR'
        }
      ],
      CODAmount: {
        value: '275.00',
        currencyID: 'EUR'
      },
      CargoInsuranceInstructionsInformation: 'Insurance instructed by sender for declared-value carriage.',
      CmrOptionalParticularsApplicable: {
        transshipmentProhibition: true,
        senderPaidCharges: true,
        cashOnDelivery: true,
        declaredValueOrSpecialInterest: true,
        insuranceInstructions: true,
        agreedTimeLimit: true,
        documentsHandedToCarrier: true
      },
      ApplicableLogisticsServiceCharge: [
        {
          PaymentArrangementCode: 'P',
          CategoryCode: '2',
          AppliedAmount: [{ value: '950.00', currencyID: 'EUR' }],
          PayingPartyRoleCode: 'CZ'
        }
      ],
      TransshipmentPermissionIndicator: false,
      ConsignorTradeParty: {
        ID: ['ES12345678Z'],
        Name: ['Empresa Exportadora S.A.'],
        PostalTradeAddress: {
          PostcodeCode: '28001',
          StreetName: ['Calle Gran Vía 123'],
          CityName: ['Madrid'],
          CountryID: 'ES'
        },
        DefinedTradeContact: [
          {
            PersonName: ['Juan Pérez'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+34912345678'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'juan.perez@empresaexportadora.es'
            }
          }
        ]
      },
      ConsigneeTradeParty: {
        ID: ['FR87654321X'],
        Name: ['Entreprise Importatrice SARL'],
        PostalTradeAddress: {
          PostcodeCode: '75001',
          StreetName: ['Avenue des Champs-Élysées 456'],
          CityName: ['Paris'],
          CountryID: 'FR'
        },
        DefinedTradeContact: [
          {
            PersonName: ['Marie Dupont'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+33123456789'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'marie.dupont@importatrice.fr'
            }
          }
        ]
      },
      CarrierTradeParty: {
        ID: ['PT98765432Y'],
        Name: ['Transportes Internacionales S.L.'],
        PostalTradeAddress: {
          PostcodeCode: '4000-123',
          StreetName: ['Rua do Comercio 789'],
          CityName: ['Porto'],
          CountryID: 'PT'
        },
        DefinedTradeContact: [
          {
            PersonName: ['António Silva'],
            TelephoneUniversalCommunication: [
              {
                CompleteNumber: '+351987654321'
              }
            ],
            EmailURIUniversalCommunication: {
              URIID: 'antonio.silva@transportes.pt'
            }
          }
        ]
      },
      CarrierAcceptanceLogisticsLocation: {
        Name: 'Almacén Central Madrid',
        Description: 'Zona de carga puerta 5',
        PhysicalGeographicalCoordinate: {
          LatitudeMeasure: '40.4168',
          LongitudeMeasure: '-3.7038'
        },
        PostalTradeAddress: {
          PostcodeCode: '28042',
          StreetName: ['Calle Mercancías 55'],
          CityName: ['Madrid'],
          CountryID: 'ES'
        }
      },
      ConsigneeReceiptLogisticsLocation: {
        Name: 'Entrepôt Central Paris',
        Description: 'Zone de déchargement porte 3',
        PhysicalGeographicalCoordinate: {
          LatitudeMeasure: '48.8566',
          LongitudeMeasure: '2.3522'
        },
        PostalTradeAddress: {
          PostcodeCode: '75015',
          StreetName: ['Rue de Commerce 123'],
          CityName: ['Paris'],
          CountryID: 'FR'
        }
      },
      AssociatedReferencedDocument: [
        {
          ID: `INV-${now.getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
          TypeCode: '380',
          Remarks: ['Invoice handed to carrier with the goods.'],
          FormattedIssueDateTime: {
            DateTimeString: now.toISOString().substring(0, 10).replace(/-/g, '')
          }
        }
      ],
      HandlingInstructions: [
        {
          Description: ['Manipular con cuidado. Producto frágil.']
        }
      ],
      ConsignorProvidedBorderClearanceTransportInstructions: [
        {
          DescriptionCode: 'CUS',
          Description: 'Customs export declaration provided to carrier.'
        }
      ],
      DeliveryInstructions: [
        {
          Description: ['Agreed delivery time limit: deliver within the scheduled two-day carriage window.']
        }
      ],
      PickUpTransportEvent: {
        ScheduledOccurrenceDateTime: {
          DateTimeString: formattedDateTime
        }
      },
      DeliveryTransportEvent: {
        ScheduledOccurrenceDateTime: {
          DateTimeString: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000)
            .toISOString()
            .replace(/\D/g, '')
            .substring(0, 14)
        }
      },
      IncludedSupplyChainConsignmentItem: [
        {
          SequenceNumeric: 1,
          TypeCode: 'componentes electrónicos',
          GrossWeightMeasure: {
            value: '750.00'
          },
          TransportLogisticsPackage: [
            {
              ID: 'PKG-001',
              TypeCode: 'PK',
              ItemQuantity: 25,
              Description: 'Cajas de componentes electrónicos'
            }
          ]
        },
        {
          SequenceNumeric: 2,
          TypeCode: 'equipamiento industrial',
          GrossWeightMeasure: {
            value: '500.00'
          },
          TransportLogisticsPackage: [
            {
              ID: 'PKG-002',
              TypeCode: 'CT',
              ItemQuantity: 10,
              Description: 'Cartones de equipamiento industrial'
            }
          ]
        }
      ],
      UtilizedLogisticsTransportEquipment: [
        {
          ID: 'ES-2345-ABC',
          CategoryCode: 'TE',
          CharacteristicCode: '6',
          OperatingPartyTradeParty: {
            Name: ['Transportes Internacionales S.L.'],
            ID: ['PT98765432Y']
          }
        }
      ],
      MainCarriageLogisticsTransportMovement: [
        {
          ID: 'ROUTE-ES-FR-001',
          ModeCode: '3',
          UsedLogisticsTransportMeans: {
            ID: 'ES-2345-ABC',
            TypeCode: '165',
            Name: 'Scania R450'
          },
          DriverPerson: {
            ID: 'LIC-123456789',
            Name: 'Carlos Rodríguez'
          }
        }
      ],
      SpecifiedTradeDeliveryTerms: {
        DeliveryTypeCode: 'DAP'
      }
    }
  };
};

// Cloud-only compatibility boundary: legacy document localStorage helpers are intentionally inert.
// Browser storage must not act as a production document repository, sync fallback, or evidence store.
export const saveECMRToLocalStorage = (_ecmr: ECMR, _key: string = 'savedECMR'): void => undefined;

export const loadECMRFromLocalStorage = (_key: string = 'savedECMR'): ECMR | null => null;

export const getAllSavedECMRKeys = (): string[] => [];

export const saveECMR = (_ecmr: ECMR): string => '';

export const getECMRList = (): { id: string, title: string, date: string }[] => [];

const firstNonEmpty = (values?: Array<string | undefined>): string | undefined =>
  values?.map(value => value?.trim()).find(Boolean);

const normalizeParticipantEmail = (value?: string): string | undefined => {
  const normalized = value?.trim().replace(/^mailto:/i, '');
  return normalized || undefined;
};

const extractPartyEmail = (party?: TradeParty): string | undefined =>
  party?.DefinedTradeContact
    ?.map(contact => normalizeParticipantEmail(contact.EmailURIUniversalCommunication?.URIID))
    .find(Boolean);

const createParticipantFromParty = (
  role: EcmrParticipant['role'],
  party?: TradeParty
): EcmrParticipant | undefined => {
  const name = firstNonEmpty(party?.Name);
  const email = extractPartyEmail(party);

  if (!name && !email) {
    return undefined;
  }

  return {
    role,
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
  };
};

const createConsignorParticipantFromUser = (user?: User | null): EcmrParticipant | undefined => {
  if (!user) {
    return undefined;
  }

  return {
    role: 'consignor',
    userId: user.id,
    ...(user.name ? { name: user.name } : {}),
    ...(user.email ? { email: user.email } : {}),
  };
};

export const deriveParticipantsFromECMR = (
  ecmr: ECMR,
  currentUser: User | null = authService.getUser()
): EcmrParticipant[] => {
  const consignment = ecmr.SpecifiedSupplyChainConsignment;
  const consignor = createParticipantFromParty('consignor', consignment.ConsignorTradeParty)
    ?? createConsignorParticipantFromUser(currentUser);
  const carrier = createParticipantFromParty('carrier', consignment.CarrierTradeParty);
  const consignee = createParticipantFromParty('consignee', consignment.ConsigneeTradeParty);

  return [consignor, carrier, consignee].filter((participant): participant is EcmrParticipant => !!participant);
};

// Función para asegurarse de que todas las partes importantes de un ECMR existen
export const ensureECMRStructure = (ecmr: ECMR): ECMR => {
  if (!ecmr.ExchangedDocumentContext) {
    ecmr.ExchangedDocumentContext = {
      GuidelineSpecifiedDocumentContextParameter: [
        {
          ID: 'urn:un:unece:uncefact:data:standard:eCMR:100::D25A',
        }
      ]
    };
  }

  // Asegurar que ExchangedDocument existe
  if (!ecmr.ExchangedDocument) {
    ecmr.ExchangedDocument = {
      ID: '',
      TypeCode: '730',
      IssueDateTime: {
        DateTimeString: compactDateTimeString()
      },
      Remarks: [],
      IncludedNote: [],
      IssueLogisticsLocation: {
        Name: ''
      },
      IssuerTradeParty: {
        ID: [''],
        Name: [''],
        DefinedTradeContact: []
      }
    };
  } else {
    // Asegurar que IssueDateTime existe
    if (!ecmr.ExchangedDocument.IssueDateTime) {
      ecmr.ExchangedDocument.IssueDateTime = {
        DateTimeString: compactDateTimeString()
      };
    }
    
    // Asegurar que Remarks existe
    if (!ecmr.ExchangedDocument.Remarks) {
      ecmr.ExchangedDocument.Remarks = [];
    }
  }

  // Asegurar que las partes esenciales de la consignación existen
  if (!ecmr.SpecifiedSupplyChainConsignment) {
    ecmr.SpecifiedSupplyChainConsignment = {
      ID: '',
      ConsignorAssignedID: '',
      CarrierAssignedID: '',
      GrossWeightMeasure: [],
      TransshipmentPermissionIndicator: false,
      ConsignorTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      ConsigneeTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      CarrierTradeParty: {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      },
      IncludedSupplyChainConsignmentItem: [],
      UtilizedLogisticsTransportEquipment: [],
      MainCarriageLogisticsTransportMovement: []
    };
  } else {
    // Asegurar que ConsignorTradeParty existe
    if (!ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty) {
      ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty = {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      };
    }

    // Asegurar que ConsigneeTradeParty existe
    if (!ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty) {
      ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty = {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      };
    }

    // Asegurar que CarrierTradeParty existe
    if (!ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty) {
      ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty = {
        ID: [''],
        Name: [''],
        PostalTradeAddress: {
          PostcodeCode: '',
          StreetName: [''],
          CityName: [''],
          CountryID: ''
        },
        DefinedTradeContact: []
      };
    }
    
    // Asegurar que los arrays existen
    if (!ecmr.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem) {
      ecmr.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem = [];
    }
    
    if (!ecmr.SpecifiedSupplyChainConsignment.UtilizedLogisticsTransportEquipment) {
      ecmr.SpecifiedSupplyChainConsignment.UtilizedLogisticsTransportEquipment = [];
    }
    
    if (!ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement) {
      ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement = [];
    }
  }

  return ecmr;
};

// Legacy demo seeding is disabled in cloud-only mode. Demo/example documents must be created
// through authenticated backend APIs or explicit non-production fixtures, not browser storage.
export const loadExampleECMRs = (): string[] => [];

// API Integration Functions
export type DocumentLifecycleStatus =
  | 'draft'
  | 'issued'
  | 'accepted_by_carrier'
  | 'in_transit'
  | 'delivered'
  | 'closed'
  | 'amended'
  | 'cancelled';

const DOCUMENT_LIFECYCLE_STATUSES: DocumentLifecycleStatus[] = [
  'draft',
  'issued',
  'accepted_by_carrier',
  'in_transit',
  'delivered',
  'closed',
  'amended',
  'cancelled',
];

export const normalizeDocumentLifecycleStatus = (status?: string): DocumentLifecycleStatus =>
  DOCUMENT_LIFECYCLE_STATUSES.includes(status as DocumentLifecycleStatus)
    ? status as DocumentLifecycleStatus
    : 'draft';

export interface ApiDocument {
  document_id?: string;
  documentId?: string;
  title?: string;
  content?: string;
  xmlContent?: string;
  type?: string;
  status: DocumentLifecycleStatus;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  user_id?: string;
  userId?: string;
  participants?: EcmrParticipant[];
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  evidenceManifest?: PublicEvidenceManifestSummary;
  evidenceSections?: PublicEvidenceManifestSections;
  currentVersion?: DocumentVersionEvidence;
  procedureAcceptances?: ProcedureSetAcceptanceEvidence[];
  procedureAcceptanceRecords?: ProcedureSetAcceptanceEvidence[];
  signatureEvidence?: SignatureEvidence[];
  signatureEvidenceRecords?: SignatureEvidence[];
  carrierTakeoverCheckRequestRecords?: CarrierTakeoverCheckRequestEvidence[];
  carrierTakeoverCheckRecords?: CarrierTakeoverCheckEvidence[];
  carrierReservationAcknowledgementRecords?: CarrierReservationAcknowledgementEvidence[];
  carrierReservations?: CarrierReservationEvidence[];
  disposalInstructionRecords?: DisposalInstructionEvidence[];
  disposalExecutionNoticeRecords?: DisposalExecutionNoticeEvidence[];
  carriageImpedimentRecords?: CarriageImpedimentEvidence[];
  carriageAccountingRecords?: CarriageAccountingEvidence[];
  carrierLiabilityIncidentRecords?: CarrierLiabilityIncidentEvidence[];
  article18_2ClaimantRebuttalRecords?: Article18_2ClaimantRebuttalEvidence[];
  deliveryTimingAssessmentRecords?: DeliveryTimingAssessmentEvidence[];
  compensationPaymentRecords?: CompensationPaymentEvidence[];
  recoveryDeliverySettlementRecords?: RecoveryDeliverySettlementEvidence[];
  recoveredGoodsDispositionRecords?: RecoveredGoodsDispositionEvidence[];
  codDeliveryOmissionRecords?: CodDeliveryOmissionEvidence[];
  dangerousGoodsNoticeRecords?: DangerousGoodsNoticeEvidence[];
  compensationAssessmentRecords?: CompensationAssessmentEvidence[];
  compensationInterestRecords?: CompensationInterestEvidence[];
  liabilityLimitationRecords?: LiabilityLimitationEvidence[];
  claimReservationRecords?: ClaimReservationEvidence[];
  disputeProcedureRecords?: DisputeProcedureEvidence[];
  successiveCarrierRecords?: SuccessiveCarrierEvidence[];
  multimodalCarriageRecords?: MultimodalCarriageEvidence[];
  consignmentNoteCopyRecords?: ConsignmentNoteCopyEvidence[];
  ecmrReceiptAccessRequestRecords?: EcmrReceiptAccessRequestEvidence[];
  ecmrReceiptAccessRecords?: EcmrReceiptAccessEvidence[];
  senderParticularsResponsibilityRecords?: SenderParticularsResponsibilityEvidence[];
  prohibitedClauseRecords?: ProhibitedClauseEvidence[];
  consigneeDeliveryRequestRecords?: ConsigneeDeliveryRequestEvidence[];
  xmlValidationRecords?: XmlValidationEvidence[];
  cmrBusinessValidationRecords?: CmrBusinessValidationEvidence[];
  ecmrProtocolValidationRecords?: EcmrProtocolValidationEvidence[];
  deliveryReservationAcknowledgementRecords?: DeliveryReservationAcknowledgementEvidence[];
  deliveryConfirmation?: DeliveryConfirmationEvidence;
  amendmentRequests?: AmendmentEvidence[];
  retentionPolicy?: RetentionPolicyEvidence;
  legalHold?: LegalHoldEvidence;
}

export interface SignatureEvidence {
  signatureId?: string;
  documentVersionId?: string;
  signerUserId?: string;
  signerRole?: string;
  signedDigest?: string;
  signedDigestAvailable?: boolean;
  digestAlgorithm?: string;
  signatureMethod?: string;
  providerMetadata?: Record<string, unknown>;
  timestamp?: string;
  verificationStatus?: 'verified' | 'invalid' | 'pending' | string;
  contractParticipantReferencePresent?: boolean;
  reason?: string;
}

export type SignatureCreationMethod = 'simple-electronic-attestation';

const DEFAULT_SIGNATURE_CREATION_METHOD: SignatureCreationMethod = 'simple-electronic-attestation';

export interface SignatureVerificationResult {
  valid: boolean;
  integrity?: {
    valid: boolean;
  };
  signatures: PublicSignatureVerificationEvidence[];
  count: number;
}

export type CarrierReservationCategory =
  | 'package_count'
  | 'marks_numbers'
  | 'condition'
  | 'packaging'
  | 'unable_to_verify'
  | 'other';

export interface CarrierReservationRequest {
  category: CarrierReservationCategory;
  grounds: string;
  packageReference?: string;
  observedValue?: string;
  expectedValue?: string;
}

export interface CarrierReservationEvidence {
  reservationId?: string;
  reservationReferencePresent?: boolean;
  category?: CarrierReservationCategory | string;
  grounds?: string;
  packageReference?: string;
  observedValue?: string;
  expectedValue?: string;
  createdBy?: string;
  createdByRoles?: string[];
  createdAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtReservation?: DocumentLifecycleStatus | string;
  actorRoleCount?: number;
  optionalEvidencePresent?: boolean;
}

export type CarrierReservationAcknowledgementDecision =
  | 'acknowledged'
  | 'rejected';

export interface CarrierReservationAcknowledgementRequest {
  decision: CarrierReservationAcknowledgementDecision;
  comment?: string;
  clientReferenceId?: string;
}

export interface CarrierReservationAcknowledgementEvidence {
  acknowledgementId?: string;
  acknowledgementReferencePresent?: boolean;
  decision?: CarrierReservationAcknowledgementDecision | string;
  acknowledgedAt?: string;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAcknowledgement?: DocumentLifecycleStatus | string;
  actorRoleCount?: number;
  reservationReferencePresent?: boolean;
  deliveryReferencePresent?: boolean;
}

export type CarrierTakeoverCheckResult =
  | 'accepted'
  | 'reservation_recorded'
  | 'unable_to_verify';

export interface CarrierTakeoverCheckRequest {
  result: CarrierTakeoverCheckResult;
  packageCountChecked: boolean;
  marksAndNumbersChecked: boolean;
  apparentConditionChecked: boolean;
  grossWeightChecked?: boolean;
  quantityChecked?: boolean;
  contentsChecked?: boolean;
  checkedAt?: string;
  reservationId?: string;
  comment?: string;
  clientReferenceId?: string;
}

export interface CarrierTakeoverCheckEvidence {
  takeoverCheckId?: string;
  takeoverCheckReferencePresent?: boolean;
  result?: CarrierTakeoverCheckResult | string;
  checkedAt?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtCheck?: DocumentLifecycleStatus | string;
  packageCountChecked?: boolean;
  marksAndNumbersChecked?: boolean;
  apparentConditionChecked?: boolean;
  grossWeightChecked?: boolean;
  quantityChecked?: boolean;
  contentsChecked?: boolean;
  actorRoleCount?: number;
  reservationReferencePresent?: boolean;
  commentPresent?: boolean;
}

export type CarrierTakeoverRequestedCheck =
  | 'gross_weight'
  | 'quantity'
  | 'contents';

export interface CarrierTakeoverCheckRequestRequest {
  requestedChecks: CarrierTakeoverRequestedCheck[];
  reason?: string;
  clientReferenceId?: string;
}

export interface CarrierTakeoverCheckRequestEvidence {
  checkRequestId?: string;
  checkRequestReferencePresent?: boolean;
  requestedChecks?: CarrierTakeoverRequestedCheck[] | string[];
  requestedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRequest?: DocumentLifecycleStatus | string;
  requestedCheckCount?: number;
  grossWeightRequested?: boolean;
  quantityRequested?: boolean;
  contentsRequested?: boolean;
  actorRoleCount?: number;
  reasonPresent?: boolean;
}

export type DeliveryReservationCategory =
  | 'damage'
  | 'shortage'
  | 'late'
  | 'unable_to_verify'
  | 'other';

export interface DeliveryLocationEvidence {
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

export interface DeliveryReservationRequest {
  category: DeliveryReservationCategory;
  grounds: string;
  packageReference?: string;
  observedValue?: string;
  expectedValue?: string;
}

export interface DeliveryConfirmationRequest {
  deliveredAt: string;
  recipientName: string;
  deliveryLocation: DeliveryLocationEvidence;
  signatureMethod?: SignatureCreationMethod;
  providerMetadata?: Record<string, unknown>;
  deliveryReservations?: DeliveryReservationRequest[];
  reason?: string;
}

export interface DeliveryReservationEvidence extends DeliveryReservationRequest {
  reservationId?: string;
  reservationReferencePresent?: boolean;
}

export interface DeliveryConfirmationEvidence {
  deliveryId?: string;
  deliveryReferencePresent?: boolean;
  deliveredAt?: string;
  recipientName?: string;
  recipientNamePresent?: boolean;
  deliveryLocation?: DeliveryLocationEvidence;
  deliveryLocationPresent?: boolean;
  confirmedBy?: string;
  confirmedByRoles?: string[];
  confirmedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtConfirmation?: DocumentLifecycleStatus | string;
  signatureEvidence?: SignatureEvidence;
  deliverySignatureAvailable?: boolean;
  signatureMethod?: string;
  actorRoleCount?: number;
  reservationCount?: number;
  deliveryReservations?: DeliveryReservationEvidence[];
}

export type DeliveryReservationAcknowledgementDecision =
  | 'acknowledged'
  | 'rejected';

export interface DeliveryReservationAcknowledgementRequest {
  decision: DeliveryReservationAcknowledgementDecision;
  comment?: string;
  clientReferenceId?: string;
}

export interface DeliveryReservationAcknowledgementEvidence {
  acknowledgementId?: string;
  acknowledgementReferencePresent?: boolean;
  decision?: DeliveryReservationAcknowledgementDecision | string;
  acknowledgedAt?: string;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAcknowledgement?: DocumentLifecycleStatus | string;
  actorRoleCount?: number;
  reservationReferencePresent?: boolean;
  deliveryReferencePresent?: boolean;
}

export interface SupplementaryDocumentUploadRequest {
  fileName: string;
  mimeType: string;
  contentBase64: string;
  documentType?: string;
  description?: string;
}

export interface SupplementaryDocumentEvidence {
  attachmentId: string;
  ecmrLinkReference?: string;
  ecmrLinkReferencePresent?: boolean;
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  digestAlgorithm?: string;
  sha256Digest?: string;
  digestAvailable?: boolean;
  payloadStoredExternally?: boolean;
  evidenceType?: string;
  documentVersionId?: string;
  documentVersionReferenceAvailable?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  uploadedBy?: string;
  uploadedByRoles?: string[];
  uploadedAt?: string;
  storageBucket?: string;
  storageKey?: string;
  documentType?: string;
  description?: string;
}

export interface SupplementaryDocumentDownloadResult {
  downloadUrl: string;
  expiresIn?: number;
}

export interface SupplementaryDocumentUploadIntentRequest {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sha256Digest?: string;
  documentType?: string;
  description?: string;
}

export interface SupplementaryDocumentUploadIntent {
  attachmentId: string;
  uploadUrl: string;
  method: 'PUT' | string;
  expiresInSeconds: number;
  requiredHeaders?: Record<string, string>;
}

export interface SupplementaryDocumentDirectUploadConfirmationRequest extends SupplementaryDocumentUploadIntentRequest {
  sha256Digest: string;
}

export interface RetentionPolicyRequest {
  retentionDays: number;
  jurisdiction?: string;
  reason?: string;
}

export interface RetentionPolicyEvidence extends Partial<RetentionPolicyRequest> {
  policyId?: string;
  retentionPolicyReferencePresent?: boolean;
  reasonPresent?: boolean;
  retentionMode?: string;
  retainUntil?: string;
  retainUntilPresent?: boolean;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  createdBy?: string;
  createdAt?: string;
  status?: 'active' | string;
}

export interface LegalHoldEvidence {
  holdId?: string;
  legalHoldReferencePresent?: boolean;
  active: boolean;
  reason?: string;
  reasonPresent?: boolean;
  appliedBy?: string;
  appliedAt?: string;
  appliedAtPresent?: boolean;
  releasedBy?: string;
  releasedAt?: string;
  releasedAtPresent?: boolean;
  releaseReason?: string;
  releaseReasonPresent?: boolean;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  history?: Array<Record<string, unknown>>;
  historyCount?: number;
}

const publicRetentionPolicyEvidenceFromApi = (policy: unknown): RetentionPolicyEvidence => {
  if (!policy || typeof policy !== 'object') {
    return {};
  }
  const source = policy as Record<string, unknown>;
  const result: RetentionPolicyEvidence = {};
  const retentionDays = safePublicManifestCount(source.retentionDays);
  if (retentionDays !== undefined) {
    result.retentionDays = retentionDays;
  }
  const jurisdiction = safeManifestJurisdiction(source.jurisdiction);
  if (jurisdiction) {
    result.jurisdiction = jurisdiction;
  }
  const retentionMode = safeManifestLabel(source.retentionMode, retentionModes);
  if (retentionMode) {
    result.retentionMode = retentionMode;
  }
  const status = safeManifestLabel(source.status, retentionPolicyStatuses);
  if (status) {
    result.status = status;
  }
  if (source.retentionPolicyReferencePresent === true || typeof source.policyId === 'string') {
    result.retentionPolicyReferencePresent = true;
  }
  if (source.reasonPresent === true) {
    result.reasonPresent = true;
  }
  if (source.retainUntilPresent === true || typeof source.retainUntil === 'string') {
    result.retainUntilPresent = true;
  }
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  return result;
};

const publicLegalHoldEvidenceFromApi = (legalHold: unknown): LegalHoldEvidence => {
  if (!legalHold || typeof legalHold !== 'object') {
    return { active: false };
  }
  const source = legalHold as Record<string, unknown>;
  const result: LegalHoldEvidence = {
    active: source.active === true,
  };
  if (source.legalHoldReferencePresent === true || typeof source.holdId === 'string') {
    result.legalHoldReferencePresent = true;
  }
  if (source.reasonPresent === true) {
    result.reasonPresent = true;
  }
  if (source.appliedAtPresent === true || typeof source.appliedAt === 'string') {
    result.appliedAtPresent = true;
  }
  if (source.releasedAtPresent === true || typeof source.releasedAt === 'string') {
    result.releasedAtPresent = true;
  }
  if (source.releaseReasonPresent === true) {
    result.releaseReasonPresent = true;
  }
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  const historyCount = safePublicManifestCount(source.historyCount);
  if (historyCount !== undefined) {
    result.historyCount = historyCount;
  }
  return result;
};

export interface ActiveProcedureSet {
  procedureSetId: string;
  version: string;
  title?: string;
  procedures: Record<string, string>;
  article5Categories?: Record<string, {
    article?: string;
    label?: string;
    procedureKeys?: string[];
  }>;
  sha256Digest?: string;
  digestAvailable?: boolean;
  legalDisclaimer?: string;
  active?: boolean;
}

export interface DocumentReferencedProcedureSets {
  procedureSets: ActiveProcedureSet[];
  procedureSetReferenceCount: number;
  resolvedProcedureSetCount: number;
  unresolvedProcedureSetCount: number;
  allProcedureSetReferencesResolved: boolean;
}

export interface ProcedureSetAcceptanceEvidence {
  acceptanceId?: string;
  procedureAcceptanceReferencePresent?: boolean;
  procedureSetId?: string;
  procedureSetReferencePresent?: boolean;
  procedureSetVersion?: string;
  procedureSetDigest?: string;
  procedureSetDigestAvailable?: boolean;
  article5CategoryCoverage?: string[];
  acceptedBy?: string;
  acceptedByRoles?: string[];
  accepted?: boolean;
  acceptedAt?: string;
  acceptedAtPresent?: boolean;
  contractParticipantReferencePresent?: boolean;
}

export type ReplacementByOtherMeansMedium =
  | 'paper'
  | 'pdf_export'
  | 'other_electronic_system'
  | 'authority_request';

export interface ReplacementByOtherMeansRequest {
  replacementMedium: ReplacementByOtherMeansMedium;
  reason: string;
  replacedAt?: string;
  externalReference?: string;
  clientReferenceId?: string;
}

export interface ReplacementByOtherMeansEvidence {
  replacementId?: string;
  replacementReferencePresent?: boolean;
  replacementMedium: ReplacementByOtherMeansMedium | string;
  replacementStatus?: 'recorded' | string;
  recordedAt?: string;
  replacedAt?: string;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtReplacement?: string;
  actorRoleCount?: number;
  reasonPresent?: boolean;
  externalReferencePresent?: boolean;
}

export type DisposalInstructionType =
  | 'stop_in_transit'
  | 'change_delivery_place'
  | 'change_consignee';

export interface DisposalInstructionRequest {
  instructionType: DisposalInstructionType;
  rightOfDisposalNotCeased: true;
  firstCopyInstructionProduced: true;
  carrierIndemnityAccepted: true;
  instructionFeasible: true;
  doesNotPrejudiceOtherConsignments: true;
  doesNotDivideConsignment: true;
  linkedConsignmentNoteCopyRecordId: string;
  requestedExecutionAt?: string;
  reason?: string;
  newDeliveryPlace?: string | Record<string, string | number>;
  newConsignee?: string | Record<string, string | number>;
  successiveConsigneeInstructionFinal?: boolean;
  clientReferenceId?: string;
}

export interface DisposalInstructionEvidence {
  instructionId?: string;
  instructionReferencePresent?: boolean;
  instructionType: DisposalInstructionType | string;
  instructionStatus?: 'recorded' | string;
  recordedAt?: string;
  requestedExecutionAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  consignmentNoteCopyReferencePresent?: boolean;
  statusAtInstruction?: DocumentLifecycleStatus | string;
  actorRoleCount?: number;
  rightOfDisposalNotCeased?: boolean;
  firstCopyInstructionProduced?: boolean;
  carrierIndemnityAccepted?: boolean;
  instructionFeasible?: boolean;
  doesNotPrejudiceOtherConsignments?: boolean;
  doesNotDivideConsignment?: boolean;
  successiveConsigneeInstructionFinal?: boolean;
  reasonPresent?: boolean;
  newDeliveryPlacePresent?: boolean;
  newConsigneePresent?: boolean;
}

export type DisposalExecutionNoticeType = 'unable_to_execute';

export type DisposalExecutionNoticeTargetRole =
  | 'article12_entitled_person'
  | 'consignor'
  | 'consignee'
  | 'owner';

export interface DisposalExecutionNoticeRequest {
  noticeType: DisposalExecutionNoticeType;
  unableToExecute: true;
  notificationSent: true;
  notificationSentAt: string;
  notificationTargetRole: DisposalExecutionNoticeTargetRole;
  reason?: string;
  notificationDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface DisposalExecutionNoticeEvidence {
  noticeId?: string;
  noticeReferencePresent?: boolean;
  noticeType: DisposalExecutionNoticeType | string;
  noticeStatus?: 'recorded' | string;
  recordedAt?: string;
  notificationSentAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtNotice?: DocumentLifecycleStatus | string;
  instructionType?: DisposalInstructionType | string;
  unableToExecute?: boolean;
  notificationSent?: boolean;
  notificationTargetRole?: DisposalExecutionNoticeTargetRole | string;
  actorRoleCount?: number;
  instructionReferencePresent?: boolean;
  reasonPresent?: boolean;
  notificationDetailPresent?: boolean;
}

export type CarriageImpedimentType =
  | 'carriage_impossible_before_arrival'
  | 'delivery_prevented_after_arrival'
  | 'consignee_refusal'
  | 'alternative_carriage_taken'
  | 'unload_for_account'
  | 'sale_of_goods';

export type CarriageImpedimentInstructionTargetRole =
  | 'article12_entitled_person'
  | 'sender'
  | 'consignee_as_sender';

export type CarriageImpedimentSaleReason =
  | 'perishable'
  | 'condition_warrants_sale'
  | 'storage_expenses_disproportionate'
  | 'reasonable_period_without_instructions';

export type CarriageImpedimentSaleProcedureBasis =
  | 'applicable_law'
  | 'local_custom'
  | 'applicable_law_and_local_custom';

export interface CarriageImpedimentRequest {
  impedimentType: CarriageImpedimentType;
  instructionsRequested?: boolean;
  instructionRequestTargetRole?: CarriageImpedimentInstructionTargetRole;
  instructionRequestSentAt?: string;
  instructionRequestReceivedAt?: string;
  linkedInstructionRequestEvidenceAttachmentId?: string;
  instructionsUnavailableInReasonableTime?: boolean;
  bestInterestsActionTaken?: boolean;
  arrivalAtDeliveryPlace?: boolean;
  consigneeRefusedGoods?: boolean;
  article14Or15ImpedimentBasis?: boolean;
  goodsUnloadedForAccount?: boolean;
  goodsHeldForEntitledPerson?: boolean;
  thirdPartyCustodianUsed?: boolean;
  thirdPartyCustodianSelectedWithReasonableCare?: boolean;
  saleReason?: CarriageImpedimentSaleReason;
  goodsLocationCountryCode?: string;
  goodsLocationDetail?: string | Record<string, string | number>;
  saleProcedureBasis?: CarriageImpedimentSaleProcedureBasis;
  saleProcedureReference?: string | Record<string, string | number>;
  saleProcedureAssessedAt?: string;
  linkedSaleProcedureEvidenceAttachmentId?: string;
  proceedsPlacedAtDisposal?: boolean;
  saleProcedureLawOrCustomAcknowledged?: boolean;
  expensesRecoverableClaimed?: boolean;
  reason?: string;
  instructionRequestDetail?: string | Record<string, string | number>;
  alternativeCarriageConditions?: string | Record<string, string | number>;
  storageOrSaleDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface CarriageImpedimentEvidence {
  impedimentId?: string;
  impedimentReferencePresent?: boolean;
  impedimentType: CarriageImpedimentType | string;
  impedimentStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtImpediment?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  instructionsRequested?: boolean;
  instructionRequestTargetRole?: CarriageImpedimentInstructionTargetRole | string;
  instructionRequestSentAtPresent?: boolean;
  instructionRequestReceivedAtPresent?: boolean;
  instructionRequestEvidenceAttachmentReferencePresent?: boolean;
  instructionRequestChronologyValid?: boolean;
  instructionsUnavailableInReasonableTime?: boolean;
  bestInterestsActionTaken?: boolean;
  arrivalAtDeliveryPlace?: boolean;
  consigneeRefusedGoods?: boolean;
  article14Or15ImpedimentBasis?: boolean;
  goodsUnloadedForAccount?: boolean;
  goodsHeldForEntitledPerson?: boolean;
  thirdPartyCustodianUsed?: boolean;
  thirdPartyCustodianSelectedWithReasonableCare?: boolean;
  saleReason?: CarriageImpedimentSaleReason | string;
  saleProcedureBasis?: CarriageImpedimentSaleProcedureBasis | string;
  goodsLocationCountryCodePresent?: boolean;
  goodsLocationDetailPresent?: boolean;
  saleProcedureReferencePresent?: boolean;
  saleProcedureAssessedAtPresent?: boolean;
  saleProcedureEvidenceAttachmentReferencePresent?: boolean;
  saleProcedureChronologyValid?: boolean;
  proceedsPlacedAtDisposal?: boolean;
  saleProcedureLawOrCustomAcknowledged?: boolean;
  expensesRecoverableClaimed?: boolean;
  reasonPresent?: boolean;
  instructionRequestDetailPresent?: boolean;
  alternativeCarriageConditionsPresent?: boolean;
  storageOrSaleDetailPresent?: boolean;
}

export type CarriageAccountingType =
  | 'instruction_expense'
  | 'goods_disposition_expense'
  | 'sale_proceeds'
  | 'charge_shortfall';

export interface CarriageAccountingRequest {
  accountingType: CarriageAccountingType;
  amount: string | number;
  currencyID: string;
  accountingAt: string;
  linkedAccountingEvidenceAttachmentId: string;
  carrierWrongfulActOrNeglectNotAsserted?: boolean;
  chargeableAgainstGoods?: boolean;
  proceedsPlacedAtDisposal?: boolean;
  chargesExceedProceeds?: boolean;
  grossSaleProceedsAmount?: string | number;
  deductedSaleExpensesAmount?: string | number;
  linkedSaleProceedsAccountingId?: string;
  totalChargesDueAmount?: string | number;
  accountingDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface CarriageAccountingEvidence {
  accountingType: CarriageAccountingType | string;
  accountingStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAccounting?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  linkedImpedimentType?: CarriageImpedimentType | string;
  actorRoleCount?: number;
  impedimentReferencePresent?: boolean;
  amountPresent?: boolean;
  positiveAmount?: boolean;
  currencyPresent?: boolean;
  recognizedCurrency?: boolean;
  accountingAtPresent?: boolean;
  accountingEvidenceAttachmentReferencePresent?: boolean;
  accountingChronologyValid?: boolean;
  instructionRequestEvidencePresent?: boolean;
  grossSaleProceedsAmountPresent?: boolean;
  deductedSaleExpensesAmountPresent?: boolean;
  netSaleProceedsReconciled?: boolean;
  saleProceedsAccountingReferencePresent?: boolean;
  totalChargesDueAmountPresent?: boolean;
  chargeShortfallReconciled?: boolean;
  carrierWrongfulActOrNeglectNotAsserted?: boolean;
  chargeableAgainstGoods?: boolean;
  proceedsPlacedAtDisposal?: boolean;
  chargesExceedProceeds?: boolean;
  accountingDetailPresent?: boolean;
}

export type CarrierLiabilityIncidentType =
  | 'total_loss'
  | 'partial_loss'
  | 'damage'
  | 'delay'
  | 'article12_disposal_instruction_noncompliance';

export type CarrierLiabilityIncidentSpecialRisk =
  | 'open_unsheeted_vehicle'
  | 'defective_or_missing_packing'
  | 'sender_loading_handling_stowage_unloading'
  | 'goods_nature'
  | 'insufficient_marks_numbers'
  | 'livestock';

export type CarrierLiabilityArticle17_4cActorCapacity =
  | 'consignor'
  | 'consignee'
  | 'acting_on_behalf_of_consignor'
  | 'acting_on_behalf_of_consignee';

export type CarrierLiabilityArticle17_4dSusceptibilityBasis =
  | 'breakage'
  | 'rust'
  | 'decay'
  | 'desiccation'
  | 'leakage'
  | 'normal_wastage'
  | 'moth_or_vermin'
  | 'other_intrinsic_nature';

export type CarrierLiabilityArticle17_4eDeficiencyBasis =
  | 'insufficiency'
  | 'inadequacy'
  | 'insufficiency_and_inadequacy';

export interface CarrierLiabilityIncidentRequest {
  incidentType: CarrierLiabilityIncidentType;
  takingOverAt?: string;
  incidentAt?: string;
  deliveryAt?: string;
  linkedIncidentEvidenceAttachmentId?: string;
  linkedCarrierProofEvidenceAttachmentId?: string;
  lossObserved?: boolean;
  damageObserved?: boolean;
  delayObserved?: boolean;
  claimantFaultOrNeglectAlleged?: boolean;
  claimantInstructionAlleged?: boolean;
  inherentViceAlleged?: boolean;
  unavoidableCircumstancesAlleged?: boolean;
  specialRiskClaimed?: boolean;
  specialRiskType?: CarrierLiabilityIncidentSpecialRisk;
  specialRiskPresumptionClaimed?: boolean;
  article18_2SpecialRiskCircumstancesEstablished?: boolean;
  article18_2CircumstancesAssessmentDetail?: unknown;
  linkedArticle18_2EvidenceAttachmentId?: string;
  article17_4aOpenUnsheetedVehicleUsed?: boolean;
  article17_4aAssessmentDetail?: unknown;
  linkedArticle17_4aEvidenceAttachmentId?: string;
  article17_4bPackingAbsentOrDefectiveObserved?: boolean;
  article17_4bGoodsLiableWithoutProperPacking?: boolean;
  article17_4bAssessmentDetail?: unknown;
  linkedArticle17_4bEvidenceAttachmentId?: string;
  article17_4cHandlingPerformed?: boolean;
  article17_4cLoadingPerformed?: boolean;
  article17_4cStowagePerformed?: boolean;
  article17_4cUnloadingPerformed?: boolean;
  article17_4cActorCapacity?: CarrierLiabilityArticle17_4cActorCapacity;
  article17_4cAssessmentDetail?: unknown;
  linkedArticle17_4cEvidenceAttachmentId?: string;
  article17_4dGoodsParticularlyExposedByNature?: boolean;
  article17_4dSusceptibilityBasis?: CarrierLiabilityArticle17_4dSusceptibilityBasis;
  article17_4dAssessmentDetail?: unknown;
  linkedArticle17_4dEvidenceAttachmentId?: string;
  article17_4ePackageMarksInsufficientOrInadequate?: boolean;
  article17_4ePackageNumbersInsufficientOrInadequate?: boolean;
  article17_4eDeficiencyBasis?: CarrierLiabilityArticle17_4eDeficiencyBasis;
  article17_4eAssessmentDetail?: unknown;
  linkedArticle17_4eEvidenceAttachmentId?: string;
  article17_4fLivestockCarriageConfirmed?: boolean;
  article17_4fAssessmentDetail?: unknown;
  linkedArticle17_4fEvidenceAttachmentId?: string;
  temperatureControlledVehicle?: boolean;
  equipmentStepsEvidencePresent?: boolean;
  livestockCareStepsEvidencePresent?: boolean;
  article18_4EquipmentChoiceStepsTaken?: boolean;
  article18_4EquipmentMaintenanceStepsTaken?: boolean;
  article18_4EquipmentUseStepsTaken?: boolean;
  article18_4SpecialInstructionsIssued?: boolean;
  article18_4SpecialInstructionsComplied?: boolean;
  article18_4AssessmentDetail?: unknown;
  linkedArticle18_4EvidenceAttachmentId?: string;
  article18_5NormallyIncumbentStepsTaken?: boolean;
  article18_5SpecialInstructionsIssued?: boolean;
  article18_5SpecialInstructionsComplied?: boolean;
  article18_5AssessmentDetail?: unknown;
  linkedArticle18_5EvidenceAttachmentId?: string;
  abnormalShortageObserved?: boolean;
  anyPackageLost?: boolean;
  vehicleDefectContributed?: boolean;
  hiredVehicleProviderActOrNeglectContributed?: boolean;
  article17_3AssessmentDetail?: unknown;
  linkedArticle17_3EvidenceAttachmentId?: string;
  article18_1CauseProofEstablished?: boolean;
  article18_1ProofAssessmentDetail?: unknown;
  linkedArticle18_1EvidenceAttachmentId?: string;
  article17_2CauseContributed?: boolean;
  article17_5CarrierLiableFactorSharePercent?: string | number;
  article17_5ReliefFactorSharePercent?: string | number;
  article17_5AssessmentDetail?: unknown;
  linkedArticle17_5EvidenceAttachmentId?: string;
  linkedDisposalInstructionId?: string;
  instructionNotCarriedOut?: boolean;
  instructionCarriedOutWithoutFirstCopy?: boolean;
  claimantEntitlementAsserted?: boolean;
  lossOrDamageCaused?: boolean;
  incidentDetail?: string | Record<string, string | number>;
  claimantStatementDetail?: string | Record<string, string | number>;
  carrierProofDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface CarrierLiabilityIncidentEvidence {
  incidentType: CarrierLiabilityIncidentType | string;
  incidentStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtIncident?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  occurredBetweenTakeoverAndDelivery?: boolean;
  lossObserved?: boolean;
  damageObserved?: boolean;
  delayObserved?: boolean;
  claimantFaultOrNeglectAlleged?: boolean;
  claimantInstructionAlleged?: boolean;
  inherentViceAlleged?: boolean;
  unavoidableCircumstancesAlleged?: boolean;
  specialRiskClaimed?: boolean;
  specialRiskType?: CarrierLiabilityIncidentSpecialRisk | string;
  specialRiskPresumptionClaimed?: boolean;
  temperatureControlledVehicle?: boolean;
  equipmentStepsEvidencePresent?: boolean;
  livestockCareStepsEvidencePresent?: boolean;
  disposalInstructionReferencePresent?: boolean;
  instructionNotCarriedOut?: boolean;
  instructionCarriedOutWithoutFirstCopy?: boolean;
  claimantEntitlementAsserted?: boolean;
  lossOrDamageCaused?: boolean;
  takingOverAtPresent?: boolean;
  incidentAtPresent?: boolean;
  deliveryAtPresent?: boolean;
  incidentEvidenceAttachmentReferencePresent?: boolean;
  carrierProofRequired?: boolean;
  carrierProofEvidenceAttachmentReferencePresent?: boolean;
  incidentChronologyValid?: boolean;
  openUnsheetedVehicleAgreementSpecifiedInConsignmentNote?: boolean;
  article18OpenVehiclePresumptionExceptionAssessed?: boolean;
  article18OpenVehiclePresumptionExceptionAbsent?: boolean;
  article17_4aVehicleUseAssessmentRecorded?: boolean;
  article17_4aOpenUnsheetedVehicleUseReported?: boolean;
  article17_4aAssessmentDetailPresent?: boolean;
  article17_4aEvidenceAttachmentReferencePresent?: boolean;
  article17_3NonRelievingFactorsAssessed?: boolean;
  article17_3NonRelievingFactorReported?: boolean;
  article17_3AssessmentDetailPresent?: boolean;
  article17_3EvidenceAttachmentReferencePresent?: boolean;
  article18_1BurdenAssessmentRecorded?: boolean;
  article18_1CauseProofReported?: boolean;
  article18_1AssessmentDetailPresent?: boolean;
  article18_1EvidenceAttachmentReferencePresent?: boolean;
  article18_2CircumstancesAssessmentRecorded?: boolean;
  article18_2CircumstancesEstablishedReported?: boolean;
  article18_2AssessmentDetailPresent?: boolean;
  article18_2EvidenceAttachmentReferencePresent?: boolean;
  article17_4bPackingConditionAssessmentRecorded?: boolean;
  article17_4bPackingAbsentOrDefectiveReported?: boolean;
  article17_4bGoodsLiabilityWithoutProperPackingReported?: boolean;
  article17_4bAssessmentDetailPresent?: boolean;
  article17_4bEvidenceAttachmentReferencePresent?: boolean;
  article17_4cOperationAssessmentRecorded?: boolean;
  article17_4cQualifyingOperationReported?: boolean;
  article17_4cActorCapacityAssessmentRecorded?: boolean;
  article17_4cQualifyingActorCapacityReported?: boolean;
  article17_4cAssessmentDetailPresent?: boolean;
  article17_4cEvidenceAttachmentReferencePresent?: boolean;
  article17_4dNatureAssessmentRecorded?: boolean;
  article17_4dParticularExposureReported?: boolean;
  article17_4dSusceptibilityBasisAssessmentRecorded?: boolean;
  article17_4dQualifyingSusceptibilityBasisReported?: boolean;
  article17_4dAssessmentDetailPresent?: boolean;
  article17_4dEvidenceAttachmentReferencePresent?: boolean;
  article17_4eMarkingAssessmentRecorded?: boolean;
  article17_4eQualifyingMarkingConditionReported?: boolean;
  article17_4eDeficiencyBasisAssessmentRecorded?: boolean;
  article17_4eQualifyingDeficiencyBasisReported?: boolean;
  article17_4eAssessmentDetailPresent?: boolean;
  article17_4eEvidenceAttachmentReferencePresent?: boolean;
  article17_4fLivestockAssessmentRecorded?: boolean;
  article17_4fLivestockCarriageReported?: boolean;
  article17_4fAssessmentDetailPresent?: boolean;
  article17_4fEvidenceAttachmentReferencePresent?: boolean;
  article18_4EquipmentStepsAssessmentRecorded?: boolean;
  article18_4ChoiceMaintenanceUseStepsReported?: boolean;
  article18_4SpecialInstructionsAssessmentRecorded?: boolean;
  article18_4SpecialInstructionsConsistencyValid?: boolean;
  article18_4AssessmentDetailPresent?: boolean;
  article18_4EvidenceAttachmentReferencePresent?: boolean;
  article18_5NormallyIncumbentStepsAssessmentRecorded?: boolean;
  article18_5NormallyIncumbentStepsReported?: boolean;
  article18_5SpecialInstructionsAssessmentRecorded?: boolean;
  article18_5SpecialInstructionsConsistencyValid?: boolean;
  article18_5AssessmentDetailPresent?: boolean;
  article18_5EvidenceAttachmentReferencePresent?: boolean;
  article17_5MixedCausationAssessed?: boolean;
  article17_5MixedCausationReported?: boolean;
  article17_5ContributionSharesPresent?: boolean;
  article17_5ContributionSharesReconciled?: boolean;
  article17_5AssessmentDetailPresent?: boolean;
  article17_5EvidenceAttachmentReferencePresent?: boolean;
  incidentDetailPresent?: boolean;
  claimantStatementDetailPresent?: boolean;
  carrierProofDetailPresent?: boolean;
  liabilityDetermination?: boolean;
}

export interface Article18_2ClaimantRebuttalRequest {
  lossOrDamageAttributionDisputed: true;
  claimantCounterEvidenceSubmitted: true;
  counterEvidenceDetail: string | Record<string, string | number>;
  linkedCounterEvidenceAttachmentId: string;
  clientReferenceId?: string;
}

export interface Article18_2ClaimantRebuttalEvidence {
  rebuttalStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRebuttal?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  carrierSpecialRiskPresumptionReferencePresent?: boolean;
  claimantRoleAttributed?: boolean;
  lossOrDamageAttributionDisputeRecorded?: boolean;
  claimantCounterEvidenceSubmissionRecorded?: boolean;
  counterEvidenceDetailPresent?: boolean;
  counterEvidenceAttachmentReferencePresent?: boolean;
  legalDetermination?: boolean;
}

export type DeliveryTimingAssessmentType =
  | 'article19_delay'
  | 'article20_loss_presumption'
  | 'article20_recovery_notice';

export interface DeliveryTimingAssessmentRequest {
  assessmentType: DeliveryTimingAssessmentType;
  takingOverAt?: string;
  actualDeliveryAt?: string;
  assessmentAt?: string;
  recoveryNotificationAt?: string;
  recoveryNotificationReceivedAt?: string;
  deliveryRequestAt?: string;
  goodsNotDelivered?: boolean;
  agreedTimeLimitPresent?: boolean;
  agreedTimeLimitExpired?: boolean;
  diligentCarrierAllowedDurationMinutes?: number;
  reasonableDurationExceeded?: boolean;
  partialLoadAssemblyDurationMinutes?: number;
  partialLoadAssemblyTimeConsidered?: boolean;
  diligentCarrierDurationExceeded?: boolean;
  thirtyDaysAfterAgreedLimitExpired?: boolean;
  sixtyDaysAfterTakeoverExpired?: boolean;
  recoveryNotificationRequested?: boolean;
  writtenAcknowledgementIssued?: boolean;
  goodsRecovered?: boolean;
  recoveryNotificationSent?: boolean;
  deliveryRequested?: boolean;
  chargesAndRefundConditionAcknowledged?: boolean;
  timingDetail?: string | Record<string, string | number>;
  claimDetail?: string | Record<string, string | number>;
  recoveryDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface DeliveryTimingAssessmentEvidence {
  assessmentType: DeliveryTimingAssessmentType | string;
  assessmentStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAssessment?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  goodsNotDelivered?: boolean;
  agreedTimeLimitPresent?: boolean;
  agreedTimeLimitExpired?: boolean;
  agreedTimeLimitConsignmentNoteReferencePresent?: boolean;
  article19AgreedTimeLimitComputed?: boolean;
  article19AgreedTimeLimitExceeded?: boolean;
  article19ReasonableDurationComputed?: boolean;
  article19ElapsedDurationOrderValid?: boolean;
  diligentCarrierAllowedDurationPresent?: boolean;
  diligentCarrierAllowedDurationPositive?: boolean;
  reasonableDurationExceeded?: boolean;
  partialLoadAssemblyDurationPresent?: boolean;
  partialLoadAssemblyDurationPositive?: boolean;
  partialLoadAssemblyTimeConsidered?: boolean;
  diligentCarrierDurationExceeded?: boolean;
  thirtyDaysAfterAgreedLimitExpired?: boolean;
  sixtyDaysAfterTakeoverExpired?: boolean;
  article20LossPresumptionPeriodComputed?: boolean;
  article20LossPresumptionPeriodSatisfied?: boolean;
  article20LossPresumptionAssessmentReferencePresent?: boolean;
  article20CompensationAssessmentReferencePresent?: boolean;
  article20CompensationPaymentReferencePresent?: boolean;
  compensationReceived?: boolean;
  recoveryNotificationRequested?: boolean;
  writtenAcknowledgementIssued?: boolean;
  goodsRecovered?: boolean;
  recoveryNotificationSent?: boolean;
  deliveryRequested?: boolean;
  article20DeliveryRequestWindowComputed?: boolean;
  article20DeliveryRequestChronologyValid?: boolean;
  article20DeliveryRequestWithinThirtyDays?: boolean;
  chargesAndRefundConditionAcknowledged?: boolean;
  takingOverAtPresent?: boolean;
  agreedTimeLimitAtPresent?: boolean;
  actualDeliveryAtPresent?: boolean;
  assessmentAtPresent?: boolean;
  recoveryNotificationAtPresent?: boolean;
  recoveryNotificationReceivedAtPresent?: boolean;
  deliveryRequestAtPresent?: boolean;
  timingDetailPresent?: boolean;
  claimDetailPresent?: boolean;
  recoveryDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type CompensationPaymentType = 'missing_goods_compensation_payment';

export type CompensationPaymentEvidenceType =
  | 'bank_transfer_confirmation'
  | 'payment_provider_receipt'
  | 'signed_settlement_receipt'
  | 'court_payment_record'
  | 'other_documented_payment';

export interface CompensationPaymentRequest {
  paymentType?: CompensationPaymentType;
  paymentEvidenceType: CompensationPaymentEvidenceType;
  paidAt: string;
  paymentAmount: string | number;
  paymentCurrency: string;
  compensationForMissingGoods: boolean;
  paymentSettled: boolean;
  recipientAcknowledgedReceipt: boolean;
  linkedPaymentEvidenceAttachmentId: string;
  paymentDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface CompensationPaymentEvidence {
  paymentType: CompensationPaymentType | string;
  paymentStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtPayment?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  paymentEvidenceType?: CompensationPaymentEvidenceType | string;
  lossPresumptionAssessmentReferencePresent?: boolean;
  compensationAssessmentReferencePresent?: boolean;
  paymentEvidenceAttachmentReferencePresent?: boolean;
  compensationForMissingGoods?: boolean;
  paymentSettled?: boolean;
  recipientAcknowledgedReceipt?: boolean;
  paymentAmountPresent?: boolean;
  paymentAmountPositive?: boolean;
  paymentCurrencyPresent?: boolean;
  paymentCurrencyRecognized?: boolean;
  paidAtPresent?: boolean;
  paymentDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type RecoveryDeliverySettlementType = 'article20_recovery_delivery_settlement';

export interface RecoveryDeliverySettlementChargeAmount {
  amount: string | number;
  currency: string;
}

export interface RecoveryDeliverySettlementRequest {
  settlementType?: RecoveryDeliverySettlementType;
  chargesPaidAmounts: RecoveryDeliverySettlementChargeAmount[];
  chargesPaidAt: string;
  compensationRefundAmount: string | number;
  compensationRefundCurrency: string;
  chargesIncludedInCompensationAmount: string | number;
  compensationRefundedAt: string;
  chargesPaymentSettled: boolean;
  compensationRefundSettled: boolean;
  delayClaimsRightsPreserved: boolean;
  linkedChargesPaymentEvidenceAttachmentId: string;
  linkedCompensationRefundEvidenceAttachmentId: string;
  settlementDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface RecoveryDeliverySettlementEvidence {
  settlementType: RecoveryDeliverySettlementType | string;
  settlementStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtSettlement?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  recoveryAssessmentReferencePresent?: boolean;
  compensationPaymentReferencePresent?: boolean;
  deliveryReferencePresent?: boolean;
  chargesPaymentEvidenceAttachmentReferencePresent?: boolean;
  compensationRefundEvidenceAttachmentReferencePresent?: boolean;
  consignmentNoteChargesComputed?: boolean;
  consignmentNoteChargesMatched?: boolean;
  chargesPaymentLineCount?: number;
  chargesPaymentAmountsPositive?: boolean;
  chargesPaymentCurrenciesRecognized?: boolean;
  chargesPaymentSettled?: boolean;
  chargesPaidAtPresent?: boolean;
  compensationRefundAmountPresent?: boolean;
  compensationRefundAmountNonNegative?: boolean;
  compensationRefundCurrencyPresent?: boolean;
  compensationRefundCurrencyRecognized?: boolean;
  chargesIncludedInCompensationAmountPresent?: boolean;
  chargesIncludedInCompensationAmountNonNegative?: boolean;
  compensationRefundFormulaComputed?: boolean;
  compensationRefundFormulaMatched?: boolean;
  compensationRefundSettled?: boolean;
  compensationRefundedAtPresent?: boolean;
  deliveryChronologyComputed?: boolean;
  deliveryChronologyValid?: boolean;
  delayClaimsRightsPreserved?: boolean;
  settlementDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type RecoveredGoodsDispositionType = 'article20_recovered_goods_disposition';

export type RecoveredGoodsDispositionBasis =
  | 'no_written_recovery_notification_request'
  | 'no_instructions_after_thirty_days'
  | 'recovered_more_than_one_year_after_compensation';

export type RecoveredGoodsDispositionActionType =
  | 'stored'
  | 'sold'
  | 'destroyed'
  | 'transferred'
  | 'other';

export interface RecoveredGoodsDispositionRequest {
  recordType?: RecoveredGoodsDispositionType;
  dispositionBasis: RecoveredGoodsDispositionBasis;
  dispositionActionType: RecoveredGoodsDispositionActionType;
  goodsRecovered: boolean;
  goodsRecoveredAt: string;
  dispositionAt: string;
  writtenRecoveryNotificationRequestAbsent?: boolean;
  recoveryNotificationReceivedAt?: string;
  instructionsAbsent?: boolean;
  instructionsAbsenceAssessedAt?: string;
  goodsLocationCountryCode: string;
  goodsLocationDetail: string | Record<string, string | number>;
  applicableLocalLawIdentified: boolean;
  applicableLawDetail: string | Record<string, string | number>;
  dispositionUnderLocalLawRecorded: boolean;
  dispositionDetail: string | Record<string, string | number>;
  linkedRecoveryEvidenceAttachmentId: string;
  linkedDispositionEvidenceAttachmentId: string;
  clientReferenceId?: string;
}

export interface RecoveredGoodsDispositionEvidence {
  recordType: RecoveredGoodsDispositionType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  dispositionBasis?: RecoveredGoodsDispositionBasis | string;
  dispositionActionType?: RecoveredGoodsDispositionActionType | string;
  compensationPaymentReferencePresent?: boolean;
  recoveryNoticeReferenceRequired?: boolean;
  recoveryNoticeReferencePresent?: boolean;
  recoveryEvidenceAttachmentReferencePresent?: boolean;
  dispositionEvidenceAttachmentReferencePresent?: boolean;
  goodsRecovered?: boolean;
  compensationPaidAtPresent?: boolean;
  goodsRecoveredAtPresent?: boolean;
  dispositionAtPresent?: boolean;
  paymentRecoveryDispositionChronologyComputed?: boolean;
  paymentRecoveryDispositionChronologyValid?: boolean;
  writtenRecoveryNotificationRequestAbsent?: boolean;
  recoveryNotificationSent?: boolean;
  recoveryNotificationReceivedAtPresent?: boolean;
  instructionsAbsent?: boolean;
  instructionsAbsenceAssessedAtPresent?: boolean;
  article20ThirtyDayInstructionPeriodComputed?: boolean;
  article20ThirtyDayInstructionPeriodElapsed?: boolean;
  article20OneYearRecoveryPeriodComputed?: boolean;
  goodsRecoveredWithinOneYearOfCompensation?: boolean;
  goodsRecoveredMoreThanOneYearAfterCompensation?: boolean;
  goodsLocationCountryCodePresent?: boolean;
  goodsLocationDetailPresent?: boolean;
  applicableLocalLawIdentified?: boolean;
  applicableLawDetailPresent?: boolean;
  dispositionUnderLocalLawRecorded?: boolean;
  dispositionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type CodDeliveryOmissionType = 'cod_uncollected_delivery';

export interface CodDeliveryOmissionRequest {
  omissionType?: CodDeliveryOmissionType;
  deliveryAt: string;
  goodsDeliveredToConsignee?: boolean;
  codChargeUncollected?: boolean;
  consignorCompensationClaimed?: boolean;
  carrierActionAgainstConsigneeReserved?: boolean;
  omissionDetail?: string | Record<string, string | number>;
  compensationClaimDetail?: string | Record<string, string | number>;
  consigneeActionDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface CodDeliveryOmissionEvidence {
  omissionType: CodDeliveryOmissionType | string;
  omissionStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtOmission?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  goodsDeliveredToConsignee?: boolean;
  codChargeRequiredByContract?: boolean;
  codChargeConsignmentNoteReferencePresent?: boolean;
  codChargeUncollected?: boolean;
  codAmountPositive?: boolean;
  codCurrencyRecognized?: boolean;
  consignorCompensationClaimed?: boolean;
  carrierActionAgainstConsigneeReserved?: boolean;
  deliveryAtPresent?: boolean;
  codAmountPresent?: boolean;
  codCurrencyPresent?: boolean;
  omissionDetailPresent?: boolean;
  compensationClaimDetailPresent?: boolean;
  consigneeActionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type DangerousGoodsNoticeType =
  | 'danger_information_notice'
  | 'carrier_unaware_dangerous_goods_action';

export type DangerousGoodsCarrierActionType =
  | 'unloaded'
  | 'destroyed'
  | 'rendered_harmless';

export interface DangerousGoodsNoticeRequest {
  noticeType: DangerousGoodsNoticeType;
  noticeAt?: string;
  actionAt?: string;
  dangerousGoodsHandedToCarrier?: boolean;
  precautionsRequired?: boolean;
  carrierKnowledgeProvedByOtherMeans?: boolean;
  carrierDidNotKnowDangerousNature?: boolean;
  carrierActionType?: DangerousGoodsCarrierActionType;
  carrierActionTaken?: boolean;
  withoutCompensationBasisRecorded?: boolean;
  senderExpenseLiabilityClaimed?: boolean;
  dangerNatureDetail?: string | Record<string, string | number>;
  precautionsDetail?: string | Record<string, string | number>;
  carrierKnowledgeProofDetail?: string | Record<string, string | number>;
  linkedCarrierKnowledgeProofAttachmentId?: string;
  carrierActionDetail?: string | Record<string, string | number>;
  expenseClaimDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface DangerousGoodsNoticeEvidence {
  noticeType: DangerousGoodsNoticeType | string;
  noticeStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtNotice?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  dangerousGoodsHandedToCarrier?: boolean;
  exactNatureInformed?: boolean;
  precautionsRequired?: boolean;
  precautionsIndicated?: boolean;
  informationEnteredInConsignmentNote?: boolean;
  carrierKnowledgeProvedByOtherMeans?: boolean;
  dangerousGoodsConsignmentNoteReferencePresent?: boolean;
  carrierKnowledgeProofAttachmentReferencePresent?: boolean;
  carrierDidNotKnowDangerousNature?: boolean;
  carrierActionType?: DangerousGoodsCarrierActionType | string;
  carrierActionTaken?: boolean;
  withoutCompensationBasisRecorded?: boolean;
  senderExpenseLiabilityClaimed?: boolean;
  noticeAtPresent?: boolean;
  actionAtPresent?: boolean;
  dangerNatureDetailPresent?: boolean;
  precautionsDetailPresent?: boolean;
  carrierKnowledgeProofDetailPresent?: boolean;
  carrierActionDetailPresent?: boolean;
  expenseClaimDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type CompensationAssessmentType =
  | 'loss_compensation_limit'
  | 'damage_depreciation_compensation_limit'
  | 'delay_compensation_limit'
  | 'declared_value_substitution'
  | 'special_interest_additional_compensation';

export type CompensationGoodsValueBasis =
  | 'commodity_exchange_price'
  | 'current_market_price'
  | 'same_kind_quality_value';

export type CompensationLiabilityLimitBasis =
  | 'sdr_8_33_per_kg'
  | 'gold_franc_25_per_kg';

export type CompensationArticle23LossExtentBasis =
  | 'total_loss'
  | 'partial_loss';

export type CompensationArticle23ChargeRefundBasis =
  | 'full_total_loss'
  | 'proportional_partial_loss';

export interface CompensationAssessmentRequest {
  assessmentType: CompensationAssessmentType;
  assessmentAt: string;
  linkedIncidentId: string;
  carrierLiabilityBasisRecorded?: boolean;
  goodsValueBasis?: CompensationGoodsValueBasis;
  liabilityLimitBasis?: CompensationLiabilityLimitBasis;
  goodsValueAmount?: string | number;
  goodsValueCurrency?: string;
  grossWeightShortKg?: string | number;
  claimAmount?: string | number;
  claimCurrency?: string;
  carriageChargesAmount?: string | number;
  carriageChargesCurrency?: string;
  declaredValueAmount?: string | number;
  declaredValueCurrency?: string;
  specialInterestAmount?: string | number;
  specialInterestCurrency?: string;
  diminishedValueAmount?: string | number;
  diminishedValueCurrency?: string;
  article23LimitApplied?: boolean;
  carriageChargesRefundConsidered?: boolean;
  delayDamageProved?: boolean;
  carriageChargesLimitApplied?: boolean;
  damageAffectedWholeConsignment?: boolean;
  article25LossLimitApplied?: boolean;
  declaredValueEnteredInConsignmentNote?: boolean;
  declaredValueSurchargeAgreed?: boolean;
  declaredValueExceedsArticle23Limit?: boolean;
  declaredValueLimitSubstituted?: boolean;
  specialInterestEnteredInConsignmentNote?: boolean;
  specialInterestSurchargeAgreed?: boolean;
  additionalLossOrDamageProved?: boolean;
  specialInterestLimitApplied?: boolean;
  agreedTimeLimitExceeded?: boolean;
  valuationDetail?: string | Record<string, string | number>;
  linkedValuationEvidenceAttachmentId?: string;
  calculationDetail?: string | Record<string, string | number>;
  claimDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface CompensationAssessmentEvidence {
  assessmentType: CompensationAssessmentType | string;
  assessmentStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAssessment?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  incidentReferencePresent?: boolean;
  takingOverPlaceAndTimeConsignmentNoteReferencePresent?: boolean;
  valuationEvidenceAttachmentReferencePresent?: boolean;
  article23LossExtentBasis?: CompensationArticle23LossExtentBasis | string;
  article23ChargeRefundBasis?: CompensationArticle23ChargeRefundBasis | string;
  article23WeightAndRefundEvidenceReferencePresent?: boolean;
  carrierLiabilityBasisRecorded?: boolean;
  goodsValueBasis?: CompensationGoodsValueBasis | string;
  liabilityLimitBasis?: CompensationLiabilityLimitBasis | string;
  goodsValueAmountPresent?: boolean;
  goodsValueAmountPositive?: boolean;
  goodsValueCurrencyPresent?: boolean;
  goodsValueCurrencyRecognized?: boolean;
  grossWeightShortPresent?: boolean;
  grossWeightShortPositive?: boolean;
  claimAmountPresent?: boolean;
  claimAmountPositive?: boolean;
  claimCurrencyPresent?: boolean;
  claimCurrencyRecognized?: boolean;
  carriageChargesAmountPresent?: boolean;
  carriageChargesAmountPositive?: boolean;
  carriageChargesCurrencyPresent?: boolean;
  carriageChargesCurrencyRecognized?: boolean;
  declaredValueAmountPresent?: boolean;
  declaredValueAmountPositive?: boolean;
  declaredValueCurrencyPresent?: boolean;
  declaredValueCurrencyRecognized?: boolean;
  specialInterestAmountPresent?: boolean;
  specialInterestAmountPositive?: boolean;
  specialInterestCurrencyPresent?: boolean;
  specialInterestCurrencyRecognized?: boolean;
  diminishedValueAmountPresent?: boolean;
  diminishedValueAmountPositive?: boolean;
  diminishedValueCurrencyPresent?: boolean;
  diminishedValueCurrencyRecognized?: boolean;
  article23LimitApplied?: boolean;
  carriageChargesRefundConsidered?: boolean;
  delayDamageProved?: boolean;
  carriageChargesLimitApplied?: boolean;
  damageAffectedWholeConsignment?: boolean;
  article25LossLimitApplied?: boolean;
  declaredValueEnteredInConsignmentNote?: boolean;
  declaredValueSurchargeAgreed?: boolean;
  declaredValueExceedsArticle23Limit?: boolean;
  declaredValueLimitSubstituted?: boolean;
  specialInterestEnteredInConsignmentNote?: boolean;
  specialInterestSurchargeAgreed?: boolean;
  additionalLossOrDamageProved?: boolean;
  specialInterestLimitApplied?: boolean;
  agreedTimeLimitExceeded?: boolean;
  higherCompensationBasisPresent?: boolean;
  assessmentAtPresent?: boolean;
  valuationDetailPresent?: boolean;
  calculationDetailPresent?: boolean;
  claimDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type CompensationInterestRecordType =
  | 'interest_on_compensation'
  | 'currency_conversion';

export type CompensationInterestStartBasis =
  | 'written_claim'
  | 'legal_proceedings';

export interface CompensationInterestRequest {
  recordType: CompensationInterestRecordType;
  linkedCompensationAssessmentId?: string;
  compensationPayableBasisRecorded?: boolean;
  interestStartBasis?: CompensationInterestStartBasis;
  claimSentInWriting?: boolean;
  legalProceedingsInstituted?: boolean;
  interestStartAt?: string;
  interestCalculatedAt?: string;
  fivePercentPerAnnumApplied?: boolean;
  compensationAmount?: string | number;
  compensationCurrency?: string;
  interestAmount?: string | number;
  interestCurrency?: string;
  amountsNotExpressedInPaymentClaimCurrency?: boolean;
  paymentClaimCurrencyCountryBasisRecorded?: boolean;
  paymentPlaceBasisRecorded?: boolean;
  paymentAt?: string;
  exchangeRateApplicableAtPaymentPlaceAndDate?: boolean;
  sourceAmount?: string | number;
  sourceCurrency?: string;
  targetCurrency?: string;
  convertedAmount?: string | number;
  convertedCurrency?: string;
  interestCalculationDetail?: string | Record<string, string | number>;
  exchangeRateDetail?: string | Record<string, string | number>;
  claimDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface CompensationInterestEvidence {
  recordType: CompensationInterestRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  compensationAssessmentReferencePresent?: boolean;
  compensationPayableBasisRecorded?: boolean;
  interestStartBasis?: CompensationInterestStartBasis | string;
  claimSentInWriting?: boolean;
  legalProceedingsInstituted?: boolean;
  fivePercentPerAnnumApplied?: boolean;
  compensationAmountPresent?: boolean;
  compensationAmountPositive?: boolean;
  compensationCurrencyPresent?: boolean;
  compensationCurrencyRecognized?: boolean;
  interestAmountPresent?: boolean;
  interestAmountPositive?: boolean;
  interestCurrencyPresent?: boolean;
  interestCurrencyRecognized?: boolean;
  amountsNotExpressedInPaymentClaimCurrency?: boolean;
  paymentClaimCurrencyCountryBasisRecorded?: boolean;
  paymentPlaceBasisRecorded?: boolean;
  exchangeRateApplicableAtPaymentPlaceAndDate?: boolean;
  sourceAmountPresent?: boolean;
  sourceAmountPositive?: boolean;
  sourceCurrencyPresent?: boolean;
  sourceCurrencyRecognized?: boolean;
  targetCurrencyPresent?: boolean;
  targetCurrencyRecognized?: boolean;
  convertedAmountPresent?: boolean;
  convertedAmountPositive?: boolean;
  convertedCurrencyPresent?: boolean;
  convertedCurrencyRecognized?: boolean;
  interestStartAtPresent?: boolean;
  interestCalculatedAtPresent?: boolean;
  interestDateOrderComputed?: boolean;
  interestCalculatedAfterStart?: boolean;
  paymentAtPresent?: boolean;
  interestCalculationDetailPresent?: boolean;
  exchangeRateDetailPresent?: boolean;
  claimDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type LiabilityLimitationRecordType =
  | 'extra_contractual_claim_defense'
  | 'wilful_misconduct_limitation_exception';

export type LiabilityLimitationPersonRole =
  | 'carrier'
  | 'article3_agent_or_servant'
  | 'article3_other_service_person';

export interface LiabilityLimitationRequest {
  recordType: LiabilityLimitationRecordType;
  linkedIncidentId: string;
  liablePersonRole?: LiabilityLimitationPersonRole;
  claimArisesFromCarriageUnderCmr?: boolean;
  lossDamageOrDelayClaim?: boolean;
  extraContractualClaimUnderApplicableLaw?: boolean;
  cmrExclusionOrLimitInvoked?: boolean;
  article3ResponsiblePersonInvolved?: boolean;
  article3PersonScopeRecorded?: boolean;
  actingWithinScopeOfEmployment?: boolean;
  limitationOrBurdenShiftProvisionInvoked?: boolean;
  wilfulMisconductAlleged?: boolean;
  equivalentDefaultAlleged?: boolean;
  courtOrTribunalLawBasisRecorded?: boolean;
  limitationDisapplicationBasisRecorded?: boolean;
  claimDetail?: string | Record<string, string | number>;
  applicableLawDetail?: string | Record<string, string | number>;
  article3PersonDetail?: string | Record<string, string | number>;
  misconductDetail?: string | Record<string, string | number>;
  courtLawDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface LiabilityLimitationEvidence {
  recordType: LiabilityLimitationRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  liablePersonRole?: LiabilityLimitationPersonRole | string;
  incidentReferencePresent?: boolean;
  claimArisesFromCarriageUnderCmr?: boolean;
  lossDamageOrDelayClaim?: boolean;
  extraContractualClaimUnderApplicableLaw?: boolean;
  cmrExclusionOrLimitInvoked?: boolean;
  article3ResponsiblePersonInvolved?: boolean;
  article3PersonScopeRecorded?: boolean;
  actingWithinScopeOfEmployment?: boolean;
  limitationOrBurdenShiftProvisionInvoked?: boolean;
  wilfulMisconductAlleged?: boolean;
  equivalentDefaultAlleged?: boolean;
  courtOrTribunalLawBasisRecorded?: boolean;
  limitationDisapplicationBasisRecorded?: boolean;
  claimDetailPresent?: boolean;
  applicableLawDetailPresent?: boolean;
  article3PersonDetailPresent?: boolean;
  misconductDetailPresent?: boolean;
  courtLawDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type ClaimReservationRecordType =
  | 'apparent_loss_or_damage_reservation'
  | 'non_apparent_loss_or_damage_reservation'
  | 'delay_compensation_reservation'
  | 'condition_check_facility';

export interface ClaimReservationRequest {
  recordType: ClaimReservationRecordType;
  linkedDeliveryId?: string;
  linkedClaimId?: string;
  linkedConditionCheckId?: string;
  deliveryAt?: string;
  conditionCheckedAt?: string;
  goodsAvailableAt?: string;
  reservationSentAt?: string;
  lossOrDamageApparent?: boolean;
  lossOrDamageNotApparent?: boolean;
  delayCompensationClaim?: boolean;
  reservationSentAtDelivery?: boolean;
  reservationSentInWriting?: boolean;
  reservationWithinSevenDaysExcludingHolidaysRecorded?: boolean;
  conditionCheckedWithCarrier?: boolean;
  contraryEvidenceReservationSentInWriting?: boolean;
  delayReservationSentInWriting?: boolean;
  reservationWithinTwentyOneDaysRecorded?: boolean;
  relevantStartDateExcludedFromLimitCalculation?: boolean;
  reasonableFacilitiesProvided?: boolean;
  excludedPublicHolidayDates?: string[];
  reservationDetail?: string | Record<string, string | number>;
  conditionCheckDetail?: string | Record<string, string | number>;
  delayClaimDetail?: string | Record<string, string | number>;
  investigationFacilityDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface ClaimReservationEvidence {
  recordType: ClaimReservationRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  deliveryReferencePresent?: boolean;
  lossOrDamageApparent?: boolean;
  lossOrDamageNotApparent?: boolean;
  delayCompensationClaim?: boolean;
  reservationSentAtDelivery?: boolean;
  reservationSentInWriting?: boolean;
  reservationWithinSevenDaysExcludingHolidaysRecorded?: boolean;
  conditionCheckedWithCarrier?: boolean;
  contraryEvidenceReservationSentInWriting?: boolean;
  delayReservationSentInWriting?: boolean;
  reservationWithinTwentyOneDaysRecorded?: boolean;
  relevantStartDateExcludedFromLimitCalculation?: boolean;
  reasonableFacilitiesProvided?: boolean;
  timeLimitComputed?: boolean;
  withinComputedTimeLimit?: boolean;
  timeLimitComputationBasis?: string;
  publicHolidaysExcludedFromTimeLimit?: boolean;
  publicHolidayExclusionCount?: number;
  deliveryAtPresent?: boolean;
  conditionCheckedAtPresent?: boolean;
  goodsAvailableAtPresent?: boolean;
  reservationSentAtPresent?: boolean;
  reservationDetailPresent?: boolean;
  conditionCheckDetailPresent?: boolean;
  delayClaimDetailPresent?: boolean;
  investigationFacilityDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type DisputeProcedureRecordType =
  | 'jurisdiction_basis_notice'
  | 'pending_or_judgment_bar_notice'
  | 'limitation_period_record'
  | 'written_claim_suspension_record'
  | 'arbitration_clause_record';

export type DisputeProcedureClaimCategory =
  | 'partial_loss'
  | 'damage'
  | 'delay'
  | 'total_loss'
  | 'other';

export type DisputeProcedureLimitationPeriodBasis =
  | 'one_year'
  | 'three_year_wilful_misconduct';

export type DisputeProcedureLimitationStartBasis =
  | 'delivery_date'
  | 'thirty_days_after_agreed_time_limit'
  | 'sixty_days_after_takeover'
  | 'three_months_after_contract';

export interface DisputeProcedureRequest {
  recordType: DisputeProcedureRecordType;
  linkedClaimId?: string;
  linkedProceedingId?: string;
  linkedJudgmentId?: string;
  linkedWrittenClaimId?: string;
  linkedArbitrationClauseId?: string;
  claimCategory?: DisputeProcedureClaimCategory;
  limitationPeriodBasis?: DisputeProcedureLimitationPeriodBasis;
  limitationStartBasis?: DisputeProcedureLimitationStartBasis;
  actionFiledAt?: string;
  judgmentEnteredAt?: string;
  judgmentEnforceableAt?: string;
  deliveryAt?: string;
  takeoverAt?: string;
  agreedTimeLimitExpiresAt?: string;
  contractMadeAt?: string;
  limitationStartedAt?: string;
  limitationExpiresAt?: string;
  writtenClaimReceivedAt?: string;
  claimRejectedAt?: string;
  documentsReturnedAt?: string;
  partyDesignatedCourtOrTribunal?: boolean;
  defendantResidenceOrBusinessPlaceBasis?: boolean;
  contractBranchOrAgencyBasis?: boolean;
  takeoverPlaceBasis?: boolean;
  deliveryPlaceBasis?: boolean;
  contractingCountryCourtOrTribunal?: boolean;
  securityForCostsNotRequiredRecorded?: boolean;
  samePartiesAndGroundsRecorded?: boolean;
  pendingCompetentProceedingRecorded?: boolean;
  judgmentEnteredByCompetentCourt?: boolean;
  freshProceedingOnlyIfJudgmentNotEnforceableRecorded?: boolean;
  judgmentEnforceabilityFormalitiesOnlyRecorded?: boolean;
  meritsReopeningProhibitedRecorded?: boolean;
  wilfulMisconductOrEquivalentDefaultAlleged?: boolean;
  limitationPeriodRecorded?: boolean;
  limitationStartDateExcluded?: boolean;
  claimBarredNotAvailableByCounterclaimOrSetoffRecorded?: boolean;
  courtLawExtensionOrFreshAccrualBasisRecorded?: boolean;
  writtenClaimReceived?: boolean;
  carrierRejectedClaimInWriting?: boolean;
  documentsReturned?: boolean;
  claimPartlyAdmitted?: boolean;
  remainingPartStillInDispute?: boolean;
  receiptReplyReturnProofBurdenRecorded?: boolean;
  furtherSameObjectClaimsDoNotSuspendRecorded?: boolean;
  arbitrationClausePresent?: boolean;
  arbitrationTribunalCompetenceRecorded?: boolean;
  tribunalAppliesCmrConvention?: boolean;
  jurisdictionDetail?: string | Record<string, string | number>;
  partyAgreementDetail?: string | Record<string, string | number>;
  takeoverDeliveryPlaceDetail?: string | Record<string, string | number>;
  pendingActionDetail?: string | Record<string, string | number>;
  judgmentDetail?: string | Record<string, string | number>;
  limitationCalculationDetail?: string | Record<string, string | number>;
  writtenClaimDetail?: string | Record<string, string | number>;
  rejectionDetail?: string | Record<string, string | number>;
  arbitrationClauseDetail?: string | Record<string, string | number>;
  courtOrTribunalDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface DisputeProcedureEvidence {
  recordType: DisputeProcedureRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  claimCategory?: DisputeProcedureClaimCategory | string;
  limitationPeriodBasis?: DisputeProcedureLimitationPeriodBasis | string;
  limitationStartBasis?: DisputeProcedureLimitationStartBasis | string;
  limitationStartComputed?: boolean;
  limitationStartComputationBasis?: string;
  limitationStartedAtMatchesComputedBasis?: boolean;
  limitationExpiryComputed?: boolean;
  limitationExpiryComputationBasis?: string;
  limitationExpiresAtMatchesComputedBasis?: boolean;
  suspensionReleaseDateOrderComputed?: boolean;
  suspensionReleaseDatesOrdered?: boolean;
  partyDesignatedCourtOrTribunal?: boolean;
  defendantResidenceOrBusinessPlaceBasis?: boolean;
  contractBranchOrAgencyBasis?: boolean;
  takeoverPlaceBasis?: boolean;
  deliveryPlaceBasis?: boolean;
  contractingCountryCourtOrTribunal?: boolean;
  securityForCostsNotRequiredRecorded?: boolean;
  samePartiesAndGroundsRecorded?: boolean;
  pendingCompetentProceedingRecorded?: boolean;
  judgmentEnteredByCompetentCourt?: boolean;
  freshProceedingOnlyIfJudgmentNotEnforceableRecorded?: boolean;
  judgmentEnforceabilityFormalitiesOnlyRecorded?: boolean;
  meritsReopeningProhibitedRecorded?: boolean;
  wilfulMisconductOrEquivalentDefaultAlleged?: boolean;
  limitationPeriodRecorded?: boolean;
  limitationStartDateExcluded?: boolean;
  claimBarredNotAvailableByCounterclaimOrSetoffRecorded?: boolean;
  courtLawExtensionOrFreshAccrualBasisRecorded?: boolean;
  writtenClaimReceived?: boolean;
  carrierRejectedClaimInWriting?: boolean;
  documentsReturned?: boolean;
  claimPartlyAdmitted?: boolean;
  remainingPartStillInDispute?: boolean;
  receiptReplyReturnProofBurdenRecorded?: boolean;
  furtherSameObjectClaimsDoNotSuspendRecorded?: boolean;
  arbitrationClausePresent?: boolean;
  arbitrationTribunalCompetenceRecorded?: boolean;
  tribunalAppliesCmrConvention?: boolean;
  actionFiledAtPresent?: boolean;
  judgmentEnteredAtPresent?: boolean;
  judgmentEnforceableAtPresent?: boolean;
  deliveryAtPresent?: boolean;
  takeoverAtPresent?: boolean;
  agreedTimeLimitExpiresAtPresent?: boolean;
  contractMadeAtPresent?: boolean;
  limitationStartedAtPresent?: boolean;
  limitationExpiresAtPresent?: boolean;
  writtenClaimReceivedAtPresent?: boolean;
  claimRejectedAtPresent?: boolean;
  documentsReturnedAtPresent?: boolean;
  jurisdictionDetailPresent?: boolean;
  partyAgreementDetailPresent?: boolean;
  takeoverDeliveryPlaceDetailPresent?: boolean;
  pendingActionDetailPresent?: boolean;
  judgmentDetailPresent?: boolean;
  limitationCalculationDetailPresent?: boolean;
  writtenClaimDetailPresent?: boolean;
  rejectionDetailPresent?: boolean;
  arbitrationClauseDetailPresent?: boolean;
  courtOrTribunalDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type SuccessiveCarrierRecordType =
  | 'successive_carrier_acceptance'
  | 'successive_carrier_action_scope'
  | 'successive_carrier_recovery_allocation'
  | 'successive_carrier_recovery_proceeding'
  | 'successive_carrier_agreement_variation';

export type SuccessiveCarrierAllocationBasis =
  | 'responsible_carrier'
  | 'multiple_carriers_liability_share'
  | 'multiple_carriers_payment_share'
  | 'unknown_attribution_payment_share'
  | 'insolvent_carrier_share_reallocated';

export type SuccessiveCarrierRecoveryLimitationStartBasis =
  | 'final_judicial_decision'
  | 'actual_payment';

export interface SuccessiveCarrierRequest {
  recordType: SuccessiveCarrierRecordType;
  linkedSuccessiveCarrierId?: string;
  linkedPreviousCarrierId?: string;
  linkedNextCarrierId?: string;
  linkedConsignmentNoteCopyId?: string;
  linkedReservationId?: string;
  linkedClaimId?: string;
  linkedCompensationPaymentId?: string;
  linkedCompensationAssessmentId?: string;
  linkedProceedingId?: string;
  linkedCarrierAgreementId?: string;
  allocationBasis?: SuccessiveCarrierAllocationBasis;
  recoveryLimitationStartBasis?: SuccessiveCarrierRecoveryLimitationStartBasis;
  carrierAcceptedAt?: string;
  receiptIssuedAt?: string;
  compensationPaidAt?: string;
  finalJudicialDecisionAt?: string;
  recoveryClaimFiledAt?: string;
  agreementRecordedAt?: string;
  singleContractCarriage?: boolean;
  successiveRoadCarrier?: boolean;
  goodsAccepted?: boolean;
  consignmentNoteAccepted?: boolean;
  becomesPartyByAcceptance?: boolean;
  datedSignedReceiptIssued?: boolean;
  carrierNameAddressEnteredOnSecondCopy?: boolean;
  article8ReservationsEnteredWhereApplicable?: boolean;
  article9AppliesBetweenSuccessiveCarriers?: boolean;
  liabilityForWholeOperationRecorded?: boolean;
  lossDamageOrDelayProceeding?: boolean;
  actionAgainstFirstCarrier?: boolean;
  actionAgainstLastCarrier?: boolean;
  actionAgainstEventCarrier?: boolean;
  counterclaimOrSetoffSameContract?: boolean;
  multiplePermittedCarriersJoined?: boolean;
  compensationPaidUnderCmr?: boolean;
  interestAndCostsIncluded?: boolean;
  responsibleCarrierSoleLiabilityRecorded?: boolean;
  multipleCarrierLiabilityApportioned?: boolean;
  liabilityCannotBeAttributed?: boolean;
  paymentShareApportionmentRecorded?: boolean;
  insolventCarrierShareUnpaid?: boolean;
  insolventShareReallocatedByPaymentShare?: boolean;
  judicialAuthorityDeterminedCompensation?: boolean;
  carrierGivenDueNoticeOfProceedings?: boolean;
  carrierHadOpportunityToAppear?: boolean;
  paymentValidityDisputeBarRecorded?: boolean;
  competentRecoveryForumRecorded?: boolean;
  allConcernedCarriersMayBeDefendants?: boolean;
  article31JudgmentRulesApplied?: boolean;
  article32LimitationApplied?: boolean;
  recoveryLimitationStartRecorded?: boolean;
  carriersAgreedAlternativeAllocation?: boolean;
  agreementLimitedToArticles37And38?: boolean;
  article40AgreementRecorded?: boolean;
  receiptDetail?: string | Record<string, string | number>;
  carrierIdentityEntryDetail?: string | Record<string, string | number>;
  reservationDetail?: string | Record<string, string | number>;
  actionScopeDetail?: string | Record<string, string | number>;
  allocationDetail?: string | Record<string, string | number>;
  insolvencyDetail?: string | Record<string, string | number>;
  proceedingDetail?: string | Record<string, string | number>;
  agreementDetail?: string | Record<string, string | number>;
  noticeOpportunityDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface SuccessiveCarrierEvidence {
  recordType: SuccessiveCarrierRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  allocationBasis?: SuccessiveCarrierAllocationBasis | string;
  recoveryLimitationStartBasis?: SuccessiveCarrierRecoveryLimitationStartBasis | string;
  successiveReceiptDateOrderComputed?: boolean;
  successiveReceiptIssuedAfterAcceptance?: boolean;
  recoveryLimitationPeriodComputed?: boolean;
  recoveryLimitationComputationBasis?: string;
  recoveryClaimFiledWithinComputedLimit?: boolean;
  consignmentNoteCopyReferencePresent?: boolean;
  reservationReferencePresent?: boolean;
  compensationAssessmentReferencePresent?: boolean;
  singleContractCarriage?: boolean;
  successiveRoadCarrier?: boolean;
  goodsAccepted?: boolean;
  consignmentNoteAccepted?: boolean;
  becomesPartyByAcceptance?: boolean;
  datedSignedReceiptIssued?: boolean;
  carrierNameAddressEnteredOnSecondCopy?: boolean;
  article8ReservationsEnteredWhereApplicable?: boolean;
  article9AppliesBetweenSuccessiveCarriers?: boolean;
  liabilityForWholeOperationRecorded?: boolean;
  lossDamageOrDelayProceeding?: boolean;
  actionAgainstFirstCarrier?: boolean;
  actionAgainstLastCarrier?: boolean;
  actionAgainstEventCarrier?: boolean;
  counterclaimOrSetoffSameContract?: boolean;
  multiplePermittedCarriersJoined?: boolean;
  compensationPaidUnderCmr?: boolean;
  interestAndCostsIncluded?: boolean;
  responsibleCarrierSoleLiabilityRecorded?: boolean;
  multipleCarrierLiabilityApportioned?: boolean;
  liabilityCannotBeAttributed?: boolean;
  paymentShareApportionmentRecorded?: boolean;
  insolventCarrierShareUnpaid?: boolean;
  insolventShareReallocatedByPaymentShare?: boolean;
  judicialAuthorityDeterminedCompensation?: boolean;
  carrierGivenDueNoticeOfProceedings?: boolean;
  carrierHadOpportunityToAppear?: boolean;
  paymentValidityDisputeBarRecorded?: boolean;
  competentRecoveryForumRecorded?: boolean;
  allConcernedCarriersMayBeDefendants?: boolean;
  article31JudgmentRulesApplied?: boolean;
  article32LimitationApplied?: boolean;
  recoveryLimitationStartRecorded?: boolean;
  carriersAgreedAlternativeAllocation?: boolean;
  agreementLimitedToArticles37And38?: boolean;
  article40AgreementRecorded?: boolean;
  carrierAcceptedAtPresent?: boolean;
  receiptIssuedAtPresent?: boolean;
  compensationPaidAtPresent?: boolean;
  finalJudicialDecisionAtPresent?: boolean;
  recoveryClaimFiledAtPresent?: boolean;
  agreementRecordedAtPresent?: boolean;
  receiptDetailPresent?: boolean;
  carrierIdentityEntryDetailPresent?: boolean;
  reservationDetailPresent?: boolean;
  actionScopeDetailPresent?: boolean;
  allocationDetailPresent?: boolean;
  insolvencyDetailPresent?: boolean;
  proceedingDetailPresent?: boolean;
  agreementDetailPresent?: boolean;
  noticeOpportunityDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type MultimodalCarriageRecordType =
  | 'loaded_vehicle_other_mode_carriage'
  | 'article14_unloading_impediment_basis';

export interface MultimodalCarriageRequest {
  recordType: MultimodalCarriageRecordType;
  otherModeType: CmrArticle2OtherModeType;
  linkedOtherModeCarriageDocumentId?: string;
  linkedArticle14ImpedimentRecordId?: string;
  linkedMultimodalDeclarationId?: string;
  vehicleCarriedByOtherMode?: boolean;
  goodsRemainedLoadedOnVehicle?: boolean;
  goodsUnloadedFromVehicle?: boolean;
  article14ImpedimentBasisRecorded?: boolean;
  otherModeCarriageEvidencePresent?: boolean;
  otherModeCarriageDetail?: string | Record<string, string | number>;
  article14ImpedimentDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface MultimodalCarriageEvidence {
  recordType: MultimodalCarriageRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  otherModeType?: CmrArticle2OtherModeType | string;
  documentVersionReferencePresent?: boolean;
  vehicleCarriedByOtherMode?: boolean;
  goodsRemainedLoadedOnVehicle?: boolean;
  goodsUnloadedFromVehicle?: boolean;
  article14ImpedimentBasisRecorded?: boolean;
  otherModeCarriageEvidencePresent?: boolean;
  otherModeCarriageDocumentReferencePresent?: boolean;
  article14ImpedimentReferencePresent?: boolean;
  otherModeCarriageDetailPresent?: boolean;
  article14ImpedimentDetailPresent?: boolean;
  liabilityDetermination?: boolean;
  legalDetermination?: boolean;
}

export type ConsignmentNoteCopyRecordType =
  | 'sender_first_copy_access_available'
  | 'goods_accompanying_second_copy_access_available'
  | 'carrier_retained_third_copy_access_available';

export interface ConsignmentNoteCopyRequest {
  recordType: ConsignmentNoteCopyRecordType;
  linkedCopyAccessRecordId?: string;
  linkedSignatureRecordId?: string;
  linkedHandoverRecordId?: string;
  electronicCopyAccessAvailable?: boolean;
  entitledPartyAccessAvailable?: boolean;
  senderCarrierSignatureEvidenceAvailable?: boolean;
  senderFirstCopyAvailable?: boolean;
  goodsAccompanyingSecondCopyAvailable?: boolean;
  carrierRetainedThirdCopyAvailable?: boolean;
  copyAccessDetail?: string | Record<string, string | number>;
  signatureDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface ConsignmentNoteCopyEvidence {
  recordType: ConsignmentNoteCopyRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  documentVersionReferencePresent?: boolean;
  electronicCopyAccessAvailable?: boolean;
  entitledPartyAccessAvailable?: boolean;
  senderCarrierSignatureEvidenceAvailable?: boolean;
  contractParticipantReferencePresent?: boolean;
  senderFirstCopyAvailable?: boolean;
  goodsAccompanyingSecondCopyAvailable?: boolean;
  carrierRetainedThirdCopyAvailable?: boolean;
  copyAccessDetailPresent?: boolean;
  signatureDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type EcmrReceiptAccessRecordType = 'sender_receipt_access_information_handover';

export type EcmrReceiptAccessRequestType = 'sender_receipt_access_request';

export interface EcmrReceiptAccessSenderRequest {
  requestDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface EcmrReceiptAccessRequestEvidence {
  requestType?: EcmrReceiptAccessRequestType | string;
  requestStatus?: 'requested' | string;
  requestedAt?: string;
  documentVersionNumber?: number;
  statusAtRequest?: DocumentLifecycleStatus | string;
  sourceInstrument?: 'ECMR_PROTOCOL_2008' | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  senderRequestRecorded?: boolean;
  documentVersionReferencePresent?: boolean;
  requestDetailPresent?: boolean;
  technicalEvidenceOnly?: boolean;
  legalDetermination?: boolean;
}

export interface EcmrReceiptAccessRequest {
  recordType: EcmrReceiptAccessRecordType;
  handoverAt: string;
  linkedGoodsReceiptId?: string;
  linkedShipmentIdentificationRecordId?: string;
  linkedAccessInformationRecordId?: string;
  senderRequestReceived?: boolean;
  receiptForGoodsProvided?: boolean;
  shipmentIdentificationInformationProvided?: boolean;
  electronicConsignmentNoteAccessInformationProvided?: boolean;
  receiptDetail?: string | Record<string, string | number>;
  shipmentIdentificationDetail?: string | Record<string, string | number>;
  accessInformationDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface EcmrReceiptAccessEvidence {
  recordType: EcmrReceiptAccessRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceInstrument?: 'ECMR_PROTOCOL_2008' | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  documentVersionReferencePresent?: boolean;
  senderRequestReferencePresent?: boolean;
  senderRequestReceived?: boolean;
  receiptForGoodsProvided?: boolean;
  shipmentIdentificationInformationProvided?: boolean;
  electronicConsignmentNoteAccessInformationProvided?: boolean;
  handoverAtPresent?: boolean;
  senderRequestDetailPresent?: boolean;
  receiptDetailPresent?: boolean;
  shipmentIdentificationDetailPresent?: boolean;
  accessInformationDetailPresent?: boolean;
  technicalEvidenceOnly?: boolean;
  legalDetermination?: boolean;
}

export type SenderParticularsResponsibilityRecordType =
  | 'sender_article6_particular_inaccuracy'
  | 'sender_optional_particular_inaccuracy'
  | 'sender_instruction_inaccuracy'
  | 'carrier_entered_particulars_on_sender_behalf'
  | 'carrier_cmr_statement_omission';

export type SenderParticularsResponsibilityCategory =
  | 'article6_1_b_sender'
  | 'article6_1_d_taking_over_delivery'
  | 'article6_1_e_consignee'
  | 'article6_1_f_goods_packing'
  | 'article6_1_g_packages_marks'
  | 'article6_1_h_weight_quantity'
  | 'article6_1_j_customs_formalities'
  | 'article6_2_optional_particular'
  | 'sender_other_particular_or_instruction'
  | 'article6_1_k_cmr_statement';

export interface SenderParticularsResponsibilityRequest {
  recordType: SenderParticularsResponsibilityRecordType;
  affectedParticularCategory: SenderParticularsResponsibilityCategory;
  linkedParticularRecordId?: string;
  linkedInstructionId?: string;
  linkedClaimId?: string;
  linkedExpenseRecordId?: string;
  linkedCarrierEntryRecordId?: string;
  linkedConsignmentNoteReviewId?: string;
  reviewedAt?: string;
  inaccuracyOrInadequacyRecorded?: boolean;
  expenseLossDamageClaimed?: boolean;
  carrierEnteredAtSenderRequest?: boolean;
  senderBehalfPresumptionRecorded?: boolean;
  cmrStatementOmissionRecorded?: boolean;
  carrierLiabilityBasisRecorded?: boolean;
  particularDetail?: string | Record<string, string | number>;
  instructionDetail?: string | Record<string, string | number>;
  expenseLossDamageDetail?: string | Record<string, string | number>;
  carrierEntryDetail?: string | Record<string, string | number>;
  cmrStatementOmissionDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface SenderParticularsResponsibilityEvidence {
  recordType: SenderParticularsResponsibilityRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  affectedParticularCategory?: SenderParticularsResponsibilityCategory | string;
  actorRoleCount?: number;
  documentVersionReferencePresent?: boolean;
  inaccuracyOrInadequacyRecorded?: boolean;
  expenseLossDamageClaimed?: boolean;
  carrierEnteredAtSenderRequest?: boolean;
  senderBehalfPresumptionRecorded?: boolean;
  cmrStatementOmissionRecorded?: boolean;
  carrierLiabilityBasisRecorded?: boolean;
  reviewedAtPresent?: boolean;
  particularDetailPresent?: boolean;
  instructionDetailPresent?: boolean;
  expenseLossDamageDetailPresent?: boolean;
  carrierEntryDetailPresent?: boolean;
  cmrStatementOmissionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type ProhibitedClauseRecordType =
  | 'convention_derogation_clause'
  | 'insurance_benefit_to_carrier_clause'
  | 'burden_of_proof_shift_clause';

export interface ProhibitedClauseRequest {
  recordType: ProhibitedClauseRecordType;
  linkedContractTermId?: string;
  linkedClauseReviewId?: string;
  linkedInsuranceRecordId?: string;
  linkedClaimId?: string;
  linkedArticle40AgreementId?: string;
  clauseReviewedAt?: string;
  contractStipulationReviewed?: boolean;
  clauseDetected?: boolean;
  directOrIndirectDerogationDetected?: boolean;
  benefitOfInsuranceForCarrierDetected?: boolean;
  burdenOfProofShiftDetected?: boolean;
  article40ExceptionAssessed?: boolean;
  article40ExceptionNotApplicable?: boolean;
  nullitySeverabilityRecorded?: boolean;
  reviewOutcomeRecorded?: boolean;
  clauseDetail?: string | Record<string, string | number>;
  reviewDetail?: string | Record<string, string | number>;
  insuranceBenefitDetail?: string | Record<string, string | number>;
  burdenShiftDetail?: string | Record<string, string | number>;
  article40ExceptionDetail?: string | Record<string, string | number>;
  clientReferenceId?: string;
}

export interface ProhibitedClauseEvidence {
  recordType: ProhibitedClauseRecordType | string;
  recordStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionNumber?: number;
  statusAtRecord?: DocumentLifecycleStatus | string;
  sourceArticle?: string;
  actorRoleCount?: number;
  documentVersionReferencePresent?: boolean;
  article40AgreementReferencePresent?: boolean;
  contractStipulationReviewed?: boolean;
  clauseDetected?: boolean;
  directOrIndirectDerogationDetected?: boolean;
  benefitOfInsuranceForCarrierDetected?: boolean;
  burdenOfProofShiftDetected?: boolean;
  article40ExceptionAssessed?: boolean;
  article40ExceptionNotApplicable?: boolean;
  nullitySeverabilityRecorded?: boolean;
  reviewOutcomeRecorded?: boolean;
  clauseReviewedAtPresent?: boolean;
  clauseDetailPresent?: boolean;
  reviewDetailPresent?: boolean;
  insuranceBenefitDetailPresent?: boolean;
  burdenShiftDetailPresent?: boolean;
  article40ExceptionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export type ConsigneeDeliveryRequestType =
  | 'delivery_after_arrival'
  | 'rights_enforcement_notice'
  | 'delivery_receipt_and_second_copy_handover';

export interface ConsigneeDeliveryRequestRequest {
  requestType: ConsigneeDeliveryRequestType;
  arrivalAtDeliveryPlace?: boolean;
  deliveryRequested?: boolean;
  secondCopyRequested?: boolean;
  receiptCommitment?: boolean;
  chargesPaymentAcknowledged?: boolean;
  chargesDisputed?: boolean;
  securityFurnished?: boolean;
  chargesPaidAmounts?: Array<{ amount: string | number; currency: string }>;
  chargesPaidAt?: string;
  linkedChargesPaymentEvidenceAttachmentId?: string;
  securityFurnishedAt?: string;
  linkedChargesSecurityEvidenceAttachmentId?: string;
  lossEstablished?: boolean;
  article19DelayPeriodExpired?: boolean;
  rightsReservedNotice?: boolean;
  linkedDeliveryId?: string;
  linkedConsignmentNoteCopyRecordId?: string;
  goodsReceived?: boolean;
  secondCopyReceived?: boolean;
  receiptIssued?: boolean;
  receiptDetail?: string | Record<string, string | number>;
  chargesSecurityDetail?: string | Record<string, string | number>;
  enforcementBasisDetail?: string | Record<string, string | number>;
  reason?: string;
  clientReferenceId?: string;
}

export interface ConsigneeDeliveryRequestEvidence {
  deliveryRequestId?: string;
  deliveryRequestReferencePresent?: boolean;
  requestType: ConsigneeDeliveryRequestType | string;
  requestStatus?: 'recorded' | string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRequest?: DocumentLifecycleStatus | string;
  actorRoleCount?: number;
  arrivalAtDeliveryPlace?: boolean;
  deliveryRequested?: boolean;
  secondCopyRequested?: boolean;
  receiptCommitment?: boolean;
  chargesPaymentAcknowledged?: boolean;
  chargesDisputed?: boolean;
  securityFurnished?: boolean;
  consignmentNoteChargesComputed?: boolean;
  consignmentNoteChargesMatched?: boolean;
  chargesPaymentLineCount?: number;
  chargesPaymentAmountsPositive?: boolean;
  chargesPaymentCurrenciesRecognized?: boolean;
  chargesPaidAtPresent?: boolean;
  chargesPaymentEvidenceAttachmentReferencePresent?: boolean;
  securityFurnishedAtPresent?: boolean;
  chargesSecurityEvidenceAttachmentReferencePresent?: boolean;
  chargesEvidenceChronologyValid?: boolean;
  lossEstablished?: boolean;
  lossEvidenceReferencePresent?: boolean;
  article19DelayPeriodExpired?: boolean;
  article19DelayAssessmentReferencePresent?: boolean;
  rightsReservedNotice?: boolean;
  deliveryConfirmationReferencePresent?: boolean;
  consignmentNoteCopyReferencePresent?: boolean;
  goodsReceived?: boolean;
  secondCopyReceived?: boolean;
  receiptIssued?: boolean;
  receiptDetailPresent?: boolean;
  chargesSecurityDetailPresent?: boolean;
  enforcementBasisDetailPresent?: boolean;
  reasonPresent?: boolean;
}

export interface DocumentVersionEvidence {
  versionId: string;
  versionNumber?: number;
  digestAlgorithm?: string;
  digest?: string;
  xmlContentDigest?: string;
  digestAvailable?: boolean;
  canonicalizationMethod?: string;
  createdAt?: string;
  status?: DocumentLifecycleStatus | string;
}

export interface DocumentAuditEvent {
  eventId?: string;
  eventType: string;
  documentId?: string;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  eventTimestamp?: string;
  timestamp?: string;
  actorReferencePresent?: boolean;
  actorUserId?: string;
  actorRoles?: string[];
  requestMetadataPresent?: boolean;
  sourceIp?: string;
  userAgent?: string;
  details?: Record<string, unknown>;
}

export interface DocumentTask {
  taskId: string;
  status: 'open' | 'in_progress' | 'completed' | string;
  priority?: 'low' | 'medium' | 'high' | string;
  label?: string;
  href?: string;
  actionLabel?: string;
  roles?: string[];
  evidenceGapPresent?: boolean;
  evidenceGap?: string;
  assignedGroup?: string;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}

export interface DocumentTaskQueue {
  documentId: string;
  source: string;
  legalDisclaimer?: string;
  tasks: DocumentTask[];
}

const documentTaskQueueSourceLabels = new Set(['server-authored-baseline']);
const documentTaskIds = new Set([
  'contract_participant_identity_required',
  'entitled_party_access_required',
  'procedure_acceptance_required',
  'procedure_acceptance_role_coverage_required',
  'carrier_takeover_check_required',
  'carrier_requested_check_result_required',
  'carrier_reservation_review',
  'carrier_reservation_acknowledgement_review',
  'carrier_acceptance_required',
  'transit_start_required',
  'delivery_confirmation_required',
  'delivery_reservation_acknowledgement_review',
  'server_side_signature_required',
  'signature_verification_review',
  'contract_signature_role_coverage_required',
  'cmr_article5_copy_coverage_required',
  'ecmr_article6_receipt_access_handover_required',
  'ecmr_article6_supplementary_document_transfer_required',
  'supplementary_documents_review',
  'amendment_acknowledgement_review',
  'retention_governance_review',
  'final_xml_archive_review',
  'evidence_manifest_archive_review',
  'final_xml_download_completion_review',
  'evidence_manifest_download_completion_review',
  'supplementary_attachment_download_completion_review',
  'exception_review_required',
  'manual_governance_review_required',
  'manual_governance_completed',
  'retention_governance_required',
]);
const documentTaskStatuses = new Set(['open', 'in_progress', 'completed', 'deferred']);
const documentTaskPriorities = new Set(['low', 'medium', 'high']);
const documentTaskRoles = new Set(['owner', 'consignor', 'carrier', 'consignee', 'authority', 'delegate']);
const documentTaskAssignedGroups = new Set(['compliance_review', 'legal_review', 'operations_review']);

const operationalRoleCount = (roles: unknown): number | undefined =>
  Array.isArray(roles)
    ? uniqueNonEmptyStrings(
        roles
          .map((role) => safeManifestLabel(role, documentTaskRoles))
          .filter((role): role is string => Boolean(role) && role !== 'other')
      ).length
    : undefined;

const publicOperationalRoleCount = (count: unknown, roles: unknown): number | undefined => {
  const countFromRoles = operationalRoleCount(roles);
  return countFromRoles !== undefined ? countFromRoles : safePublicManifestCount(count);
};

const publicDocumentTaskFromApi = (task: unknown): DocumentTask | undefined => {
  if (!task || typeof task !== 'object') {
    return undefined;
  }
  const source = task as Record<string, unknown>;
  const result: DocumentTask = {
    taskId: safeManifestLabel(source.taskId, documentTaskIds) ?? 'other',
    status: safeManifestLabel(source.status, documentTaskStatuses) ?? 'other',
  };
  const priority = safeManifestLabel(source.priority, documentTaskPriorities);
  if (priority && priority !== 'other') {
    result.priority = priority;
  }
  if (Array.isArray(source.roles)) {
    const roles = source.roles
      .map((role) => safeManifestLabel(role, documentTaskRoles))
      .filter((role): role is string => Boolean(role && role !== 'other'));
    if (roles.length > 0) {
      result.roles = Array.from(new Set(roles)).sort();
    }
  }
  if (typeof source.evidenceGapPresent === 'boolean') {
    result.evidenceGapPresent = source.evidenceGapPresent;
  } else if (typeof source.evidenceGap === 'string' && source.evidenceGap.trim().length > 0) {
    result.evidenceGapPresent = true;
  }
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  if (typeof source.assignedGroup === 'string') {
    const assignedGroup = source.assignedGroup.trim().toLowerCase();
    if (documentTaskAssignedGroups.has(assignedGroup)) {
      result.assignedGroup = assignedGroup;
    }
  }
  return result;
};

const publicDocumentTaskQueueFromApi = (
  documentId: string,
  payload: unknown
): DocumentTaskQueue => {
  const source = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  const tasks = Array.isArray(source.tasks)
    ? source.tasks
        .map(publicDocumentTaskFromApi)
        .filter((task): task is DocumentTask => Boolean(task))
    : [];
  return {
    documentId,
    source: safeManifestLabel(source.source, documentTaskQueueSourceLabels) ?? 'other',
    tasks,
  };
};

const publicDocumentTaskStatusUpdateResultFromApi = (
  documentId: string,
  payload: unknown
): DocumentTaskStatusUpdateResult => {
  const source = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    documentId,
    task: publicDocumentTaskFromApi(source.task) ?? {
      taskId: 'other',
      status: 'other',
    },
  };
};

export interface DocumentWorkflowAction {
  actionId: string;
  targetStatus: DocumentLifecycleStatus;
  actorAuthorized: boolean;
  prerequisitesSatisfied: boolean;
  availability: 'available' | 'blocked_by_role' | 'blocked_by_prerequisite' | string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}

export interface DocumentAuthorityWorkflowAction {
  actionId: string;
  actionType?: string;
  actorAuthorized: boolean;
  prerequisitesSatisfied?: boolean;
  availability: 'available' | 'blocked_by_role' | 'blocked_by_prerequisite' | string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}

const workflowActionSourceLabels = new Set(['server-authored-lifecycle-policy']);
const workflowActionAvailabilities = new Set(['available', 'blocked_by_role', 'blocked_by_prerequisite']);
const workflowActionIdsByTargetStatus: Partial<Record<DocumentLifecycleStatus, string>> = {
  accepted_by_carrier: 'transition_to_accepted_by_carrier',
  in_transit: 'transition_to_in_transit',
  delivered: 'transition_to_delivered',
};
const workflowActionIds = new Set(Object.values(workflowActionIdsByTargetStatus));
const authorityWorkflowActionIds = new Set(['authority_document_review']);
const authorityWorkflowActionTypes = new Set(['authority_review']);

const publicDocumentWorkflowActionFromApi = (action: unknown): DocumentWorkflowAction | undefined => {
  if (!action || typeof action !== 'object') {
    return undefined;
  }
  const source = action as Record<string, unknown>;
  const targetStatus = safeManifestLabel(source.targetStatus, documentLifecycleStatuses);
  if (!targetStatus || targetStatus === 'other') {
    return undefined;
  }
  const result: DocumentWorkflowAction = {
    actionId: safeManifestLabel(source.actionId, workflowActionIds) ?? 'other',
    targetStatus: targetStatus as DocumentLifecycleStatus,
    actorAuthorized: source.actorAuthorized === true,
    prerequisitesSatisfied: source.prerequisitesSatisfied === true,
    availability: safeManifestLabel(source.availability, workflowActionAvailabilities) ?? 'other',
  };
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  return result;
};

const publicAuthorityWorkflowActionFromApi = (action: unknown): DocumentAuthorityWorkflowAction | undefined => {
  if (!action || typeof action !== 'object') {
    return undefined;
  }
  const source = action as Record<string, unknown>;
  const result: DocumentAuthorityWorkflowAction = {
    actionId: safeManifestLabel(source.actionId, authorityWorkflowActionIds) ?? 'other',
    actionType: safeManifestLabel(source.actionType, authorityWorkflowActionTypes) ?? 'other',
    actorAuthorized: source.actorAuthorized === true,
    prerequisitesSatisfied: source.prerequisitesSatisfied === true,
    availability: safeManifestLabel(source.availability, workflowActionAvailabilities) ?? 'other',
  };
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  return result;
};

const publicDocumentWorkflowActionsResponseFromApi = (
  documentId: string,
  payload: unknown
): DocumentWorkflowActionsResponse => {
  const source = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  const safeSource = safeManifestLabel(source.source, workflowActionSourceLabels);
  const actions = Array.isArray(source.actions)
    ? source.actions
        .map(publicDocumentWorkflowActionFromApi)
        .filter((action): action is DocumentWorkflowAction => Boolean(action))
    : [];
  const authorityActions = Array.isArray(source.authorityActions)
    ? source.authorityActions
        .map(publicAuthorityWorkflowActionFromApi)
        .filter((action): action is DocumentAuthorityWorkflowAction => Boolean(action))
    : [];
  const result: DocumentWorkflowActionsResponse = {
    documentId,
    source: safeSource ?? 'other',
    currentStatus: normalizeDocumentLifecycleStatus(
      typeof source.currentStatus === 'string' ? source.currentStatus : undefined
    ),
    actions,
  };
  if (authorityActions.length > 0) {
    result.authorityActions = authorityActions;
  }
  return result;
};

export interface AuthorityReviewRequest {
  outcome?: 'reviewed' | 'deferred';
  notes?: string;
}

export interface AuthorityReviewRecord {
  actionId: string;
  status: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  notesPresent?: boolean;
  reviewerRoleCount?: number;
}

const authorityReviewActionIds = new Set(['authority_document_review']);
const authorityReviewStatuses = new Set(['reviewed', 'deferred']);

const publicAuthorityReviewRecordFromApi = (review: unknown): AuthorityReviewRecord => {
  const result: AuthorityReviewRecord = {
    actionId: 'other',
    status: 'other',
  };
  if (!review || typeof review !== 'object') {
    return result;
  }
  const source = review as Record<string, unknown>;
  const actionId = safeManifestLabel(source.actionId, authorityReviewActionIds);
  if (actionId) {
    result.actionId = actionId;
  }
  const status = safeManifestLabel(source.status, authorityReviewStatuses);
  if (status) {
    result.status = status;
  }
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  if (typeof source.notesPresent === 'boolean') {
    result.notesPresent = source.notesPresent;
  } else if (typeof source.notes === 'string' && source.notes.trim().length > 0) {
    result.notesPresent = true;
  }
  const reviewerRoleCount = publicOperationalRoleCount(source.reviewerRoleCount, source.reviewerRoles);
  if (reviewerRoleCount !== undefined) {
    result.reviewerRoleCount = reviewerRoleCount;
  }
  return result;
};

export interface DocumentWorkflowActionsResponse {
  documentId: string;
  source: string;
  legalDisclaimer?: string;
  currentStatus: DocumentLifecycleStatus;
  actions: DocumentWorkflowAction[];
  authorityActions?: DocumentAuthorityWorkflowAction[];
}

export interface DocumentTaskStatusUpdateRequest {
  status: 'open' | 'in_progress' | 'completed';
  assignedGroup?: string;
}

export interface DocumentTaskStatusUpdateResult {
  documentId: string;
  legalDisclaimer?: string;
  task: DocumentTask;
}

export const amendmentAuthorizationBasisValues = [
  'cmr_article8_carrier_check',
  'cmr_article8_carrier_reservation',
  'cmr_article12_disposal_instruction',
  'cmr_articles14_15_carriage_impediment',
  'cmr_article22_dangerous_goods_notice',
  'cmr_articles34_35_successive_carrier_acceptance',
] as const;

export type AmendmentAuthorizationBasis = typeof amendmentAuthorizationBasisValues[number];

export interface AmendmentEvidenceManifestItem {
  amendmentId?: string;
  reason?: string;
  reasonPresent?: boolean;
  parentVersionId?: string;
  parentVersionReferencePresent?: boolean;
  parentVersionNumber?: number;
  status?: string;
  requestedBy?: string;
  requestedAt?: string;
  acknowledgementList?: AmendmentAcknowledgementEvidence[];
  acknowledgementCount?: number;
  acknowledgedActorRoleCount?: number;
  proposedChanges?: Record<string, unknown>;
  proposedChangeFieldCount?: number;
  documentVersionReferencePresent?: boolean;
  appliedVersionId?: string;
  appliedVersionReferencePresent?: boolean;
  appliedVersionNumber?: number;
  appliedAt?: string;
  lastAcknowledgementDecision?: string;
  authorizationBasis?: AmendmentAuthorizationBasis;
  authorizationEvidenceReferencePresent?: boolean;
  proposedXmlDigestAvailable?: boolean;
  legalDetermination?: boolean;
}

export interface AmendmentRequestPayload {
  reason: string;
  authorizationBasis: AmendmentAuthorizationBasis;
  proposedChanges: { xmlContent: string };
  clientReferenceId?: string;
}

export interface AmendmentEvidence extends AmendmentEvidenceManifestItem {
  amendmentId: string;
  proposedChanges?: Record<string, unknown>;
  parentVersionNumber?: number;
  requestedAt?: string;
  acknowledgementList?: AmendmentAcknowledgementEvidence[];
  appliedVersionId?: string;
}

export interface AmendmentAcknowledgementRequest {
  acknowledged: boolean;
  comment?: string;
}

export interface AmendmentAcknowledgementEvidence extends AmendmentAcknowledgementRequest {
  acknowledgementId?: string;
  amendmentId?: string;
  actorUserId?: string;
  actorRoles?: string[];
  actorRoleCount?: number;
  acknowledgementReferencePresent?: boolean;
  amendmentReferencePresent?: boolean;
  commentPresent?: boolean;
  decision?: string;
  acknowledgedAt?: string;
}

export interface AmendmentApplyResult {
  amendment: AmendmentEvidence;
  document: ApiDocument;
}

export interface ImmutableStorageEvidenceReference {
  evidenceId?: string;
  evidenceType?: string;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  sha256Digest?: string;
  digestAvailable?: boolean;
  digestAlgorithm?: string;
  storageKey?: string;
  retentionMode?: string;
  retainUntil?: string;
}

export interface EcmrArticle4IntegrityEvidence {
  sourceInstrument?: 'ECMR_PROTOCOL_2008' | string;
  sourceArticle?: '4' | string;
  sameParticularsAsCmrValidationGatePresent?: boolean;
  finalFormIntegrityProcedurePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  currentVersionDigestPresent?: boolean;
  currentVersionIntegrityValid?: boolean;
  currentVersionIntegrityCheckPerformed?: boolean;
  finalXmlArchiveReferencePresent?: boolean;
  finalXmlArchiveCurrentVersionReferencePresent?: boolean;
  finalXmlArchiveDigestPresent?: boolean;
  finalXmlArchiveObjectVersionReferencePresent?: boolean;
  supplementOrAmendmentRecordCount?: number;
  appliedAmendmentRecordCount?: number;
  amendmentDetectabilityEvidencePresent?: boolean;
  originalParticularsPreservationEvidencePresent?: boolean;
  technicalEvidenceOnly?: boolean;
  legalDetermination?: boolean;
}

export interface XmlValidationEvidence {
  xmlValidationId?: string;
  xmlValidationReferencePresent?: boolean;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  validatedAt?: string;
  valid?: boolean;
  errorCount?: number;
  schemaProfile?: string;
  validationContext?: string;
  actorRoles?: string[];
  actorRoleCount?: number;
}

export interface CmrBusinessValidationEvidence {
  cmrBusinessValidationId?: string;
  cmrBusinessValidationReferencePresent?: boolean;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  failedAt?: string;
  status?: string;
  validationContext?: string;
  instrument?: string;
  article?: string;
  errorCount?: number;
  warningCount?: number;
  findingCount?: number;
  actorRoles?: string[];
  actorRoleCount?: number;
}

export interface EcmrProtocolValidationEvidence {
  ecmrProtocolValidationId?: string;
  ecmrProtocolValidationReferencePresent?: boolean;
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  failedAt?: string;
  status?: string;
  validationContext?: string;
  instrument?: string;
  article?: string;
  errorCount?: number;
  warningCount?: number;
  findingCount?: number;
  actorRoles?: string[];
  actorRoleCount?: number;
}

export type EvidenceDownloadCompletionType = 'final_xml' | 'evidence_manifest_xml' | 'supplementary_attachment';

export interface EvidenceDownloadCompletionRequest {
  evidenceId?: string;
  evidenceType: EvidenceDownloadCompletionType;
}

export interface EvidenceDownloadCompletionResult {
  documentId?: string;
  documentVersionNumber?: number;
  documentVersionReferenceAvailable?: boolean;
  documentVersionReferencePresent?: boolean;
  actorRoleCount?: number;
  evidenceType?: EvidenceDownloadCompletionType | 'other';
  clientConfirmed: boolean;
  independentTransferProof?: boolean;
  confirmedAt?: string;
  evidenceReferencePresent?: boolean;
}

const publicEvidenceDownloadCompletionResultFromApi = (
  completion: unknown
): EvidenceDownloadCompletionResult => {
  if (!completion || typeof completion !== 'object') {
    return { clientConfirmed: false };
  }
  const source = completion as Record<string, unknown>;
  const result: EvidenceDownloadCompletionResult = {
    clientConfirmed: source.clientConfirmed === true,
  };
  const documentId = safeManifestVersionLabel(source.documentId);
  if (documentId) {
    result.documentId = documentId;
  }
  const evidenceType = safeManifestLabel(source.evidenceType, downloadCompletionEvidenceTypes);
  if (evidenceType) {
    result.evidenceType = evidenceType as EvidenceDownloadCompletionResult['evidenceType'];
  }
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  if (source.documentVersionReferenceAvailable === true) {
    result.documentVersionReferenceAvailable = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  const actorRoleCount = publicOperationalRoleCount(source.actorRoleCount, source.actorRoles);
  if (actorRoleCount !== undefined) {
    result.actorRoleCount = actorRoleCount;
  }
  if (typeof source.confirmedAt === 'string') {
    result.confirmedAt = source.confirmedAt;
  }
  if (source.evidenceReferencePresent === true || typeof source.evidenceId === 'string') {
    result.evidenceReferencePresent = true;
  }
  if (typeof source.independentTransferProof === 'boolean') {
    result.independentTransferProof = false;
  }
  return result;
};

export interface PublicImmutableStorageReference {
  evidenceType?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  digestAvailable?: boolean;
  digestAlgorithm?: string;
  retentionMode?: string;
  evidenceReferencePresent?: boolean;
  manifestReferencePresent?: boolean;
  payloadStoredExternally?: boolean;
  objectVersionReferencePresent?: boolean;
  archivedAtPresent?: boolean;
  retainUntilPresent?: boolean;
}

const publicDocumentVersionReferencePresentFromApi = (source: Record<string, unknown>): boolean =>
  source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string';

const publicImmutableStorageReferenceFromApi = (
  reference: unknown,
  sanitizeLabels = true
): PublicImmutableStorageReference | undefined => {
  if (!reference || typeof reference !== 'object') {
    return undefined;
  }
  const source = reference as Record<string, unknown>;
  const result: PublicImmutableStorageReference = {};
  const stringKeys: Array<keyof PublicImmutableStorageReference> = [
    'evidenceType',
    'digestAlgorithm',
    'retentionMode',
  ];
  const booleanKeys: Array<keyof PublicImmutableStorageReference> = [
    'documentVersionReferencePresent',
    'digestAvailable',
    'evidenceReferencePresent',
    'manifestReferencePresent',
    'payloadStoredExternally',
    'objectVersionReferencePresent',
    'archivedAtPresent',
    'retainUntilPresent',
  ];

  stringKeys.forEach((key) => {
    if (typeof source[key] === 'string') {
      if (sanitizeLabels && key === 'evidenceType') {
        result.evidenceType = safeManifestLabel(source[key], manifestEvidenceTypes);
      } else if (sanitizeLabels && key === 'digestAlgorithm') {
        result.digestAlgorithm = safeManifestLabel(source[key], digestAlgorithms);
      } else if (sanitizeLabels && key === 'retentionMode') {
        result.retentionMode = safeManifestLabel(source[key], retentionModes);
      } else {
        (result as Record<string, unknown>)[key] = source[key];
      }
    }
  });
  booleanKeys.forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  if (publicDocumentVersionReferencePresentFromApi(source)) {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  return result;
};

export interface PublicEvidenceManifestSummary {
  manifestReferencePresent?: boolean;
  documentReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  documentDigestAlgorithm?: string;
  documentDigestAvailable?: boolean;
  evidenceSections?: PublicEvidenceManifestSections;
}

export interface PublicEvidenceManifestSections {
  treatySnapshotMetadata?: PublicTreatySnapshotManifestMetadata;
  ecmrArticle4Integrity?: PublicEcmrArticle4IntegrityEvidence;
  procedureAcceptances?: PublicProcedureAcceptanceEvidence[];
  replacementByOtherMeansRecords?: PublicReplacementByOtherMeansEvidence[];
  signatureEvidence?: PublicSignatureEvidence[];
  supplementaryDocuments?: PublicSupplementaryDocumentEvidence[];
  carrierTakeoverCheckRequests?: PublicCarrierTakeoverCheckRequestEvidence[];
  carrierTakeoverChecks?: PublicCarrierTakeoverCheckEvidence[];
  carrierReservations?: PublicCarrierReservationEvidence[];
  carrierReservationAcknowledgements?: PublicReservationAcknowledgementEvidence[];
  deliveryConfirmation?: PublicDeliveryConfirmationEvidence;
  deliveryReservationAcknowledgements?: PublicReservationAcknowledgementEvidence[];
  cmrArticle9Presumption?: PublicCmrArticle9PresumptionEvidence;
  cmrArticle10PackagingReservations?: PublicCmrArticle10PackagingReservationsEvidence;
  cmrArticle11CustomsDocuments?: PublicCmrArticle11CustomsDocumentsEvidence;
  disposalInstructionRecords?: PublicDisposalInstructionEvidence[];
  disposalExecutionNoticeRecords?: PublicDisposalExecutionNoticeEvidence[];
  consigneeDeliveryRequestRecords?: PublicConsigneeDeliveryRequestEvidence[];
  carriageImpedimentRecords?: PublicCarriageImpedimentEvidence[];
  carriageAccountingRecords?: PublicCarriageAccountingEvidence[];
  carrierLiabilityIncidentRecords?: PublicCarrierLiabilityIncidentEvidence[];
  article18_2ClaimantRebuttalRecords?: PublicArticle18_2ClaimantRebuttalEvidence[];
  deliveryTimingAssessmentRecords?: PublicDeliveryTimingAssessmentEvidence[];
  compensationPaymentRecords?: PublicCompensationPaymentEvidence[];
  recoveryDeliverySettlementRecords?: PublicRecoveryDeliverySettlementEvidence[];
  recoveredGoodsDispositionRecords?: PublicRecoveredGoodsDispositionEvidence[];
  codDeliveryOmissionRecords?: PublicCodDeliveryOmissionEvidence[];
  dangerousGoodsNoticeRecords?: PublicDangerousGoodsNoticeEvidence[];
  compensationAssessmentRecords?: PublicCompensationAssessmentEvidence[];
  compensationInterestRecords?: PublicCompensationInterestEvidence[];
  liabilityLimitationRecords?: PublicLiabilityLimitationEvidence[];
  claimReservationRecords?: PublicClaimReservationEvidence[];
  disputeProcedureRecords?: PublicDisputeProcedureEvidence[];
  successiveCarrierRecords?: PublicSuccessiveCarrierEvidence[];
  multimodalCarriageRecords?: PublicMultimodalCarriageEvidence[];
  consignmentNoteCopyRecords?: PublicConsignmentNoteCopyEvidence[];
  ecmrReceiptAccessRequestRecords?: PublicEcmrReceiptAccessRequestEvidence[];
  ecmrReceiptAccessRecords?: PublicEcmrReceiptAccessEvidence[];
  senderParticularsResponsibilityRecords?: PublicSenderParticularsResponsibilityEvidence[];
  prohibitedClauseRecords?: PublicProhibitedClauseEvidence[];
  retentionPolicy?: PublicRetentionPolicyManifestEvidence;
  legalHold?: PublicLegalHoldManifestEvidence;
  xmlValidationRecords?: XmlValidationEvidence[];
  cmrBusinessValidationRecords?: CmrBusinessValidationEvidence[];
  ecmrProtocolValidationRecords?: EcmrProtocolValidationEvidence[];
  signatureVerificationRecords?: PublicSignatureVerificationEvidence[];
  lifecycleEventRecords?: PublicLifecycleEventEvidence[];
  exceptionReviewTaskRecords?: PublicExceptionReviewTaskEvidence[];
  integrityCompromisedRecords?: PublicIntegrityCompromisedEvidence[];
  attachmentUploadRejectionRecords?: PublicAttachmentUploadRejectionEvidence[];
  immutableStorageReferences?: PublicImmutableStorageReference[];
  evidenceDownloadCompletions?: PublicEvidenceDownloadCompletion[];
  evidenceTransferAuditCorrelations?: PublicTransferAuditCorrelation[];
  finalXmlRetrievalRecords?: PublicFinalXmlRetrievalEvidence[];
  evidenceManifestExportRecords?: PublicEvidenceManifestExportEvidence[];
  documentAccessRecords?: PublicDocumentAccessEvidence[];
  attachmentAccessRecords?: PublicAttachmentAccessEvidence[];
  tombstoneRecords?: PublicTombstoneEvidence[];
  appendOnlyEvidenceStoreRecords?: PublicAppendOnlyEvidenceStoreRecord[];
  amendmentRequests?: PublicAmendmentManifestEvidence[];
}

export interface PublicTreatySnapshotInstrumentMetadata {
  source?: string;
  statusDate?: string;
  partyCount?: number;
  scopeRequirementId?: string;
}

export interface PublicTreatySnapshotManifestMetadata {
  cmrConvention?: PublicTreatySnapshotInstrumentMetadata;
  ecmrProtocol?: PublicTreatySnapshotInstrumentMetadata;
  finalProvisionsGovernance?: string;
  countryCodeListsExposed?: boolean;
  territorialReservationsEvaluated?: boolean;
  legalDetermination?: boolean;
}

export interface PublicEcmrArticle4IntegrityEvidence {
  sourceInstrument?: string;
  sourceArticle?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  sameParticularsAsCmrValidationGatePresent?: boolean;
  finalFormIntegrityProcedurePresent?: boolean;
  currentVersionDigestPresent?: boolean;
  currentVersionIntegrityValid?: boolean;
  currentVersionIntegrityCheckPerformed?: boolean;
  finalXmlArchiveReferencePresent?: boolean;
  finalXmlArchiveCurrentVersionReferencePresent?: boolean;
  finalXmlArchiveDigestPresent?: boolean;
  finalXmlArchiveObjectVersionReferencePresent?: boolean;
  supplementOrAmendmentRecordCount?: number;
  appliedAmendmentRecordCount?: number;
  amendmentDetectabilityEvidencePresent?: boolean;
  originalParticularsPreservationEvidencePresent?: boolean;
  technicalEvidenceOnly?: boolean;
  legalDetermination?: boolean;
}

export interface PublicReplacementByOtherMeansEvidence {
  replacementReferencePresent?: boolean;
  replacementMedium?: string;
  replacementStatus?: string;
  recordedAt?: string;
  replacedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtReplacement?: string;
  actorRoleCount?: number;
  reasonPresent?: boolean;
  externalReferencePresent?: boolean;
}

export interface PublicAmendmentManifestEvidence {
  status?: string;
  requestedAt?: string;
  parentVersionReferencePresent?: boolean;
  parentVersionNumber?: number;
  acknowledgementCount?: number;
  acknowledgedActorRoleCount?: number;
  proposedChangeFieldCount?: number;
  documentVersionReferencePresent?: boolean;
  appliedVersionReferencePresent?: boolean;
  lastAcknowledgementDecision?: string;
}

export interface PublicProcedureAcceptanceEvidence {
  procedureAcceptanceReferencePresent?: boolean;
  procedureSetReferencePresent?: boolean;
  procedureSetVersion?: string;
  accepted?: boolean;
  acceptedRoleCount?: number;
  procedureSetDigestAvailable?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  article5CategoryCount?: number;
  acceptedAt?: string;
  contractParticipantReferencePresent?: boolean;
}

export interface PublicSignatureEvidence {
  timestamp?: string;
  digestAlgorithm?: string;
  signatureReferencePresent?: boolean;
  signerRole?: string;
  signatureMethod?: string;
  verificationStatus?: string;
  signedDigestAvailable?: boolean;
  uniqueSignerLinkPresent?: boolean;
  signerIdentityEvidencePresent?: boolean;
  soleControlAssurancePresent?: boolean;
  dataIntegrityProtectionPresent?: boolean;
  article3AssurancePresent?: boolean;
  contractParticipantReferencePresent?: boolean;
  certificateAvailable?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}

export interface PublicSupplementaryDocumentEvidence {
  mimeType?: string;
  sizeBytes?: number;
  digestAlgorithm?: string;
  uploadedAt?: string;
  documentType?: string;
  attachmentReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  evidenceType?: string;
  digestAvailable?: boolean;
  payloadStoredExternally?: boolean;
  ecmrLinkReference?: string;
  ecmrLinkReferencePresent?: boolean;
}

export interface PublicCarrierTakeoverCheckRequestEvidence {
  requestedAt?: string;
  documentVersionNumber?: number;
  statusAtRequest?: string;
  checkRequestReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  requestedChecks?: string[];
  requestedCheckCount?: number;
  grossWeightRequested?: boolean;
  quantityRequested?: boolean;
  contentsRequested?: boolean;
  actorRoleCount?: number;
  reasonPresent?: boolean;
}

export interface PublicCarrierTakeoverCheckEvidence {
  result?: string;
  checkedAt?: string;
  recordedAt?: string;
  documentVersionNumber?: number;
  statusAtCheck?: string;
  packageCountChecked?: boolean;
  marksAndNumbersChecked?: boolean;
  apparentConditionChecked?: boolean;
  grossWeightChecked?: boolean;
  quantityChecked?: boolean;
  contentsChecked?: boolean;
  takeoverCheckReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  actorRoleCount?: number;
  reservationReferencePresent?: boolean;
  commentPresent?: boolean;
}

export interface PublicCarrierReservationEvidence {
  createdAt?: string;
  documentVersionNumber?: number;
  reservationReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  category?: string;
  statusAtReservation?: string;
  actorRoleCount?: number;
  groundsPresent?: boolean;
  packageReferencePresent?: boolean;
  observedValuePresent?: boolean;
  expectedValuePresent?: boolean;
}

export interface PublicReservationAcknowledgementEvidence {
  decision?: string;
  acknowledgedAt?: string;
  statusAtAcknowledgement?: string;
  acknowledgementReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  actorRoleCount?: number;
  reservationReferencePresent?: boolean;
  deliveryReferencePresent?: boolean;
}

export interface PublicDeliveryConfirmationEvidence {
  deliveredAt?: string;
  confirmedAt?: string;
  documentVersionNumber?: number;
  deliveryReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  statusAtConfirmation?: string;
  recipientNamePresent?: boolean;
  deliveryLocationPresent?: boolean;
  reservationCount?: number;
  deliverySignatureAvailable?: boolean;
  signatureMethod?: string;
  actorRoleCount?: number;
}

export interface PublicCmrArticle9PresumptionEvidence {
  presumptionApplies?: boolean;
  basis?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  carrierTakeoverCheckAccepted?: boolean;
  packageCountAndMarksCorrespondencePresumed?: boolean;
  goodsAndPackagingGoodConditionPresumed?: boolean;
  specificCarrierReservationCount?: number;
}

export interface PublicCmrArticle10PackagingReservationsEvidence {
  packagingReservationEvidencePresent?: boolean;
  sourceInstrument?: string;
  sourceArticle?: string;
  basis?: string;
  packagingReservationCount?: number;
  currentVersionLinkedReservationCount?: number;
  ambiguousReservationCount?: number;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  liabilityDetermination?: boolean;
}

export interface PublicCmrArticle11CustomsDocumentsEvidence {
  documentsAvailable?: boolean;
  sourceInstrument?: string;
  sourceArticle?: string;
  customsFormalitiesDocumentCount?: number;
  currentVersionLinkedDocumentCount?: number;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  digestEvidenceAvailable?: boolean;
  payloadStoredExternally?: boolean;
  documentTypeCount?: number;
  documentTypes?: string[];
}

export interface PublicDisposalInstructionEvidence {
  instructionReferencePresent?: boolean;
  instructionType?: string;
  instructionStatus?: string;
  recordedAt?: string;
  requestedExecutionAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  consignmentNoteCopyReferencePresent?: boolean;
  statusAtInstruction?: string;
  actorRoleCount?: number;
  sourceInstrument?: string;
  sourceArticle?: string;
  rightOfDisposalNotCeased?: boolean;
  firstCopyInstructionProduced?: boolean;
  carrierIndemnityAccepted?: boolean;
  instructionFeasible?: boolean;
  doesNotPrejudiceOtherConsignments?: boolean;
  doesNotDivideConsignment?: boolean;
  successiveConsigneeInstructionFinal?: boolean;
  reasonPresent?: boolean;
  newDeliveryPlacePresent?: boolean;
  newConsigneePresent?: boolean;
}

export interface PublicDisposalExecutionNoticeEvidence {
  noticeReferencePresent?: boolean;
  noticeType?: string;
  noticeStatus?: string;
  recordedAt?: string;
  notificationSentAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtNotice?: string;
  actorRoleCount?: number;
  sourceInstrument?: string;
  sourceArticle?: string;
  instructionType?: string;
  instructionReferencePresent?: boolean;
  unableToExecute?: boolean;
  notificationSent?: boolean;
  notificationTargetRole?: string;
  reasonPresent?: boolean;
  notificationDetailPresent?: boolean;
}

export interface PublicConsigneeDeliveryRequestEvidence {
  deliveryRequestReferencePresent?: boolean;
  requestType?: string;
  requestStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRequest?: string;
  actorRoleCount?: number;
  sourceInstrument?: string;
  sourceArticle?: string;
  arrivalAtDeliveryPlace?: boolean;
  deliveryRequested?: boolean;
  secondCopyRequested?: boolean;
  receiptCommitment?: boolean;
  chargesPaymentAcknowledged?: boolean;
  chargesDisputed?: boolean;
  securityFurnished?: boolean;
  consignmentNoteChargesComputed?: boolean;
  consignmentNoteChargesMatched?: boolean;
  chargesPaymentLineCount?: number;
  chargesPaymentAmountsPositive?: boolean;
  chargesPaymentCurrenciesRecognized?: boolean;
  chargesPaidAtPresent?: boolean;
  chargesPaymentEvidenceAttachmentReferencePresent?: boolean;
  securityFurnishedAtPresent?: boolean;
  chargesSecurityEvidenceAttachmentReferencePresent?: boolean;
  chargesEvidenceChronologyValid?: boolean;
  lossEstablished?: boolean;
  lossEvidenceReferencePresent?: boolean;
  article19DelayPeriodExpired?: boolean;
  article19DelayAssessmentReferencePresent?: boolean;
  rightsReservedNotice?: boolean;
  deliveryConfirmationReferencePresent?: boolean;
  consignmentNoteCopyReferencePresent?: boolean;
  goodsReceived?: boolean;
  secondCopyReceived?: boolean;
  receiptIssued?: boolean;
  receiptDetailPresent?: boolean;
  chargesSecurityDetailPresent?: boolean;
  enforcementBasisDetailPresent?: boolean;
  reasonPresent?: boolean;
}

export interface PublicCarriageImpedimentEvidence {
  impedimentReferencePresent?: boolean;
  impedimentType?: string;
  impedimentStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtImpediment?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  instructionsRequested?: boolean;
  instructionRequestTargetRole?: string;
  instructionRequestSentAtPresent?: boolean;
  instructionRequestReceivedAtPresent?: boolean;
  instructionRequestEvidenceAttachmentReferencePresent?: boolean;
  instructionRequestChronologyValid?: boolean;
  instructionsUnavailableInReasonableTime?: boolean;
  bestInterestsActionTaken?: boolean;
  arrivalAtDeliveryPlace?: boolean;
  consigneeRefusedGoods?: boolean;
  article14Or15ImpedimentBasis?: boolean;
  goodsUnloadedForAccount?: boolean;
  goodsHeldForEntitledPerson?: boolean;
  thirdPartyCustodianUsed?: boolean;
  thirdPartyCustodianSelectedWithReasonableCare?: boolean;
  saleReason?: string;
  saleProcedureBasis?: string;
  goodsLocationCountryCodePresent?: boolean;
  goodsLocationDetailPresent?: boolean;
  saleProcedureReferencePresent?: boolean;
  saleProcedureAssessedAtPresent?: boolean;
  saleProcedureEvidenceAttachmentReferencePresent?: boolean;
  saleProcedureChronologyValid?: boolean;
  proceedsPlacedAtDisposal?: boolean;
  saleProcedureLawOrCustomAcknowledged?: boolean;
  expensesRecoverableClaimed?: boolean;
  reasonPresent?: boolean;
  instructionRequestDetailPresent?: boolean;
  alternativeCarriageConditionsPresent?: boolean;
  storageOrSaleDetailPresent?: boolean;
}

export interface PublicCarriageAccountingEvidence {
  accountingType?: string;
  accountingStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAccounting?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  linkedImpedimentType?: string;
  actorRoleCount?: number;
  impedimentReferencePresent?: boolean;
  amountPresent?: boolean;
  positiveAmount?: boolean;
  currencyPresent?: boolean;
  recognizedCurrency?: boolean;
  accountingAtPresent?: boolean;
  accountingEvidenceAttachmentReferencePresent?: boolean;
  accountingChronologyValid?: boolean;
  instructionRequestEvidencePresent?: boolean;
  grossSaleProceedsAmountPresent?: boolean;
  deductedSaleExpensesAmountPresent?: boolean;
  netSaleProceedsReconciled?: boolean;
  saleProceedsAccountingReferencePresent?: boolean;
  totalChargesDueAmountPresent?: boolean;
  chargeShortfallReconciled?: boolean;
  carrierWrongfulActOrNeglectNotAsserted?: boolean;
  chargeableAgainstGoods?: boolean;
  proceedsPlacedAtDisposal?: boolean;
  chargesExceedProceeds?: boolean;
  accountingDetailPresent?: boolean;
}

export interface PublicCarrierLiabilityIncidentEvidence {
  incidentType?: string;
  incidentStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtIncident?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  occurredBetweenTakeoverAndDelivery?: boolean;
  lossObserved?: boolean;
  damageObserved?: boolean;
  delayObserved?: boolean;
  claimantFaultOrNeglectAlleged?: boolean;
  claimantInstructionAlleged?: boolean;
  inherentViceAlleged?: boolean;
  unavoidableCircumstancesAlleged?: boolean;
  specialRiskClaimed?: boolean;
  specialRiskType?: string;
  specialRiskPresumptionClaimed?: boolean;
  temperatureControlledVehicle?: boolean;
  equipmentStepsEvidencePresent?: boolean;
  livestockCareStepsEvidencePresent?: boolean;
  disposalInstructionReferencePresent?: boolean;
  instructionNotCarriedOut?: boolean;
  instructionCarriedOutWithoutFirstCopy?: boolean;
  claimantEntitlementAsserted?: boolean;
  lossOrDamageCaused?: boolean;
  takingOverAtPresent?: boolean;
  incidentAtPresent?: boolean;
  deliveryAtPresent?: boolean;
  incidentEvidenceAttachmentReferencePresent?: boolean;
  carrierProofRequired?: boolean;
  carrierProofEvidenceAttachmentReferencePresent?: boolean;
  incidentChronologyValid?: boolean;
  openUnsheetedVehicleAgreementSpecifiedInConsignmentNote?: boolean;
  article18OpenVehiclePresumptionExceptionAssessed?: boolean;
  article18OpenVehiclePresumptionExceptionAbsent?: boolean;
  article17_4aVehicleUseAssessmentRecorded?: boolean;
  article17_4aOpenUnsheetedVehicleUseReported?: boolean;
  article17_4aAssessmentDetailPresent?: boolean;
  article17_4aEvidenceAttachmentReferencePresent?: boolean;
  article17_3NonRelievingFactorsAssessed?: boolean;
  article17_3NonRelievingFactorReported?: boolean;
  article17_3AssessmentDetailPresent?: boolean;
  article17_3EvidenceAttachmentReferencePresent?: boolean;
  article18_1BurdenAssessmentRecorded?: boolean;
  article18_1CauseProofReported?: boolean;
  article18_1AssessmentDetailPresent?: boolean;
  article18_1EvidenceAttachmentReferencePresent?: boolean;
  article18_2CircumstancesAssessmentRecorded?: boolean;
  article18_2CircumstancesEstablishedReported?: boolean;
  article18_2AssessmentDetailPresent?: boolean;
  article18_2EvidenceAttachmentReferencePresent?: boolean;
  article17_4bPackingConditionAssessmentRecorded?: boolean;
  article17_4bPackingAbsentOrDefectiveReported?: boolean;
  article17_4bGoodsLiabilityWithoutProperPackingReported?: boolean;
  article17_4bAssessmentDetailPresent?: boolean;
  article17_4bEvidenceAttachmentReferencePresent?: boolean;
  article17_4cOperationAssessmentRecorded?: boolean;
  article17_4cQualifyingOperationReported?: boolean;
  article17_4cActorCapacityAssessmentRecorded?: boolean;
  article17_4cQualifyingActorCapacityReported?: boolean;
  article17_4cAssessmentDetailPresent?: boolean;
  article17_4cEvidenceAttachmentReferencePresent?: boolean;
  article17_4dNatureAssessmentRecorded?: boolean;
  article17_4dParticularExposureReported?: boolean;
  article17_4dSusceptibilityBasisAssessmentRecorded?: boolean;
  article17_4dQualifyingSusceptibilityBasisReported?: boolean;
  article17_4dAssessmentDetailPresent?: boolean;
  article17_4dEvidenceAttachmentReferencePresent?: boolean;
  article17_4eMarkingAssessmentRecorded?: boolean;
  article17_4eQualifyingMarkingConditionReported?: boolean;
  article17_4eDeficiencyBasisAssessmentRecorded?: boolean;
  article17_4eQualifyingDeficiencyBasisReported?: boolean;
  article17_4eAssessmentDetailPresent?: boolean;
  article17_4eEvidenceAttachmentReferencePresent?: boolean;
  article17_4fLivestockAssessmentRecorded?: boolean;
  article17_4fLivestockCarriageReported?: boolean;
  article17_4fAssessmentDetailPresent?: boolean;
  article17_4fEvidenceAttachmentReferencePresent?: boolean;
  article18_4EquipmentStepsAssessmentRecorded?: boolean;
  article18_4ChoiceMaintenanceUseStepsReported?: boolean;
  article18_4SpecialInstructionsAssessmentRecorded?: boolean;
  article18_4SpecialInstructionsConsistencyValid?: boolean;
  article18_4AssessmentDetailPresent?: boolean;
  article18_4EvidenceAttachmentReferencePresent?: boolean;
  article18_5NormallyIncumbentStepsAssessmentRecorded?: boolean;
  article18_5NormallyIncumbentStepsReported?: boolean;
  article18_5SpecialInstructionsAssessmentRecorded?: boolean;
  article18_5SpecialInstructionsConsistencyValid?: boolean;
  article18_5AssessmentDetailPresent?: boolean;
  article18_5EvidenceAttachmentReferencePresent?: boolean;
  article17_5MixedCausationAssessed?: boolean;
  article17_5MixedCausationReported?: boolean;
  article17_5ContributionSharesPresent?: boolean;
  article17_5ContributionSharesReconciled?: boolean;
  article17_5AssessmentDetailPresent?: boolean;
  article17_5EvidenceAttachmentReferencePresent?: boolean;
  incidentDetailPresent?: boolean;
  claimantStatementDetailPresent?: boolean;
  carrierProofDetailPresent?: boolean;
  liabilityDetermination?: boolean;
}

export interface PublicArticle18_2ClaimantRebuttalEvidence {
  rebuttalStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRebuttal?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  carrierSpecialRiskPresumptionReferencePresent?: boolean;
  claimantRoleAttributed?: boolean;
  lossOrDamageAttributionDisputeRecorded?: boolean;
  claimantCounterEvidenceSubmissionRecorded?: boolean;
  counterEvidenceDetailPresent?: boolean;
  counterEvidenceAttachmentReferencePresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicDeliveryTimingAssessmentEvidence {
  assessmentType?: string;
  assessmentStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAssessment?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  goodsNotDelivered?: boolean;
  agreedTimeLimitPresent?: boolean;
  agreedTimeLimitExpired?: boolean;
  agreedTimeLimitConsignmentNoteReferencePresent?: boolean;
  article19AgreedTimeLimitComputed?: boolean;
  article19AgreedTimeLimitExceeded?: boolean;
  article19ReasonableDurationComputed?: boolean;
  article19ElapsedDurationOrderValid?: boolean;
  diligentCarrierAllowedDurationPresent?: boolean;
  diligentCarrierAllowedDurationPositive?: boolean;
  reasonableDurationExceeded?: boolean;
  partialLoadAssemblyDurationPresent?: boolean;
  partialLoadAssemblyDurationPositive?: boolean;
  partialLoadAssemblyTimeConsidered?: boolean;
  diligentCarrierDurationExceeded?: boolean;
  thirtyDaysAfterAgreedLimitExpired?: boolean;
  sixtyDaysAfterTakeoverExpired?: boolean;
  article20LossPresumptionPeriodComputed?: boolean;
  article20LossPresumptionPeriodSatisfied?: boolean;
  article20LossPresumptionAssessmentReferencePresent?: boolean;
  article20CompensationAssessmentReferencePresent?: boolean;
  article20CompensationPaymentReferencePresent?: boolean;
  compensationReceived?: boolean;
  recoveryNotificationRequested?: boolean;
  writtenAcknowledgementIssued?: boolean;
  goodsRecovered?: boolean;
  recoveryNotificationSent?: boolean;
  deliveryRequested?: boolean;
  article20DeliveryRequestWindowComputed?: boolean;
  article20DeliveryRequestChronologyValid?: boolean;
  article20DeliveryRequestWithinThirtyDays?: boolean;
  chargesAndRefundConditionAcknowledged?: boolean;
  takingOverAtPresent?: boolean;
  agreedTimeLimitAtPresent?: boolean;
  actualDeliveryAtPresent?: boolean;
  assessmentAtPresent?: boolean;
  recoveryNotificationAtPresent?: boolean;
  recoveryNotificationReceivedAtPresent?: boolean;
  deliveryRequestAtPresent?: boolean;
  timingDetailPresent?: boolean;
  claimDetailPresent?: boolean;
  recoveryDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicCompensationPaymentEvidence {
  paymentType?: string;
  paymentStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtPayment?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  paymentEvidenceType?: string;
  lossPresumptionAssessmentReferencePresent?: boolean;
  compensationAssessmentReferencePresent?: boolean;
  paymentEvidenceAttachmentReferencePresent?: boolean;
  compensationForMissingGoods?: boolean;
  paymentSettled?: boolean;
  recipientAcknowledgedReceipt?: boolean;
  paymentAmountPresent?: boolean;
  paymentAmountPositive?: boolean;
  paymentCurrencyPresent?: boolean;
  paymentCurrencyRecognized?: boolean;
  paidAtPresent?: boolean;
  paymentDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicRecoveryDeliverySettlementEvidence {
  settlementType?: string;
  settlementStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtSettlement?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  recoveryAssessmentReferencePresent?: boolean;
  compensationPaymentReferencePresent?: boolean;
  deliveryReferencePresent?: boolean;
  chargesPaymentEvidenceAttachmentReferencePresent?: boolean;
  compensationRefundEvidenceAttachmentReferencePresent?: boolean;
  consignmentNoteChargesComputed?: boolean;
  consignmentNoteChargesMatched?: boolean;
  chargesPaymentLineCount?: number;
  chargesPaymentAmountsPositive?: boolean;
  chargesPaymentCurrenciesRecognized?: boolean;
  chargesPaymentSettled?: boolean;
  chargesPaidAtPresent?: boolean;
  compensationRefundAmountPresent?: boolean;
  compensationRefundAmountNonNegative?: boolean;
  compensationRefundCurrencyPresent?: boolean;
  compensationRefundCurrencyRecognized?: boolean;
  chargesIncludedInCompensationAmountPresent?: boolean;
  chargesIncludedInCompensationAmountNonNegative?: boolean;
  compensationRefundFormulaComputed?: boolean;
  compensationRefundFormulaMatched?: boolean;
  compensationRefundSettled?: boolean;
  compensationRefundedAtPresent?: boolean;
  deliveryChronologyComputed?: boolean;
  deliveryChronologyValid?: boolean;
  delayClaimsRightsPreserved?: boolean;
  settlementDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicRecoveredGoodsDispositionEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  dispositionBasis?: string;
  dispositionActionType?: string;
  compensationPaymentReferencePresent?: boolean;
  recoveryNoticeReferenceRequired?: boolean;
  recoveryNoticeReferencePresent?: boolean;
  recoveryEvidenceAttachmentReferencePresent?: boolean;
  dispositionEvidenceAttachmentReferencePresent?: boolean;
  goodsRecovered?: boolean;
  compensationPaidAtPresent?: boolean;
  goodsRecoveredAtPresent?: boolean;
  dispositionAtPresent?: boolean;
  paymentRecoveryDispositionChronologyComputed?: boolean;
  paymentRecoveryDispositionChronologyValid?: boolean;
  writtenRecoveryNotificationRequestAbsent?: boolean;
  recoveryNotificationSent?: boolean;
  recoveryNotificationReceivedAtPresent?: boolean;
  instructionsAbsent?: boolean;
  instructionsAbsenceAssessedAtPresent?: boolean;
  article20ThirtyDayInstructionPeriodComputed?: boolean;
  article20ThirtyDayInstructionPeriodElapsed?: boolean;
  article20OneYearRecoveryPeriodComputed?: boolean;
  goodsRecoveredWithinOneYearOfCompensation?: boolean;
  goodsRecoveredMoreThanOneYearAfterCompensation?: boolean;
  goodsLocationCountryCodePresent?: boolean;
  goodsLocationDetailPresent?: boolean;
  applicableLocalLawIdentified?: boolean;
  applicableLawDetailPresent?: boolean;
  dispositionUnderLocalLawRecorded?: boolean;
  dispositionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicCodDeliveryOmissionEvidence {
  omissionType?: string;
  omissionStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtOmission?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  goodsDeliveredToConsignee?: boolean;
  codChargeRequiredByContract?: boolean;
  codChargeConsignmentNoteReferencePresent?: boolean;
  codChargeUncollected?: boolean;
  codAmountPresent?: boolean;
  codAmountPositive?: boolean;
  codCurrencyPresent?: boolean;
  codCurrencyRecognized?: boolean;
  consignorCompensationClaimed?: boolean;
  carrierActionAgainstConsigneeReserved?: boolean;
  deliveryAtPresent?: boolean;
  omissionDetailPresent?: boolean;
  compensationClaimDetailPresent?: boolean;
  consigneeActionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicDangerousGoodsNoticeEvidence {
  noticeType?: string;
  noticeStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtNotice?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  dangerousGoodsHandedToCarrier?: boolean;
  exactNatureInformed?: boolean;
  precautionsRequired?: boolean;
  precautionsIndicated?: boolean;
  informationEnteredInConsignmentNote?: boolean;
  carrierKnowledgeProvedByOtherMeans?: boolean;
  dangerousGoodsConsignmentNoteReferencePresent?: boolean;
  carrierKnowledgeProofAttachmentReferencePresent?: boolean;
  carrierDidNotKnowDangerousNature?: boolean;
  carrierActionType?: string;
  carrierActionTaken?: boolean;
  withoutCompensationBasisRecorded?: boolean;
  senderExpenseLiabilityClaimed?: boolean;
  noticeAtPresent?: boolean;
  actionAtPresent?: boolean;
  dangerNatureDetailPresent?: boolean;
  precautionsDetailPresent?: boolean;
  carrierKnowledgeProofDetailPresent?: boolean;
  carrierActionDetailPresent?: boolean;
  expenseClaimDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicCompensationAssessmentEvidence {
  assessmentType?: string;
  assessmentStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtAssessment?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  incidentReferencePresent?: boolean;
  takingOverPlaceAndTimeConsignmentNoteReferencePresent?: boolean;
  valuationEvidenceAttachmentReferencePresent?: boolean;
  article23LossExtentBasis?: CompensationArticle23LossExtentBasis | string;
  article23ChargeRefundBasis?: CompensationArticle23ChargeRefundBasis | string;
  article23WeightAndRefundEvidenceReferencePresent?: boolean;
  carrierLiabilityBasisRecorded?: boolean;
  goodsValueBasis?: string;
  liabilityLimitBasis?: string;
  goodsValueAmountPresent?: boolean;
  goodsValueAmountPositive?: boolean;
  goodsValueCurrencyPresent?: boolean;
  goodsValueCurrencyRecognized?: boolean;
  grossWeightShortPresent?: boolean;
  grossWeightShortPositive?: boolean;
  claimAmountPresent?: boolean;
  claimAmountPositive?: boolean;
  claimCurrencyPresent?: boolean;
  claimCurrencyRecognized?: boolean;
  carriageChargesAmountPresent?: boolean;
  carriageChargesAmountPositive?: boolean;
  carriageChargesCurrencyPresent?: boolean;
  carriageChargesCurrencyRecognized?: boolean;
  declaredValueAmountPresent?: boolean;
  declaredValueAmountPositive?: boolean;
  declaredValueCurrencyPresent?: boolean;
  declaredValueCurrencyRecognized?: boolean;
  specialInterestAmountPresent?: boolean;
  specialInterestAmountPositive?: boolean;
  specialInterestCurrencyPresent?: boolean;
  specialInterestCurrencyRecognized?: boolean;
  diminishedValueAmountPresent?: boolean;
  diminishedValueAmountPositive?: boolean;
  diminishedValueCurrencyPresent?: boolean;
  diminishedValueCurrencyRecognized?: boolean;
  article23LimitApplied?: boolean;
  carriageChargesRefundConsidered?: boolean;
  delayDamageProved?: boolean;
  carriageChargesLimitApplied?: boolean;
  damageAffectedWholeConsignment?: boolean;
  article25LossLimitApplied?: boolean;
  declaredValueEnteredInConsignmentNote?: boolean;
  declaredValueSurchargeAgreed?: boolean;
  declaredValueExceedsArticle23Limit?: boolean;
  declaredValueLimitSubstituted?: boolean;
  specialInterestEnteredInConsignmentNote?: boolean;
  specialInterestSurchargeAgreed?: boolean;
  additionalLossOrDamageProved?: boolean;
  specialInterestLimitApplied?: boolean;
  agreedTimeLimitExceeded?: boolean;
  higherCompensationBasisPresent?: boolean;
  assessmentAtPresent?: boolean;
  valuationDetailPresent?: boolean;
  calculationDetailPresent?: boolean;
  claimDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicCompensationInterestEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  compensationAssessmentReferencePresent?: boolean;
  compensationPayableBasisRecorded?: boolean;
  interestStartBasis?: string;
  claimSentInWriting?: boolean;
  legalProceedingsInstituted?: boolean;
  fivePercentPerAnnumApplied?: boolean;
  compensationAmountPresent?: boolean;
  compensationAmountPositive?: boolean;
  compensationCurrencyPresent?: boolean;
  compensationCurrencyRecognized?: boolean;
  interestAmountPresent?: boolean;
  interestAmountPositive?: boolean;
  interestCurrencyPresent?: boolean;
  interestCurrencyRecognized?: boolean;
  amountsNotExpressedInPaymentClaimCurrency?: boolean;
  paymentClaimCurrencyCountryBasisRecorded?: boolean;
  paymentPlaceBasisRecorded?: boolean;
  exchangeRateApplicableAtPaymentPlaceAndDate?: boolean;
  sourceAmountPresent?: boolean;
  sourceAmountPositive?: boolean;
  sourceCurrencyPresent?: boolean;
  sourceCurrencyRecognized?: boolean;
  targetCurrencyPresent?: boolean;
  targetCurrencyRecognized?: boolean;
  convertedAmountPresent?: boolean;
  convertedAmountPositive?: boolean;
  convertedCurrencyPresent?: boolean;
  convertedCurrencyRecognized?: boolean;
  interestStartAtPresent?: boolean;
  interestCalculatedAtPresent?: boolean;
  interestDateOrderComputed?: boolean;
  interestCalculatedAfterStart?: boolean;
  paymentAtPresent?: boolean;
  interestCalculationDetailPresent?: boolean;
  exchangeRateDetailPresent?: boolean;
  claimDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicLiabilityLimitationEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  liablePersonRole?: string;
  incidentReferencePresent?: boolean;
  claimArisesFromCarriageUnderCmr?: boolean;
  lossDamageOrDelayClaim?: boolean;
  extraContractualClaimUnderApplicableLaw?: boolean;
  cmrExclusionOrLimitInvoked?: boolean;
  article3ResponsiblePersonInvolved?: boolean;
  article3PersonScopeRecorded?: boolean;
  actingWithinScopeOfEmployment?: boolean;
  limitationOrBurdenShiftProvisionInvoked?: boolean;
  wilfulMisconductAlleged?: boolean;
  equivalentDefaultAlleged?: boolean;
  courtOrTribunalLawBasisRecorded?: boolean;
  limitationDisapplicationBasisRecorded?: boolean;
  claimDetailPresent?: boolean;
  applicableLawDetailPresent?: boolean;
  article3PersonDetailPresent?: boolean;
  misconductDetailPresent?: boolean;
  courtLawDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicClaimReservationEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  deliveryReferencePresent?: boolean;
  lossOrDamageApparent?: boolean;
  lossOrDamageNotApparent?: boolean;
  delayCompensationClaim?: boolean;
  reservationSentAtDelivery?: boolean;
  reservationSentInWriting?: boolean;
  reservationWithinSevenDaysExcludingHolidaysRecorded?: boolean;
  conditionCheckedWithCarrier?: boolean;
  contraryEvidenceReservationSentInWriting?: boolean;
  delayReservationSentInWriting?: boolean;
  reservationWithinTwentyOneDaysRecorded?: boolean;
  relevantStartDateExcludedFromLimitCalculation?: boolean;
  reasonableFacilitiesProvided?: boolean;
  timeLimitComputed?: boolean;
  withinComputedTimeLimit?: boolean;
  timeLimitComputationBasis?: string;
  publicHolidaysExcludedFromTimeLimit?: boolean;
  publicHolidayExclusionCount?: number;
  deliveryAtPresent?: boolean;
  conditionCheckedAtPresent?: boolean;
  goodsAvailableAtPresent?: boolean;
  reservationSentAtPresent?: boolean;
  reservationDetailPresent?: boolean;
  conditionCheckDetailPresent?: boolean;
  delayClaimDetailPresent?: boolean;
  investigationFacilityDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicDisputeProcedureEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  claimCategory?: string;
  limitationPeriodBasis?: string;
  limitationStartBasis?: string;
  limitationStartComputed?: boolean;
  limitationStartComputationBasis?: string;
  limitationStartedAtMatchesComputedBasis?: boolean;
  limitationExpiryComputed?: boolean;
  limitationExpiryComputationBasis?: string;
  limitationExpiresAtMatchesComputedBasis?: boolean;
  suspensionReleaseDateOrderComputed?: boolean;
  suspensionReleaseDatesOrdered?: boolean;
  partyDesignatedCourtOrTribunal?: boolean;
  defendantResidenceOrBusinessPlaceBasis?: boolean;
  contractBranchOrAgencyBasis?: boolean;
  takeoverPlaceBasis?: boolean;
  deliveryPlaceBasis?: boolean;
  contractingCountryCourtOrTribunal?: boolean;
  securityForCostsNotRequiredRecorded?: boolean;
  samePartiesAndGroundsRecorded?: boolean;
  pendingCompetentProceedingRecorded?: boolean;
  judgmentEnteredByCompetentCourt?: boolean;
  freshProceedingOnlyIfJudgmentNotEnforceableRecorded?: boolean;
  judgmentEnforceabilityFormalitiesOnlyRecorded?: boolean;
  meritsReopeningProhibitedRecorded?: boolean;
  wilfulMisconductOrEquivalentDefaultAlleged?: boolean;
  limitationPeriodRecorded?: boolean;
  limitationStartDateExcluded?: boolean;
  claimBarredNotAvailableByCounterclaimOrSetoffRecorded?: boolean;
  courtLawExtensionOrFreshAccrualBasisRecorded?: boolean;
  writtenClaimReceived?: boolean;
  carrierRejectedClaimInWriting?: boolean;
  documentsReturned?: boolean;
  claimPartlyAdmitted?: boolean;
  remainingPartStillInDispute?: boolean;
  receiptReplyReturnProofBurdenRecorded?: boolean;
  furtherSameObjectClaimsDoNotSuspendRecorded?: boolean;
  arbitrationClausePresent?: boolean;
  arbitrationTribunalCompetenceRecorded?: boolean;
  tribunalAppliesCmrConvention?: boolean;
  actionFiledAtPresent?: boolean;
  judgmentEnteredAtPresent?: boolean;
  judgmentEnforceableAtPresent?: boolean;
  deliveryAtPresent?: boolean;
  takeoverAtPresent?: boolean;
  agreedTimeLimitExpiresAtPresent?: boolean;
  contractMadeAtPresent?: boolean;
  limitationStartedAtPresent?: boolean;
  limitationExpiresAtPresent?: boolean;
  writtenClaimReceivedAtPresent?: boolean;
  claimRejectedAtPresent?: boolean;
  documentsReturnedAtPresent?: boolean;
  jurisdictionDetailPresent?: boolean;
  partyAgreementDetailPresent?: boolean;
  takeoverDeliveryPlaceDetailPresent?: boolean;
  pendingActionDetailPresent?: boolean;
  judgmentDetailPresent?: boolean;
  limitationCalculationDetailPresent?: boolean;
  writtenClaimDetailPresent?: boolean;
  rejectionDetailPresent?: boolean;
  arbitrationClauseDetailPresent?: boolean;
  courtOrTribunalDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicSuccessiveCarrierEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  allocationBasis?: string;
  recoveryLimitationStartBasis?: string;
  successiveReceiptDateOrderComputed?: boolean;
  successiveReceiptIssuedAfterAcceptance?: boolean;
  recoveryLimitationPeriodComputed?: boolean;
  recoveryLimitationComputationBasis?: string;
  recoveryClaimFiledWithinComputedLimit?: boolean;
  consignmentNoteCopyReferencePresent?: boolean;
  reservationReferencePresent?: boolean;
  compensationAssessmentReferencePresent?: boolean;
  singleContractCarriage?: boolean;
  successiveRoadCarrier?: boolean;
  goodsAccepted?: boolean;
  consignmentNoteAccepted?: boolean;
  becomesPartyByAcceptance?: boolean;
  datedSignedReceiptIssued?: boolean;
  carrierNameAddressEnteredOnSecondCopy?: boolean;
  article8ReservationsEnteredWhereApplicable?: boolean;
  article9AppliesBetweenSuccessiveCarriers?: boolean;
  liabilityForWholeOperationRecorded?: boolean;
  lossDamageOrDelayProceeding?: boolean;
  actionAgainstFirstCarrier?: boolean;
  actionAgainstLastCarrier?: boolean;
  actionAgainstEventCarrier?: boolean;
  counterclaimOrSetoffSameContract?: boolean;
  multiplePermittedCarriersJoined?: boolean;
  compensationPaidUnderCmr?: boolean;
  interestAndCostsIncluded?: boolean;
  responsibleCarrierSoleLiabilityRecorded?: boolean;
  multipleCarrierLiabilityApportioned?: boolean;
  liabilityCannotBeAttributed?: boolean;
  paymentShareApportionmentRecorded?: boolean;
  insolventCarrierShareUnpaid?: boolean;
  insolventShareReallocatedByPaymentShare?: boolean;
  judicialAuthorityDeterminedCompensation?: boolean;
  carrierGivenDueNoticeOfProceedings?: boolean;
  carrierHadOpportunityToAppear?: boolean;
  paymentValidityDisputeBarRecorded?: boolean;
  competentRecoveryForumRecorded?: boolean;
  allConcernedCarriersMayBeDefendants?: boolean;
  article31JudgmentRulesApplied?: boolean;
  article32LimitationApplied?: boolean;
  recoveryLimitationStartRecorded?: boolean;
  carriersAgreedAlternativeAllocation?: boolean;
  agreementLimitedToArticles37And38?: boolean;
  article40AgreementRecorded?: boolean;
  carrierAcceptedAtPresent?: boolean;
  receiptIssuedAtPresent?: boolean;
  compensationPaidAtPresent?: boolean;
  finalJudicialDecisionAtPresent?: boolean;
  recoveryClaimFiledAtPresent?: boolean;
  agreementRecordedAtPresent?: boolean;
  receiptDetailPresent?: boolean;
  carrierIdentityEntryDetailPresent?: boolean;
  reservationDetailPresent?: boolean;
  actionScopeDetailPresent?: boolean;
  allocationDetailPresent?: boolean;
  insolvencyDetailPresent?: boolean;
  proceedingDetailPresent?: boolean;
  agreementDetailPresent?: boolean;
  noticeOpportunityDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicMultimodalCarriageEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  otherModeType?: string;
  vehicleCarriedByOtherMode?: boolean;
  goodsRemainedLoadedOnVehicle?: boolean;
  goodsUnloadedFromVehicle?: boolean;
  article14ImpedimentBasisRecorded?: boolean;
  otherModeCarriageEvidencePresent?: boolean;
  otherModeCarriageDocumentReferencePresent?: boolean;
  article14ImpedimentReferencePresent?: boolean;
  otherModeCarriageDetailPresent?: boolean;
  article14ImpedimentDetailPresent?: boolean;
  liabilityDetermination?: boolean;
  legalDetermination?: boolean;
}

export interface PublicConsignmentNoteCopyEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  electronicCopyAccessAvailable?: boolean;
  entitledPartyAccessAvailable?: boolean;
  senderCarrierSignatureEvidenceAvailable?: boolean;
  contractParticipantReferencePresent?: boolean;
  senderFirstCopyAvailable?: boolean;
  goodsAccompanyingSecondCopyAvailable?: boolean;
  carrierRetainedThirdCopyAvailable?: boolean;
  copyAccessDetailPresent?: boolean;
  signatureDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicEcmrReceiptAccessEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  senderRequestReferencePresent?: boolean;
  senderRequestReceived?: boolean;
  receiptForGoodsProvided?: boolean;
  shipmentIdentificationInformationProvided?: boolean;
  electronicConsignmentNoteAccessInformationProvided?: boolean;
  handoverAtPresent?: boolean;
  senderRequestDetailPresent?: boolean;
  receiptDetailPresent?: boolean;
  shipmentIdentificationDetailPresent?: boolean;
  accessInformationDetailPresent?: boolean;
  technicalEvidenceOnly?: boolean;
  legalDetermination?: boolean;
}

export interface PublicEcmrReceiptAccessRequestEvidence {
  requestType?: string;
  requestStatus?: string;
  requestedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRequest?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  senderRequestRecorded?: boolean;
  requestDetailPresent?: boolean;
  technicalEvidenceOnly?: boolean;
  legalDetermination?: boolean;
}

export interface PublicSenderParticularsResponsibilityEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  affectedParticularCategory?: string;
  inaccuracyOrInadequacyRecorded?: boolean;
  expenseLossDamageClaimed?: boolean;
  carrierEnteredAtSenderRequest?: boolean;
  senderBehalfPresumptionRecorded?: boolean;
  cmrStatementOmissionRecorded?: boolean;
  carrierLiabilityBasisRecorded?: boolean;
  reviewedAtPresent?: boolean;
  particularDetailPresent?: boolean;
  instructionDetailPresent?: boolean;
  expenseLossDamageDetailPresent?: boolean;
  carrierEntryDetailPresent?: boolean;
  cmrStatementOmissionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicProhibitedClauseEvidence {
  recordType?: string;
  recordStatus?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  statusAtRecord?: string;
  sourceInstrument?: string;
  sourceArticle?: string;
  actorRoleCount?: number;
  article40AgreementReferencePresent?: boolean;
  contractStipulationReviewed?: boolean;
  clauseDetected?: boolean;
  directOrIndirectDerogationDetected?: boolean;
  benefitOfInsuranceForCarrierDetected?: boolean;
  burdenOfProofShiftDetected?: boolean;
  article40ExceptionAssessed?: boolean;
  article40ExceptionNotApplicable?: boolean;
  nullitySeverabilityRecorded?: boolean;
  reviewOutcomeRecorded?: boolean;
  clauseReviewedAtPresent?: boolean;
  clauseDetailPresent?: boolean;
  reviewDetailPresent?: boolean;
  insuranceBenefitDetailPresent?: boolean;
  burdenShiftDetailPresent?: boolean;
  article40ExceptionDetailPresent?: boolean;
  legalDetermination?: boolean;
}

export interface PublicRetentionPolicyManifestEvidence {
  retentionMode?: string;
  jurisdiction?: string;
  retentionDays?: number;
  retentionPolicyReferencePresent?: boolean;
  reasonPresent?: boolean;
  retainUntilPresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}

export interface PublicLegalHoldManifestEvidence {
  active?: boolean;
  status?: string;
  legalHoldReferencePresent?: boolean;
  reasonPresent?: boolean;
  releaseReasonPresent?: boolean;
  appliedAtPresent?: boolean;
  releasedAtPresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}

export interface PublicSignatureVerificationEvidence {
  verificationStatus?: string;
  verifiedAt?: string;
  verificationReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  signatureReferencePresent?: boolean;
}

export interface PublicLifecycleEventEvidence {
  fromStatus?: string;
  toStatus?: string;
  timestampPresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  reasonPresent?: boolean;
  actorRoleCount?: number;
}

export interface PublicExceptionReviewTaskEvidence {
  taskId?: string;
  status?: string;
  assignedGroupPresent?: boolean;
  updatedAtPresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}

export interface PublicIntegrityCompromisedEvidence {
  integrityCompromisedReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  detectedAt?: string;
  status?: string;
  reason?: string;
  detectedDuring?: string;
  evidenceReferencePresent?: boolean;
  evidenceType?: string;
  actorRoleCount?: number;
}

export interface PublicAttachmentUploadRejectionEvidence {
  attachmentUploadRejectionReferencePresent?: boolean;
  reason?: string;
  policy?: string;
  declaredMimeType?: string;
  sizeBytes?: number;
  maxSizeBytes?: number;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  actorRoleCount?: number;
}

export interface PublicEvidenceDownloadCompletion {
  evidenceType?: string;
  confirmedAt?: string;
  clientConfirmed?: boolean;
  independentTransferProof?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  downloadCompletionReferencePresent?: boolean;
  evidenceReferencePresent?: boolean;
  actorRoleCount?: number;
}

export interface PublicTransferAuditCorrelation {
  transferAuditCorrelationReferencePresent?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  evidenceReferencePresent?: boolean;
  evidenceType?: string;
  downloadCompletionReferencePresent?: boolean;
  correlatedAt?: string;
  auditSource?: string;
  correlationStatus?: string;
  downloadCompletionMatched?: boolean;
  objectReferenceMatched?: boolean;
  eventTimeMatched?: boolean;
  actorReferenceMatched?: boolean;
  logFileValidationAvailable?: boolean;
  technicalCorrelationEvidence?: boolean;
  independentTransferProof?: boolean;
  legalDetermination?: boolean;
  actorRoleCount?: number;
}

export interface PublicFinalXmlRetrievalEvidence {
  retrievalReferencePresent?: boolean;
  evidenceReferencePresent?: boolean;
  evidenceType?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  retrievedAt?: string;
  digestAlgorithm?: string;
  retentionMode?: string;
  integrityChecked?: boolean;
  actorRoleCount?: number;
}

export interface PublicEvidenceManifestExportEvidence {
  manifestExportReferencePresent?: boolean;
  manifestReferencePresent?: boolean;
  evidenceType?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  exportedAt?: string;
  archived?: boolean;
  evidenceSectionCount?: number;
  actorRoleCount?: number;
}

export interface PublicDocumentAccessEvidence {
  accessReferencePresent?: boolean;
  accessPath?: string;
  accessedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  actorRoleCount?: number;
}

export interface PublicAttachmentAccessEvidence {
  accessReferencePresent?: boolean;
  accessType?: string;
  accessedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  attachmentReferencePresent?: boolean;
  attachmentCount?: number;
  mimeType?: string;
  sizeBytes?: number;
  integrityChecked?: boolean;
  actorRoleCount?: number;
}

export interface PublicTombstoneEvidence {
  tombstoneReferencePresent?: boolean;
  deletedAtPresent?: boolean;
  tombstone?: boolean;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  reasonPresent?: boolean;
  actorRoleCount?: number;
}

export interface PublicAppendOnlyEvidenceStoreRecord {
  evidenceRecordType?: string;
  sourceRecordType?: string;
  recordedAt?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
  actorRoleCount?: number;
  requestMetadataPresent?: boolean;
  evidenceRecordReferencePresent?: boolean;
  sourceRecordReferencePresent?: boolean;
  accessRecordReferencePresent?: boolean;
  attachmentReferencePresent?: boolean;
  attachmentCount?: number;
  integrityChecked?: boolean;
  tombstoneReferencePresent?: boolean;
  evidenceReferencePresent?: boolean;
  manifestReferencePresent?: boolean;
  retrievalReferencePresent?: boolean;
  manifestExportReferencePresent?: boolean;
  downloadCompletionReferencePresent?: boolean;
  transferAuditCorrelationReferencePresent?: boolean;
  valid?: boolean;
  errorCount?: number;
  warningCount?: number;
  findingCount?: number;
  schemaProfile?: string;
  validationContext?: string;
  instrument?: string;
  article?: string;
}

const publicXmlValidationRecordFromApi = (record: unknown): XmlValidationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: XmlValidationEvidence = {};
  if (source.xmlValidationReferencePresent === true) {
    result.xmlValidationReferencePresent = true;
  }
  if (publicDocumentVersionReferencePresentFromApi(source)) {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  if (typeof source.validatedAt === 'string') {
    result.validatedAt = source.validatedAt;
  }
  if (typeof source.valid === 'boolean') {
    result.valid = source.valid;
  }
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, ['errorCount']);
  const schemaProfile = safeManifestLabel(source.schemaProfile, xmlValidationSchemaProfiles);
  if (schemaProfile) {
    result.schemaProfile = schemaProfile;
  }
  const validationContext = safeManifestLabel(source.validationContext, validationContextLabels);
  if (validationContext) {
    result.validationContext = validationContext;
  }
  const actorRoleCount = publicOperationalRoleCount(source.actorRoleCount, source.actorRoles);
  if (actorRoleCount !== undefined) {
    result.actorRoleCount = actorRoleCount;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicCmrBusinessValidationRecordFromApi = (record: unknown): CmrBusinessValidationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: CmrBusinessValidationEvidence = {};
  if (source.cmrBusinessValidationReferencePresent === true) {
    result.cmrBusinessValidationReferencePresent = true;
  }
  if (publicDocumentVersionReferencePresentFromApi(source)) {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  if (typeof source.failedAt === 'string') {
    result.failedAt = source.failedAt;
  }
  const status = safeManifestLabel(source.status, cmrBusinessValidationStatuses);
  if (status) {
    result.status = status;
  }
  const validationContext = safeManifestLabel(source.validationContext, validationContextLabels);
  if (validationContext) {
    result.validationContext = validationContext;
  }
  const instrument = safeManifestLabel(source.instrument, cmrBusinessValidationInstruments);
  if (instrument) {
    result.instrument = instrument;
  }
  const article = safeManifestLabel(source.article, cmrBusinessValidationArticles);
  if (article) {
    result.article = article;
  }
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'errorCount',
    'warningCount',
    'findingCount',
  ]);
  const actorRoleCount = publicOperationalRoleCount(source.actorRoleCount, source.actorRoles);
  if (actorRoleCount !== undefined) {
    result.actorRoleCount = actorRoleCount;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicEcmrProtocolValidationRecordFromApi = (record: unknown): EcmrProtocolValidationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: EcmrProtocolValidationEvidence = {};
  if (source.ecmrProtocolValidationReferencePresent === true) {
    result.ecmrProtocolValidationReferencePresent = true;
  }
  if (publicDocumentVersionReferencePresentFromApi(source)) {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  if (typeof source.failedAt === 'string') {
    result.failedAt = source.failedAt;
  }
  const status = safeManifestLabel(source.status, ecmrProtocolValidationStatuses);
  if (status) {
    result.status = status;
  }
  const validationContext = safeManifestLabel(source.validationContext, validationContextLabels);
  if (validationContext) {
    result.validationContext = validationContext;
  }
  const instrument = safeManifestLabel(source.instrument, ecmrProtocolValidationInstruments);
  if (instrument) {
    result.instrument = instrument;
  }
  const article = safeManifestLabel(source.article, ecmrProtocolValidationArticles);
  if (article) {
    result.article = article;
  }
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'errorCount',
    'warningCount',
    'findingCount',
  ]);
  const actorRoleCount = publicOperationalRoleCount(source.actorRoleCount, source.actorRoles);
  if (actorRoleCount !== undefined) {
    result.actorRoleCount = actorRoleCount;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const safeActiveProcedureSetHandle = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  return /^[A-Za-z0-9._:-]{1,128}$/.test(normalized) ? normalized : undefined;
};

const safeActiveProcedureText = (value: unknown, maxLength = 4000): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  if (normalized.length === 0) {
    return undefined;
  }
  return normalized.length > maxLength ? normalized.slice(0, maxLength) : normalized;
};

const publicActiveProcedureEntriesFromApi = (record: unknown): Record<string, string> => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return {};
  }
  return Object.entries(record as Record<string, unknown>).reduce<Record<string, string>>((result, [key, value]) => {
    const procedureKey = safeActiveProcedureSetHandle(key);
    const procedureText = safeActiveProcedureText(value);
    if (procedureKey && procedureText) {
      result[procedureKey] = procedureText;
    }
    return result;
  }, {});
};

const publicArticle5CategoriesFromApi = (
  record: unknown,
  procedureKeys: Set<string>
): ActiveProcedureSet['article5Categories'] | undefined => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return undefined;
  }
  const categories = Object.entries(record as Record<string, unknown>).reduce<NonNullable<ActiveProcedureSet['article5Categories']>>(
    (result, [key, value]) => {
      const categoryKey = safeActiveProcedureSetHandle(key);
      if (!categoryKey || !value || typeof value !== 'object' || Array.isArray(value)) {
        return result;
      }
      const source = value as Record<string, unknown>;
      const category: NonNullable<ActiveProcedureSet['article5Categories']>[string] = {};
      const article = safeManifestVersionLabel(source.article);
      if (article) {
        category.article = article;
      }
      const label = safeActiveProcedureText(source.label, 500);
      if (label) {
        category.label = label;
      }
      if (Array.isArray(source.procedureKeys)) {
        const safeProcedureKeys = source.procedureKeys
          .map(safeActiveProcedureSetHandle)
          .filter((procedureKey): procedureKey is string => Boolean(procedureKey && procedureKeys.has(procedureKey)));
        if (safeProcedureKeys.length > 0) {
          category.procedureKeys = Array.from(new Set(safeProcedureKeys));
        }
      }
      if (Object.keys(category).length > 0) {
        result[categoryKey] = category;
      }
      return result;
    },
    {}
  );
  return Object.keys(categories).length > 0 ? categories : undefined;
};

const publicActiveProcedureSetFromApi = (record: unknown): ActiveProcedureSet | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const procedureSetId = safeActiveProcedureSetHandle(source.procedureSetId);
  if (!procedureSetId) {
    return undefined;
  }
  const procedures = publicActiveProcedureEntriesFromApi(source.procedures);
  const version = safeManifestVersionLabel(source.version) ?? 'other';
  const result: ActiveProcedureSet = {
    procedureSetId,
    version,
    procedures,
  };
  const title = safeActiveProcedureText(source.title, 500);
  if (title) {
    result.title = title;
  }
  const article5Categories = publicArticle5CategoriesFromApi(source.article5Categories, new Set(Object.keys(procedures)));
  if (article5Categories) {
    result.article5Categories = article5Categories;
  }
  const legalDisclaimer = safeActiveProcedureText(source.legalDisclaimer, 1000);
  if (legalDisclaimer) {
    result.legalDisclaimer = legalDisclaimer;
  }
  if (source.digestAvailable === true || typeof source.sha256Digest === 'string' || typeof source.procedureSetDigest === 'string') {
    result.digestAvailable = true;
  } else if (source.digestAvailable === false) {
    result.digestAvailable = false;
  }
  if (typeof source.active === 'boolean') {
    result.active = source.active;
  }
  return result;
};

const requirePublicActiveProcedureSetFromApi = (record: unknown): ActiveProcedureSet => {
  const result = publicActiveProcedureSetFromApi(record);
  if (!result) {
    throw new Error('Respuesta invalida del conjunto activo de procedimientos');
  }
  return result;
};

const controlledProcedureSetIds = new Set(['oecmr-standard-procedures']);

const publicDocumentReferencedProcedureSetsFromApi = (record: unknown): DocumentReferencedProcedureSets => {
  const source = record && typeof record === 'object' && !Array.isArray(record)
    ? record as Record<string, unknown>
    : {};
  const procedureSetsByReference = new Map<string, ActiveProcedureSet>();
  if (Array.isArray(source.procedureSets)) {
    source.procedureSets.forEach((candidate) => {
      const procedureSet = publicActiveProcedureSetFromApi(candidate);
      if (!procedureSet || !controlledProcedureSetIds.has(procedureSet.procedureSetId)) {
        return;
      }
      procedureSetsByReference.set(`${procedureSet.procedureSetId}@${procedureSet.version}`, procedureSet);
    });
  }
  const procedureSets = Array.from(procedureSetsByReference.values());
  const sourceReferenceCount = safePublicManifestCount(source.procedureSetReferenceCount) ?? 0;
  const sourceResolvedCount = safePublicManifestCount(source.resolvedProcedureSetCount) ?? procedureSets.length;
  const sourceUnresolvedCount = safePublicManifestCount(source.unresolvedProcedureSetCount) ?? 0;
  const droppedResolvedCount = Math.max(0, sourceResolvedCount - procedureSets.length);
  const unresolvedProcedureSetCount = Math.min(
    Number.MAX_SAFE_INTEGER,
    sourceUnresolvedCount + droppedResolvedCount
  );
  const procedureSetReferenceCount = Math.max(
    sourceReferenceCount,
    Math.min(Number.MAX_SAFE_INTEGER, procedureSets.length + unresolvedProcedureSetCount)
  );
  return {
    procedureSets,
    procedureSetReferenceCount,
    resolvedProcedureSetCount: procedureSets.length,
    unresolvedProcedureSetCount,
    allProcedureSetReferencesResolved: (
      source.allProcedureSetReferencesResolved === true
      && procedureSetReferenceCount > 0
      && procedureSets.length > 0
      && unresolvedProcedureSetCount === 0
    ),
  };
};

const publicProcedureAcceptanceRecordFromApi = (
  record: unknown
): PublicProcedureAcceptanceEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicProcedureAcceptanceEvidence = {};
  if (
    source.procedureAcceptanceReferencePresent === true ||
    source.acceptanceReferencePresent === true ||
    typeof source.acceptanceId === 'string'
  ) {
    result.procedureAcceptanceReferencePresent = true;
  }
  if (source.procedureSetReferencePresent === true || typeof source.procedureSetId === 'string') {
    result.procedureSetReferencePresent = true;
  }
  const procedureSetVersion = safeManifestVersionLabel(source.procedureSetVersion);
  if (procedureSetVersion) {
    result.procedureSetVersion = procedureSetVersion;
  }
  if (typeof source.accepted === 'boolean') {
    result.accepted = source.accepted;
  }
  if (source.procedureSetDigestAvailable === true || typeof source.procedureSetDigest === 'string') {
    result.procedureSetDigestAvailable = true;
  } else if (source.procedureSetDigestAvailable === false) {
    result.procedureSetDigestAvailable = false;
  }
  const acceptedRoleCount = publicOperationalRoleCount(source.acceptedRoleCount, source.acceptedByRoles);
  if (acceptedRoleCount !== undefined) {
    result.acceptedRoleCount = acceptedRoleCount;
  }
  const article5CategoryCount = safePublicManifestCount(source.article5CategoryCount)
    ?? safePublicManifestCount(Array.isArray(source.article5CategoryCoverage) ? source.article5CategoryCoverage.length : undefined);
  if (article5CategoryCount !== undefined) {
    result.article5CategoryCount = article5CategoryCount;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (typeof source.acceptedAt === 'string') {
    result.acceptedAt = source.acceptedAt;
  }
  if (typeof source.contractParticipantReferencePresent === 'boolean') {
    result.contractParticipantReferencePresent = source.contractParticipantReferencePresent;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const procedureAcceptanceEvidenceFromApi = (
  record: unknown
): ProcedureSetAcceptanceEvidence => publicProcedureAcceptanceRecordFromApi(record) ?? {};

const publicSignatureEvidenceRecordFromApi = (record: unknown): PublicSignatureEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicSignatureEvidence = {};
  if (typeof source.timestamp === 'string') {
    result.timestamp = source.timestamp;
  }
  const digestAlgorithm = safeManifestLabel(source.digestAlgorithm, digestAlgorithms);
  if (digestAlgorithm) {
    result.digestAlgorithm = digestAlgorithm;
  }
  if (source.signatureReferencePresent === true || typeof source.signatureId === 'string') {
    result.signatureReferencePresent = true;
  }
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const signerRole = safeManifestLabel(source.signerRole, signatureManifestRoles);
  if (signerRole) {
    result.signerRole = signerRole;
  }
  const signatureMethod = safeManifestLabel(source.signatureMethod, signatureManifestMethods);
  if (signatureMethod) {
    result.signatureMethod = signatureMethod;
  }
  const verificationStatus = safeManifestLabel(source.verificationStatus, signatureVerificationStatuses);
  if (verificationStatus) {
    result.verificationStatus = verificationStatus;
  }
  if (source.signedDigestAvailable === true || typeof source.signedDigest === 'string') {
    result.signedDigestAvailable = true;
  } else if (source.signedDigestAvailable === false) {
    result.signedDigestAvailable = false;
  }
  const rawUniqueSignerLinkPresent = Object.prototype.hasOwnProperty.call(source, 'uniqueSignerLink');
  const rawIdentityEvidencePresent = Object.prototype.hasOwnProperty.call(source, 'signerIdentityEvidenceType');
  const rawSoleControlAssurancePresent = Object.prototype.hasOwnProperty.call(source, 'soleControlAssuranceType');
  const rawDataIntegrityProtectionPresent = Object.prototype.hasOwnProperty.call(source, 'dataIntegrityProtection');
  const rawArticle3AssurancePresent = rawUniqueSignerLinkPresent
    || rawIdentityEvidencePresent
    || rawSoleControlAssurancePresent
    || rawDataIntegrityProtectionPresent;
  const publicArticle3AssuranceSignalsPresent = [
    'uniqueSignerLinkPresent',
    'signerIdentityEvidencePresent',
    'soleControlAssurancePresent',
    'dataIntegrityProtectionPresent',
    'article3AssurancePresent',
  ].some((fieldName) => Object.prototype.hasOwnProperty.call(source, fieldName));
  const uniqueSignerLinkPresent = rawUniqueSignerLinkPresent
    ? source.uniqueSignerLink === true
    : source.uniqueSignerLinkPresent === true;
  const signerIdentityEvidencePresent =
    rawIdentityEvidencePresent
      ? typeof source.signerIdentityEvidenceType === 'string'
        && signatureIdentityEvidenceTypes.has(source.signerIdentityEvidenceType)
      : source.signerIdentityEvidencePresent === true;
  const soleControlAssurancePresent =
    rawSoleControlAssurancePresent
      ? typeof source.soleControlAssuranceType === 'string'
        && signatureControlAssuranceTypes.has(source.soleControlAssuranceType)
      : source.soleControlAssurancePresent === true;
  const dataIntegrityProtectionPresent =
    rawDataIntegrityProtectionPresent
      ? typeof source.dataIntegrityProtection === 'string'
        && signatureDataIntegrityProtections.has(source.dataIntegrityProtection)
      : source.dataIntegrityProtectionPresent === true;
  if (uniqueSignerLinkPresent) {
    result.uniqueSignerLinkPresent = true;
  } else if (source.uniqueSignerLinkPresent === false || source.uniqueSignerLink === false) {
    result.uniqueSignerLinkPresent = false;
  }
  if (signerIdentityEvidencePresent) {
    result.signerIdentityEvidencePresent = true;
  } else if (source.signerIdentityEvidencePresent === false || typeof source.signerIdentityEvidenceType === 'string') {
    result.signerIdentityEvidencePresent = false;
  }
  if (soleControlAssurancePresent) {
    result.soleControlAssurancePresent = true;
  } else if (source.soleControlAssurancePresent === false || typeof source.soleControlAssuranceType === 'string') {
    result.soleControlAssurancePresent = false;
  }
  if (dataIntegrityProtectionPresent) {
    result.dataIntegrityProtectionPresent = true;
  } else if (source.dataIntegrityProtectionPresent === false || typeof source.dataIntegrityProtection === 'string') {
    result.dataIntegrityProtectionPresent = false;
  }
  const completeArticle3Assurance = uniqueSignerLinkPresent
    && signerIdentityEvidencePresent
    && soleControlAssurancePresent
    && dataIntegrityProtectionPresent;
  if (completeArticle3Assurance) {
    result.article3AssurancePresent = true;
  } else if (rawArticle3AssurancePresent || publicArticle3AssuranceSignalsPresent) {
    result.article3AssurancePresent = false;
  }
  if (source.contractParticipantReferencePresent === true) {
    result.contractParticipantReferencePresent = true;
  } else if (source.contractParticipantReferencePresent === false) {
    result.contractParticipantReferencePresent = false;
  }
  const providerMetadata = source.providerMetadata;
  if (
    source.certificateAvailable === true ||
    typeof source.certificatePem === 'string' ||
    (providerMetadata && typeof providerMetadata === 'object' && (
      typeof (providerMetadata as Record<string, unknown>).certificate === 'string' ||
      typeof (providerMetadata as Record<string, unknown>).certificateId === 'string'
    ))
  ) {
    result.certificateAvailable = true;
  } else if (source.certificateAvailable === false) {
    result.certificateAvailable = false;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  return Object.keys(result).length > 0 ? result : undefined;
};

const signatureEvidenceFromApi = (record: unknown): SignatureEvidence =>
  publicSignatureEvidenceRecordFromApi(record) ?? {};

const publicSupplementaryDocumentRecordFromApi = (
  record: unknown
): PublicSupplementaryDocumentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicSupplementaryDocumentEvidence = {};
  const mimeType = safeManifestMimeType(source.mimeType);
  if (mimeType) {
    result.mimeType = mimeType;
  }
  copyPublicManifestByteSizeFields(source, result as Record<string, unknown>, ['sizeBytes']);
  const digestAlgorithm = safeManifestLabel(source.digestAlgorithm, digestAlgorithms);
  if (digestAlgorithm) {
    result.digestAlgorithm = digestAlgorithm;
  }
  if (typeof source.uploadedAt === 'string') {
    result.uploadedAt = source.uploadedAt;
  }
  const documentType = safeTokenRecordType(source.documentType);
  if (documentType) {
    result.documentType = documentType;
  } else if (source.documentType) {
    result.documentType = 'other';
  }
  if (source.attachmentReferencePresent === true) {
    result.attachmentReferencePresent = true;
  }
  const ecmrLinkReference = safeSupplementaryDocumentLinkReference(source.ecmrLinkReference);
  if (ecmrLinkReference) {
    result.ecmrLinkReference = ecmrLinkReference;
    result.ecmrLinkReferencePresent = true;
  } else if (source.ecmrLinkReferencePresent === false) {
    result.ecmrLinkReferencePresent = false;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  const evidenceType = safeManifestLabel(source.evidenceType, supplementaryDocumentEvidenceTypes);
  if (evidenceType) {
    result.evidenceType = evidenceType;
  }
  ['digestAvailable', 'payloadStoredExternally'].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicCarrierTakeoverCheckRequestRecordFromApi = (
  record: unknown
): PublicCarrierTakeoverCheckRequestEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCarrierTakeoverCheckRequestEvidence = {};
  if (typeof source.requestedAt === 'string') {
    result.requestedAt = source.requestedAt;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const statusAtRequest = safeManifestLabel(source.statusAtRequest, documentLifecycleStatuses);
  if (statusAtRequest) {
    result.statusAtRequest = statusAtRequest;
  }
  if (source.checkRequestReferencePresent === true || typeof source.checkRequestId === 'string') {
    result.checkRequestReferencePresent = true;
  }
  if (Array.isArray(source.requestedChecks)) {
    const requestedChecks = source.requestedChecks
      .map((check) => safeManifestLabel(check, carrierTakeoverRequestedChecks))
      .filter((check): check is string => Boolean(check && check !== 'other'));
    if (requestedChecks.length > 0) {
      result.requestedChecks = Array.from(new Set(requestedChecks));
      if (safePublicManifestCount(source.requestedCheckCount) === undefined) {
        result.requestedCheckCount = result.requestedChecks.length;
      }
    }
  }
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, ['requestedCheckCount']);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  ['grossWeightRequested', 'quantityRequested', 'contentsRequested'].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  if (source.reasonPresent === true || typeof source.reason === 'string') {
    result.reasonPresent = true;
  } else if (source.reasonPresent === false) {
    result.reasonPresent = false;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicCarrierTakeoverCheckRecordFromApi = (
  record: unknown
): PublicCarrierTakeoverCheckEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCarrierTakeoverCheckEvidence = {};
  const resultLabel = safeManifestLabel(source.result, carrierTakeoverCheckResults);
  if (resultLabel) {
    result.result = resultLabel;
  }
  ['checkedAt', 'recordedAt'].forEach((key) => {
    if (typeof source[key] === 'string') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const statusAtCheck = safeManifestLabel(source.statusAtCheck, documentLifecycleStatuses);
  if (statusAtCheck) {
    result.statusAtCheck = statusAtCheck;
  }
  [
    'packageCountChecked',
    'marksAndNumbersChecked',
    'apparentConditionChecked',
    'grossWeightChecked',
    'quantityChecked',
    'contentsChecked',
  ].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  if (source.takeoverCheckReferencePresent === true || typeof source.takeoverCheckId === 'string') {
    result.takeoverCheckReferencePresent = true;
  }
  if (source.reservationReferencePresent === true || typeof source.reservationId === 'string') {
    result.reservationReferencePresent = true;
  } else if (source.reservationReferencePresent === false) {
    result.reservationReferencePresent = false;
  }
  if (source.commentPresent === true || typeof source.comment === 'string') {
    result.commentPresent = true;
  } else if (source.commentPresent === false) {
    result.commentPresent = false;
  }
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicCarrierReservationRecordFromApi = (
  record: unknown
): PublicCarrierReservationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCarrierReservationEvidence = {};
  if (typeof source.createdAt === 'string') {
    result.createdAt = source.createdAt;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  if (source.reservationReferencePresent === true || typeof source.reservationId === 'string') {
    result.reservationReferencePresent = true;
  }
  const category = safeManifestLabel(source.category, carrierReservationCategories);
  if (category) {
    result.category = category;
  }
  const statusAtReservation = safeManifestLabel(source.statusAtReservation, documentLifecycleStatuses);
  if (statusAtReservation) {
    result.statusAtReservation = statusAtReservation;
  }
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  [
    ['groundsPresent', 'grounds'],
    ['packageReferencePresent', 'packageReference'],
    ['observedValuePresent', 'observedValue'],
    ['expectedValuePresent', 'expectedValue'],
  ].forEach(([flagKey, rawKey]) => {
    if (source[flagKey] === true || typeof source[rawKey] === 'string') {
      (result as Record<string, unknown>)[flagKey] = true;
    } else if (source[flagKey] === false) {
      (result as Record<string, unknown>)[flagKey] = false;
    }
  });
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicReservationAcknowledgementRecordFromApi = (
  record: unknown
): PublicReservationAcknowledgementEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicReservationAcknowledgementEvidence = {};
  const decision = safeManifestLabel(source.decision, reservationAcknowledgementDecisions);
  if (decision) {
    result.decision = decision;
  }
  if (typeof source.acknowledgedAt === 'string') {
    result.acknowledgedAt = source.acknowledgedAt;
  }
  const statusAtAcknowledgement = safeManifestLabel(source.statusAtAcknowledgement, documentLifecycleStatuses);
  if (statusAtAcknowledgement) {
    result.statusAtAcknowledgement = statusAtAcknowledgement;
  }
  if (source.acknowledgementReferencePresent === true || typeof source.acknowledgementId === 'string') {
    result.acknowledgementReferencePresent = true;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  if (source.reservationReferencePresent === true || typeof source.reservationId === 'string') {
    result.reservationReferencePresent = true;
  } else if (source.reservationReferencePresent === false) {
    result.reservationReferencePresent = false;
  }
  if (source.deliveryReferencePresent === true || typeof source.deliveryId === 'string') {
    result.deliveryReferencePresent = true;
  } else if (source.deliveryReferencePresent === false) {
    result.deliveryReferencePresent = false;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicDeliveryConfirmationRecordFromApi = (
  record: unknown
): PublicDeliveryConfirmationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicDeliveryConfirmationEvidence = {};
  ['deliveredAt', 'confirmedAt'].forEach((key) => {
    if (typeof source[key] === 'string') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  const statusAtConfirmation = safeManifestLabel(source.statusAtConfirmation, documentLifecycleStatuses);
  if (statusAtConfirmation) {
    result.statusAtConfirmation = statusAtConfirmation;
  }
  if (source.deliveryReferencePresent === true || typeof source.deliveryId === 'string') {
    result.deliveryReferencePresent = true;
  }
  if (source.recipientNamePresent === true || typeof source.recipientName === 'string') {
    result.recipientNamePresent = true;
  } else if (source.recipientNamePresent === false) {
    result.recipientNamePresent = false;
  }
  if (
    source.deliveryLocationPresent === true ||
    typeof source.deliveryLocation === 'string' ||
    (source.deliveryLocation && typeof source.deliveryLocation === 'object')
  ) {
    result.deliveryLocationPresent = true;
  } else if (source.deliveryLocationPresent === false) {
    result.deliveryLocationPresent = false;
  }
  if (
    source.deliverySignatureAvailable === true ||
    Boolean(source.signatureEvidence && typeof source.signatureEvidence === 'object')
  ) {
    result.deliverySignatureAvailable = true;
  } else if (source.deliverySignatureAvailable === false) {
    result.deliverySignatureAvailable = false;
  }
  const actorRoleCount = publicOperationalRoleCount(source.actorRoleCount, source.confirmedByRoles);
  if (actorRoleCount !== undefined) {
    result.actorRoleCount = actorRoleCount;
  }
  const reservationCount = safePublicManifestCount(source.reservationCount)
    ?? safePublicManifestCount(Array.isArray(source.deliveryReservations) ? source.deliveryReservations.length : undefined);
  if (reservationCount !== undefined) {
    result.reservationCount = reservationCount;
  }
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'reservationCount',
  ]);
  const signatureMethod = safeManifestLabel(source.signatureMethod, signatureManifestMethods);
  if (signatureMethod) {
    result.signatureMethod = signatureMethod;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const carrierReservationEvidenceFromApi = (record: unknown): CarrierReservationEvidence =>
  publicCarrierReservationRecordFromApi(record) ?? {};

const reservationAcknowledgementEvidenceFromApi = (
  record: unknown
): CarrierReservationAcknowledgementEvidence =>
  publicReservationAcknowledgementRecordFromApi(record) ?? {};

const carrierTakeoverCheckEvidenceFromApi = (record: unknown): CarrierTakeoverCheckEvidence =>
  publicCarrierTakeoverCheckRecordFromApi(record) ?? {};

const carrierTakeoverCheckRequestEvidenceFromApi = (
  record: unknown
): CarrierTakeoverCheckRequestEvidence =>
  publicCarrierTakeoverCheckRequestRecordFromApi(record) ?? {};

const deliveryConfirmationEvidenceFromApi = (record: unknown): DeliveryConfirmationEvidence =>
  publicDeliveryConfirmationRecordFromApi(record) ?? {};

const deliveryReservationAcknowledgementEvidenceFromApi = (
  record: unknown
): DeliveryReservationAcknowledgementEvidence =>
  publicReservationAcknowledgementRecordFromApi(record) ?? {};

const publicCmrArticle9PresumptionFromApi = (
  record: unknown
): PublicCmrArticle9PresumptionEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCmrArticle9PresumptionEvidence = {};
  const basis = safeManifestLabel(source.basis, cmrArticle9BasisValues);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, cmrArticle9Articles);
  if (basis) {
    result.basis = basis;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  [
    'presumptionApplies',
    'carrierTakeoverCheckAccepted',
    'packageCountAndMarksCorrespondencePresumed',
    'goodsAndPackagingGoodConditionPresumed',
  ].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'specificCarrierReservationCount',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicCmrArticle10PackagingReservationsFromApi = (
  record: unknown
): PublicCmrArticle10PackagingReservationsEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCmrArticle10PackagingReservationsEvidence = {};
  const basis = safeManifestLabel(source.basis, cmrArticle10BasisValues);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, cmrArticle10Articles);
  if (basis) {
    result.basis = basis;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  ['packagingReservationEvidencePresent', 'liabilityDetermination'].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'packagingReservationCount',
    'currentVersionLinkedReservationCount',
    'ambiguousReservationCount',
  ]);
  publicDocumentVersionMetadataFromApi(source, result);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicCmrArticle11CustomsDocumentsFromApi = (
  record: unknown
): PublicCmrArticle11CustomsDocumentsEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCmrArticle11CustomsDocumentsEvidence = {};
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, cmrArticle11Articles);
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  ['documentsAvailable', 'digestEvidenceAvailable', 'payloadStoredExternally'].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'customsFormalitiesDocumentCount',
    'currentVersionLinkedDocumentCount',
    'documentTypeCount',
  ]);
  publicDocumentVersionMetadataFromApi(source, result);
  if (Array.isArray(source.documentTypes)) {
    const documentTypes = source.documentTypes
      .map((documentType) => safeTokenRecordType(documentType))
      .filter((documentType): documentType is string => Boolean(documentType));
    if (documentTypes.length > 0) {
      result.documentTypes = Array.from(new Set(documentTypes));
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const copyPublicManifestStringFields = (
  source: Record<string, unknown>,
  result: Record<string, unknown>,
  fields: string[]
): void => {
  fields.forEach((key) => {
    if (typeof source[key] === 'string') {
      const value = isPublicManifestTemporalFieldName(key)
        ? safePublicManifestTemporalValue(source[key])
        : source[key];
      if (value !== undefined) {
        result[key] = value;
      }
    }
  });
};

const publicManifestTemporalFieldNamePattern = /(?:At|Date|Timestamp|RetainUntil)$/;

const isPublicManifestTemporalFieldName = (name: string): boolean =>
  publicManifestTemporalFieldNamePattern.test(name) && !name.endsWith('Present');

const safePublicManifestTemporalValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed === 'present') {
    return trimmed;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,6})?)?(?:Z|[+-](\d{2}):(\d{2}))?)?$/.exec(trimmed);
  if (!match) {
    return undefined;
  }

  const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue, offsetHourValue, offsetMinuteValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth) {
    return undefined;
  }
  if (hourValue !== undefined) {
    const hour = Number(hourValue);
    const minute = Number(minuteValue);
    const second = secondValue === undefined ? 0 : Number(secondValue);
    if (hour > 23 || minute > 59 || second > 59) {
      return undefined;
    }
  }
  if (offsetHourValue !== undefined) {
    const offsetHour = Number(offsetHourValue);
    const offsetMinute = Number(offsetMinuteValue);
    if (offsetHour > 23 || offsetMinute > 59) {
      return undefined;
    }
  }
  return trimmed;
};

const safePublicManifestCount = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    return undefined;
  }
  return value;
};

const safePublicManifestByteSize = (value: unknown): number | undefined =>
  safePublicManifestCount(value);

const safePublicManifestVersionNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1) {
    return undefined;
  }
  return value;
};

const copyPublicManifestNumberFields = (
  source: Record<string, unknown>,
  result: Record<string, unknown>,
  fields: string[]
): void => {
  fields.forEach((key) => {
    const count = safePublicManifestCount(source[key]);
    if (count !== undefined) {
      result[key] = count;
    }
  });
};

const copyPublicManifestActorRoleCount = (
  source: Record<string, unknown>,
  result: Record<string, unknown>
): void => {
  const actorRoleCount = publicOperationalRoleCount(source.actorRoleCount, source.actorRoles);
  if (actorRoleCount !== undefined) {
    result.actorRoleCount = actorRoleCount;
  }
};

const copyPublicManifestByteSizeFields = (
  source: Record<string, unknown>,
  result: Record<string, unknown>,
  fields: string[]
): void => {
  fields.forEach((key) => {
    const byteSize = safePublicManifestByteSize(source[key]);
    if (byteSize !== undefined) {
      result[key] = byteSize;
    }
  });
};

const copyPublicManifestBooleanFields = (
  source: Record<string, unknown>,
  result: Record<string, unknown>,
  fields: string[]
): void => {
  const forcedBoundaryValues: Record<string, boolean> = {
    legalDetermination: false,
    liabilityDetermination: false,
    technicalEvidenceOnly: true,
  };
  fields.forEach((key) => {
    if (typeof source[key] === 'boolean') {
      result[key] = Object.prototype.hasOwnProperty.call(forcedBoundaryValues, key)
        ? forcedBoundaryValues[key]
        : source[key];
    }
  });
};

const safeManifestIsoDate = (value: unknown): string | undefined =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;

const publicTreatySnapshotInstrumentFromApi = (
  record: unknown
): PublicTreatySnapshotInstrumentMetadata | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicTreatySnapshotInstrumentMetadata = {};
  const treatySource = safeManifestLabel(source.source, treatySnapshotSources);
  const scopeRequirementId = safeManifestLabel(source.scopeRequirementId, treatySnapshotScopeRequirementIds);
  const statusDate = safeManifestIsoDate(source.statusDate);
  if (treatySource) {
    result.source = treatySource;
  }
  if (statusDate) {
    result.statusDate = statusDate;
  }
  const partyCount = safePublicManifestCount(source.partyCount);
  if (partyCount !== undefined) {
    result.partyCount = partyCount;
  }
  if (scopeRequirementId) {
    result.scopeRequirementId = scopeRequirementId;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicTreatySnapshotMetadataFromApi = (
  metadata: unknown
): PublicTreatySnapshotManifestMetadata | undefined => {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }
  const source = metadata as Record<string, unknown>;
  const result: PublicTreatySnapshotManifestMetadata = {};
  const cmrConvention = publicTreatySnapshotInstrumentFromApi(source.cmrConvention);
  const ecmrProtocol = publicTreatySnapshotInstrumentFromApi(source.ecmrProtocol);
  const finalProvisionsGovernance = safeManifestLabel(
    source.finalProvisionsGovernance,
    treatySnapshotGovernanceValues
  );
  if (cmrConvention) {
    result.cmrConvention = cmrConvention;
  }
  if (ecmrProtocol) {
    result.ecmrProtocol = ecmrProtocol;
  }
  if (finalProvisionsGovernance) {
    result.finalProvisionsGovernance = finalProvisionsGovernance;
  }
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'countryCodeListsExposed',
    'territorialReservationsEvaluated',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicEcmrArticle4IntegrityFromApi = (
  record: unknown
): PublicEcmrArticle4IntegrityEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicEcmrArticle4IntegrityEvidence = {};
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, ecmrArticle4SourceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, ecmrArticle4SourceArticles);
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'supplementOrAmendmentRecordCount',
    'appliedAmendmentRecordCount',
  ]);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'sameParticularsAsCmrValidationGatePresent',
    'finalFormIntegrityProcedurePresent',
    'currentVersionDigestPresent',
    'currentVersionIntegrityValid',
    'currentVersionIntegrityCheckPerformed',
    'finalXmlArchiveReferencePresent',
    'finalXmlArchiveCurrentVersionReferencePresent',
    'finalXmlArchiveDigestPresent',
    'finalXmlArchiveObjectVersionReferencePresent',
    'amendmentDetectabilityEvidencePresent',
    'originalParticularsPreservationEvidencePresent',
    'technicalEvidenceOnly',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicReplacementByOtherMeansRecordFromApi = (
  record: unknown
): PublicReplacementByOtherMeansEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicReplacementByOtherMeansEvidence = {};
  const replacementMedium = safeManifestLabel(source.replacementMedium, replacementByOtherMeansMedia);
  const replacementStatus = safeManifestLabel(source.replacementStatus, operationalRecordStatuses);
  const statusAtReplacement = safeManifestLabel(source.statusAtReplacement, documentLifecycleStatuses);
  if (replacementMedium) {
    result.replacementMedium = replacementMedium;
  }
  if (replacementStatus) {
    result.replacementStatus = replacementStatus;
  }
  if (statusAtReplacement) {
    result.statusAtReplacement = statusAtReplacement;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt', 'replacedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'replacementReferencePresent',
    'reasonPresent',
    'externalReferencePresent',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const replacementByOtherMeansEvidenceFromApi = (
  record: unknown
): ReplacementByOtherMeansEvidence => {
  const evidence = publicReplacementByOtherMeansRecordFromApi(record);
  return {
    ...evidence,
    replacementMedium: evidence?.replacementMedium ?? 'other',
  };
};

const publicAmendmentManifestRecordFromApi = (
  record: unknown
): PublicAmendmentManifestEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicAmendmentManifestEvidence = {};
  const status = safeManifestLabel(source.status, amendmentStatuses);
  const lastAcknowledgementDecision = safeManifestLabel(
    source.lastAcknowledgementDecision,
    amendmentDecisions
  );
  if (status) {
    result.status = status;
  }
  if (lastAcknowledgementDecision) {
    result.lastAcknowledgementDecision = lastAcknowledgementDecision;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['requestedAt']);
  const acknowledgementSource = Array.isArray(source.acknowledgementList)
    ? source.acknowledgementList
    : Array.isArray(source.acknowledgements)
      ? source.acknowledgements
      : [];
  const acknowledgementList = acknowledgementSource
    .map(publicAmendmentAcknowledgementEvidenceFromApi)
    .filter((acknowledgement): acknowledgement is AmendmentAcknowledgementEvidence => Boolean(acknowledgement));
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'parentVersionNumber',
    'acknowledgementCount',
    'proposedChangeFieldCount',
  ]);
  const acknowledgedActorRoleCount = publicAcknowledgedActorRoleCount(acknowledgementList)
    ?? safePublicManifestCount(source.acknowledgedActorRoleCount);
  if (acknowledgedActorRoleCount !== undefined) {
    result.acknowledgedActorRoleCount = acknowledgedActorRoleCount;
  }
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'parentVersionReferencePresent',
    'documentVersionReferencePresent',
    'appliedVersionReferencePresent',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicAmendmentAcknowledgementEvidenceFromApi = (
  record: unknown
): AmendmentAcknowledgementEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const decision = safeManifestLabel(source.decision, amendmentDecisions);
  const acknowledged = typeof source.acknowledged === 'boolean'
    ? source.acknowledged
    : decision === 'acknowledged';
  const result: AmendmentAcknowledgementEvidence = {
    acknowledged,
  };
  if (decision) {
    result.decision = decision;
  }
  if (source.acknowledgementReferencePresent === true || typeof source.acknowledgementId === 'string') {
    result.acknowledgementReferencePresent = true;
  }
  if (source.amendmentReferencePresent === true || typeof source.amendmentId === 'string') {
    result.amendmentReferencePresent = true;
  }
  if (source.commentPresent === true || typeof source.comment === 'string') {
    result.commentPresent = true;
  } else if (source.commentPresent === false) {
    result.commentPresent = false;
  }
  const actorRoleCount = publicOperationalRoleCount(source.actorRoleCount, source.actorRoles);
  if (actorRoleCount !== undefined) {
    result.actorRoleCount = actorRoleCount;
  }
  if (typeof source.acknowledgedAt === 'string') {
    result.acknowledgedAt = source.acknowledgedAt;
  }
  return result;
};

const publicAcknowledgedActorRoleCount = (
  acknowledgements: AmendmentAcknowledgementEvidence[]
): number | undefined => {
  let hasRoleCount = false;
  const total = acknowledgements.reduce((sum, acknowledgement) => {
    const count = safePublicManifestCount(acknowledgement.actorRoleCount);
    if (count === undefined) {
      return sum;
    }
    hasRoleCount = true;
    return sum + count;
  }, 0);
  return hasRoleCount ? total : undefined;
};

const publicAmendmentEvidenceFromApi = (
  record: unknown
): AmendmentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  if (typeof source.amendmentId !== 'string' || source.amendmentId.trim().length === 0) {
    return undefined;
  }
  const result: AmendmentEvidence = {
    amendmentId: source.amendmentId.trim(),
  };
  const status = safeManifestLabel(source.status, amendmentStatuses);
  if (status) {
    result.status = status;
  }
  if (source.reasonPresent === true || typeof source.reason === 'string') {
    result.reasonPresent = true;
  } else if (source.reasonPresent === false) {
    result.reasonPresent = false;
  }
  if (source.parentVersionReferencePresent === true) {
    result.parentVersionReferencePresent = true;
  } else if (source.parentVersionReferencePresent === false) {
    result.parentVersionReferencePresent = false;
  }
  const parentVersionNumber = safePublicManifestVersionNumber(source.parentVersionNumber);
  if (parentVersionNumber !== undefined) {
    result.parentVersionNumber = parentVersionNumber;
  }
  if (source.documentVersionReferencePresent === true) {
    result.documentVersionReferencePresent = true;
  } else if (source.documentVersionReferencePresent === false) {
    result.documentVersionReferencePresent = false;
  }
  if (source.appliedVersionReferencePresent === true) {
    result.appliedVersionReferencePresent = true;
  } else if (source.appliedVersionReferencePresent === false) {
    result.appliedVersionReferencePresent = false;
  }
  const authorizationBasis = strictAmendmentAuthorizationBasis(source.authorizationBasis);
  if (authorizationBasis) {
    result.authorizationBasis = authorizationBasis;
  }
  if (source.authorizationEvidenceReferencePresent === true) {
    result.authorizationEvidenceReferencePresent = true;
  } else if (source.authorizationEvidenceReferencePresent === false) {
    result.authorizationEvidenceReferencePresent = false;
  }
  if (source.proposedXmlDigestAvailable === true) {
    result.proposedXmlDigestAvailable = true;
  } else if (source.proposedXmlDigestAvailable === false) {
    result.proposedXmlDigestAvailable = false;
  }
  if (source.legalDetermination === false) {
    result.legalDetermination = false;
  }
  const appliedVersionNumber = safePublicManifestVersionNumber(source.appliedVersionNumber);
  if (appliedVersionNumber !== undefined) {
    result.appliedVersionNumber = appliedVersionNumber;
  }
  const acknowledgementSource = Array.isArray(source.acknowledgementList)
    ? source.acknowledgementList
    : Array.isArray(source.acknowledgements)
      ? source.acknowledgements
      : [];
  const acknowledgementList = acknowledgementSource
    .map(publicAmendmentAcknowledgementEvidenceFromApi)
    .filter((acknowledgement): acknowledgement is AmendmentAcknowledgementEvidence => Boolean(acknowledgement));
  const acknowledgementCount = safePublicManifestCount(source.acknowledgementCount)
    ?? safePublicManifestCount(acknowledgementSource.length);
  if (acknowledgementCount !== undefined) {
    result.acknowledgementCount = acknowledgementCount;
  }
  const acknowledgedActorRoleCount = publicAcknowledgedActorRoleCount(acknowledgementList)
    ?? safePublicManifestCount(source.acknowledgedActorRoleCount);
  if (acknowledgedActorRoleCount !== undefined) {
    result.acknowledgedActorRoleCount = acknowledgedActorRoleCount;
  }
  const proposedChangeFieldCount = safePublicManifestCount(source.proposedChangeFieldCount)
    ?? safePublicManifestCount(
      source.proposedChanges && typeof source.proposedChanges === 'object'
        ? Object.keys(source.proposedChanges as Record<string, unknown>).length
        : undefined
    );
  if (proposedChangeFieldCount !== undefined) {
    result.proposedChangeFieldCount = proposedChangeFieldCount;
  }
  const lastAcknowledgementDecision = safeManifestLabel(source.lastAcknowledgementDecision, amendmentDecisions)
    ?? [...acknowledgementSource]
      .reverse()
      .map((acknowledgement) =>
        acknowledgement && typeof acknowledgement === 'object'
          ? publicAmendmentAcknowledgementEvidenceFromApi(acknowledgement)?.decision
          : undefined
      )
      .find((decision): decision is string => Boolean(decision));
  if (lastAcknowledgementDecision) {
    result.lastAcknowledgementDecision = lastAcknowledgementDecision;
  }
  if (typeof source.requestedAt === 'string') {
    result.requestedAt = source.requestedAt;
  }
  if (typeof source.appliedAt === 'string') {
    result.appliedAt = source.appliedAt;
  }
  if (acknowledgementList.length > 0) {
    result.acknowledgementList = acknowledgementList;
  }
  return result;
};

const requirePublicAmendmentEvidenceFromApi = (
  record: unknown
): AmendmentEvidence => {
  const result = publicAmendmentEvidenceFromApi(record);
  if (!result) {
    throw new Error('Respuesta invalida de modificacion');
  }
  return result;
};

const requirePublicAmendmentAcknowledgementEvidenceFromApi = (
  record: unknown
): AmendmentAcknowledgementEvidence => {
  const result = publicAmendmentAcknowledgementEvidenceFromApi(record);
  if (!result) {
    throw new Error('Respuesta invalida de acuse de modificacion');
  }
  return result;
};

const publicDisposalInstructionRecordFromApi = (
  record: unknown
): PublicDisposalInstructionEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicDisposalInstructionEvidence = {};
  const instructionType = safeManifestLabel(source.instructionType, disposalInstructionTypes);
  const instructionStatus = safeManifestLabel(source.instructionStatus, operationalRecordStatuses);
  const statusAtInstruction = safeManifestLabel(source.statusAtInstruction, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, cmrArticle12Articles);
  if (instructionType) {
    result.instructionType = instructionType;
  }
  if (instructionStatus) {
    result.instructionStatus = instructionStatus;
  }
  if (statusAtInstruction) {
    result.statusAtInstruction = statusAtInstruction;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt', 'requestedExecutionAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'instructionReferencePresent',
    'consignmentNoteCopyReferencePresent',
    'rightOfDisposalNotCeased',
    'firstCopyInstructionProduced',
    'carrierIndemnityAccepted',
    'instructionFeasible',
    'doesNotPrejudiceOtherConsignments',
    'doesNotDivideConsignment',
    'successiveConsigneeInstructionFinal',
    'reasonPresent',
    'newDeliveryPlacePresent',
    'newConsigneePresent',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const disposalInstructionEvidenceFromApi = (
  record: unknown
): DisposalInstructionEvidence => {
  const evidence = publicDisposalInstructionRecordFromApi(record);
  return {
    ...evidence,
    instructionType: evidence?.instructionType ?? 'other',
  };
};

const publicDisposalExecutionNoticeRecordFromApi = (
  record: unknown
): PublicDisposalExecutionNoticeEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicDisposalExecutionNoticeEvidence = {};
  const noticeType = safeManifestLabel(source.noticeType, disposalExecutionNoticeTypes);
  const noticeStatus = safeManifestLabel(source.noticeStatus, operationalRecordStatuses);
  const statusAtNotice = safeManifestLabel(source.statusAtNotice, documentLifecycleStatuses);
  const instructionType = safeManifestLabel(source.instructionType, disposalInstructionTypes);
  const notificationTargetRole = safeManifestLabel(source.notificationTargetRole, disposalExecutionNoticeTargetRoles);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, cmrArticle126Articles);
  if (noticeType) {
    result.noticeType = noticeType;
  }
  if (noticeStatus) {
    result.noticeStatus = noticeStatus;
  }
  if (statusAtNotice) {
    result.statusAtNotice = statusAtNotice;
  }
  if (instructionType) {
    result.instructionType = instructionType;
  }
  if (notificationTargetRole) {
    result.notificationTargetRole = notificationTargetRole;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt', 'notificationSentAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'noticeReferencePresent',
    'instructionReferencePresent',
    'unableToExecute',
    'notificationSent',
    'reasonPresent',
    'notificationDetailPresent',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const disposalExecutionNoticeEvidenceFromApi = (
  record: unknown
): DisposalExecutionNoticeEvidence => {
  const evidence = publicDisposalExecutionNoticeRecordFromApi(record);
  return {
    ...evidence,
    noticeType: evidence?.noticeType ?? 'other',
  };
};

const publicConsigneeDeliveryRequestRecordFromApi = (
  record: unknown
): PublicConsigneeDeliveryRequestEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicConsigneeDeliveryRequestEvidence = {};
  const requestType = safeManifestLabel(source.requestType, consigneeDeliveryRequestTypes);
  const requestStatus = safeManifestLabel(source.requestStatus, operationalRecordStatuses);
  const statusAtRequest = safeManifestLabel(source.statusAtRequest, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, cmrArticle13Articles);
  if (requestType) {
    result.requestType = requestType;
  }
  if (requestStatus) {
    result.requestStatus = requestStatus;
  }
  if (statusAtRequest) {
    result.statusAtRequest = statusAtRequest;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'deliveryRequestReferencePresent',
    'arrivalAtDeliveryPlace',
    'deliveryRequested',
    'secondCopyRequested',
    'receiptCommitment',
    'chargesPaymentAcknowledged',
    'chargesDisputed',
    'securityFurnished',
    'lossEstablished',
    'lossEvidenceReferencePresent',
    'article19DelayPeriodExpired',
    'article19DelayAssessmentReferencePresent',
    'rightsReservedNotice',
    'receiptDetailPresent',
    'chargesSecurityDetailPresent',
    'enforcementBasisDetailPresent',
    'reasonPresent',
  ]);
  if (requestType === 'delivery_after_arrival') {
    copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
      'chargesPaymentLineCount',
    ]);
    copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
      'consignmentNoteChargesComputed',
      'consignmentNoteChargesMatched',
      'chargesPaymentAmountsPositive',
      'chargesPaymentCurrenciesRecognized',
      'chargesPaidAtPresent',
      'chargesPaymentEvidenceAttachmentReferencePresent',
      'securityFurnishedAtPresent',
      'chargesSecurityEvidenceAttachmentReferencePresent',
      'chargesEvidenceChronologyValid',
    ]);
  }
  if (requestType === 'delivery_receipt_and_second_copy_handover') {
    copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
      'deliveryConfirmationReferencePresent',
      'consignmentNoteCopyReferencePresent',
      'goodsReceived',
      'secondCopyReceived',
      'receiptIssued',
    ]);
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const consigneeDeliveryRequestEvidenceFromApi = (
  record: unknown
): ConsigneeDeliveryRequestEvidence => {
  const evidence = publicConsigneeDeliveryRequestRecordFromApi(record);
  return {
    ...evidence,
    requestType: evidence?.requestType ?? 'other',
  };
};

const publicCarriageImpedimentRecordFromApi = (
  record: unknown
): PublicCarriageImpedimentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCarriageImpedimentEvidence = {};
  const impedimentType = safeManifestLabel(source.impedimentType, carriageImpedimentTypes);
  const impedimentStatus = safeManifestLabel(source.impedimentStatus, operationalRecordStatuses);
  const statusAtImpediment = safeManifestLabel(source.statusAtImpediment, documentLifecycleStatuses);
  const instructionRequestTargetRole = safeManifestLabel(
    source.instructionRequestTargetRole,
    carriageImpedimentInstructionTargetRoles
  );
  const saleReason = safeManifestLabel(source.saleReason, carriageImpedimentSaleReasons);
  const saleProcedureBasis = safeManifestLabel(
    source.saleProcedureBasis,
    carriageImpedimentSaleProcedureBases
  );
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, carriageImpedimentSourceArticles);
  if (impedimentType) {
    result.impedimentType = impedimentType;
  }
  if (impedimentStatus) {
    result.impedimentStatus = impedimentStatus;
  }
  if (statusAtImpediment) {
    result.statusAtImpediment = statusAtImpediment;
  }
  if (instructionRequestTargetRole) {
    result.instructionRequestTargetRole = instructionRequestTargetRole;
  }
  if (saleReason) {
    result.saleReason = saleReason;
  }
  if (impedimentType === 'sale_of_goods' && saleProcedureBasis) {
    result.saleProcedureBasis = saleProcedureBasis;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'impedimentReferencePresent',
    'instructionsRequested',
    'instructionsUnavailableInReasonableTime',
    'bestInterestsActionTaken',
    'arrivalAtDeliveryPlace',
    'consigneeRefusedGoods',
    'article14Or15ImpedimentBasis',
    'goodsUnloadedForAccount',
    'goodsHeldForEntitledPerson',
    'thirdPartyCustodianUsed',
    'thirdPartyCustodianSelectedWithReasonableCare',
    'proceedsPlacedAtDisposal',
    'saleProcedureLawOrCustomAcknowledged',
    'expensesRecoverableClaimed',
    'reasonPresent',
    'instructionRequestDetailPresent',
    'alternativeCarriageConditionsPresent',
    'storageOrSaleDetailPresent',
  ]);
  if (impedimentType && carriageImpedimentInstructionRequestTypes.has(impedimentType)) {
    copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
      'instructionRequestSentAtPresent',
      'instructionRequestReceivedAtPresent',
      'instructionRequestEvidenceAttachmentReferencePresent',
      'instructionRequestChronologyValid',
    ]);
  }
  if (impedimentType === 'sale_of_goods') {
    copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
      'goodsLocationCountryCodePresent',
      'goodsLocationDetailPresent',
      'saleProcedureReferencePresent',
      'saleProcedureAssessedAtPresent',
      'saleProcedureEvidenceAttachmentReferencePresent',
      'saleProcedureChronologyValid',
    ]);
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const carriageImpedimentEvidenceFromApi = (
  record: unknown
): CarriageImpedimentEvidence => {
  const evidence = publicCarriageImpedimentRecordFromApi(record);
  return {
    ...evidence,
    impedimentType: evidence?.impedimentType ?? 'other',
  };
};

const publicCarriageAccountingRecordFromApi = (
  record: unknown
): PublicCarriageAccountingEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCarriageAccountingEvidence = {};
  const accountingType = safeManifestLabel(source.accountingType, carriageAccountingTypes);
  const accountingStatus = safeManifestLabel(source.accountingStatus, operationalRecordStatuses);
  const statusAtAccounting = safeManifestLabel(source.statusAtAccounting, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, carriageAccountingSourceArticles);
  const linkedImpedimentType = safeManifestLabel(source.linkedImpedimentType, carriageImpedimentTypes);
  if (accountingType) {
    result.accountingType = accountingType;
  }
  if (accountingStatus) {
    result.accountingStatus = accountingStatus;
  }
  if (statusAtAccounting) {
    result.statusAtAccounting = statusAtAccounting;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (linkedImpedimentType) {
    result.linkedImpedimentType = linkedImpedimentType;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'impedimentReferencePresent',
    'amountPresent',
    'positiveAmount',
    'currencyPresent',
    'recognizedCurrency',
    'accountingAtPresent',
    'accountingEvidenceAttachmentReferencePresent',
    'accountingChronologyValid',
    'carrierWrongfulActOrNeglectNotAsserted',
    'chargeableAgainstGoods',
    'proceedsPlacedAtDisposal',
    'chargesExceedProceeds',
    'accountingDetailPresent',
  ]);
  if (accountingType === 'instruction_expense') {
    copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
      'instructionRequestEvidencePresent',
    ]);
  } else if (accountingType === 'sale_proceeds') {
    copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
      'grossSaleProceedsAmountPresent',
      'deductedSaleExpensesAmountPresent',
      'netSaleProceedsReconciled',
    ]);
  } else if (accountingType === 'charge_shortfall') {
    copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
      'saleProceedsAccountingReferencePresent',
      'totalChargesDueAmountPresent',
      'chargeShortfallReconciled',
    ]);
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const carriageAccountingEvidenceFromApi = (
  record: unknown
): CarriageAccountingEvidence => {
  const evidence = publicCarriageAccountingRecordFromApi(record);
  return {
    ...evidence,
    accountingType: evidence?.accountingType ?? 'other',
  };
};

const publicCarrierLiabilityIncidentRecordFromApi = (
  record: unknown
): PublicCarrierLiabilityIncidentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCarrierLiabilityIncidentEvidence = {};
  const incidentType = safeManifestLabel(source.incidentType, carrierLiabilityIncidentTypes);
  const incidentStatus = safeManifestLabel(source.incidentStatus, operationalRecordStatuses);
  const statusAtIncident = safeManifestLabel(source.statusAtIncident, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, carrierLiabilityIncidentSourceArticles);
  const specialRiskType = safeManifestLabel(source.specialRiskType, carrierLiabilityIncidentSpecialRisks);
  if (incidentType) {
    result.incidentType = incidentType;
  }
  if (incidentStatus) {
    result.incidentStatus = incidentStatus;
  }
  if (statusAtIncident) {
    result.statusAtIncident = statusAtIncident;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (specialRiskType) {
    result.specialRiskType = specialRiskType;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'occurredBetweenTakeoverAndDelivery',
    'lossObserved',
    'damageObserved',
    'delayObserved',
    'claimantFaultOrNeglectAlleged',
    'claimantInstructionAlleged',
    'inherentViceAlleged',
    'unavoidableCircumstancesAlleged',
    'specialRiskClaimed',
    'specialRiskPresumptionClaimed',
    'temperatureControlledVehicle',
    'equipmentStepsEvidencePresent',
    'livestockCareStepsEvidencePresent',
    'disposalInstructionReferencePresent',
    'instructionNotCarriedOut',
    'instructionCarriedOutWithoutFirstCopy',
    'claimantEntitlementAsserted',
    'lossOrDamageCaused',
    'takingOverAtPresent',
    'incidentAtPresent',
    'deliveryAtPresent',
    'incidentEvidenceAttachmentReferencePresent',
    'carrierProofRequired',
    'carrierProofEvidenceAttachmentReferencePresent',
    'incidentChronologyValid',
    'openUnsheetedVehicleAgreementSpecifiedInConsignmentNote',
    'article18OpenVehiclePresumptionExceptionAssessed',
    'article18OpenVehiclePresumptionExceptionAbsent',
    'article17_4aVehicleUseAssessmentRecorded',
    'article17_4aOpenUnsheetedVehicleUseReported',
    'article17_4aAssessmentDetailPresent',
    'article17_4aEvidenceAttachmentReferencePresent',
    'article17_3NonRelievingFactorsAssessed',
    'article17_3NonRelievingFactorReported',
    'article17_3AssessmentDetailPresent',
    'article17_3EvidenceAttachmentReferencePresent',
    'article18_1BurdenAssessmentRecorded',
    'article18_1CauseProofReported',
    'article18_1AssessmentDetailPresent',
    'article18_1EvidenceAttachmentReferencePresent',
    'article18_2CircumstancesAssessmentRecorded',
    'article18_2CircumstancesEstablishedReported',
    'article18_2AssessmentDetailPresent',
    'article18_2EvidenceAttachmentReferencePresent',
    'article17_4bPackingConditionAssessmentRecorded',
    'article17_4bPackingAbsentOrDefectiveReported',
    'article17_4bGoodsLiabilityWithoutProperPackingReported',
    'article17_4bAssessmentDetailPresent',
    'article17_4bEvidenceAttachmentReferencePresent',
    'article17_4cOperationAssessmentRecorded',
    'article17_4cQualifyingOperationReported',
    'article17_4cActorCapacityAssessmentRecorded',
    'article17_4cQualifyingActorCapacityReported',
    'article17_4cAssessmentDetailPresent',
    'article17_4cEvidenceAttachmentReferencePresent',
    'article17_4dNatureAssessmentRecorded',
    'article17_4dParticularExposureReported',
    'article17_4dSusceptibilityBasisAssessmentRecorded',
    'article17_4dQualifyingSusceptibilityBasisReported',
    'article17_4dAssessmentDetailPresent',
    'article17_4dEvidenceAttachmentReferencePresent',
    'article17_4eMarkingAssessmentRecorded',
    'article17_4eQualifyingMarkingConditionReported',
    'article17_4eDeficiencyBasisAssessmentRecorded',
    'article17_4eQualifyingDeficiencyBasisReported',
    'article17_4eAssessmentDetailPresent',
    'article17_4eEvidenceAttachmentReferencePresent',
    'article17_4fLivestockAssessmentRecorded',
    'article17_4fLivestockCarriageReported',
    'article17_4fAssessmentDetailPresent',
    'article17_4fEvidenceAttachmentReferencePresent',
    'article18_4EquipmentStepsAssessmentRecorded',
    'article18_4ChoiceMaintenanceUseStepsReported',
    'article18_4SpecialInstructionsAssessmentRecorded',
    'article18_4SpecialInstructionsConsistencyValid',
    'article18_4AssessmentDetailPresent',
    'article18_4EvidenceAttachmentReferencePresent',
    'article18_5NormallyIncumbentStepsAssessmentRecorded',
    'article18_5NormallyIncumbentStepsReported',
    'article18_5SpecialInstructionsAssessmentRecorded',
    'article18_5SpecialInstructionsConsistencyValid',
    'article18_5AssessmentDetailPresent',
    'article18_5EvidenceAttachmentReferencePresent',
    'article17_5MixedCausationAssessed',
    'article17_5MixedCausationReported',
    'article17_5ContributionSharesPresent',
    'article17_5ContributionSharesReconciled',
    'article17_5AssessmentDetailPresent',
    'article17_5EvidenceAttachmentReferencePresent',
    'incidentDetailPresent',
    'claimantStatementDetailPresent',
    'carrierProofDetailPresent',
    'liabilityDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const carrierLiabilityIncidentEvidenceFromApi = (
  record: unknown
): CarrierLiabilityIncidentEvidence => {
  const evidence = publicCarrierLiabilityIncidentRecordFromApi(record);
  const result: CarrierLiabilityIncidentEvidence = {
    ...evidence,
    incidentType: evidence?.incidentType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'liabilityDetermination')) {
    result.liabilityDetermination = false;
  }
  return result;
};

const publicArticle18_2ClaimantRebuttalRecordFromApi = (
  record: unknown
): PublicArticle18_2ClaimantRebuttalEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicArticle18_2ClaimantRebuttalEvidence = {};
  const rebuttalStatus = safeManifestLabel(source.rebuttalStatus, operationalRecordStatuses);
  const statusAtRebuttal = safeManifestLabel(source.statusAtRebuttal, documentLifecycleStatuses);
  const sourceArticle = safeManifestLabel(
    source.sourceArticle,
    article18_2ClaimantRebuttalSourceArticles
  );
  if (rebuttalStatus) {
    result.rebuttalStatus = rebuttalStatus;
  }
  if (statusAtRebuttal) {
    result.statusAtRebuttal = statusAtRebuttal;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'carrierSpecialRiskPresumptionReferencePresent',
    'claimantRoleAttributed',
    'lossOrDamageAttributionDisputeRecorded',
    'claimantCounterEvidenceSubmissionRecorded',
    'counterEvidenceDetailPresent',
    'counterEvidenceAttachmentReferencePresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const article18_2ClaimantRebuttalEvidenceFromApi = (
  record: unknown
): Article18_2ClaimantRebuttalEvidence => ({
  ...publicArticle18_2ClaimantRebuttalRecordFromApi(record),
});

const publicDeliveryTimingAssessmentRecordFromApi = (
  record: unknown
): PublicDeliveryTimingAssessmentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicDeliveryTimingAssessmentEvidence = {};
  const assessmentType = safeManifestLabel(source.assessmentType, deliveryTimingAssessmentTypes);
  const assessmentStatus = safeManifestLabel(source.assessmentStatus, operationalRecordStatuses);
  const statusAtAssessment = safeManifestLabel(source.statusAtAssessment, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, deliveryTimingAssessmentSourceArticles);
  if (assessmentType) {
    result.assessmentType = assessmentType;
  }
  if (assessmentStatus) {
    result.assessmentStatus = assessmentStatus;
  }
  if (statusAtAssessment) {
    result.statusAtAssessment = statusAtAssessment;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'goodsNotDelivered',
    'agreedTimeLimitPresent',
    'agreedTimeLimitExpired',
    'agreedTimeLimitConsignmentNoteReferencePresent',
    'article19AgreedTimeLimitComputed',
    'article19AgreedTimeLimitExceeded',
    'article19ReasonableDurationComputed',
    'article19ElapsedDurationOrderValid',
    'diligentCarrierAllowedDurationPresent',
    'diligentCarrierAllowedDurationPositive',
    'reasonableDurationExceeded',
    'partialLoadAssemblyDurationPresent',
    'partialLoadAssemblyDurationPositive',
    'partialLoadAssemblyTimeConsidered',
    'diligentCarrierDurationExceeded',
    'thirtyDaysAfterAgreedLimitExpired',
    'sixtyDaysAfterTakeoverExpired',
    'article20LossPresumptionPeriodComputed',
    'article20LossPresumptionPeriodSatisfied',
    'article20LossPresumptionAssessmentReferencePresent',
    'article20CompensationAssessmentReferencePresent',
    'article20CompensationPaymentReferencePresent',
    'compensationReceived',
    'recoveryNotificationRequested',
    'writtenAcknowledgementIssued',
    'goodsRecovered',
    'recoveryNotificationSent',
    'deliveryRequested',
    'article20DeliveryRequestWindowComputed',
    'article20DeliveryRequestChronologyValid',
    'article20DeliveryRequestWithinThirtyDays',
    'chargesAndRefundConditionAcknowledged',
    'takingOverAtPresent',
    'agreedTimeLimitAtPresent',
    'actualDeliveryAtPresent',
    'assessmentAtPresent',
    'recoveryNotificationAtPresent',
    'recoveryNotificationReceivedAtPresent',
    'deliveryRequestAtPresent',
    'timingDetailPresent',
    'claimDetailPresent',
    'recoveryDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const deliveryTimingAssessmentEvidenceFromApi = (
  record: unknown
): DeliveryTimingAssessmentEvidence => {
  const evidence = publicDeliveryTimingAssessmentRecordFromApi(record);
  const result: DeliveryTimingAssessmentEvidence = {
    ...evidence,
    assessmentType: evidence?.assessmentType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicCompensationPaymentRecordFromApi = (
  record: unknown
): PublicCompensationPaymentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCompensationPaymentEvidence = {};
  const paymentType = safeManifestLabel(source.paymentType, compensationPaymentTypes);
  const paymentStatus = safeManifestLabel(source.paymentStatus, operationalRecordStatuses);
  const statusAtPayment = safeManifestLabel(source.statusAtPayment, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, compensationPaymentSourceArticles);
  const paymentEvidenceType = safeManifestLabel(source.paymentEvidenceType, compensationPaymentEvidenceTypes);
  if (paymentType) {
    result.paymentType = paymentType;
  }
  if (paymentStatus) {
    result.paymentStatus = paymentStatus;
  }
  if (statusAtPayment) {
    result.statusAtPayment = statusAtPayment;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (paymentEvidenceType) {
    result.paymentEvidenceType = paymentEvidenceType;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'lossPresumptionAssessmentReferencePresent',
    'compensationAssessmentReferencePresent',
    'paymentEvidenceAttachmentReferencePresent',
    'compensationForMissingGoods',
    'paymentSettled',
    'recipientAcknowledgedReceipt',
    'paymentAmountPresent',
    'paymentAmountPositive',
    'paymentCurrencyPresent',
    'paymentCurrencyRecognized',
    'paidAtPresent',
    'paymentDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const compensationPaymentEvidenceFromApi = (
  record: unknown
): CompensationPaymentEvidence => {
  const evidence = publicCompensationPaymentRecordFromApi(record);
  const result: CompensationPaymentEvidence = {
    ...evidence,
    paymentType: evidence?.paymentType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicRecoveryDeliverySettlementRecordFromApi = (
  record: unknown
): PublicRecoveryDeliverySettlementEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicRecoveryDeliverySettlementEvidence = {};
  const settlementType = safeManifestLabel(source.settlementType, recoveryDeliverySettlementTypes);
  const settlementStatus = safeManifestLabel(source.settlementStatus, operationalRecordStatuses);
  const statusAtSettlement = safeManifestLabel(source.statusAtSettlement, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, recoveryDeliverySettlementSourceArticles);
  if (settlementType) {
    result.settlementType = settlementType;
  }
  if (settlementStatus) {
    result.settlementStatus = settlementStatus;
  }
  if (statusAtSettlement) {
    result.statusAtSettlement = statusAtSettlement;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, ['chargesPaymentLineCount']);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'recoveryAssessmentReferencePresent',
    'compensationPaymentReferencePresent',
    'deliveryReferencePresent',
    'chargesPaymentEvidenceAttachmentReferencePresent',
    'compensationRefundEvidenceAttachmentReferencePresent',
    'consignmentNoteChargesComputed',
    'consignmentNoteChargesMatched',
    'chargesPaymentAmountsPositive',
    'chargesPaymentCurrenciesRecognized',
    'chargesPaymentSettled',
    'chargesPaidAtPresent',
    'compensationRefundAmountPresent',
    'compensationRefundAmountNonNegative',
    'compensationRefundCurrencyPresent',
    'compensationRefundCurrencyRecognized',
    'chargesIncludedInCompensationAmountPresent',
    'chargesIncludedInCompensationAmountNonNegative',
    'compensationRefundFormulaComputed',
    'compensationRefundFormulaMatched',
    'compensationRefundSettled',
    'compensationRefundedAtPresent',
    'deliveryChronologyComputed',
    'deliveryChronologyValid',
    'delayClaimsRightsPreserved',
    'settlementDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const recoveryDeliverySettlementEvidenceFromApi = (
  record: unknown
): RecoveryDeliverySettlementEvidence => {
  const evidence = publicRecoveryDeliverySettlementRecordFromApi(record);
  const result: RecoveryDeliverySettlementEvidence = {
    ...evidence,
    settlementType: evidence?.settlementType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicRecoveredGoodsDispositionRecordFromApi = (
  record: unknown
): PublicRecoveredGoodsDispositionEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicRecoveredGoodsDispositionEvidence = {};
  const labelSpecs: Array<[keyof PublicRecoveredGoodsDispositionEvidence, unknown, Set<string>]> = [
    ['recordType', source.recordType, recoveredGoodsDispositionTypes],
    ['recordStatus', source.recordStatus, operationalRecordStatuses],
    ['statusAtRecord', source.statusAtRecord, documentLifecycleStatuses],
    ['sourceInstrument', source.sourceInstrument, cmrEvidenceInstruments],
    ['sourceArticle', source.sourceArticle, recoveredGoodsDispositionSourceArticles],
    ['dispositionBasis', source.dispositionBasis, recoveredGoodsDispositionBases],
    ['dispositionActionType', source.dispositionActionType, recoveredGoodsDispositionActionTypes],
  ];
  labelSpecs.forEach(([fieldName, value, allowedValues]) => {
    const label = safeManifestLabel(value, allowedValues);
    if (label) {
      (result as Record<string, unknown>)[fieldName] = label;
    }
  });
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'compensationPaymentReferencePresent',
    'recoveryNoticeReferenceRequired',
    'recoveryNoticeReferencePresent',
    'recoveryEvidenceAttachmentReferencePresent',
    'dispositionEvidenceAttachmentReferencePresent',
    'goodsRecovered',
    'compensationPaidAtPresent',
    'goodsRecoveredAtPresent',
    'dispositionAtPresent',
    'paymentRecoveryDispositionChronologyComputed',
    'paymentRecoveryDispositionChronologyValid',
    'writtenRecoveryNotificationRequestAbsent',
    'recoveryNotificationSent',
    'recoveryNotificationReceivedAtPresent',
    'instructionsAbsent',
    'instructionsAbsenceAssessedAtPresent',
    'article20ThirtyDayInstructionPeriodComputed',
    'article20ThirtyDayInstructionPeriodElapsed',
    'article20OneYearRecoveryPeriodComputed',
    'goodsRecoveredWithinOneYearOfCompensation',
    'goodsRecoveredMoreThanOneYearAfterCompensation',
    'goodsLocationCountryCodePresent',
    'goodsLocationDetailPresent',
    'applicableLocalLawIdentified',
    'applicableLawDetailPresent',
    'dispositionUnderLocalLawRecorded',
    'dispositionDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const recoveredGoodsDispositionEvidenceFromApi = (
  record: unknown
): RecoveredGoodsDispositionEvidence => {
  const evidence = publicRecoveredGoodsDispositionRecordFromApi(record);
  const result: RecoveredGoodsDispositionEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicCodDeliveryOmissionRecordFromApi = (
  record: unknown
): PublicCodDeliveryOmissionEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCodDeliveryOmissionEvidence = {};
  const omissionType = safeManifestLabel(source.omissionType, codDeliveryOmissionTypes);
  const omissionStatus = safeManifestLabel(source.omissionStatus, operationalRecordStatuses);
  const statusAtOmission = safeManifestLabel(source.statusAtOmission, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, codDeliveryOmissionSourceArticles);
  if (omissionType) {
    result.omissionType = omissionType;
  }
  if (omissionStatus) {
    result.omissionStatus = omissionStatus;
  }
  if (statusAtOmission) {
    result.statusAtOmission = statusAtOmission;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'goodsDeliveredToConsignee',
    'codChargeRequiredByContract',
    'codChargeConsignmentNoteReferencePresent',
    'codChargeUncollected',
    'codAmountPresent',
    'codAmountPositive',
    'codCurrencyPresent',
    'codCurrencyRecognized',
    'consignorCompensationClaimed',
    'carrierActionAgainstConsigneeReserved',
    'deliveryAtPresent',
    'omissionDetailPresent',
    'compensationClaimDetailPresent',
    'consigneeActionDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const codDeliveryOmissionEvidenceFromApi = (
  record: unknown
): CodDeliveryOmissionEvidence => {
  const evidence = publicCodDeliveryOmissionRecordFromApi(record);
  const result: CodDeliveryOmissionEvidence = {
    ...evidence,
    omissionType: evidence?.omissionType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicDangerousGoodsNoticeRecordFromApi = (
  record: unknown
): PublicDangerousGoodsNoticeEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicDangerousGoodsNoticeEvidence = {};
  const noticeType = safeManifestLabel(source.noticeType, dangerousGoodsNoticeTypes);
  const noticeStatus = safeManifestLabel(source.noticeStatus, operationalRecordStatuses);
  const statusAtNotice = safeManifestLabel(source.statusAtNotice, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, dangerousGoodsNoticeSourceArticles);
  const carrierActionType = safeManifestLabel(source.carrierActionType, dangerousGoodsCarrierActionTypes);
  if (noticeType) {
    result.noticeType = noticeType;
  }
  if (noticeStatus) {
    result.noticeStatus = noticeStatus;
  }
  if (statusAtNotice) {
    result.statusAtNotice = statusAtNotice;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (carrierActionType) {
    result.carrierActionType = carrierActionType;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'dangerousGoodsHandedToCarrier',
    'exactNatureInformed',
    'precautionsRequired',
    'precautionsIndicated',
    'informationEnteredInConsignmentNote',
    'carrierKnowledgeProvedByOtherMeans',
    'dangerousGoodsConsignmentNoteReferencePresent',
    'carrierKnowledgeProofAttachmentReferencePresent',
    'carrierDidNotKnowDangerousNature',
    'carrierActionTaken',
    'withoutCompensationBasisRecorded',
    'senderExpenseLiabilityClaimed',
    'noticeAtPresent',
    'actionAtPresent',
    'dangerNatureDetailPresent',
    'precautionsDetailPresent',
    'carrierKnowledgeProofDetailPresent',
    'carrierActionDetailPresent',
    'expenseClaimDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const dangerousGoodsNoticeEvidenceFromApi = (
  record: unknown
): DangerousGoodsNoticeEvidence => {
  const evidence = publicDangerousGoodsNoticeRecordFromApi(record);
  const result: DangerousGoodsNoticeEvidence = {
    ...evidence,
    noticeType: evidence?.noticeType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicCompensationAssessmentRecordFromApi = (
  record: unknown
): PublicCompensationAssessmentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCompensationAssessmentEvidence = {};
  const assessmentType = safeManifestLabel(source.assessmentType, compensationAssessmentTypes);
  const assessmentStatus = safeManifestLabel(source.assessmentStatus, operationalRecordStatuses);
  const statusAtAssessment = safeManifestLabel(source.statusAtAssessment, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, compensationAssessmentSourceArticles);
  const goodsValueBasis = safeManifestLabel(source.goodsValueBasis, compensationGoodsValueBases);
  const liabilityLimitBasis = safeManifestLabel(source.liabilityLimitBasis, compensationLiabilityLimitBases);
  const article23LossExtentBasis = safeManifestLabel(
    source.article23LossExtentBasis,
    compensationArticle23LossExtentBases
  );
  const article23ChargeRefundBasis = safeManifestLabel(
    source.article23ChargeRefundBasis,
    compensationArticle23ChargeRefundBases
  );
  if (assessmentType) {
    result.assessmentType = assessmentType;
  }
  if (assessmentStatus) {
    result.assessmentStatus = assessmentStatus;
  }
  if (statusAtAssessment) {
    result.statusAtAssessment = statusAtAssessment;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (goodsValueBasis) {
    result.goodsValueBasis = goodsValueBasis;
  }
  if (liabilityLimitBasis) {
    result.liabilityLimitBasis = liabilityLimitBasis;
  }
  if (article23LossExtentBasis) {
    result.article23LossExtentBasis = article23LossExtentBasis;
  }
  if (article23ChargeRefundBasis) {
    result.article23ChargeRefundBasis = article23ChargeRefundBasis;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'incidentReferencePresent',
    'takingOverPlaceAndTimeConsignmentNoteReferencePresent',
    'valuationEvidenceAttachmentReferencePresent',
    'article23WeightAndRefundEvidenceReferencePresent',
    'carrierLiabilityBasisRecorded',
    'goodsValueAmountPresent',
    'goodsValueAmountPositive',
    'goodsValueCurrencyPresent',
    'goodsValueCurrencyRecognized',
    'grossWeightShortPresent',
    'grossWeightShortPositive',
    'claimAmountPresent',
    'claimAmountPositive',
    'claimCurrencyPresent',
    'claimCurrencyRecognized',
    'carriageChargesAmountPresent',
    'carriageChargesAmountPositive',
    'carriageChargesCurrencyPresent',
    'carriageChargesCurrencyRecognized',
    'declaredValueAmountPresent',
    'declaredValueAmountPositive',
    'declaredValueCurrencyPresent',
    'declaredValueCurrencyRecognized',
    'specialInterestAmountPresent',
    'specialInterestAmountPositive',
    'specialInterestCurrencyPresent',
    'specialInterestCurrencyRecognized',
    'diminishedValueAmountPresent',
    'diminishedValueAmountPositive',
    'diminishedValueCurrencyPresent',
    'diminishedValueCurrencyRecognized',
    'article23LimitApplied',
    'carriageChargesRefundConsidered',
    'delayDamageProved',
    'carriageChargesLimitApplied',
    'damageAffectedWholeConsignment',
    'article25LossLimitApplied',
    'declaredValueEnteredInConsignmentNote',
    'declaredValueSurchargeAgreed',
    'declaredValueExceedsArticle23Limit',
    'declaredValueLimitSubstituted',
    'specialInterestEnteredInConsignmentNote',
    'specialInterestSurchargeAgreed',
    'additionalLossOrDamageProved',
    'specialInterestLimitApplied',
    'agreedTimeLimitExceeded',
    'higherCompensationBasisPresent',
    'assessmentAtPresent',
    'valuationDetailPresent',
    'calculationDetailPresent',
    'claimDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const compensationAssessmentEvidenceFromApi = (
  record: unknown
): CompensationAssessmentEvidence => {
  const evidence = publicCompensationAssessmentRecordFromApi(record);
  const result: CompensationAssessmentEvidence = {
    ...evidence,
    assessmentType: evidence?.assessmentType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicCompensationInterestRecordFromApi = (
  record: unknown
): PublicCompensationInterestEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicCompensationInterestEvidence = {};
  const recordType = safeManifestLabel(source.recordType, compensationInterestRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, compensationInterestSourceArticles);
  const interestStartBasis = safeManifestLabel(source.interestStartBasis, compensationInterestStartBases);
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (interestStartBasis) {
    result.interestStartBasis = interestStartBasis;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'compensationAssessmentReferencePresent',
    'compensationPayableBasisRecorded',
    'claimSentInWriting',
    'legalProceedingsInstituted',
    'fivePercentPerAnnumApplied',
    'compensationAmountPresent',
    'compensationAmountPositive',
    'compensationCurrencyPresent',
    'compensationCurrencyRecognized',
    'interestAmountPresent',
    'interestAmountPositive',
    'interestCurrencyPresent',
    'interestCurrencyRecognized',
    'amountsNotExpressedInPaymentClaimCurrency',
    'paymentClaimCurrencyCountryBasisRecorded',
    'paymentPlaceBasisRecorded',
    'exchangeRateApplicableAtPaymentPlaceAndDate',
    'sourceAmountPresent',
    'sourceAmountPositive',
    'sourceCurrencyPresent',
    'sourceCurrencyRecognized',
    'targetCurrencyPresent',
    'targetCurrencyRecognized',
    'convertedAmountPresent',
    'convertedAmountPositive',
    'convertedCurrencyPresent',
    'convertedCurrencyRecognized',
    'interestStartAtPresent',
    'interestCalculatedAtPresent',
    'interestDateOrderComputed',
    'interestCalculatedAfterStart',
    'paymentAtPresent',
    'interestCalculationDetailPresent',
    'exchangeRateDetailPresent',
    'claimDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const compensationInterestEvidenceFromApi = (
  record: unknown
): CompensationInterestEvidence => {
  const evidence = publicCompensationInterestRecordFromApi(record);
  const result: CompensationInterestEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicLiabilityLimitationRecordFromApi = (
  record: unknown
): PublicLiabilityLimitationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicLiabilityLimitationEvidence = {};
  const recordType = safeManifestLabel(source.recordType, liabilityLimitationRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, liabilityLimitationSourceArticles);
  const liablePersonRole = safeManifestLabel(source.liablePersonRole, liabilityLimitationPersonRoles);
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (liablePersonRole) {
    result.liablePersonRole = liablePersonRole;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'incidentReferencePresent',
    'claimArisesFromCarriageUnderCmr',
    'lossDamageOrDelayClaim',
    'extraContractualClaimUnderApplicableLaw',
    'cmrExclusionOrLimitInvoked',
    'article3ResponsiblePersonInvolved',
    'article3PersonScopeRecorded',
    'actingWithinScopeOfEmployment',
    'limitationOrBurdenShiftProvisionInvoked',
    'wilfulMisconductAlleged',
    'equivalentDefaultAlleged',
    'courtOrTribunalLawBasisRecorded',
    'limitationDisapplicationBasisRecorded',
    'claimDetailPresent',
    'applicableLawDetailPresent',
    'article3PersonDetailPresent',
    'misconductDetailPresent',
    'courtLawDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const liabilityLimitationEvidenceFromApi = (
  record: unknown
): LiabilityLimitationEvidence => {
  const evidence = publicLiabilityLimitationRecordFromApi(record);
  const result: LiabilityLimitationEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicClaimReservationRecordFromApi = (
  record: unknown
): PublicClaimReservationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicClaimReservationEvidence = {};
  const recordType = safeManifestLabel(source.recordType, claimReservationRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, claimReservationSourceArticles);
  const timeLimitComputationBasis = safeManifestLabel(
    source.timeLimitComputationBasis,
    claimReservationTimeLimitBases
  );
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (timeLimitComputationBasis) {
    result.timeLimitComputationBasis = timeLimitComputationBasis;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, ['publicHolidayExclusionCount']);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'deliveryReferencePresent',
    'lossOrDamageApparent',
    'lossOrDamageNotApparent',
    'delayCompensationClaim',
    'reservationSentAtDelivery',
    'reservationSentInWriting',
    'reservationWithinSevenDaysExcludingHolidaysRecorded',
    'conditionCheckedWithCarrier',
    'contraryEvidenceReservationSentInWriting',
    'delayReservationSentInWriting',
    'reservationWithinTwentyOneDaysRecorded',
    'relevantStartDateExcludedFromLimitCalculation',
    'reasonableFacilitiesProvided',
    'timeLimitComputed',
    'withinComputedTimeLimit',
    'publicHolidaysExcludedFromTimeLimit',
    'deliveryAtPresent',
    'conditionCheckedAtPresent',
    'goodsAvailableAtPresent',
    'reservationSentAtPresent',
    'reservationDetailPresent',
    'conditionCheckDetailPresent',
    'delayClaimDetailPresent',
    'investigationFacilityDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const claimReservationEvidenceFromApi = (
  record: unknown
): ClaimReservationEvidence => {
  const evidence = publicClaimReservationRecordFromApi(record);
  const result: ClaimReservationEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicDisputeProcedureRecordFromApi = (
  record: unknown
): PublicDisputeProcedureEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicDisputeProcedureEvidence = {};
  const recordType = safeManifestLabel(source.recordType, disputeProcedureRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, disputeProcedureSourceArticles);
  const claimCategory = safeManifestLabel(source.claimCategory, disputeProcedureClaimCategories);
  const limitationPeriodBasis = safeManifestLabel(
    source.limitationPeriodBasis,
    disputeProcedureLimitationPeriodBases
  );
  const limitationStartBasis = safeManifestLabel(
    source.limitationStartBasis,
    disputeProcedureLimitationStartBases
  );
  const limitationStartComputationBasis = safeManifestLabel(
    source.limitationStartComputationBasis,
    disputeProcedureLimitationStartComputationBases
  );
  const limitationExpiryComputationBasis = safeManifestLabel(
    source.limitationExpiryComputationBasis,
    disputeProcedureLimitationExpiryComputationBases
  );
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (claimCategory) {
    result.claimCategory = claimCategory;
  }
  if (limitationPeriodBasis) {
    result.limitationPeriodBasis = limitationPeriodBasis;
  }
  if (limitationStartBasis) {
    result.limitationStartBasis = limitationStartBasis;
  }
  if (limitationStartComputationBasis) {
    result.limitationStartComputationBasis = limitationStartComputationBasis;
  }
  if (limitationExpiryComputationBasis) {
    result.limitationExpiryComputationBasis = limitationExpiryComputationBasis;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'limitationStartComputed',
    'limitationStartedAtMatchesComputedBasis',
    'limitationExpiryComputed',
    'limitationExpiresAtMatchesComputedBasis',
    'suspensionReleaseDateOrderComputed',
    'suspensionReleaseDatesOrdered',
    'partyDesignatedCourtOrTribunal',
    'defendantResidenceOrBusinessPlaceBasis',
    'contractBranchOrAgencyBasis',
    'takeoverPlaceBasis',
    'deliveryPlaceBasis',
    'contractingCountryCourtOrTribunal',
    'securityForCostsNotRequiredRecorded',
    'samePartiesAndGroundsRecorded',
    'pendingCompetentProceedingRecorded',
    'judgmentEnteredByCompetentCourt',
    'freshProceedingOnlyIfJudgmentNotEnforceableRecorded',
    'judgmentEnforceabilityFormalitiesOnlyRecorded',
    'meritsReopeningProhibitedRecorded',
    'wilfulMisconductOrEquivalentDefaultAlleged',
    'limitationPeriodRecorded',
    'limitationStartDateExcluded',
    'claimBarredNotAvailableByCounterclaimOrSetoffRecorded',
    'courtLawExtensionOrFreshAccrualBasisRecorded',
    'writtenClaimReceived',
    'carrierRejectedClaimInWriting',
    'documentsReturned',
    'claimPartlyAdmitted',
    'remainingPartStillInDispute',
    'receiptReplyReturnProofBurdenRecorded',
    'furtherSameObjectClaimsDoNotSuspendRecorded',
    'arbitrationClausePresent',
    'arbitrationTribunalCompetenceRecorded',
    'tribunalAppliesCmrConvention',
    'actionFiledAtPresent',
    'judgmentEnteredAtPresent',
    'judgmentEnforceableAtPresent',
    'deliveryAtPresent',
    'takeoverAtPresent',
    'agreedTimeLimitExpiresAtPresent',
    'contractMadeAtPresent',
    'limitationStartedAtPresent',
    'limitationExpiresAtPresent',
    'writtenClaimReceivedAtPresent',
    'claimRejectedAtPresent',
    'documentsReturnedAtPresent',
    'jurisdictionDetailPresent',
    'partyAgreementDetailPresent',
    'takeoverDeliveryPlaceDetailPresent',
    'pendingActionDetailPresent',
    'judgmentDetailPresent',
    'limitationCalculationDetailPresent',
    'writtenClaimDetailPresent',
    'rejectionDetailPresent',
    'arbitrationClauseDetailPresent',
    'courtOrTribunalDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const disputeProcedureEvidenceFromApi = (
  record: unknown
): DisputeProcedureEvidence => {
  const evidence = publicDisputeProcedureRecordFromApi(record);
  const result: DisputeProcedureEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicSuccessiveCarrierRecordFromApi = (
  record: unknown
): PublicSuccessiveCarrierEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicSuccessiveCarrierEvidence = {};
  const recordType = safeManifestLabel(source.recordType, successiveCarrierRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, successiveCarrierSourceArticles);
  const allocationBasis = safeManifestLabel(source.allocationBasis, successiveCarrierAllocationBases);
  const recoveryLimitationStartBasis = safeManifestLabel(
    source.recoveryLimitationStartBasis,
    successiveCarrierRecoveryLimitationStartBases
  );
  const recoveryLimitationComputationBasis = safeManifestLabel(
    source.recoveryLimitationComputationBasis,
    successiveCarrierRecoveryLimitationComputationBases
  );
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (allocationBasis) {
    result.allocationBasis = allocationBasis;
  }
  if (recoveryLimitationStartBasis) {
    result.recoveryLimitationStartBasis = recoveryLimitationStartBasis;
  }
  if (recoveryLimitationComputationBasis) {
    result.recoveryLimitationComputationBasis = recoveryLimitationComputationBasis;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'successiveReceiptDateOrderComputed',
    'successiveReceiptIssuedAfterAcceptance',
    'recoveryLimitationPeriodComputed',
    'recoveryClaimFiledWithinComputedLimit',
    'consignmentNoteCopyReferencePresent',
    'reservationReferencePresent',
    'compensationAssessmentReferencePresent',
    'singleContractCarriage',
    'successiveRoadCarrier',
    'goodsAccepted',
    'consignmentNoteAccepted',
    'becomesPartyByAcceptance',
    'datedSignedReceiptIssued',
    'carrierNameAddressEnteredOnSecondCopy',
    'article8ReservationsEnteredWhereApplicable',
    'article9AppliesBetweenSuccessiveCarriers',
    'liabilityForWholeOperationRecorded',
    'lossDamageOrDelayProceeding',
    'actionAgainstFirstCarrier',
    'actionAgainstLastCarrier',
    'actionAgainstEventCarrier',
    'counterclaimOrSetoffSameContract',
    'multiplePermittedCarriersJoined',
    'compensationPaidUnderCmr',
    'interestAndCostsIncluded',
    'responsibleCarrierSoleLiabilityRecorded',
    'multipleCarrierLiabilityApportioned',
    'liabilityCannotBeAttributed',
    'paymentShareApportionmentRecorded',
    'insolventCarrierShareUnpaid',
    'insolventShareReallocatedByPaymentShare',
    'judicialAuthorityDeterminedCompensation',
    'carrierGivenDueNoticeOfProceedings',
    'carrierHadOpportunityToAppear',
    'paymentValidityDisputeBarRecorded',
    'competentRecoveryForumRecorded',
    'allConcernedCarriersMayBeDefendants',
    'article31JudgmentRulesApplied',
    'article32LimitationApplied',
    'recoveryLimitationStartRecorded',
    'carriersAgreedAlternativeAllocation',
    'agreementLimitedToArticles37And38',
    'article40AgreementRecorded',
    'carrierAcceptedAtPresent',
    'receiptIssuedAtPresent',
    'compensationPaidAtPresent',
    'finalJudicialDecisionAtPresent',
    'recoveryClaimFiledAtPresent',
    'agreementRecordedAtPresent',
    'receiptDetailPresent',
    'carrierIdentityEntryDetailPresent',
    'reservationDetailPresent',
    'actionScopeDetailPresent',
    'allocationDetailPresent',
    'insolvencyDetailPresent',
    'proceedingDetailPresent',
    'agreementDetailPresent',
    'noticeOpportunityDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const successiveCarrierEvidenceFromApi = (
  record: unknown
): SuccessiveCarrierEvidence => {
  const evidence = publicSuccessiveCarrierRecordFromApi(record);
  const result: SuccessiveCarrierEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicMultimodalCarriageRecordFromApi = (
  record: unknown
): PublicMultimodalCarriageEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicMultimodalCarriageEvidence = {};
  const recordType = safeManifestLabel(source.recordType, multimodalCarriageRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, multimodalCarriageSourceArticles);
  const otherModeType = safeManifestLabel(source.otherModeType, multimodalCarriageOtherModes);
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (otherModeType) {
    result.otherModeType = otherModeType;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'vehicleCarriedByOtherMode',
    'goodsRemainedLoadedOnVehicle',
    'goodsUnloadedFromVehicle',
    'article14ImpedimentBasisRecorded',
    'otherModeCarriageEvidencePresent',
    'otherModeCarriageDocumentReferencePresent',
    'article14ImpedimentReferencePresent',
    'otherModeCarriageDetailPresent',
    'article14ImpedimentDetailPresent',
    'liabilityDetermination',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const multimodalCarriageEvidenceFromApi = (
  record: unknown
): MultimodalCarriageEvidence => {
  const evidence = publicMultimodalCarriageRecordFromApi(record);
  const result: MultimodalCarriageEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'liabilityDetermination')) {
    result.liabilityDetermination = false;
  }
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicConsignmentNoteCopyRecordFromApi = (
  record: unknown
): PublicConsignmentNoteCopyEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicConsignmentNoteCopyEvidence = {};
  const recordType = safeManifestLabel(source.recordType, consignmentNoteCopyRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, consignmentNoteCopySourceArticles);
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'electronicCopyAccessAvailable',
    'entitledPartyAccessAvailable',
    'senderCarrierSignatureEvidenceAvailable',
    'contractParticipantReferencePresent',
    'senderFirstCopyAvailable',
    'goodsAccompanyingSecondCopyAvailable',
    'carrierRetainedThirdCopyAvailable',
    'copyAccessDetailPresent',
    'signatureDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const consignmentNoteCopyEvidenceFromApi = (
  record: unknown
): ConsignmentNoteCopyEvidence => {
  const evidence = publicConsignmentNoteCopyRecordFromApi(record);
  const result: ConsignmentNoteCopyEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicEcmrReceiptAccessRequestFromApi = (
  record: unknown
): PublicEcmrReceiptAccessRequestEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicEcmrReceiptAccessRequestEvidence = {};
  const requestType = safeManifestLabel(source.requestType, ecmrReceiptAccessRequestTypes);
  const requestStatus = safeManifestLabel(source.requestStatus, ecmrReceiptAccessRequestStatuses);
  const statusAtRequest = safeManifestLabel(source.statusAtRequest, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, ecmrReceiptAccessSourceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, ecmrReceiptAccessSourceArticles);
  if (requestType) {
    result.requestType = requestType;
  }
  if (requestStatus) {
    result.requestStatus = requestStatus;
  }
  if (statusAtRequest) {
    result.statusAtRequest = statusAtRequest;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['requestedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'senderRequestRecorded',
    'requestDetailPresent',
  ]);
  if (Object.prototype.hasOwnProperty.call(source, 'technicalEvidenceOnly')) {
    result.technicalEvidenceOnly = true;
  }
  if (Object.prototype.hasOwnProperty.call(source, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const ecmrReceiptAccessRequestEvidenceFromApi = (
  record: unknown
): EcmrReceiptAccessRequestEvidence => publicEcmrReceiptAccessRequestFromApi(record) ?? {};

const publicEcmrReceiptAccessRecordFromApi = (
  record: unknown
): PublicEcmrReceiptAccessEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicEcmrReceiptAccessEvidence = {};
  const recordType = safeManifestLabel(source.recordType, ecmrReceiptAccessRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, ecmrReceiptAccessSourceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, ecmrReceiptAccessSourceArticles);
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'senderRequestReferencePresent',
    'senderRequestReceived',
    'receiptForGoodsProvided',
    'shipmentIdentificationInformationProvided',
    'electronicConsignmentNoteAccessInformationProvided',
    'handoverAtPresent',
    'senderRequestDetailPresent',
    'receiptDetailPresent',
    'shipmentIdentificationDetailPresent',
    'accessInformationDetailPresent',
  ]);
  if (Object.prototype.hasOwnProperty.call(source, 'technicalEvidenceOnly')) {
    result.technicalEvidenceOnly = true;
  }
  if (Object.prototype.hasOwnProperty.call(source, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const ecmrReceiptAccessEvidenceFromApi = (
  record: unknown
): EcmrReceiptAccessEvidence => {
  const evidence = publicEcmrReceiptAccessRecordFromApi(record);
  return {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
};

const publicSenderParticularsResponsibilityRecordFromApi = (
  record: unknown
): PublicSenderParticularsResponsibilityEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicSenderParticularsResponsibilityEvidence = {};
  const recordType = safeManifestLabel(source.recordType, senderParticularsResponsibilityRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, senderParticularsResponsibilitySourceArticles);
  const affectedParticularCategory = safeManifestLabel(
    source.affectedParticularCategory,
    senderParticularsResponsibilityCategories
  );
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  if (affectedParticularCategory) {
    result.affectedParticularCategory = affectedParticularCategory;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'inaccuracyOrInadequacyRecorded',
    'expenseLossDamageClaimed',
    'carrierEnteredAtSenderRequest',
    'senderBehalfPresumptionRecorded',
    'cmrStatementOmissionRecorded',
    'carrierLiabilityBasisRecorded',
    'reviewedAtPresent',
    'particularDetailPresent',
    'instructionDetailPresent',
    'expenseLossDamageDetailPresent',
    'carrierEntryDetailPresent',
    'cmrStatementOmissionDetailPresent',
  ]);
  if (Object.prototype.hasOwnProperty.call(source, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const senderParticularsResponsibilityEvidenceFromApi = (
  record: unknown
): SenderParticularsResponsibilityEvidence => {
  const evidence = publicSenderParticularsResponsibilityRecordFromApi(record);
  return {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
};

const publicProhibitedClauseRecordFromApi = (
  record: unknown
): PublicProhibitedClauseEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicProhibitedClauseEvidence = {};
  const recordType = safeManifestLabel(source.recordType, prohibitedClauseRecordTypes);
  const recordStatus = safeManifestLabel(source.recordStatus, operationalRecordStatuses);
  const statusAtRecord = safeManifestLabel(source.statusAtRecord, documentLifecycleStatuses);
  const sourceInstrument = safeManifestLabel(source.sourceInstrument, cmrEvidenceInstruments);
  const sourceArticle = safeManifestLabel(source.sourceArticle, prohibitedClauseSourceArticles);
  if (recordType) {
    result.recordType = recordType;
  }
  if (recordStatus) {
    result.recordStatus = recordStatus;
  }
  if (statusAtRecord) {
    result.statusAtRecord = statusAtRecord;
  }
  if (sourceInstrument) {
    result.sourceInstrument = sourceInstrument;
  }
  if (sourceArticle) {
    result.sourceArticle = sourceArticle;
  }
  copyPublicManifestStringFields(source, result as Record<string, unknown>, ['recordedAt']);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestBooleanFields(source, result as Record<string, unknown>, [
    'article40AgreementReferencePresent',
    'contractStipulationReviewed',
    'clauseDetected',
    'directOrIndirectDerogationDetected',
    'benefitOfInsuranceForCarrierDetected',
    'burdenOfProofShiftDetected',
    'article40ExceptionAssessed',
    'article40ExceptionNotApplicable',
    'nullitySeverabilityRecorded',
    'reviewOutcomeRecorded',
    'clauseReviewedAtPresent',
    'clauseDetailPresent',
    'reviewDetailPresent',
    'insuranceBenefitDetailPresent',
    'burdenShiftDetailPresent',
    'article40ExceptionDetailPresent',
    'legalDetermination',
  ]);
  return Object.keys(result).length > 0 ? result : undefined;
};

const prohibitedClauseEvidenceFromApi = (
  record: unknown
): ProhibitedClauseEvidence => {
  const evidence = publicProhibitedClauseRecordFromApi(record);
  const result: ProhibitedClauseEvidence = {
    ...evidence,
    recordType: evidence?.recordType ?? 'other',
  };
  if (evidence && Object.prototype.hasOwnProperty.call(evidence, 'legalDetermination')) {
    result.legalDetermination = false;
  }
  return result;
};

const publicRetentionPolicyManifestFromApi = (
  policy: unknown
): PublicRetentionPolicyManifestEvidence | undefined => {
  if (!policy || typeof policy !== 'object') {
    return undefined;
  }
  const source = policy as Record<string, unknown>;
  const result: PublicRetentionPolicyManifestEvidence = {};
  const retentionMode = safeManifestLabel(source.retentionMode, retentionModes);
  if (retentionMode) {
    result.retentionMode = retentionMode;
  }
  const jurisdiction = safeManifestJurisdiction(source.jurisdiction);
  if (jurisdiction) {
    result.jurisdiction = jurisdiction;
  }
  const retentionDays = safePublicManifestCount(source.retentionDays);
  if (retentionDays !== undefined) {
    result.retentionDays = retentionDays;
  }
  ['retentionPolicyReferencePresent', 'reasonPresent', 'retainUntilPresent'].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicLegalHoldManifestFromApi = (
  legalHold: unknown
): PublicLegalHoldManifestEvidence | undefined => {
  if (!legalHold || typeof legalHold !== 'object') {
    return undefined;
  }
  const source = legalHold as Record<string, unknown>;
  const result: PublicLegalHoldManifestEvidence = {};
  if (typeof source.active === 'boolean') {
    result.active = source.active;
  }
  const status = safeManifestLabel(source.status, legalHoldStatuses);
  if (status) {
    result.status = status;
  }
  [
    'legalHoldReferencePresent',
    'reasonPresent',
    'releaseReasonPresent',
    'appliedAtPresent',
    'releasedAtPresent',
  ].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicSignatureVerificationRecordFromApi = (
  record: unknown
): PublicSignatureVerificationEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicSignatureVerificationEvidence = {};
  const verificationStatus = safeManifestLabel(source.verificationStatus, signatureVerificationStatuses);
  if (verificationStatus) {
    result.verificationStatus = verificationStatus;
  }
  if (typeof source.verifiedAt === 'string') {
    result.verifiedAt = source.verifiedAt;
  }
  if (source.verificationReferencePresent === true || typeof source.verificationId === 'string') {
    result.verificationReferencePresent = true;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.documentVersionReferencePresent === true || typeof source.documentVersionId === 'string') {
    result.documentVersionReferencePresent = true;
  }
  if (
    source.signatureReferencePresent === true ||
    typeof source.signatureReferenceHash === 'string' ||
    typeof source.signatureId === 'string'
  ) {
    result.signatureReferencePresent = true;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicSignatureVerificationResultFromApi = (payload: unknown): SignatureVerificationResult => {
  if (!payload || typeof payload !== 'object') {
    return { valid: false, signatures: [], count: 0 };
  }
  const source = payload as Record<string, unknown>;
  const signatures = Array.isArray(source.signatures)
    ? source.signatures
        .map(publicSignatureVerificationRecordFromApi)
        .filter((signature): signature is PublicSignatureVerificationEvidence => Boolean(signature))
    : [];
  const count = safePublicManifestCount(source.count) ?? signatures.length;
  const result: SignatureVerificationResult = {
    valid: source.valid === true,
    signatures,
    count,
  };
  const integrity = source.integrity;
  if (integrity && typeof integrity === 'object' && typeof (integrity as Record<string, unknown>).valid === 'boolean') {
    result.integrity = {
      valid: (integrity as Record<string, unknown>).valid === true,
    };
  }
  return result;
};

const publicSupplementaryDocumentDownloadResultFromApi = (
  payload: unknown
): SupplementaryDocumentDownloadResult => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Respuesta invalida de descarga de documento suplementario');
  }
  const source = payload as Record<string, unknown>;
  if (typeof source.downloadUrl !== 'string' || source.downloadUrl.length === 0) {
    throw new Error('Respuesta invalida de descarga de documento suplementario');
  }
  const result: SupplementaryDocumentDownloadResult = {
    downloadUrl: source.downloadUrl,
  };
  const expiresIn = safePublicManifestCount(source.expiresIn)
    ?? safePublicManifestCount(source.expiresInSeconds);
  if (expiresIn !== undefined) {
    result.expiresIn = expiresIn;
  }
  return result;
};

const publicSupplementaryDocumentUploadIntentHeadersFromApi = (
  headers: unknown
): Record<string, string> | undefined => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }
  const result: Record<string, string> = {};
  Object.entries(headers as Record<string, unknown>).forEach(([headerName, value]) => {
    if (typeof value !== 'string') {
      return;
    }
    const normalizedName = headerName.trim().toLowerCase();
    const normalizedValue = value.trim();
    if (
      normalizedName === 'content-type' &&
      /^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/i.test(normalizedValue)
    ) {
      result['Content-Type'] = normalizedValue.toLowerCase();
      return;
    }
    if (
      normalizedName === 'x-amz-meta-sha256digest' &&
      /^[a-fA-F0-9]{64}$/.test(normalizedValue)
    ) {
      result['x-amz-meta-sha256digest'] = normalizedValue.toLowerCase();
      return;
    }
    if (
      normalizedName === 'x-amz-checksum-sha256' &&
      /^[A-Za-z0-9+/]{43}=$/.test(normalizedValue)
    ) {
      result['x-amz-checksum-sha256'] = normalizedValue;
    }
  });
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicSupplementaryDocumentUploadIntentFromApi = (
  payload: unknown
): SupplementaryDocumentUploadIntent => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Respuesta invalida de intencion de subida directa de documento suplementario');
  }
  const source = payload as Record<string, unknown>;
  if (typeof source.attachmentId !== 'string' || source.attachmentId.trim().length === 0) {
    throw new Error('Respuesta invalida de intencion de subida directa de documento suplementario');
  }
  if (typeof source.uploadUrl !== 'string' || source.uploadUrl.trim().length === 0) {
    throw new Error('Respuesta invalida de intencion de subida directa de documento suplementario');
  }
  try {
    const parsedUrl = new URL(source.uploadUrl);
    if (!['https:', 'http:'].includes(parsedUrl.protocol)) {
      throw new Error('invalid protocol');
    }
  } catch {
    throw new Error('Respuesta invalida de intencion de subida directa de documento suplementario');
  }
  const expiresInSeconds = safePublicManifestCount(source.expiresInSeconds)
    ?? safePublicManifestCount(source.expiresIn);
  if (expiresInSeconds === undefined) {
    throw new Error('Respuesta invalida de intencion de subida directa de documento suplementario');
  }
  const result: SupplementaryDocumentUploadIntent = {
    attachmentId: source.attachmentId.trim(),
    uploadUrl: source.uploadUrl.trim(),
    method: 'PUT',
    expiresInSeconds,
  };
  const requiredHeaders = publicSupplementaryDocumentUploadIntentHeadersFromApi(source.requiredHeaders);
  if (requiredHeaders) {
    result.requiredHeaders = requiredHeaders;
  }
  return result;
};

const publicSupplementaryDocumentEvidenceFromApi = (
  record: unknown
): SupplementaryDocumentEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  if (typeof source.attachmentId !== 'string' || source.attachmentId.trim().length === 0) {
    return undefined;
  }
  const result: SupplementaryDocumentEvidence = {
    attachmentId: source.attachmentId.trim(),
  };
  const ecmrLinkReference = safeSupplementaryDocumentLinkReference(source.ecmrLinkReference);
  if (ecmrLinkReference) {
    result.ecmrLinkReference = ecmrLinkReference;
    result.ecmrLinkReferencePresent = true;
  } else if (source.ecmrLinkReferencePresent === false) {
    result.ecmrLinkReferencePresent = false;
  }
  const mimeType = safeManifestMimeType(source.mimeType);
  if (mimeType) {
    result.mimeType = mimeType;
  }
  const sizeBytes = safePublicManifestByteSize(source.sizeBytes);
  if (sizeBytes !== undefined) {
    result.sizeBytes = sizeBytes;
  }
  const digestAlgorithm = safeDigestAlgorithmLabel(source.digestAlgorithm);
  if (digestAlgorithm) {
    result.digestAlgorithm = digestAlgorithm;
  }
  const documentType = safeTokenRecordType(source.documentType);
  if (documentType) {
    result.documentType = documentType;
  } else if (typeof source.documentType === 'string' && source.documentType.trim().length > 0) {
    result.documentType = 'other';
  }
  const evidenceType = safeManifestLabel(source.evidenceType, supplementaryDocumentEvidenceTypes);
  if (evidenceType) {
    result.evidenceType = evidenceType;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  if (
    source.documentVersionReferencePresent === true ||
    source.documentVersionReferenceAvailable === true ||
    typeof source.documentVersionId === 'string'
  ) {
    result.documentVersionReferencePresent = true;
    result.documentVersionReferenceAvailable = true;
  } else if (source.documentVersionReferencePresent === false) {
    result.documentVersionReferencePresent = false;
  }
  if (
    source.digestAvailable === true ||
    isPresent(source.sha256Digest) ||
    isPresent(source.digest)
  ) {
    result.digestAvailable = true;
  } else if (source.digestAvailable === false) {
    result.digestAvailable = false;
  }
  if (
    source.payloadStoredExternally === true ||
    isPresent(source.storageBucket) ||
    isPresent(source.storageKey) ||
    isPresent(source.objectVersionId)
  ) {
    result.payloadStoredExternally = true;
  } else if (source.payloadStoredExternally === false) {
    result.payloadStoredExternally = false;
  }
  return result;
};

const requirePublicSupplementaryDocumentEvidenceFromApi = (
  record: unknown
): SupplementaryDocumentEvidence => {
  const result = publicSupplementaryDocumentEvidenceFromApi(record);
  if (!result) {
    throw new Error('Respuesta invalida de documento suplementario');
  }
  return result;
};

const publicLifecycleEventRecordFromApi = (record: unknown): PublicLifecycleEventEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicLifecycleEventEvidence = {};
  const fromStatus = safeManifestLabel(source.fromStatus, documentLifecycleStatuses);
  if (fromStatus) {
    result.fromStatus = fromStatus;
  }
  const toStatus = safeManifestLabel(source.toStatus, documentLifecycleStatuses);
  if (toStatus) {
    result.toStatus = toStatus;
  }
  ['timestampPresent', 'reasonPresent'].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicExceptionReviewTaskRecordFromApi = (
  record: unknown
): PublicExceptionReviewTaskEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicExceptionReviewTaskEvidence = {};
  const taskId = safeManifestLabel(source.taskId, exceptionReviewTaskIds);
  if (taskId) {
    result.taskId = taskId;
  }
  const status = safeManifestLabel(source.status, exceptionReviewTaskStatuses);
  if (status) {
    result.status = status;
  }
  ['assignedGroupPresent', 'updatedAtPresent'].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicIntegrityCompromisedRecordFromApi = (
  record: unknown
): PublicIntegrityCompromisedEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicIntegrityCompromisedEvidence = {};
  if (source.integrityCompromisedReferencePresent === true) {
    result.integrityCompromisedReferencePresent = true;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (typeof source.detectedAt === 'string') {
    result.detectedAt = source.detectedAt;
  }
  const status = safeManifestLabel(source.status, integrityCompromisedStatuses);
  if (status) {
    result.status = status;
  }
  const reason = safeManifestLabel(source.reason, integrityCompromisedReasons);
  if (reason) {
    result.reason = reason;
  }
  const detectedDuring = safeManifestLabel(source.detectedDuring, integrityCompromisedContexts);
  if (detectedDuring) {
    result.detectedDuring = detectedDuring;
  }
  if (source.evidenceReferencePresent === true) {
    result.evidenceReferencePresent = true;
  }
  const evidenceType = safeManifestLabel(source.evidenceType, manifestEvidenceTypes);
  if (evidenceType) {
    result.evidenceType = evidenceType;
  }
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicAttachmentUploadRejectionRecordFromApi = (
  record: unknown
): PublicAttachmentUploadRejectionEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicAttachmentUploadRejectionEvidence = {};
  if (source.attachmentUploadRejectionReferencePresent === true) {
    result.attachmentUploadRejectionReferencePresent = true;
  }
  const reason = safeManifestLabel(source.reason, attachmentUploadRejectionReasons);
  if (reason) {
    result.reason = reason;
  }
  const policy = safeManifestLabel(source.policy, attachmentUploadRejectionPolicies);
  if (policy) {
    result.policy = policy;
  }
  const declaredMimeType = safeManifestMimeType(source.declaredMimeType);
  if (declaredMimeType) {
    result.declaredMimeType = declaredMimeType;
  }
  copyPublicManifestByteSizeFields(source, result as Record<string, unknown>, [
    'sizeBytes',
    'maxSizeBytes',
  ]);
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicEvidenceDownloadCompletionFromApi = (
  record: unknown
): PublicEvidenceDownloadCompletion | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicEvidenceDownloadCompletion = {};
  const evidenceType = safeManifestLabel(source.evidenceType, downloadCompletionEvidenceTypes);
  if (evidenceType) {
    result.evidenceType = evidenceType;
  }
  if (typeof source.confirmedAt === 'string') {
    result.confirmedAt = source.confirmedAt;
  }
  [
    'clientConfirmed',
    'independentTransferProof',
    'downloadCompletionReferencePresent',
    'evidenceReferencePresent',
  ].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicTransferAuditCorrelationFromApi = (
  record: unknown
): PublicTransferAuditCorrelation | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicTransferAuditCorrelation = {};
  const evidenceType = safeManifestLabel(source.evidenceType, downloadCompletionEvidenceTypes);
  const auditSource = safeManifestLabel(source.auditSource, transferAuditSources);
  const correlationStatus = safeManifestLabel(source.correlationStatus, transferAuditCorrelationStatuses);
  if (source.transferAuditCorrelationReferencePresent === true) {
    result.transferAuditCorrelationReferencePresent = true;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.evidenceReferencePresent === true) {
    result.evidenceReferencePresent = true;
  }
  if (evidenceType) {
    result.evidenceType = evidenceType;
  }
  if (source.downloadCompletionReferencePresent === true) {
    result.downloadCompletionReferencePresent = true;
  }
  if (typeof source.correlatedAt === 'string') {
    result.correlatedAt = source.correlatedAt;
  }
  if (auditSource) {
    result.auditSource = auditSource;
  }
  if (correlationStatus) {
    result.correlationStatus = correlationStatus;
  }
  [
    'downloadCompletionMatched',
    'objectReferenceMatched',
    'eventTimeMatched',
    'actorReferenceMatched',
    'logFileValidationAvailable',
    'technicalCorrelationEvidence',
    'independentTransferProof',
    'legalDetermination',
  ].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicFinalXmlRetrievalRecordFromApi = (
  record: unknown
): PublicFinalXmlRetrievalEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicFinalXmlRetrievalEvidence = {};
  if (source.retrievalReferencePresent === true) {
    result.retrievalReferencePresent = true;
  }
  if (source.evidenceReferencePresent === true) {
    result.evidenceReferencePresent = true;
  }
  const evidenceType = safeManifestLabel(source.evidenceType, manifestEvidenceTypes);
  if (evidenceType) {
    result.evidenceType = evidenceType;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (typeof source.retrievedAt === 'string') {
    result.retrievedAt = source.retrievedAt;
  }
  const digestAlgorithm = safeManifestLabel(source.digestAlgorithm, digestAlgorithms);
  if (digestAlgorithm) {
    result.digestAlgorithm = digestAlgorithm;
  }
  const retentionMode = safeManifestLabel(source.retentionMode, retentionModes);
  if (retentionMode) {
    result.retentionMode = retentionMode;
  }
  if (typeof source.integrityChecked === 'boolean') {
    result.integrityChecked = source.integrityChecked;
  }
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicEvidenceManifestExportRecordFromApi = (
  record: unknown
): PublicEvidenceManifestExportEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicEvidenceManifestExportEvidence = {};
  if (source.manifestExportReferencePresent === true) {
    result.manifestExportReferencePresent = true;
  }
  if (source.manifestReferencePresent === true) {
    result.manifestReferencePresent = true;
  }
  const evidenceType = safeManifestLabel(source.evidenceType, manifestEvidenceTypes);
  if (evidenceType) {
    result.evidenceType = evidenceType;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (typeof source.exportedAt === 'string') {
    result.exportedAt = source.exportedAt;
  }
  if (typeof source.archived === 'boolean') {
    result.archived = source.archived;
  }
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, ['evidenceSectionCount']);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicDocumentVersionMetadataFromApi = <T extends {
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}>(source: Record<string, unknown>, result: T): void => {
  if (publicDocumentVersionReferencePresentFromApi(source)) {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
};

const publicDocumentAccessRecordFromApi = (record: unknown): PublicDocumentAccessEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicDocumentAccessEvidence = {};
  if (source.accessReferencePresent === true) {
    result.accessReferencePresent = true;
  }
  const accessPath = safeManifestLabel(source.accessPath, documentAccessManifestPaths);
  if (accessPath) {
    result.accessPath = accessPath;
  }
  if (typeof source.accessedAt === 'string') {
    result.accessedAt = source.accessedAt;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicAttachmentAccessRecordFromApi = (record: unknown): PublicAttachmentAccessEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicAttachmentAccessEvidence = {};
  if (source.accessReferencePresent === true) {
    result.accessReferencePresent = true;
  }
  const accessType = safeManifestLabel(source.accessType, attachmentAccessManifestTypes);
  if (accessType) {
    result.accessType = accessType;
  }
  if (typeof source.accessedAt === 'string') {
    result.accessedAt = source.accessedAt;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (source.attachmentReferencePresent === true) {
    result.attachmentReferencePresent = true;
  }
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, ['attachmentCount']);
  const mimeType = safeManifestMimeType(source.mimeType);
  if (mimeType) {
    result.mimeType = mimeType;
  }
  copyPublicManifestByteSizeFields(source, result as Record<string, unknown>, ['sizeBytes']);
  if (typeof source.integrityChecked === 'boolean') {
    result.integrityChecked = source.integrityChecked;
  }
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicTombstoneRecordFromApi = (record: unknown): PublicTombstoneEvidence | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicTombstoneEvidence = {};
  if (source.tombstoneReferencePresent === true) {
    result.tombstoneReferencePresent = true;
  }
  if (typeof source.deletedAtPresent === 'boolean') {
    result.deletedAtPresent = source.deletedAtPresent;
  }
  if (typeof source.tombstone === 'boolean') {
    result.tombstone = source.tombstone;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  if (typeof source.reasonPresent === 'boolean') {
    result.reasonPresent = source.reasonPresent;
  }
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicAppendOnlyEvidenceStoreRecordFromApi = (
  record: unknown
): PublicAppendOnlyEvidenceStoreRecord | undefined => {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as Record<string, unknown>;
  const result: PublicAppendOnlyEvidenceStoreRecord = {};
  const evidenceRecordType = safeManifestLabel(source.evidenceRecordType, appendOnlyEvidenceRecordTypes);
  if (evidenceRecordType) {
    result.evidenceRecordType = evidenceRecordType;
  }
  const sourceRecordType = safeManifestLabel(source.sourceRecordType, appendOnlySourceRecordTypes);
  if (sourceRecordType) {
    result.sourceRecordType = sourceRecordType;
  }
  if (typeof source.recordedAt === 'string') {
    result.recordedAt = source.recordedAt;
  }
  publicDocumentVersionMetadataFromApi(source, result);
  copyPublicManifestActorRoleCount(source, result as Record<string, unknown>);
  copyPublicManifestNumberFields(source, result as Record<string, unknown>, [
    'attachmentCount',
    'errorCount',
    'warningCount',
    'findingCount',
  ]);
  [
    'requestMetadataPresent',
    'evidenceRecordReferencePresent',
    'sourceRecordReferencePresent',
    'accessRecordReferencePresent',
    'attachmentReferencePresent',
    'integrityChecked',
    'tombstoneReferencePresent',
    'evidenceReferencePresent',
    'manifestReferencePresent',
    'retrievalReferencePresent',
    'manifestExportReferencePresent',
    'downloadCompletionReferencePresent',
    'transferAuditCorrelationReferencePresent',
    'valid',
  ].forEach((key) => {
    if (typeof source[key] === 'boolean') {
      (result as Record<string, unknown>)[key] = source[key];
    }
  });

  const schemaProfile = safeManifestLabel(source.schemaProfile, xmlValidationSchemaProfiles);
  if (schemaProfile) {
    result.schemaProfile = schemaProfile;
  }
  const validationContext = safeManifestLabel(source.validationContext, validationContextLabels);
  if (validationContext) {
    result.validationContext = validationContext;
  }
  const cmrInstrument = safeManifestLabel(source.instrument, cmrBusinessValidationInstruments);
  const ecmrInstrument = safeManifestLabel(source.instrument, ecmrProtocolValidationInstruments);
  if (cmrInstrument && cmrInstrument !== 'other') {
    result.instrument = cmrInstrument;
  } else if (ecmrInstrument) {
    result.instrument = ecmrInstrument;
  } else if (source.instrument) {
    result.instrument = 'other';
  }
  const cmrArticle = safeManifestLabel(source.article, cmrBusinessValidationArticles);
  const ecmrArticle = safeManifestLabel(source.article, ecmrProtocolValidationArticles);
  if (cmrArticle && cmrArticle !== 'other') {
    result.article = cmrArticle;
  } else if (ecmrArticle) {
    result.article = ecmrArticle;
  } else if (source.article) {
    result.article = 'other';
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

const sanitizePublicManifestSectionArray = <T>(
  value: unknown,
  sanitizer: (record: unknown) => T | undefined
): T[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const records = value
    .map((record) => sanitizer(record))
    .filter((record): record is T => Boolean(record));
  return records.length > 0 ? records : undefined;
};

const publicEvidenceManifestSectionsFromApi = (sections: unknown): PublicEvidenceManifestSections | undefined => {
  if (!sections || typeof sections !== 'object') {
    return undefined;
  }
  const source = sections as Record<string, unknown>;
  const result: PublicEvidenceManifestSections = {};
  const treatySnapshotMetadata = publicTreatySnapshotMetadataFromApi(source.treatySnapshotMetadata);
  if (treatySnapshotMetadata) {
    result.treatySnapshotMetadata = treatySnapshotMetadata;
  }
  const ecmrArticle4Integrity = publicEcmrArticle4IntegrityFromApi(source.ecmrArticle4Integrity);
  if (ecmrArticle4Integrity) {
    result.ecmrArticle4Integrity = ecmrArticle4Integrity;
  }
  const procedureAcceptances = sanitizePublicManifestSectionArray(
    source.procedureAcceptances,
    publicProcedureAcceptanceRecordFromApi
  );
  if (procedureAcceptances) {
    result.procedureAcceptances = procedureAcceptances;
  }
  const replacementByOtherMeansRecords = sanitizePublicManifestSectionArray(
    source.replacementByOtherMeansRecords,
    publicReplacementByOtherMeansRecordFromApi
  );
  if (replacementByOtherMeansRecords) {
    result.replacementByOtherMeansRecords = replacementByOtherMeansRecords;
  }
  const signatureEvidence = sanitizePublicManifestSectionArray(
    source.signatureEvidence,
    publicSignatureEvidenceRecordFromApi
  );
  if (signatureEvidence) {
    result.signatureEvidence = signatureEvidence;
  }
  const supplementaryDocuments = sanitizePublicManifestSectionArray(
    source.supplementaryDocuments,
    publicSupplementaryDocumentRecordFromApi
  );
  if (supplementaryDocuments) {
    result.supplementaryDocuments = supplementaryDocuments;
  }
  const carrierTakeoverCheckRequests = sanitizePublicManifestSectionArray(
    source.carrierTakeoverCheckRequests,
    publicCarrierTakeoverCheckRequestRecordFromApi
  );
  if (carrierTakeoverCheckRequests) {
    result.carrierTakeoverCheckRequests = carrierTakeoverCheckRequests;
  }
  const carrierTakeoverChecks = sanitizePublicManifestSectionArray(
    source.carrierTakeoverChecks,
    publicCarrierTakeoverCheckRecordFromApi
  );
  if (carrierTakeoverChecks) {
    result.carrierTakeoverChecks = carrierTakeoverChecks;
  }
  const carrierReservations = sanitizePublicManifestSectionArray(
    source.carrierReservations,
    publicCarrierReservationRecordFromApi
  );
  if (carrierReservations) {
    result.carrierReservations = carrierReservations;
  }
  const carrierReservationAcknowledgements = sanitizePublicManifestSectionArray(
    source.carrierReservationAcknowledgements,
    publicReservationAcknowledgementRecordFromApi
  );
  if (carrierReservationAcknowledgements) {
    result.carrierReservationAcknowledgements = carrierReservationAcknowledgements;
  }
  const deliveryConfirmation = publicDeliveryConfirmationRecordFromApi(source.deliveryConfirmation);
  if (deliveryConfirmation) {
    result.deliveryConfirmation = deliveryConfirmation;
  }
  const deliveryReservationAcknowledgements = sanitizePublicManifestSectionArray(
    source.deliveryReservationAcknowledgements,
    publicReservationAcknowledgementRecordFromApi
  );
  if (deliveryReservationAcknowledgements) {
    result.deliveryReservationAcknowledgements = deliveryReservationAcknowledgements;
  }
  const cmrArticle9Presumption = publicCmrArticle9PresumptionFromApi(source.cmrArticle9Presumption);
  if (cmrArticle9Presumption) {
    result.cmrArticle9Presumption = cmrArticle9Presumption;
  }
  const cmrArticle10PackagingReservations = publicCmrArticle10PackagingReservationsFromApi(
    source.cmrArticle10PackagingReservations
  );
  if (cmrArticle10PackagingReservations) {
    result.cmrArticle10PackagingReservations = cmrArticle10PackagingReservations;
  }
  const cmrArticle11CustomsDocuments = publicCmrArticle11CustomsDocumentsFromApi(
    source.cmrArticle11CustomsDocuments
  );
  if (cmrArticle11CustomsDocuments) {
    result.cmrArticle11CustomsDocuments = cmrArticle11CustomsDocuments;
  }
  const disposalInstructionRecords = sanitizePublicManifestSectionArray(
    source.disposalInstructionRecords,
    publicDisposalInstructionRecordFromApi
  );
  if (disposalInstructionRecords) {
    result.disposalInstructionRecords = disposalInstructionRecords;
  }
  const disposalExecutionNoticeRecords = sanitizePublicManifestSectionArray(
    source.disposalExecutionNoticeRecords,
    publicDisposalExecutionNoticeRecordFromApi
  );
  if (disposalExecutionNoticeRecords) {
    result.disposalExecutionNoticeRecords = disposalExecutionNoticeRecords;
  }
  const consigneeDeliveryRequestRecords = sanitizePublicManifestSectionArray(
    source.consigneeDeliveryRequestRecords,
    publicConsigneeDeliveryRequestRecordFromApi
  );
  if (consigneeDeliveryRequestRecords) {
    result.consigneeDeliveryRequestRecords = consigneeDeliveryRequestRecords;
  }
  const carriageImpedimentRecords = sanitizePublicManifestSectionArray(
    source.carriageImpedimentRecords,
    publicCarriageImpedimentRecordFromApi
  );
  if (carriageImpedimentRecords) {
    result.carriageImpedimentRecords = carriageImpedimentRecords;
  }
  const carriageAccountingRecords = sanitizePublicManifestSectionArray(
    source.carriageAccountingRecords,
    publicCarriageAccountingRecordFromApi
  );
  if (carriageAccountingRecords) {
    result.carriageAccountingRecords = carriageAccountingRecords;
  }
  const carrierLiabilityIncidentRecords = sanitizePublicManifestSectionArray(
    source.carrierLiabilityIncidentRecords,
    publicCarrierLiabilityIncidentRecordFromApi
  );
  if (carrierLiabilityIncidentRecords) {
    result.carrierLiabilityIncidentRecords = carrierLiabilityIncidentRecords;
  }
  const article18_2ClaimantRebuttalRecords = sanitizePublicManifestSectionArray(
    source.article18_2ClaimantRebuttalRecords,
    publicArticle18_2ClaimantRebuttalRecordFromApi
  );
  if (article18_2ClaimantRebuttalRecords) {
    result.article18_2ClaimantRebuttalRecords = article18_2ClaimantRebuttalRecords;
  }
  const deliveryTimingAssessmentRecords = sanitizePublicManifestSectionArray(
    source.deliveryTimingAssessmentRecords,
    publicDeliveryTimingAssessmentRecordFromApi
  );
  if (deliveryTimingAssessmentRecords) {
    result.deliveryTimingAssessmentRecords = deliveryTimingAssessmentRecords;
  }
  const compensationPaymentRecords = sanitizePublicManifestSectionArray(
    source.compensationPaymentRecords,
    publicCompensationPaymentRecordFromApi
  );
  if (compensationPaymentRecords) {
    result.compensationPaymentRecords = compensationPaymentRecords;
  }
  const recoveryDeliverySettlementRecords = sanitizePublicManifestSectionArray(
    source.recoveryDeliverySettlementRecords,
    publicRecoveryDeliverySettlementRecordFromApi
  );
  if (recoveryDeliverySettlementRecords) {
    result.recoveryDeliverySettlementRecords = recoveryDeliverySettlementRecords;
  }
  const recoveredGoodsDispositionRecords = sanitizePublicManifestSectionArray(
    source.recoveredGoodsDispositionRecords,
    publicRecoveredGoodsDispositionRecordFromApi
  );
  if (recoveredGoodsDispositionRecords) {
    result.recoveredGoodsDispositionRecords = recoveredGoodsDispositionRecords;
  }
  const codDeliveryOmissionRecords = sanitizePublicManifestSectionArray(
    source.codDeliveryOmissionRecords,
    publicCodDeliveryOmissionRecordFromApi
  );
  if (codDeliveryOmissionRecords) {
    result.codDeliveryOmissionRecords = codDeliveryOmissionRecords;
  }
  const dangerousGoodsNoticeRecords = sanitizePublicManifestSectionArray(
    source.dangerousGoodsNoticeRecords,
    publicDangerousGoodsNoticeRecordFromApi
  );
  if (dangerousGoodsNoticeRecords) {
    result.dangerousGoodsNoticeRecords = dangerousGoodsNoticeRecords;
  }
  const compensationAssessmentRecords = sanitizePublicManifestSectionArray(
    source.compensationAssessmentRecords,
    publicCompensationAssessmentRecordFromApi
  );
  if (compensationAssessmentRecords) {
    result.compensationAssessmentRecords = compensationAssessmentRecords;
  }
  const compensationInterestRecords = sanitizePublicManifestSectionArray(
    source.compensationInterestRecords,
    publicCompensationInterestRecordFromApi
  );
  if (compensationInterestRecords) {
    result.compensationInterestRecords = compensationInterestRecords;
  }
  const liabilityLimitationRecords = sanitizePublicManifestSectionArray(
    source.liabilityLimitationRecords,
    publicLiabilityLimitationRecordFromApi
  );
  if (liabilityLimitationRecords) {
    result.liabilityLimitationRecords = liabilityLimitationRecords;
  }
  const claimReservationRecords = sanitizePublicManifestSectionArray(
    source.claimReservationRecords,
    publicClaimReservationRecordFromApi
  );
  if (claimReservationRecords) {
    result.claimReservationRecords = claimReservationRecords;
  }
  const disputeProcedureRecords = sanitizePublicManifestSectionArray(
    source.disputeProcedureRecords,
    publicDisputeProcedureRecordFromApi
  );
  if (disputeProcedureRecords) {
    result.disputeProcedureRecords = disputeProcedureRecords;
  }
  const successiveCarrierRecords = sanitizePublicManifestSectionArray(
    source.successiveCarrierRecords,
    publicSuccessiveCarrierRecordFromApi
  );
  if (successiveCarrierRecords) {
    result.successiveCarrierRecords = successiveCarrierRecords;
  }
  const multimodalCarriageRecords = sanitizePublicManifestSectionArray(
    source.multimodalCarriageRecords,
    publicMultimodalCarriageRecordFromApi
  );
  if (multimodalCarriageRecords) {
    result.multimodalCarriageRecords = multimodalCarriageRecords;
  }
  const consignmentNoteCopyRecords = sanitizePublicManifestSectionArray(
    source.consignmentNoteCopyRecords,
    publicConsignmentNoteCopyRecordFromApi
  );
  if (consignmentNoteCopyRecords) {
    result.consignmentNoteCopyRecords = consignmentNoteCopyRecords;
  }
  const ecmrReceiptAccessRequestRecords = sanitizePublicManifestSectionArray(
    source.ecmrReceiptAccessRequestRecords,
    publicEcmrReceiptAccessRequestFromApi
  );
  if (ecmrReceiptAccessRequestRecords) {
    result.ecmrReceiptAccessRequestRecords = ecmrReceiptAccessRequestRecords;
  }
  const ecmrReceiptAccessRecords = sanitizePublicManifestSectionArray(
    source.ecmrReceiptAccessRecords,
    publicEcmrReceiptAccessRecordFromApi
  );
  if (ecmrReceiptAccessRecords) {
    result.ecmrReceiptAccessRecords = ecmrReceiptAccessRecords;
  }
  const senderParticularsResponsibilityRecords = sanitizePublicManifestSectionArray(
    source.senderParticularsResponsibilityRecords,
    publicSenderParticularsResponsibilityRecordFromApi
  );
  if (senderParticularsResponsibilityRecords) {
    result.senderParticularsResponsibilityRecords = senderParticularsResponsibilityRecords;
  }
  const prohibitedClauseRecords = sanitizePublicManifestSectionArray(
    source.prohibitedClauseRecords,
    publicProhibitedClauseRecordFromApi
  );
  if (prohibitedClauseRecords) {
    result.prohibitedClauseRecords = prohibitedClauseRecords;
  }
  const retentionPolicy = publicRetentionPolicyManifestFromApi(source.retentionPolicy);
  if (retentionPolicy) {
    result.retentionPolicy = retentionPolicy;
  }
  const legalHold = publicLegalHoldManifestFromApi(source.legalHold);
  if (legalHold) {
    result.legalHold = legalHold;
  }
  const amendmentRequests = sanitizePublicManifestSectionArray(
    source.amendmentRequests,
    publicAmendmentManifestRecordFromApi
  );
  if (amendmentRequests) {
    result.amendmentRequests = amendmentRequests;
  }
  const xmlValidationRecords = sanitizePublicManifestSectionArray(
    source.xmlValidationRecords,
    publicXmlValidationRecordFromApi
  );
  if (xmlValidationRecords) {
    result.xmlValidationRecords = xmlValidationRecords;
  }
  const cmrBusinessValidationRecords = sanitizePublicManifestSectionArray(
    source.cmrBusinessValidationRecords,
    publicCmrBusinessValidationRecordFromApi
  );
  if (cmrBusinessValidationRecords) {
    result.cmrBusinessValidationRecords = cmrBusinessValidationRecords;
  }
  const ecmrProtocolValidationRecords = sanitizePublicManifestSectionArray(
    source.ecmrProtocolValidationRecords,
    publicEcmrProtocolValidationRecordFromApi
  );
  if (ecmrProtocolValidationRecords) {
    result.ecmrProtocolValidationRecords = ecmrProtocolValidationRecords;
  }
  const signatureVerificationRecords = sanitizePublicManifestSectionArray(
    source.signatureVerificationRecords,
    publicSignatureVerificationRecordFromApi
  );
  if (signatureVerificationRecords) {
    result.signatureVerificationRecords = signatureVerificationRecords;
  }
  const lifecycleEventRecords = sanitizePublicManifestSectionArray(
    source.lifecycleEventRecords,
    publicLifecycleEventRecordFromApi
  );
  if (lifecycleEventRecords) {
    result.lifecycleEventRecords = lifecycleEventRecords;
  }
  const exceptionReviewTaskRecords = sanitizePublicManifestSectionArray(
    source.exceptionReviewTaskRecords,
    publicExceptionReviewTaskRecordFromApi
  );
  if (exceptionReviewTaskRecords) {
    result.exceptionReviewTaskRecords = exceptionReviewTaskRecords;
  }
  const integrityCompromisedRecords = sanitizePublicManifestSectionArray(
    source.integrityCompromisedRecords,
    publicIntegrityCompromisedRecordFromApi
  );
  if (integrityCompromisedRecords) {
    result.integrityCompromisedRecords = integrityCompromisedRecords;
  }
  const attachmentUploadRejectionRecords = sanitizePublicManifestSectionArray(
    source.attachmentUploadRejectionRecords,
    publicAttachmentUploadRejectionRecordFromApi
  );
  if (attachmentUploadRejectionRecords) {
    result.attachmentUploadRejectionRecords = attachmentUploadRejectionRecords;
  }
  const immutableStorageReferences = sanitizePublicManifestSectionArray(
    source.immutableStorageReferences,
    (record) => publicImmutableStorageReferenceFromApi(record, true)
  );
  if (immutableStorageReferences) {
    result.immutableStorageReferences = immutableStorageReferences;
  }
  const evidenceDownloadCompletions = sanitizePublicManifestSectionArray(
    source.evidenceDownloadCompletions,
    publicEvidenceDownloadCompletionFromApi
  );
  if (evidenceDownloadCompletions) {
    result.evidenceDownloadCompletions = evidenceDownloadCompletions;
  }
  const evidenceTransferAuditCorrelations = sanitizePublicManifestSectionArray(
    source.evidenceTransferAuditCorrelations,
    publicTransferAuditCorrelationFromApi
  );
  if (evidenceTransferAuditCorrelations) {
    result.evidenceTransferAuditCorrelations = evidenceTransferAuditCorrelations;
  }
  const finalXmlRetrievalRecords = sanitizePublicManifestSectionArray(
    source.finalXmlRetrievalRecords,
    publicFinalXmlRetrievalRecordFromApi
  );
  if (finalXmlRetrievalRecords) {
    result.finalXmlRetrievalRecords = finalXmlRetrievalRecords;
  }
  const evidenceManifestExportRecords = sanitizePublicManifestSectionArray(
    source.evidenceManifestExportRecords,
    publicEvidenceManifestExportRecordFromApi
  );
  if (evidenceManifestExportRecords) {
    result.evidenceManifestExportRecords = evidenceManifestExportRecords;
  }
  const documentAccessRecords = sanitizePublicManifestSectionArray(
    source.documentAccessRecords,
    publicDocumentAccessRecordFromApi
  );
  if (documentAccessRecords) {
    result.documentAccessRecords = documentAccessRecords;
  }
  const attachmentAccessRecords = sanitizePublicManifestSectionArray(
    source.attachmentAccessRecords,
    publicAttachmentAccessRecordFromApi
  );
  if (attachmentAccessRecords) {
    result.attachmentAccessRecords = attachmentAccessRecords;
  }
  const tombstoneRecords = sanitizePublicManifestSectionArray(
    source.tombstoneRecords,
    publicTombstoneRecordFromApi
  );
  if (tombstoneRecords) {
    result.tombstoneRecords = tombstoneRecords;
  }
  const appendOnlyEvidenceStoreRecords = sanitizePublicManifestSectionArray(
    source.appendOnlyEvidenceStoreRecords,
    publicAppendOnlyEvidenceStoreRecordFromApi
  );
  if (appendOnlyEvidenceStoreRecords) {
    result.appendOnlyEvidenceStoreRecords = appendOnlyEvidenceStoreRecords;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const publicEvidenceManifestSummaryFromApi = (manifest: unknown): PublicEvidenceManifestSummary | undefined => {
  if (!manifest || typeof manifest !== 'object') {
    return undefined;
  }
  const source = manifest as Record<string, unknown>;
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  const summary: PublicEvidenceManifestSummary = {
    manifestReferencePresent: source.manifestReferencePresent === true,
    documentReferencePresent: source.documentReferencePresent === true,
    documentVersionReferencePresent: publicDocumentVersionReferencePresentFromApi(source),
    documentDigestAlgorithm: safeManifestLabel(source.documentDigestAlgorithm, digestAlgorithms),
    documentDigestAvailable: source.documentDigestAvailable === true,
  };
  if (documentVersionNumber !== undefined) {
    summary.documentVersionNumber = documentVersionNumber;
  }
  const evidenceSections = publicEvidenceManifestSectionsFromApi(source.evidenceSections);
  if (evidenceSections) {
    summary.evidenceSections = evidenceSections;
  }
  return summary;
};

export interface EvidenceManifestInput {
  documentId: string;
  title?: string;
  evidenceSections?: PublicEvidenceManifestSections;
  currentVersion?: DocumentVersionEvidence;
  ecmrArticle4Integrity?: EcmrArticle4IntegrityEvidence;
  procedureAcceptances?: ProcedureSetAcceptanceEvidence[];
  signatureEvidence?: SignatureEvidence[];
  supplementaryDocuments?: SupplementaryDocumentEvidence[];
  carrierTakeoverCheckRecords?: CarrierTakeoverCheckEvidence[];
  carrierReservations?: CarrierReservationEvidence[];
  disposalInstructionRecords?: DisposalInstructionEvidence[];
  disposalExecutionNoticeRecords?: DisposalExecutionNoticeEvidence[];
  carriageImpedimentRecords?: CarriageImpedimentEvidence[];
  carriageAccountingRecords?: CarriageAccountingEvidence[];
  carrierLiabilityIncidentRecords?: CarrierLiabilityIncidentEvidence[];
  article18_2ClaimantRebuttalRecords?: Article18_2ClaimantRebuttalEvidence[];
  deliveryTimingAssessmentRecords?: DeliveryTimingAssessmentEvidence[];
  compensationPaymentRecords?: CompensationPaymentEvidence[];
  recoveryDeliverySettlementRecords?: RecoveryDeliverySettlementEvidence[];
  recoveredGoodsDispositionRecords?: RecoveredGoodsDispositionEvidence[];
  codDeliveryOmissionRecords?: CodDeliveryOmissionEvidence[];
  dangerousGoodsNoticeRecords?: DangerousGoodsNoticeEvidence[];
  compensationAssessmentRecords?: CompensationAssessmentEvidence[];
  compensationInterestRecords?: CompensationInterestEvidence[];
  liabilityLimitationRecords?: LiabilityLimitationEvidence[];
  claimReservationRecords?: ClaimReservationEvidence[];
  disputeProcedureRecords?: DisputeProcedureEvidence[];
  successiveCarrierRecords?: SuccessiveCarrierEvidence[];
  multimodalCarriageRecords?: MultimodalCarriageEvidence[];
  consignmentNoteCopyRecords?: ConsignmentNoteCopyEvidence[];
  ecmrReceiptAccessRequestRecords?: EcmrReceiptAccessRequestEvidence[];
  ecmrReceiptAccessRecords?: EcmrReceiptAccessEvidence[];
  senderParticularsResponsibilityRecords?: SenderParticularsResponsibilityEvidence[];
  prohibitedClauseRecords?: ProhibitedClauseEvidence[];
  consigneeDeliveryRequestRecords?: ConsigneeDeliveryRequestEvidence[];
  xmlValidationRecords?: XmlValidationEvidence[];
  cmrBusinessValidationRecords?: CmrBusinessValidationEvidence[];
  ecmrProtocolValidationRecords?: EcmrProtocolValidationEvidence[];
  deliveryConfirmation?: DeliveryConfirmationEvidence;
  amendmentRequests?: AmendmentEvidenceManifestItem[];
  retentionPolicy?: RetentionPolicyEvidence;
  legalHold?: LegalHoldEvidence;
  auditEvents?: ECMR['auditEvents'];
  documentTasks?: ECMR['documentTasks'];
  immutableStorageReferences?: ImmutableStorageEvidenceReference[];
  sourceRecordOperationalEvidence?: ECMR['sourceRecordOperationalEvidence'];
  evidenceDownloadCompletions?: ECMR['evidenceDownloadCompletions'];
}

const apiDocumentEvidenceInputKeys: Array<keyof Omit<EvidenceManifestInput, 'documentId' | 'title' | 'evidenceSections'>> = [
  'currentVersion',
  'ecmrArticle4Integrity',
  'procedureAcceptances',
  'signatureEvidence',
  'supplementaryDocuments',
  'carrierTakeoverCheckRecords',
  'carrierReservations',
  'disposalInstructionRecords',
  'disposalExecutionNoticeRecords',
  'carriageImpedimentRecords',
  'carriageAccountingRecords',
  'carrierLiabilityIncidentRecords',
  'article18_2ClaimantRebuttalRecords',
  'deliveryTimingAssessmentRecords',
  'compensationPaymentRecords',
  'recoveryDeliverySettlementRecords',
  'recoveredGoodsDispositionRecords',
  'codDeliveryOmissionRecords',
  'dangerousGoodsNoticeRecords',
  'compensationAssessmentRecords',
  'compensationInterestRecords',
  'liabilityLimitationRecords',
  'claimReservationRecords',
  'disputeProcedureRecords',
  'successiveCarrierRecords',
  'multimodalCarriageRecords',
  'consignmentNoteCopyRecords',
  'ecmrReceiptAccessRequestRecords',
  'ecmrReceiptAccessRecords',
  'senderParticularsResponsibilityRecords',
  'prohibitedClauseRecords',
  'consigneeDeliveryRequestRecords',
  'xmlValidationRecords',
  'cmrBusinessValidationRecords',
  'ecmrProtocolValidationRecords',
  'deliveryConfirmation',
  'amendmentRequests',
  'retentionPolicy',
  'legalHold',
  'auditEvents',
  'documentTasks',
  'immutableStorageReferences',
  'sourceRecordOperationalEvidence',
  'evidenceDownloadCompletions',
];

const apiDocumentDirectManifestSectionKeys = new Set<string>([
  'ecmrArticle4Integrity',
  'procedureAcceptances',
  'signatureEvidence',
  'supplementaryDocuments',
  'carrierTakeoverChecks',
  'carrierReservations',
  'disposalInstructionRecords',
  'disposalExecutionNoticeRecords',
  'consigneeDeliveryRequestRecords',
  'carriageImpedimentRecords',
  'carriageAccountingRecords',
  'carrierLiabilityIncidentRecords',
  'article18_2ClaimantRebuttalRecords',
  'deliveryTimingAssessmentRecords',
  'compensationPaymentRecords',
  'recoveryDeliverySettlementRecords',
  'recoveredGoodsDispositionRecords',
  'codDeliveryOmissionRecords',
  'dangerousGoodsNoticeRecords',
  'compensationAssessmentRecords',
  'compensationInterestRecords',
  'liabilityLimitationRecords',
  'claimReservationRecords',
  'disputeProcedureRecords',
  'successiveCarrierRecords',
  'multimodalCarriageRecords',
  'consignmentNoteCopyRecords',
  'ecmrReceiptAccessRequestRecords',
  'ecmrReceiptAccessRecords',
  'senderParticularsResponsibilityRecords',
  'prohibitedClauseRecords',
  'retentionPolicy',
  'legalHold',
  'xmlValidationRecords',
  'cmrBusinessValidationRecords',
  'ecmrProtocolValidationRecords',
  'deliveryConfirmation',
  'amendmentRequests',
  'immutableStorageReferences',
  'evidenceDownloadCompletions',
]);

const apiDocumentEvidenceSource = (document: ApiDocument): Record<string, unknown> => {
  const source = document as unknown as Record<string, unknown>;
  return {
    ...source,
    procedureAcceptances: source.procedureAcceptances ?? source.procedureAcceptanceRecords,
    signatureEvidence: source.signatureEvidence ?? source.signatureEvidenceRecords,
    carrierTakeoverCheckRecords: source.carrierTakeoverCheckRecords ?? source.carrierTakeoverChecks,
    carrierTakeoverChecks: source.carrierTakeoverChecks ?? source.carrierTakeoverCheckRecords,
    carrierTakeoverCheckRequests: source.carrierTakeoverCheckRequests ?? source.carrierTakeoverCheckRequestRecords,
    carrierReservationAcknowledgements:
      source.carrierReservationAcknowledgements ?? source.carrierReservationAcknowledgementRecords,
    deliveryReservationAcknowledgements:
      source.deliveryReservationAcknowledgements ?? source.deliveryReservationAcknowledgementRecords,
  };
};

const publicEvidenceManifestSectionsFromApiDocument = (
  document: ApiDocument
): PublicEvidenceManifestSections | undefined => {
  const source = apiDocumentEvidenceSource(document);
  const result: PublicEvidenceManifestSections = {};

  const topLevelSections = publicEvidenceManifestSectionsFromApi(source);
  if (topLevelSections) {
    Object.entries(topLevelSections).forEach(([sectionName, sectionValue]) => {
      if (!apiDocumentDirectManifestSectionKeys.has(sectionName)) {
        (result as Record<string, unknown>)[sectionName] = sectionValue;
      }
    });
  }

  const explicitSections = publicEvidenceManifestSectionsFromApi(source.evidenceSections);
  if (explicitSections) {
    Object.assign(result, explicitSections);
  }

  const exportedManifestSections = publicEvidenceManifestSummaryFromApi(source.evidenceManifest)?.evidenceSections;
  if (exportedManifestSections) {
    Object.assign(result, exportedManifestSections);
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

const attachApiEvidenceToECMR = (ecmr: ECMR, apiDocument: ApiDocument): ECMR => {
  const source = apiDocumentEvidenceSource(apiDocument);
  const target = ecmr as ECMR & Record<string, unknown>;

  apiDocumentEvidenceInputKeys.forEach((key) => {
    const value = source[key];
    if (value !== undefined) {
      (target as Record<string, unknown>)[key] = value;
    }
  });

  const evidenceSections = publicEvidenceManifestSectionsFromApiDocument(apiDocument);
  if (evidenceSections) {
    target.evidenceSections = evidenceSections;
  }

  return ecmr;
};

export interface EvidenceManifestExportResult {
  evidenceManifestXml: string;
  evidenceManifest?: PublicEvidenceManifestSummary;
  archiveReference?: PublicImmutableStorageReference | null;
  message?: string;
}

export type SupplementaryDocumentIntegrityFailureCode = 'supplementary_document_integrity_link_required';
export type CmrArticle5CopyCoverageFailureCode = 'cmr_article5_copy_coverage_required';
export type EcmrArticle6ReceiptAccessHandoverFailureCode = 'ecmr_article6_receipt_access_handover_required';
export type EcmrArticle6SupplementaryDocumentTransferFailureCode =
  'ecmr_article6_supplementary_document_transfer_required';
export type ContractParticipantIdentityFailureCode = 'contract_participant_identity_required';
export type EntitledPartyAccessFailureCode = 'entitled_party_access_required';
export type EvidenceManifestExportStateChangedFailureCode = 'evidence_manifest_export_state_changed';
export type FinalXmlRetrievalStateChangedFailureCode = 'final_xml_retrieval_state_changed';
export type SupplementaryDocumentUploadFailureCode =
  | 'supplementary_document_upload_state_changed'
  | 'supplementary_document_upload_verification_unavailable';
export type SupplementaryDocumentAccessFailureCode =
  | 'supplementary_document_access_state_changed'
  | 'supplementary_document_access_persistence_unavailable'
  | 'supplementary_document_integrity_verification_unavailable'
  | 'supplementary_document_integrity_failed';
export type SignatureVerificationFailureCode = 'signature_verification_state_changed';
export type SignatureCreationFailureCode = 'signature_creation_state_changed';
export type EvidenceManifestExportFailureCode =
  | SupplementaryDocumentIntegrityFailureCode
  | CmrArticle5CopyCoverageFailureCode
  | EcmrArticle6ReceiptAccessHandoverFailureCode
  | EcmrArticle6SupplementaryDocumentTransferFailureCode
  | ContractParticipantIdentityFailureCode
  | EntitledPartyAccessFailureCode
  | EvidenceManifestExportStateChangedFailureCode;
export type FinalXmlArchiveRetrievalFailureCode =
  | SupplementaryDocumentIntegrityFailureCode
  | ContractParticipantIdentityFailureCode
  | EntitledPartyAccessFailureCode
  | FinalXmlRetrievalStateChangedFailureCode;
type DocumentIntegrityFailureCode =
  | 'document_integrity_failed'
  | 'document_integrity_verification_unavailable';
export type DocumentReadFailureCode =
  | DocumentIntegrityFailureCode
  | 'document_detail_access_state_changed'
  | 'document_detail_access_persistence_unavailable';
export type DocumentPolicyAccessFailureCode =
  | 'document_policy_access_state_changed'
  | 'document_policy_access_persistence_unavailable';
export type DocumentAuditAccessFailureCode =
  | 'document_audit_access_state_changed'
  | 'document_audit_access_persistence_unavailable';
export type DocumentListAccessFailureCode =
  | 'document_list_access_state_changed'
  | 'document_list_access_persistence_unavailable';
export type DocumentProcedureAccessFailureCode =
  | DocumentIntegrityFailureCode
  | 'document_procedure_access_state_changed'
  | 'document_procedure_access_persistence_unavailable';
export type DocumentLifecycleFailureCode =
  | 'lifecycle_state_changed'
  | 'append_only_lifecycle_persistence_unavailable';

export class EvidenceManifestExportError extends Error {
  readonly status: number;
  readonly code?: EvidenceManifestExportFailureCode;

  constructor(status: number, code?: EvidenceManifestExportFailureCode) {
    super('Error al exportar el manifiesto de evidencias desde la API');
    this.name = 'EvidenceManifestExportError';
    this.status = status;
    this.code = code;
  }
}

export class FinalXmlArchiveRetrievalError extends Error {
  readonly status: number;
  readonly code?: FinalXmlArchiveRetrievalFailureCode;

  constructor(status: number, code?: FinalXmlArchiveRetrievalFailureCode) {
    super('Error al recuperar el XML final archivado desde la API');
    this.name = 'FinalXmlArchiveRetrievalError';
    this.status = status;
    this.code = code;
  }
}

export class DocumentReadError extends Error {
  readonly status: number;
  readonly code?: DocumentReadFailureCode;

  constructor(status: number, code?: DocumentReadFailureCode) {
    super('Error al obtener documento de la API');
    this.name = 'DocumentReadError';
    this.status = status;
    this.code = code;
  }
}

export class DocumentPolicyAccessError extends Error {
  readonly status: number;
  readonly code?: DocumentPolicyAccessFailureCode;

  constructor(status: number, code?: DocumentPolicyAccessFailureCode) {
    super('Error al obtener la politica server-authored del documento en la API');
    this.name = 'DocumentPolicyAccessError';
    this.status = status;
    this.code = code;
  }
}

export class DocumentAuditAccessError extends Error {
  readonly status: number;
  readonly code?: DocumentAuditAccessFailureCode;

  constructor(status: number, code?: DocumentAuditAccessFailureCode) {
    super('Error al obtener la trazabilidad de auditoria del documento en la API');
    this.name = 'DocumentAuditAccessError';
    this.status = status;
    this.code = code;
  }
}

export class DocumentListAccessError extends Error {
  readonly status: number;
  readonly code?: DocumentListAccessFailureCode;

  constructor(status: number, code?: DocumentListAccessFailureCode) {
    super('Error al obtener documentos de la API');
    this.name = 'DocumentListAccessError';
    this.status = status;
    this.code = code;
  }
}

export class DocumentProcedureAccessError extends Error {
  readonly status: number;
  readonly code?: DocumentProcedureAccessFailureCode;

  constructor(status: number, code?: DocumentProcedureAccessFailureCode) {
    super('Error al obtener los procedimientos referenciados por el documento eCMR');
    this.name = 'DocumentProcedureAccessError';
    this.status = status;
    this.code = code;
  }
}

export class DocumentLifecycleError extends Error {
  readonly status: number;
  readonly code?: DocumentLifecycleFailureCode;

  constructor(status: number, code?: DocumentLifecycleFailureCode) {
    super('Error al cambiar el estado del documento en la API');
    this.name = 'DocumentLifecycleError';
    this.status = status;
    this.code = code;
  }
}

export class SupplementaryDocumentUploadError extends Error {
  readonly status: number;
  readonly code?: SupplementaryDocumentUploadFailureCode;

  constructor(status: number, code?: SupplementaryDocumentUploadFailureCode) {
    super('Error al subir documento suplementario en la API');
    this.name = 'SupplementaryDocumentUploadError';
    this.status = status;
    this.code = code;
  }
}

export class SupplementaryDocumentAccessError extends Error {
  readonly status: number;
  readonly code?: SupplementaryDocumentAccessFailureCode;

  constructor(status: number, code?: SupplementaryDocumentAccessFailureCode) {
    super('Error al acceder a documentos suplementarios en la API');
    this.name = 'SupplementaryDocumentAccessError';
    this.status = status;
    this.code = code;
  }
}

export class SignatureVerificationError extends Error {
  readonly status: number;
  readonly code?: SignatureVerificationFailureCode;

  constructor(status: number, code?: SignatureVerificationFailureCode) {
    super('Error al verificar firmas del documento en la API');
    this.name = 'SignatureVerificationError';
    this.status = status;
    this.code = code;
  }
}

export class SignatureCreationError extends Error {
  readonly status: number;
  readonly code?: SignatureCreationFailureCode;

  constructor(status: number, code?: SignatureCreationFailureCode) {
    super('Error al firmar la version del documento en la API');
    this.name = 'SignatureCreationError';
    this.status = status;
    this.code = code;
  }
}

const SUPPLEMENTARY_DOCUMENT_INTEGRITY_ERROR = 'Supplementary document integrity link required';
const ECMR_PROTOCOL_VALIDATION_ERROR = 'eCMR protocol validation failed';
const ECMR_PROTOCOL_ARTICLE_6_REQUIREMENT = 'eCMR-Protocol-Article-6';
const ECMR_PROTOCOL_ARTICLE_6_SUPPLEMENTARY_REQUIREMENT =
  'eCMR-Protocol-Article-6-supplementary-document-integrity-link';
const CMR_ARTICLE_5_COPY_COVERAGE_ERROR = 'CMR Article 5 copy coverage required';
const CMR_ARTICLE_5_COPY_COVERAGE_REQUIREMENT = 'CMR-5.1-electronic-copy-coverage';
const ECMR_ARTICLE_6_RECEIPT_ACCESS_HANDOVER_ERROR = 'eCMR Article 6.1 receipt/access handover required';
const ECMR_ARTICLE_6_RECEIPT_ACCESS_HANDOVER_REQUIREMENT =
  'eCMR-Protocol-Article-6.1-receipt-access-handover';
const ECMR_ARTICLE_6_SUPPLEMENTARY_TRANSFER_ERROR =
  'eCMR Article 6.2 supplementary document transfer required';
const ECMR_ARTICLE_6_SUPPLEMENTARY_TRANSFER_REQUIREMENT =
  'eCMR-Protocol-Article-6.2-sender-carrier-transfer';
const CONTRACT_PARTICIPANT_IDENTITY_ERROR = 'Contract participant identity required';
const CONTRACT_PARTICIPANT_IDENTITY_REQUIREMENT = 'CMR-5.1-contract-party-identification';
const ENTITLED_PARTY_ACCESS_ERROR = 'Entitled party access required';
const ENTITLED_PARTY_ACCESS_REQUIREMENT = 'eCMR-Protocol-Article-3.3-entitled-party-access';
const EVIDENCE_MANIFEST_EXPORT_STATE_CHANGED_ERROR = 'Evidence manifest export state changed';
const EVIDENCE_MANIFEST_EXPORT_STATE_CHANGED_REQUIREMENT = 'OECMR-evidence-manifest-export-concurrency';
const FINAL_XML_RETRIEVAL_STATE_CHANGED_ERROR = 'Final XML retrieval state changed';
const FINAL_XML_RETRIEVAL_STATE_CHANGED_REQUIREMENT = 'OECMR-final-xml-retrieval-concurrency';
const SUPPLEMENTARY_DOCUMENT_UPLOAD_STATE_CHANGED_ERROR = 'Supplementary document upload state changed';
const SUPPLEMENTARY_DOCUMENT_UPLOAD_STATE_CHANGED_REQUIREMENT =
  'OECMR-supplementary-document-upload-concurrency';
const ATTACHMENT_UPLOAD_VERIFICATION_UNAVAILABLE_ERROR = 'Attachment upload verification unavailable';
const ATTACHMENT_INTEGRITY_VERIFICATION_UNAVAILABLE_ERROR = 'Attachment integrity verification unavailable';
const ATTACHMENT_INTEGRITY_CHECK_FAILED_ERROR = 'Attachment integrity check failed';
const SUPPLEMENTARY_DOCUMENT_OBJECT_VERSION_INTEGRITY_REQUIREMENT =
  'OECMR-supplementary-document-object-version-integrity';
const SUPPLEMENTARY_DOCUMENT_ACCESS_STATE_CHANGED_ERROR = 'Supplementary document access state changed';
const SUPPLEMENTARY_DOCUMENT_ACCESS_STATE_CHANGED_REQUIREMENT =
  'OECMR-supplementary-document-access-concurrency';
const ATTACHMENT_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR = 'Attachment access persistence unavailable';
const ATTACHMENT_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT =
  'OECMR-attachment-access-evidence-persistence';
const SIGNATURE_VERIFICATION_STATE_CHANGED_ERROR = 'Signature verification state changed';
const SIGNATURE_VERIFICATION_STATE_CHANGED_REQUIREMENT = 'OECMR-signature-verification-concurrency';
const SIGNATURE_CREATION_STATE_CHANGED_ERROR = 'Signature creation state changed';
const SIGNATURE_CREATION_STATE_CHANGED_REQUIREMENT = 'OECMR-signature-creation-concurrency';
const DOCUMENT_INTEGRITY_FAILED_ERROR = 'Document integrity check failed';
const DOCUMENT_INTEGRITY_UNAVAILABLE_ERROR = 'Document integrity verification unavailable';
const DOCUMENT_DETAIL_ACCESS_STATE_CHANGED_ERROR = 'Document detail access state changed';
const DOCUMENT_DETAIL_ACCESS_STATE_CHANGED_REQUIREMENT = 'OECMR-document-detail-access-concurrency';
const DOCUMENT_DETAIL_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR = 'Document detail access persistence unavailable';
const DOCUMENT_DETAIL_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT =
  'OECMR-document-detail-access-evidence-persistence';
const DOCUMENT_POLICY_ACCESS_STATE_CHANGED_ERROR = 'Document policy access state changed';
const DOCUMENT_POLICY_ACCESS_STATE_CHANGED_REQUIREMENT = 'OECMR-document-policy-access-concurrency';
const DOCUMENT_POLICY_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR = 'Document policy access persistence unavailable';
const DOCUMENT_POLICY_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT =
  'OECMR-document-policy-access-evidence-persistence';
const DOCUMENT_AUDIT_ACCESS_STATE_CHANGED_ERROR = 'Document audit access state changed';
const DOCUMENT_AUDIT_ACCESS_STATE_CHANGED_REQUIREMENT = 'OECMR-document-audit-access-concurrency';
const DOCUMENT_AUDIT_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR = 'Document audit access persistence unavailable';
const DOCUMENT_AUDIT_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT =
  'OECMR-document-audit-access-evidence-persistence';
const DOCUMENT_LIST_ACCESS_STATE_CHANGED_ERROR = 'Document list access state changed';
const DOCUMENT_LIST_ACCESS_STATE_CHANGED_REQUIREMENT = 'OECMR-document-list-access-concurrency';
const DOCUMENT_LIST_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR = 'Document list access persistence unavailable';
const DOCUMENT_LIST_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT =
  'OECMR-document-list-access-evidence-persistence';
const DOCUMENT_PROCEDURE_ACCESS_STATE_CHANGED_ERROR = 'Document procedure access state changed';
const DOCUMENT_PROCEDURE_ACCESS_STATE_CHANGED_REQUIREMENT = 'OECMR-document-procedure-access-concurrency';
const DOCUMENT_PROCEDURE_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR =
  'Document procedure access persistence unavailable';
const DOCUMENT_PROCEDURE_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT =
  'OECMR-document-procedure-access-evidence-persistence';
const DOCUMENT_LIFECYCLE_STATE_CHANGED_ERROR = 'Document lifecycle state changed';
const DOCUMENT_LIFECYCLE_STATE_CHANGED_REQUIREMENT = 'OECMR-lifecycle-concurrency';
const APPEND_ONLY_LIFECYCLE_PERSISTENCE_UNAVAILABLE_ERROR = 'Append-only lifecycle persistence unavailable';
const APPEND_ONLY_LIFECYCLE_PERSISTENCE_REQUIREMENT = 'OECMR-append-only-lifecycle-integrity';
const DOCUMENT_READ_INTEGRITY_REQUIREMENTS = new Set([
  'eCMR-Protocol-Article-4.2-integrity',
  'OECMR-current-version-integrity',
]);

const responseJsonObjectOrUndefined = async (response: Response): Promise<Record<string, unknown> | undefined> => {
  try {
    const payload = await response.json();
    return payload && typeof payload === 'object' ? payload as Record<string, unknown> : undefined;
  } catch {
    return undefined;
  }
};

const payloadHasSupplementaryDocumentIntegrityRequirement = (payload: Record<string, unknown> | undefined): boolean => {
  if (!payload) {
    return false;
  }

  if (
    payload.error === SUPPLEMENTARY_DOCUMENT_INTEGRITY_ERROR &&
    payload.requirementId === ECMR_PROTOCOL_ARTICLE_6_REQUIREMENT
  ) {
    return true;
  }

  if (payload.error !== ECMR_PROTOCOL_VALIDATION_ERROR) {
    return false;
  }

  if (payload.requirementId === ECMR_PROTOCOL_ARTICLE_6_SUPPLEMENTARY_REQUIREMENT) {
    return true;
  }

  return Array.isArray(payload.errors) && payload.errors.some((error) =>
    Boolean(
      error &&
      typeof error === 'object' &&
      (error as Record<string, unknown>).requirementId === ECMR_PROTOCOL_ARTICLE_6_SUPPLEMENTARY_REQUIREMENT
    )
  );
};

const supplementaryDocumentIntegrityFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): SupplementaryDocumentIntegrityFailureCode | undefined => {
  if ((status === 409 || status === 422) && payloadHasSupplementaryDocumentIntegrityRequirement(payload)) {
    return 'supplementary_document_integrity_link_required';
  }
  return undefined;
};

const contractParticipantIdentityFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): ContractParticipantIdentityFailureCode | undefined =>
  status === 409 &&
  payload?.error === CONTRACT_PARTICIPANT_IDENTITY_ERROR &&
  payload.requirementId === CONTRACT_PARTICIPANT_IDENTITY_REQUIREMENT
    ? 'contract_participant_identity_required'
    : undefined;

const entitledPartyAccessFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): EntitledPartyAccessFailureCode | undefined =>
  status === 409 &&
  payload?.error === ENTITLED_PARTY_ACCESS_ERROR &&
  payload.requirementId === ENTITLED_PARTY_ACCESS_REQUIREMENT
    ? 'entitled_party_access_required'
    : undefined;

const evidenceManifestFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): EvidenceManifestExportFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === EVIDENCE_MANIFEST_EXPORT_STATE_CHANGED_ERROR &&
    payload.requirementId === EVIDENCE_MANIFEST_EXPORT_STATE_CHANGED_REQUIREMENT
  ) {
    return 'evidence_manifest_export_state_changed';
  }
  const participantIdentityCode = contractParticipantIdentityFailureCodeFromResponse(status, payload);
  if (participantIdentityCode) {
    return participantIdentityCode;
  }
  const entitledPartyAccessCode = entitledPartyAccessFailureCodeFromResponse(status, payload);
  if (entitledPartyAccessCode) {
    return entitledPartyAccessCode;
  }
  const supplementaryCode = supplementaryDocumentIntegrityFailureCodeFromResponse(status, payload);
  if (supplementaryCode) {
    return supplementaryCode;
  }
  if (
    status === 409 &&
    payload?.error === CMR_ARTICLE_5_COPY_COVERAGE_ERROR &&
    payload.requirementId === CMR_ARTICLE_5_COPY_COVERAGE_REQUIREMENT
  ) {
    return 'cmr_article5_copy_coverage_required';
  }
  if (
    status === 409 &&
    payload?.error === ECMR_ARTICLE_6_RECEIPT_ACCESS_HANDOVER_ERROR &&
    payload.requirementId === ECMR_ARTICLE_6_RECEIPT_ACCESS_HANDOVER_REQUIREMENT
  ) {
    return 'ecmr_article6_receipt_access_handover_required';
  }
  if (
    status === 409 &&
    payload?.error === ECMR_ARTICLE_6_SUPPLEMENTARY_TRANSFER_ERROR &&
    payload.requirementId === ECMR_ARTICLE_6_SUPPLEMENTARY_TRANSFER_REQUIREMENT
  ) {
    return 'ecmr_article6_supplementary_document_transfer_required';
  }
  return undefined;
};

const finalXmlArchiveFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): FinalXmlArchiveRetrievalFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === FINAL_XML_RETRIEVAL_STATE_CHANGED_ERROR &&
    payload.requirementId === FINAL_XML_RETRIEVAL_STATE_CHANGED_REQUIREMENT
  ) {
    return 'final_xml_retrieval_state_changed';
  }
  return (
    contractParticipantIdentityFailureCodeFromResponse(status, payload) ??
    entitledPartyAccessFailureCodeFromResponse(status, payload) ??
    supplementaryDocumentIntegrityFailureCodeFromResponse(status, payload)
  );
};

const documentIntegrityFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): DocumentIntegrityFailureCode | undefined => {
  if (
    !payload ||
    typeof payload.requirementId !== 'string' ||
    !DOCUMENT_READ_INTEGRITY_REQUIREMENTS.has(payload.requirementId)
  ) {
    return undefined;
  }
  if (status === 409 && payload.error === DOCUMENT_INTEGRITY_FAILED_ERROR) {
    return 'document_integrity_failed';
  }
  if (status === 503 && payload.error === DOCUMENT_INTEGRITY_UNAVAILABLE_ERROR) {
    return 'document_integrity_verification_unavailable';
  }
  return undefined;
};

const documentReadFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): DocumentReadFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === DOCUMENT_DETAIL_ACCESS_STATE_CHANGED_ERROR &&
    payload.requirementId === DOCUMENT_DETAIL_ACCESS_STATE_CHANGED_REQUIREMENT
  ) {
    return 'document_detail_access_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === DOCUMENT_DETAIL_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR &&
    payload.requirementId === DOCUMENT_DETAIL_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT
  ) {
    return 'document_detail_access_persistence_unavailable';
  }
  return documentIntegrityFailureCodeFromResponse(status, payload);
};

const documentPolicyAccessFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): DocumentPolicyAccessFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === DOCUMENT_POLICY_ACCESS_STATE_CHANGED_ERROR &&
    payload.requirementId === DOCUMENT_POLICY_ACCESS_STATE_CHANGED_REQUIREMENT
  ) {
    return 'document_policy_access_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === DOCUMENT_POLICY_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR &&
    payload.requirementId === DOCUMENT_POLICY_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT
  ) {
    return 'document_policy_access_persistence_unavailable';
  }
  return undefined;
};

const documentAuditAccessFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): DocumentAuditAccessFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === DOCUMENT_AUDIT_ACCESS_STATE_CHANGED_ERROR &&
    payload.requirementId === DOCUMENT_AUDIT_ACCESS_STATE_CHANGED_REQUIREMENT
  ) {
    return 'document_audit_access_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === DOCUMENT_AUDIT_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR &&
    payload.requirementId === DOCUMENT_AUDIT_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT
  ) {
    return 'document_audit_access_persistence_unavailable';
  }
  return undefined;
};

const documentListAccessFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): DocumentListAccessFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === DOCUMENT_LIST_ACCESS_STATE_CHANGED_ERROR &&
    payload.requirementId === DOCUMENT_LIST_ACCESS_STATE_CHANGED_REQUIREMENT
  ) {
    return 'document_list_access_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === DOCUMENT_LIST_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR &&
    payload.requirementId === DOCUMENT_LIST_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT
  ) {
    return 'document_list_access_persistence_unavailable';
  }
  return undefined;
};

const documentProcedureAccessFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): DocumentProcedureAccessFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === DOCUMENT_PROCEDURE_ACCESS_STATE_CHANGED_ERROR &&
    payload.requirementId === DOCUMENT_PROCEDURE_ACCESS_STATE_CHANGED_REQUIREMENT
  ) {
    return 'document_procedure_access_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === DOCUMENT_PROCEDURE_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR &&
    payload.requirementId === DOCUMENT_PROCEDURE_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT
  ) {
    return 'document_procedure_access_persistence_unavailable';
  }
  return documentIntegrityFailureCodeFromResponse(status, payload);
};

const documentLifecycleFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): DocumentLifecycleFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === DOCUMENT_LIFECYCLE_STATE_CHANGED_ERROR &&
    payload.requirementId === DOCUMENT_LIFECYCLE_STATE_CHANGED_REQUIREMENT
  ) {
    return 'lifecycle_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === APPEND_ONLY_LIFECYCLE_PERSISTENCE_UNAVAILABLE_ERROR &&
    payload.requirementId === APPEND_ONLY_LIFECYCLE_PERSISTENCE_REQUIREMENT
  ) {
    return 'append_only_lifecycle_persistence_unavailable';
  }
  return undefined;
};

const supplementaryDocumentUploadFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): SupplementaryDocumentUploadFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === SUPPLEMENTARY_DOCUMENT_UPLOAD_STATE_CHANGED_ERROR &&
    payload.requirementId === SUPPLEMENTARY_DOCUMENT_UPLOAD_STATE_CHANGED_REQUIREMENT
  ) {
    return 'supplementary_document_upload_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === ATTACHMENT_UPLOAD_VERIFICATION_UNAVAILABLE_ERROR &&
    payload.requirementId === SUPPLEMENTARY_DOCUMENT_OBJECT_VERSION_INTEGRITY_REQUIREMENT
  ) {
    return 'supplementary_document_upload_verification_unavailable';
  }
  return undefined;
};

const supplementaryDocumentAccessFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): SupplementaryDocumentAccessFailureCode | undefined => {
  if (
    status === 409 &&
    payload?.error === SUPPLEMENTARY_DOCUMENT_ACCESS_STATE_CHANGED_ERROR &&
    payload.requirementId === SUPPLEMENTARY_DOCUMENT_ACCESS_STATE_CHANGED_REQUIREMENT
  ) {
    return 'supplementary_document_access_state_changed';
  }
  if (
    status === 503 &&
    payload?.error === ATTACHMENT_ACCESS_PERSISTENCE_UNAVAILABLE_ERROR &&
    payload.requirementId === ATTACHMENT_ACCESS_PERSISTENCE_UNAVAILABLE_REQUIREMENT
  ) {
    return 'supplementary_document_access_persistence_unavailable';
  }
  if (
    status === 503 &&
    payload?.error === ATTACHMENT_INTEGRITY_VERIFICATION_UNAVAILABLE_ERROR &&
    payload.requirementId === SUPPLEMENTARY_DOCUMENT_OBJECT_VERSION_INTEGRITY_REQUIREMENT
  ) {
    return 'supplementary_document_integrity_verification_unavailable';
  }
  if (
    status === 409 &&
    payload?.error === ATTACHMENT_INTEGRITY_CHECK_FAILED_ERROR &&
    payload.requirementId === SUPPLEMENTARY_DOCUMENT_OBJECT_VERSION_INTEGRITY_REQUIREMENT
  ) {
    return 'supplementary_document_integrity_failed';
  }
  return undefined;
};

const signatureVerificationFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): SignatureVerificationFailureCode | undefined =>
  status === 409 &&
  payload?.error === SIGNATURE_VERIFICATION_STATE_CHANGED_ERROR &&
  payload.requirementId === SIGNATURE_VERIFICATION_STATE_CHANGED_REQUIREMENT
    ? 'signature_verification_state_changed'
    : undefined;

const signatureCreationFailureCodeFromResponse = (
  status: number,
  payload: Record<string, unknown> | undefined
): SignatureCreationFailureCode | undefined =>
  status === 409 &&
  payload?.error === SIGNATURE_CREATION_STATE_CHANGED_ERROR &&
  payload.requirementId === SIGNATURE_CREATION_STATE_CHANGED_REQUIREMENT
    ? 'signature_creation_state_changed'
    : undefined;

export const isSupplementaryDocumentIntegrityLinkExportError = (error: unknown): boolean =>
  error instanceof EvidenceManifestExportError &&
  error.code === 'supplementary_document_integrity_link_required';

export const isCmrArticle5CopyCoverageExportError = (error: unknown): boolean =>
  error instanceof EvidenceManifestExportError &&
  error.code === 'cmr_article5_copy_coverage_required';

export const isEcmrArticle6ReceiptAccessHandoverExportError = (error: unknown): boolean =>
  error instanceof EvidenceManifestExportError &&
  error.code === 'ecmr_article6_receipt_access_handover_required';

export const isEcmrArticle6SupplementaryDocumentTransferExportError = (error: unknown): boolean =>
  error instanceof EvidenceManifestExportError &&
  error.code === 'ecmr_article6_supplementary_document_transfer_required';

export const isContractParticipantIdentityExportError = (error: unknown): boolean =>
  error instanceof EvidenceManifestExportError &&
  error.code === 'contract_participant_identity_required';

export const isEntitledPartyAccessExportError = (error: unknown): boolean =>
  error instanceof EvidenceManifestExportError &&
  error.code === 'entitled_party_access_required';

export const isEvidenceManifestExportStateChangedError = (error: unknown): boolean =>
  error instanceof EvidenceManifestExportError &&
  error.code === 'evidence_manifest_export_state_changed';

export const isSupplementaryDocumentIntegrityLinkFinalXmlError = (error: unknown): boolean =>
  error instanceof FinalXmlArchiveRetrievalError &&
  error.code === 'supplementary_document_integrity_link_required';

export const isContractParticipantIdentityFinalXmlError = (error: unknown): boolean =>
  error instanceof FinalXmlArchiveRetrievalError &&
  error.code === 'contract_participant_identity_required';

export const isEntitledPartyAccessFinalXmlError = (error: unknown): boolean =>
  error instanceof FinalXmlArchiveRetrievalError &&
  error.code === 'entitled_party_access_required';

export const isFinalXmlRetrievalStateChangedError = (error: unknown): boolean =>
  error instanceof FinalXmlArchiveRetrievalError &&
  error.code === 'final_xml_retrieval_state_changed';

export const isDocumentIntegrityReadError = (error: unknown): boolean =>
  error instanceof DocumentReadError &&
  (
    error.code === 'document_integrity_failed' ||
    error.code === 'document_integrity_verification_unavailable'
  );

export const isDocumentDetailAccessStateChangedError = (error: unknown): boolean =>
  error instanceof DocumentReadError &&
  error.code === 'document_detail_access_state_changed';

export const isDocumentDetailAccessPersistenceUnavailableReadError = (error: unknown): boolean =>
  error instanceof DocumentReadError &&
  error.code === 'document_detail_access_persistence_unavailable';

export const isDocumentPolicyAccessStateChangedError = (error: unknown): boolean =>
  error instanceof DocumentPolicyAccessError &&
  error.code === 'document_policy_access_state_changed';

export const isDocumentPolicyAccessPersistenceUnavailableError = (error: unknown): boolean =>
  error instanceof DocumentPolicyAccessError &&
  error.code === 'document_policy_access_persistence_unavailable';

export const isDocumentAuditAccessStateChangedError = (error: unknown): boolean =>
  error instanceof DocumentAuditAccessError &&
  error.code === 'document_audit_access_state_changed';

export const isDocumentAuditAccessPersistenceUnavailableError = (error: unknown): boolean =>
  error instanceof DocumentAuditAccessError &&
  error.code === 'document_audit_access_persistence_unavailable';

export const isDocumentListAccessStateChangedError = (error: unknown): boolean =>
  error instanceof DocumentListAccessError &&
  error.code === 'document_list_access_state_changed';

export const isDocumentListAccessPersistenceUnavailableError = (error: unknown): boolean =>
  error instanceof DocumentListAccessError &&
  error.code === 'document_list_access_persistence_unavailable';

export const isDocumentProcedureIntegrityError = (error: unknown): boolean =>
  error instanceof DocumentProcedureAccessError &&
  (
    error.code === 'document_integrity_failed' ||
    error.code === 'document_integrity_verification_unavailable'
  );

export const isDocumentProcedureAccessStateChangedError = (error: unknown): boolean =>
  error instanceof DocumentProcedureAccessError &&
  error.code === 'document_procedure_access_state_changed';

export const isDocumentProcedureAccessPersistenceUnavailableError = (error: unknown): boolean =>
  error instanceof DocumentProcedureAccessError &&
  error.code === 'document_procedure_access_persistence_unavailable';

const isSanitizedDocumentReadContractError = (error: unknown): boolean =>
  error instanceof DocumentReadError && error.code !== undefined;

export const isDocumentLifecycleStateChangedError = (error: unknown): boolean =>
  error instanceof DocumentLifecycleError && error.code === 'lifecycle_state_changed';

export const isAppendOnlyLifecyclePersistenceUnavailableError = (error: unknown): boolean =>
  error instanceof DocumentLifecycleError && error.code === 'append_only_lifecycle_persistence_unavailable';

export const isSupplementaryDocumentUploadStateChangedError = (error: unknown): boolean =>
  error instanceof SupplementaryDocumentUploadError &&
  error.code === 'supplementary_document_upload_state_changed';

export const isSupplementaryDocumentUploadVerificationUnavailableError = (error: unknown): boolean =>
  error instanceof SupplementaryDocumentUploadError &&
  error.code === 'supplementary_document_upload_verification_unavailable';

export const isSupplementaryDocumentAccessStateChangedError = (error: unknown): boolean =>
  error instanceof SupplementaryDocumentAccessError &&
  error.code === 'supplementary_document_access_state_changed';

export const isSupplementaryDocumentAccessPersistenceUnavailableError = (error: unknown): boolean =>
  error instanceof SupplementaryDocumentAccessError &&
  error.code === 'supplementary_document_access_persistence_unavailable';

export const isSupplementaryDocumentIntegrityVerificationUnavailableError = (error: unknown): boolean =>
  error instanceof SupplementaryDocumentAccessError &&
  error.code === 'supplementary_document_integrity_verification_unavailable';

export const isSupplementaryDocumentIntegrityFailedError = (error: unknown): boolean =>
  error instanceof SupplementaryDocumentAccessError &&
  error.code === 'supplementary_document_integrity_failed';

export const isSignatureVerificationStateChangedError = (error: unknown): boolean =>
  error instanceof SignatureVerificationError &&
  error.code === 'signature_verification_state_changed';

export const isSignatureCreationStateChangedError = (error: unknown): boolean =>
  error instanceof SignatureCreationError &&
  error.code === 'signature_creation_state_changed';

export const isDocumentIntegrityVerificationUnavailableReadError = (error: unknown): boolean =>
  error instanceof DocumentReadError &&
  error.code === 'document_integrity_verification_unavailable';

export interface FinalXmlArchiveRetrievalResult {
  xmlContent: string;
  archiveReference: PublicImmutableStorageReference;
}

const safeOecmrEvidenceElementValue = (
  name: string,
  value: string | number | boolean | undefined | null
): string | number | boolean | undefined | null => {
  if (typeof value === 'string' && isPublicManifestTemporalFieldName(name)) {
    return safePublicManifestTemporalValue(value);
  }
  if (name.endsWith('DocumentVersionNumber')) {
    return safePublicManifestVersionNumber(value);
  }
  if (name.endsWith('Count')) {
    return safePublicManifestCount(value);
  }
  if (name.endsWith('SizeBytes')) {
    return safePublicManifestByteSize(value);
  }
  if (name.endsWith('RetentionDays')) {
    return safePublicManifestCount(value);
  }
  return value;
};

const oecmrElement = (
  name: string,
  value: string | number | boolean | undefined | null,
  attributes?: Record<string, string | undefined>
): string => element(
  `oecmr:${name}`,
  safeOecmrEvidenceElementValue(name, value),
  attributes
);

const oecmrWrap = (name: string, content: string, attributes?: Record<string, string | undefined>): string => {
  const tagName = `oecmr:${name}`;
  return content ? `<${tagName}${attributesToString(attributes)}>${content}</${tagName}>` : '';
};

const presence = (value: unknown): boolean =>
  Array.isArray(value) ? value.length > 0 : isPresent(value);

const roleCount = (roles?: string[]): number | undefined =>
  operationalRoleCount(roles);

const evidenceActorRoleCount = (evidence?: { actorRoleCount?: number; actorRoles?: unknown }): number | undefined =>
  publicOperationalRoleCount(evidence?.actorRoleCount, evidence?.actorRoles);

// Frontend companion XML is technical evidence only; legal conclusions are backend/legal decisions.
const technicalEvidenceOnly = true;
const noLegalDetermination = false;
const noLiabilityDetermination = false;

const publicEvidenceManifestXmlNamePattern = /^[A-Za-z][A-Za-z0-9]{0,96}$/;

const safePublicEvidenceManifestXmlName = (value: string): string =>
  publicEvidenceManifestXmlNamePattern.test(value) ? value : 'other';

const publicEvidenceManifestValueXml = (value: unknown, fieldName?: string): string => {
  if (Array.isArray(value)) {
    return value
      .map((item) => oecmrWrap('PublicEvidenceItem', publicEvidenceManifestValueXml(item)))
      .join('');
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([fieldName, fieldValue]) => {
        const fieldContent = publicEvidenceManifestValueXml(fieldValue, fieldName);
        return oecmrWrap(
          'PublicEvidenceField',
          fieldContent,
          { name: safePublicEvidenceManifestXmlName(fieldName) }
        );
      })
      .join('');
  }
  if (typeof value === 'string') {
    const safeValue = fieldName && isPublicManifestTemporalFieldName(fieldName)
      ? safePublicManifestTemporalValue(value)
      : value;
    return safeValue !== undefined ? oecmrElement('PublicEvidenceValue', safeValue) : '';
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return oecmrElement('PublicEvidenceValue', value);
  }
  return '';
};

const publicEvidenceManifestSectionsXml = (
  sections: PublicEvidenceManifestSections | undefined
): string => {
  const sanitizedSections = publicEvidenceManifestSectionsFromApi(sections);
  if (!sanitizedSections) {
    return '';
  }
  const serializedSections = Object.entries(sanitizedSections)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([sectionName, sectionValue]) => oecmrWrap(
      'PublicEvidenceSection',
      publicEvidenceManifestValueXml(sectionValue),
      { name: safePublicEvidenceManifestXmlName(sectionName) }
    ))
    .join('');

  return oecmrWrap('PublicEvidenceSections', [
    oecmrElement('TechnicalEvidenceOnly', technicalEvidenceOnly),
    oecmrElement('LegalDetermination', noLegalDetermination),
    serializedSections,
  ].join(''));
};

const customsFormalitiesDocumentTypeCodes = new Set([
  'BORDER',
  'BORDER_CLEARANCE',
  'COMMERCIAL_INVOICE',
  'CUSTOMS',
  'CUSTOMS_DECLARATION',
  'CUSTOMS_DOCUMENT',
  'CUSTOMS_INVOICE',
  'EXPORT',
  'EXPORT_DECLARATION',
  'IMPORT',
  'IMPORT_DECLARATION',
  'MRN',
  'TRANSIT_DOCUMENT',
]);

const customsFormalitiesDocumentTextPattern =
  /customs|formalities|border|export declaration|import declaration|\bMRN\b/i;

const normalizedCustomsFormalitiesCode = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/[^A-Za-z0-9_]/g, '').toUpperCase() : '';

const safeTokenRecordType = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const token = value.trim().toLowerCase();
  return /^[a-z0-9._-]{1,64}$/.test(token) ? token : undefined;
};

const validationContextLabels = new Set([
  'create',
  'content_update',
  'document-bound',
  'issue',
  'evidence_manifest_export',
  'final_xml_retrieval',
]);

const documentAccessManifestPathValues = [
  'document',
  'list',
  'procedure_sets',
  'audit',
  'evidence_manifest',
  'tasks',
  'workflow_actions',
  'signature_verification',
  'final_xml',
];
const documentAccessManifestPaths = new Set(documentAccessManifestPathValues);
const attachmentAccessManifestTypeValues = ['metadata_list', 'download_url'];
const attachmentAccessManifestTypes = new Set(attachmentAccessManifestTypeValues);
const manifestEvidenceTypes = new Set([
  'final_xml',
  'evidence_manifest_xml',
  'supplementary_attachment',
  'proof_of_delivery',
]);
const documentLifecycleStatusValues = [
  'draft',
  'issued',
  'accepted_by_carrier',
  'in_transit',
  'delivered',
  'closed',
  'amended',
  'cancelled',
];
const documentLifecycleStatuses = new Set(documentLifecycleStatusValues);
const signatureManifestRoles = new Set([
  'consignor',
  'carrier',
  'consignee',
  'authority',
  'delegate',
  'owner',
  'other',
]);
const signatureManifestMethods = new Set([
  'simple-electronic-attestation',
  'manual_capture',
  'manual_attestation',
  'unknown',
]);
const signatureIdentityEvidenceTypes = new Set([
  'authenticated-user-session',
]);
const signatureControlAssuranceTypes = new Set([
  'session-bound-credential',
]);
const signatureDataIntegrityProtections = new Set([
  'signed-version-digest',
]);
const supplementaryDocumentEvidenceTypes = new Set(['supplementary_document_metadata']);
const carrierReservationCategories = new Set([
  'package_count',
  'marks_numbers',
  'condition',
  'packaging',
  'unable_to_verify',
  'other',
]);
const carrierTakeoverCheckResults = new Set([
  'accepted',
  'reservation_recorded',
  'unable_to_verify',
]);
const carrierTakeoverRequestedCheckValues = ['gross_weight', 'quantity', 'contents'];
const carrierTakeoverRequestedChecks = new Set(carrierTakeoverRequestedCheckValues);
const reservationAcknowledgementDecisions = new Set(['acknowledged', 'rejected']);
const cmrEvidenceInstruments = new Set(['CMR_1956']);
const cmrArticle9BasisValues = new Set(['accepted_carrier_takeover_check_without_specific_reservations']);
const cmrArticle9Articles = new Set(['9.2']);
const cmrArticle10BasisValues = new Set(['carrier_reservation_concerning_packaging_defect']);
const cmrArticle10Articles = new Set(['10']);
const cmrArticle11Articles = new Set(['11.1']);
const operationalRecordStatusValues = ['recorded'];
const operationalRecordStatuses = new Set(operationalRecordStatusValues);
const disposalInstructionValues = ['stop_in_transit', 'change_delivery_place', 'change_consignee'];
const disposalInstructionTypes = new Set(disposalInstructionValues);
const disposalExecutionNoticeValues = ['unable_to_execute'];
const disposalExecutionNoticeTypes = new Set(disposalExecutionNoticeValues);
const disposalExecutionNoticeTargetRoleValues = [
  'article12_entitled_person',
  'consignor',
  'consignee',
  'owner',
];
const disposalExecutionNoticeTargetRoles = new Set(disposalExecutionNoticeTargetRoleValues);
const cmrArticle12Articles = new Set(['12']);
const cmrArticle126Articles = new Set(['12.6']);
const consigneeDeliveryRequestValues = [
  'delivery_after_arrival',
  'rights_enforcement_notice',
  'delivery_receipt_and_second_copy_handover',
];
const consigneeDeliveryRequestTypes = new Set(consigneeDeliveryRequestValues);
const cmrArticle13Articles = new Set(['13']);
const carriageImpedimentValues = [
  'carriage_impossible_before_arrival',
  'delivery_prevented_after_arrival',
  'consignee_refusal',
  'alternative_carriage_taken',
  'unload_for_account',
  'sale_of_goods',
];
const carriageImpedimentTypes = new Set(carriageImpedimentValues);
const carriageImpedimentInstructionRequestTypes = new Set([
  'carriage_impossible_before_arrival',
  'delivery_prevented_after_arrival',
  'consignee_refusal',
  'alternative_carriage_taken',
]);
const carriageImpedimentInstructionTargetRoleValues = [
  'article12_entitled_person',
  'sender',
  'consignee_as_sender',
];
const carriageImpedimentInstructionTargetRoles = new Set(carriageImpedimentInstructionTargetRoleValues);
const carriageImpedimentSaleReasonValues = [
  'perishable',
  'condition_warrants_sale',
  'storage_expenses_disproportionate',
  'reasonable_period_without_instructions',
];
const carriageImpedimentSaleReasons = new Set(carriageImpedimentSaleReasonValues);
const carriageImpedimentSaleProcedureBasisValues = [
  'applicable_law',
  'local_custom',
  'applicable_law_and_local_custom',
];
const carriageImpedimentSaleProcedureBases = new Set(carriageImpedimentSaleProcedureBasisValues);
const carriageImpedimentSourceArticles = new Set(['14.1', '14.2', '15', '16.2', '16.3-16.5']);
const carriageAccountingValues = [
  'instruction_expense',
  'goods_disposition_expense',
  'sale_proceeds',
  'charge_shortfall',
];
const carriageAccountingTypes = new Set(carriageAccountingValues);
const carriageAccountingSourceArticles = new Set(['16.1', '16.2', '16.4']);
const carrierLiabilityIncidentValues = [
  'total_loss',
  'partial_loss',
  'damage',
  'delay',
  'article12_disposal_instruction_noncompliance',
];
const carrierLiabilityIncidentTypes = new Set(carrierLiabilityIncidentValues);
const carrierLiabilityIncidentSpecialRiskValues = [
  'open_unsheeted_vehicle',
  'defective_or_missing_packing',
  'sender_loading_handling_stowage_unloading',
  'goods_nature',
  'insufficient_marks_numbers',
  'livestock',
];
const carrierLiabilityIncidentSpecialRisks = new Set(carrierLiabilityIncidentSpecialRiskValues);
const carrierLiabilityIncidentSourceArticles = new Set(['12.7', '17-18']);
const article18_2ClaimantRebuttalSourceArticles = new Set(['18.2']);
const deliveryTimingAssessmentValues = [
  'article19_delay',
  'article20_loss_presumption',
  'article20_recovery_notice',
];
const deliveryTimingAssessmentTypes = new Set(deliveryTimingAssessmentValues);
const deliveryTimingAssessmentSourceArticles = new Set(['19', '20.1', '20.2-20.3']);
const compensationPaymentValues = ['missing_goods_compensation_payment'];
const compensationPaymentTypes = new Set(compensationPaymentValues);
const compensationPaymentEvidenceValues = [
  'bank_transfer_confirmation',
  'payment_provider_receipt',
  'signed_settlement_receipt',
  'court_payment_record',
  'other_documented_payment',
];
const compensationPaymentEvidenceTypes = new Set(compensationPaymentEvidenceValues);
const compensationPaymentSourceArticles = new Set(['20.2']);
const recoveryDeliverySettlementValues = ['article20_recovery_delivery_settlement'];
const recoveryDeliverySettlementTypes = new Set(recoveryDeliverySettlementValues);
const recoveryDeliverySettlementSourceArticles = new Set(['20.3']);
const recoveredGoodsDispositionValues = ['article20_recovered_goods_disposition'];
const recoveredGoodsDispositionTypes = new Set(recoveredGoodsDispositionValues);
const recoveredGoodsDispositionBasisValues = [
  'no_written_recovery_notification_request',
  'no_instructions_after_thirty_days',
  'recovered_more_than_one_year_after_compensation',
];
const recoveredGoodsDispositionBases = new Set(recoveredGoodsDispositionBasisValues);
const recoveredGoodsDispositionActionValues = ['stored', 'sold', 'destroyed', 'transferred', 'other'];
const recoveredGoodsDispositionActionTypes = new Set(recoveredGoodsDispositionActionValues);
const recoveredGoodsDispositionSourceArticles = new Set(['20.4']);
const codDeliveryOmissionValues = ['cod_uncollected_delivery'];
const codDeliveryOmissionTypes = new Set(codDeliveryOmissionValues);
const codDeliveryOmissionSourceArticles = new Set(['21']);
const dangerousGoodsNoticeValues = [
  'danger_information_notice',
  'carrier_unaware_dangerous_goods_action',
];
const dangerousGoodsNoticeTypes = new Set(dangerousGoodsNoticeValues);
const dangerousGoodsCarrierActionValues = ['unloaded', 'destroyed', 'rendered_harmless'];
const dangerousGoodsCarrierActionTypes = new Set(dangerousGoodsCarrierActionValues);
const dangerousGoodsNoticeSourceArticles = new Set(['22.1', '22.2']);
const compensationAssessmentValues = [
  'loss_compensation_limit',
  'damage_depreciation_compensation_limit',
  'delay_compensation_limit',
  'declared_value_substitution',
  'special_interest_additional_compensation',
];
const compensationAssessmentTypes = new Set(compensationAssessmentValues);
const compensationGoodsValueBasisValues = [
  'commodity_exchange_price',
  'current_market_price',
  'same_kind_quality_value',
];
const compensationGoodsValueBases = new Set(compensationGoodsValueBasisValues);
const compensationLiabilityLimitBasisValues = ['sdr_8_33_per_kg', 'gold_franc_25_per_kg'];
const compensationLiabilityLimitBases = new Set(compensationLiabilityLimitBasisValues);
const compensationArticle23LossExtentBases = new Set(['total_loss', 'partial_loss']);
const compensationArticle23ChargeRefundBases = new Set([
  'full_total_loss',
  'proportional_partial_loss',
]);
const compensationAssessmentSourceArticles = new Set(['23', '23.5', '24', '25', '26']);
const compensationInterestRecordValues = ['interest_on_compensation', 'currency_conversion'];
const compensationInterestRecordTypes = new Set(compensationInterestRecordValues);
const compensationInterestStartBasisValues = ['written_claim', 'legal_proceedings'];
const compensationInterestStartBases = new Set(compensationInterestStartBasisValues);
const compensationInterestSourceArticles = new Set(['27.1', '27.2']);
const liabilityLimitationRecordValues = [
  'extra_contractual_claim_defense',
  'wilful_misconduct_limitation_exception',
];
const liabilityLimitationRecordTypes = new Set(liabilityLimitationRecordValues);
const liabilityLimitationPersonRoleValues = [
  'carrier',
  'article3_agent_or_servant',
  'article3_other_service_person',
];
const liabilityLimitationPersonRoles = new Set(liabilityLimitationPersonRoleValues);
const liabilityLimitationSourceArticles = new Set(['28', '29']);
const claimReservationRecordValues = [
  'apparent_loss_or_damage_reservation',
  'non_apparent_loss_or_damage_reservation',
  'delay_compensation_reservation',
  'condition_check_facility',
];
const claimReservationRecordTypes = new Set(claimReservationRecordValues);
const claimReservationTimeLimitBasisValues = [
  'seven_days_excluding_sundays_and_start_day',
  'seven_days_excluding_sundays_public_holidays_and_start_day',
  'twenty_one_days_excluding_start_day',
];
const claimReservationTimeLimitBases = new Set(claimReservationTimeLimitBasisValues);
const claimReservationSourceArticles = new Set(['30.1', '30.1-30.2', '30.3-30.4', '30.2/30.5']);
const disputeProcedureRecordValues = [
  'jurisdiction_basis_notice',
  'pending_or_judgment_bar_notice',
  'limitation_period_record',
  'written_claim_suspension_record',
  'arbitration_clause_record',
];
const disputeProcedureRecordTypes = new Set(disputeProcedureRecordValues);
const disputeProcedureClaimCategoryValues = ['partial_loss', 'damage', 'delay', 'total_loss', 'other'];
const disputeProcedureClaimCategories = new Set(disputeProcedureClaimCategoryValues);
const disputeProcedureLimitationPeriodBasisValues = ['one_year', 'three_year_wilful_misconduct'];
const disputeProcedureLimitationPeriodBases = new Set(disputeProcedureLimitationPeriodBasisValues);
const disputeProcedureLimitationStartBasisValues = [
  'delivery_date',
  'thirty_days_after_agreed_time_limit',
  'sixty_days_after_takeover',
  'three_months_after_contract',
];
const disputeProcedureLimitationStartBases = new Set(disputeProcedureLimitationStartBasisValues);
const disputeProcedureLimitationStartComputationBasisValues = [
  'delivery_date_excluding_start_day',
  'thirty_days_after_agreed_time_limit_excluding_start_day',
  'sixty_days_after_takeover_excluding_start_day',
  'three_months_after_contract_excluding_start_day',
];
const disputeProcedureLimitationStartComputationBases = new Set(
  disputeProcedureLimitationStartComputationBasisValues
);
const disputeProcedureLimitationExpiryComputationBasisValues = [
  'one_year_from_computed_start_excluding_start_day',
  'three_years_from_computed_start_excluding_start_day',
];
const disputeProcedureLimitationExpiryComputationBases = new Set(
  disputeProcedureLimitationExpiryComputationBasisValues
);
const disputeProcedureSourceArticles = new Set(['31.1-31.5', '31.2-31.4', '32.1,32.3-32.4', '32.2', '33']);
const successiveCarrierRecordValues = [
  'successive_carrier_acceptance',
  'successive_carrier_action_scope',
  'successive_carrier_recovery_allocation',
  'successive_carrier_recovery_proceeding',
  'successive_carrier_agreement_variation',
];
const successiveCarrierRecordTypes = new Set(successiveCarrierRecordValues);
const successiveCarrierAllocationBasisValues = [
  'responsible_carrier',
  'multiple_carriers_liability_share',
  'multiple_carriers_payment_share',
  'unknown_attribution_payment_share',
  'insolvent_carrier_share_reallocated',
];
const successiveCarrierAllocationBases = new Set(successiveCarrierAllocationBasisValues);
const successiveCarrierRecoveryLimitationStartBasisValues = ['final_judicial_decision', 'actual_payment'];
const successiveCarrierRecoveryLimitationStartBases = new Set(
  successiveCarrierRecoveryLimitationStartBasisValues
);
const successiveCarrierRecoveryLimitationComputationBasisValues = [
  'final_judicial_decision_one_year_excluding_start_day',
  'actual_payment_one_year_excluding_start_day',
];
const successiveCarrierRecoveryLimitationComputationBases = new Set(
  successiveCarrierRecoveryLimitationComputationBasisValues
);
const successiveCarrierSourceArticles = new Set(['34-35', '36', '37-38', '39', '40']);
const multimodalCarriageRecordValues = [
  'loaded_vehicle_other_mode_carriage',
  'article14_unloading_impediment_basis',
];
const multimodalCarriageRecordTypes = new Set(multimodalCarriageRecordValues);
const multimodalCarriageOtherModeValues = ['sea', 'rail', 'inland_waterways', 'air'];
const multimodalCarriageOtherModes = new Set(multimodalCarriageOtherModeValues);
const multimodalCarriageSourceArticles = new Set(['2']);
const consignmentNoteCopyRecordValues = [
  'sender_first_copy_access_available',
  'goods_accompanying_second_copy_access_available',
  'carrier_retained_third_copy_access_available',
];
const consignmentNoteCopyRecordTypes = new Set(consignmentNoteCopyRecordValues);
const consignmentNoteCopySourceArticles = new Set(['5.1']);
const ecmrReceiptAccessRecordValues = ['sender_receipt_access_information_handover'];
const ecmrReceiptAccessRecordTypes = new Set(ecmrReceiptAccessRecordValues);
const ecmrReceiptAccessRequestTypes = new Set(['sender_receipt_access_request']);
const ecmrReceiptAccessRequestStatuses = new Set(['requested']);
const ecmrReceiptAccessSourceInstruments = new Set(['ECMR_PROTOCOL_2008']);
const ecmrReceiptAccessSourceArticles = new Set(['6.1']);
const senderParticularsResponsibilityRecordValues = [
  'sender_article6_particular_inaccuracy',
  'sender_optional_particular_inaccuracy',
  'sender_instruction_inaccuracy',
  'carrier_entered_particulars_on_sender_behalf',
  'carrier_cmr_statement_omission',
];
const senderParticularsResponsibilityRecordTypes = new Set(senderParticularsResponsibilityRecordValues);
const senderParticularsResponsibilityCategoryValues = [
  'article6_1_b_sender',
  'article6_1_d_taking_over_delivery',
  'article6_1_e_consignee',
  'article6_1_f_goods_packing',
  'article6_1_g_packages_marks',
  'article6_1_h_weight_quantity',
  'article6_1_j_customs_formalities',
  'article6_2_optional_particular',
  'sender_other_particular_or_instruction',
  'article6_1_k_cmr_statement',
];
const senderParticularsResponsibilityCategories = new Set(senderParticularsResponsibilityCategoryValues);
const senderParticularsResponsibilitySourceArticles = new Set(['7.1', '7.2', '7.3']);
const prohibitedClauseRecordValues = [
  'convention_derogation_clause',
  'insurance_benefit_to_carrier_clause',
  'burden_of_proof_shift_clause',
];
const prohibitedClauseRecordTypes = new Set(prohibitedClauseRecordValues);
const prohibitedClauseSourceArticles = new Set(['41.1', '41.2']);
const treatySnapshotSources = new Set(['UN Treaty Collection XI-B-11', 'UN Treaty Collection XI-B-11-b']);
const treatySnapshotScopeRequirementIds = new Set([
  'CMR-1.1-international-carriage',
  'eCMR-Protocol-Article-2-party-scope',
]);
const treatySnapshotGovernanceValues = new Set(['treaty_status_snapshot_only']);
const ecmrArticle4SourceInstruments = new Set(['ECMR_PROTOCOL_2008']);
const ecmrArticle4SourceArticles = new Set(['4']);
const replacementByOtherMeansMediumValues = [
  'paper',
  'pdf_export',
  'other_electronic_system',
  'authority_request',
];
const replacementByOtherMeansMedia = new Set(replacementByOtherMeansMediumValues);
const amendmentStatusValues = ['pending', 'acknowledged', 'rejected', 'applied'];
const amendmentStatuses = new Set(amendmentStatusValues);
const amendmentDecisionValues = ['acknowledged', 'rejected'];
const amendmentDecisions = new Set(amendmentDecisionValues);
const amendmentAuthorizationBases = new Set<string>(amendmentAuthorizationBasisValues);
const strictAmendmentAuthorizationBasis = (
  value: unknown
): AmendmentAuthorizationBasis | undefined => (
  typeof value === 'string' && amendmentAuthorizationBases.has(value)
    ? value as AmendmentAuthorizationBasis
    : undefined
);
const signatureVerificationStatusValues = ['verified', 'invalid', 'pending', 'unknown'];
const signatureVerificationStatuses = new Set(signatureVerificationStatusValues);
const legalHoldStatuses = new Set(['active', 'released', 'other']);
const exceptionReviewTaskIdValues = ['exception_review_required'];
const exceptionReviewTaskIds = new Set(exceptionReviewTaskIdValues);
const exceptionReviewTaskStatusValues = ['open', 'in_progress', 'completed', 'deferred'];
const exceptionReviewTaskStatuses = new Set(exceptionReviewTaskStatusValues);
const integrityCompromisedStatusValues = ['compromised'];
const integrityCompromisedStatuses = new Set(integrityCompromisedStatusValues);
const integrityCompromisedReasonValues = [
  'currentVersion digest mismatch',
  'final XML archive digest mismatch',
  'attachment_digest_mismatch',
];
const integrityCompromisedReasons = new Set(integrityCompromisedReasonValues);
const integrityCompromisedContextValues = [
  'document_read',
  'lifecycle_transition',
  'evidence_manifest_export',
  'signature_verification',
  'signature_creation',
  'proof_of_delivery',
  'attachment_download_url',
  'final_xml_retrieval',
];
const integrityCompromisedContexts = new Set(integrityCompromisedContextValues);
const attachmentUploadRejectionReasonValues = [
  'mime_type_not_allowed',
  'payload_too_large',
  'invalid_base64_payload',
  'missing_content_base64',
  'content_signature_mismatch',
  'digest_mismatch',
  'malware_signature_detected',
  'active_content_detected',
  'sensitive_data_pattern_detected',
];
const attachmentUploadRejectionReasons = new Set(attachmentUploadRejectionReasonValues);
const attachmentUploadRejectionPolicyValues = [
  'attachment_mime_allowlist',
  'attachment_size_limit',
  'attachment_payload_base64',
  'attachment_payload_required',
  'declared_mime_content_signature',
  'attachment_direct_upload_integrity',
  'attachment_content_safety_scan',
];
const attachmentUploadRejectionPolicies = new Set(attachmentUploadRejectionPolicyValues);
const downloadCompletionEvidenceTypes = new Set([
  'final_xml',
  'evidence_manifest_xml',
  'supplementary_attachment',
]);
const transferAuditSources = new Set(['cloudtrail_s3_data_event']);
const transferAuditCorrelationStatuses = new Set(['correlated']);
const retentionPolicyStatuses = new Set(['active']);
const digestAlgorithms = new Set(['SHA-256']);
const retentionModes = new Set(['GOVERNANCE', 'COMPLIANCE', 'APPLICATION_POLICY']);
const publicAttachmentMimeTypes = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const appendOnlyEvidenceRecordTypes = new Set([
  'audit_event',
  'document_access',
  'attachment_access',
  'download_completion',
  'transfer_audit_correlation',
  'final_xml_retrieval',
  'evidence_manifest_export',
  'immutable_storage_reference',
  'lifecycle_event',
  'tombstone',
  'signature_evidence',
  'signature_verification',
  'amendment_request',
  'amendment_acknowledgement',
  'amendment_apply',
  'exception_review_task_state',
  'supplementary_document_upload',
  'procedure_acceptance',
  'replacement_by_other_means',
  'cmr_article12_disposal_instruction',
  'cmr_article12_disposal_execution_notice',
  'cmr_articles14_16_carriage_impediment',
  'cmr_article16_accounting_record',
  'cmr_article12_7_carrier_liability_incident',
  'cmr_article17_18_carrier_liability_incident',
  'cmr_article19_20_delivery_timing_assessment',
  'cmr_article20_compensation_payment',
  'cmr_article20_recovery_delivery_settlement',
  'cmr_article20_recovered_goods_disposition',
  'cmr_article21_cod_delivery_omission',
  'cmr_article22_dangerous_goods_notice',
  'cmr_articles23_26_compensation_assessment',
  'cmr_article27_compensation_interest',
  'cmr_articles28_29_liability_limitation',
  'cmr_article30_claim_reservation',
  'cmr_articles31_33_dispute_procedure',
  'cmr_articles34_40_successive_carrier',
  'cmr_article2_multimodal_carriage',
  'cmr_article5_consignment_note_copy_access',
  'ecmr_article6_receipt_access',
  'cmr_article7_sender_particulars_responsibility',
  'cmr_article41_prohibited_clause',
  'cmr_article13_consignee_delivery_request',
  'retention_policy_apply',
  'legal_hold_apply',
  'legal_hold_release',
  'authority_review',
  'xml_validation',
  'cmr_business_validation',
  'ecmr_protocol_validation',
  'integrity_compromised',
  'attachment_upload_rejection',
]);
const appendOnlySourceRecordTypes = new Set([
  ...documentAccessManifestPathValues,
  ...attachmentAccessManifestTypeValues,
  'create',
  'content_update',
  'document-bound',
  'issue',
  'evidence_manifest_export',
  'final_xml_retrieval',
  'document_tombstone',
  'default-ecmr-d24a',
  'default-ecmr-d25a',
  ...documentLifecycleStatusValues,
  ...signatureVerificationStatusValues,
  ...exceptionReviewTaskStatusValues,
  'supplementary_document_metadata',
  ...Array.from(carrierReservationCategories),
  ...Array.from(carrierTakeoverCheckResults),
  ...carrierTakeoverRequestedCheckValues,
  ...Array.from(reservationAcknowledgementDecisions),
  ...Array.from(cmrArticle9BasisValues),
  ...Array.from(cmrArticle10BasisValues),
  ...operationalRecordStatusValues,
  ...disposalInstructionValues,
  ...disposalExecutionNoticeValues,
  ...disposalExecutionNoticeTargetRoleValues,
  ...consigneeDeliveryRequestValues,
  ...carriageImpedimentValues,
  ...carriageImpedimentInstructionTargetRoleValues,
  ...carriageImpedimentSaleReasonValues,
  ...carriageAccountingValues,
  ...carrierLiabilityIncidentValues,
  ...carrierLiabilityIncidentSpecialRiskValues,
  ...deliveryTimingAssessmentValues,
  ...compensationPaymentValues,
  ...recoveryDeliverySettlementValues,
  ...recoveredGoodsDispositionValues,
  ...recoveredGoodsDispositionBasisValues,
  ...recoveredGoodsDispositionActionValues,
  ...codDeliveryOmissionValues,
  ...dangerousGoodsNoticeValues,
  ...dangerousGoodsCarrierActionValues,
  ...compensationAssessmentValues,
  ...compensationGoodsValueBasisValues,
  ...compensationLiabilityLimitBasisValues,
  ...compensationInterestRecordValues,
  ...compensationInterestStartBasisValues,
  ...liabilityLimitationRecordValues,
  ...liabilityLimitationPersonRoleValues,
  ...claimReservationRecordValues,
  ...claimReservationTimeLimitBasisValues,
  ...disputeProcedureRecordValues,
  ...disputeProcedureClaimCategoryValues,
  ...disputeProcedureLimitationPeriodBasisValues,
  ...disputeProcedureLimitationStartBasisValues,
  ...disputeProcedureLimitationStartComputationBasisValues,
  ...disputeProcedureLimitationExpiryComputationBasisValues,
  ...successiveCarrierRecordValues,
  ...successiveCarrierAllocationBasisValues,
  ...successiveCarrierRecoveryLimitationStartBasisValues,
  ...successiveCarrierRecoveryLimitationComputationBasisValues,
  ...multimodalCarriageRecordValues,
  ...multimodalCarriageOtherModeValues,
  ...consignmentNoteCopyRecordValues,
  ...ecmrReceiptAccessRecordValues,
  ...senderParticularsResponsibilityRecordValues,
  ...senderParticularsResponsibilityCategoryValues,
  ...prohibitedClauseRecordValues,
  ...replacementByOtherMeansMediumValues,
  ...amendmentStatusValues,
  ...amendmentDecisionValues,
  ...integrityCompromisedContextValues,
  ...attachmentUploadRejectionReasonValues,
]);

const ecmrProtocolValidationStatuses = new Set(['failed']);
const xmlValidationSchemaProfiles = new Set(['default-ecmr-d24a', 'default-ecmr-d25a']);
const cmrBusinessValidationStatuses = new Set(['failed']);
const cmrBusinessValidationInstruments = new Set(['CMR_1956', 'CMR_2008']);
const cmrBusinessValidationArticles = new Set([
  '1.1',
  '2',
  '6.1',
  '6.2',
  '6.1/6.2',
  '1.1/2',
  '1.1/6.1/6.2',
  '2/6.1/6.2',
  '1.1/2/6.1/6.2',
]);
const ecmrProtocolValidationInstruments = new Set(['eCMR_Protocol_2008']);
const ecmrProtocolValidationArticles = new Set([
  '2',
  '4.3',
  '5.2',
  '6.2',
  '2/4.3',
  '2/5.2',
  '2/6.2',
  '4.3/5.2',
  '4.3/6.2',
  '5.2/6.2',
  '2/4.3/5.2',
  '2/4.3/6.2',
  '2/5.2/6.2',
  '4.3/5.2/6.2',
  '2/4.3/5.2/6.2',
]);

const safeManifestLabel = (value: unknown, allowedValues: Set<string>): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  return allowedValues.has(value) ? value : 'other';
};

const safeDigestAlgorithmLabel = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  return value.trim().toUpperCase() === 'SHA-256' ? 'SHA-256' : undefined;
};

const publicDigestAlgorithmLabel = (value: unknown, defaultValue?: string): string | undefined => {
  const sourceValue = value === undefined || value === null || value === '' ? defaultValue : value;
  if (sourceValue === undefined || sourceValue === null || sourceValue === '') {
    return undefined;
  }
  return safeDigestAlgorithmLabel(sourceValue) ?? 'other';
};

const canonicalizationMethods = new Set([
  'xml-c14n-1.0',
  'xml-c14n-1.1',
  'xml-c14n-2.0',
  'exclusive-xml-c14n-1.0',
]);

const publicCanonicalizationMethodLabel = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  if (!normalized) {
    return undefined;
  }
  return canonicalizationMethods.has(normalized) ? normalized : 'other';
};

const safeManifestMimeType = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  return publicAttachmentMimeTypes.has(normalized) ? normalized : 'other';
};

const safeManifestVersionLabel = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  return /^[A-Za-z0-9._:-]{1,64}$/.test(normalized) ? normalized : 'other';
};

const safeManifestJurisdiction = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  return /^[A-Za-z0-9._:-]{1,32}$/.test(normalized) ? normalized : 'other';
};

const evidenceRecordHasCurrentVersionReference = (
  record: { documentVersionId?: string; documentVersionNumber?: number },
  currentVersion?: DocumentVersionEvidence
): boolean => {
  if (!currentVersion) {
    return false;
  }
  if (currentVersion.versionId && record.documentVersionId) {
    return record.documentVersionId === currentVersion.versionId;
  }
  if (currentVersion.versionNumber !== undefined && record.documentVersionNumber !== undefined) {
    return record.documentVersionNumber === currentVersion.versionNumber;
  }
  return false;
};

const currentVersionManifestRecords = <T extends { documentVersionId?: string; documentVersionNumber?: number }>(
  records: T[] | undefined,
  currentVersion?: DocumentVersionEvidence
): T[] | undefined => {
  if (!records) {
    return undefined;
  }
  const hasCurrentVersionReference = Boolean(
    currentVersion &&
      (currentVersion.versionId || currentVersion.versionNumber !== undefined)
  );
  if (!hasCurrentVersionReference) {
    return records;
  }
  return records.filter((record) => evidenceRecordHasCurrentVersionReference(record, currentVersion));
};

const carrierReservationBlocksCurrentArticle9Presumption = (
  reservation: CarrierReservationEvidence,
  currentVersion?: DocumentVersionEvidence
): boolean => {
  if (!currentVersion) {
    return true;
  }
  const legacyVersionId = (reservation as { documentVersionId?: string }).documentVersionId;
  if (currentVersion.versionId && legacyVersionId) {
    return legacyVersionId === currentVersion.versionId;
  }
  if (currentVersion.versionNumber !== undefined && reservation.documentVersionNumber !== undefined) {
    return reservation.documentVersionNumber === currentVersion.versionNumber;
  }
  return true;
};

const carrierReservationMatchesCurrentVersionOrIsAmbiguous = (
  reservation: CarrierReservationEvidence,
  currentVersion?: DocumentVersionEvidence
): boolean => {
  if (!currentVersion) {
    return true;
  }
  const legacyVersionId = (reservation as { documentVersionId?: string }).documentVersionId;
  if (currentVersion.versionId && legacyVersionId) {
    return legacyVersionId === currentVersion.versionId;
  }
  if (currentVersion.versionNumber !== undefined && reservation.documentVersionNumber !== undefined) {
    return reservation.documentVersionNumber === currentVersion.versionNumber;
  }
  return true;
};

const currentOrAmbiguousPackagingReservations = (
  manifest: EvidenceManifestInput
): CarrierReservationEvidence[] =>
  (manifest.carrierReservations ?? []).filter((reservation) =>
    reservation.category === 'packaging' &&
    carrierReservationMatchesCurrentVersionOrIsAmbiguous(reservation, manifest.currentVersion)
  );

const cmrArticle9PresumptionApplies = (manifest: EvidenceManifestInput): boolean => {
  const acceptedCurrentTakeoverCheck = manifest.carrierTakeoverCheckRecords?.some((check) =>
    evidenceRecordHasCurrentVersionReference(check, manifest.currentVersion) &&
    check.result === 'accepted' &&
    check.packageCountChecked === true &&
    check.marksAndNumbersChecked === true &&
    check.apparentConditionChecked === true &&
    check.reservationReferencePresent !== true
  ) === true;
  if (!acceptedCurrentTakeoverCheck) {
    return false;
  }
  return !(manifest.carrierReservations ?? []).some((reservation) =>
    carrierReservationBlocksCurrentArticle9Presumption(reservation, manifest.currentVersion)
  );
};

const supplementaryDocumentHasCurrentVersionIntegrityLink = (
  document: SupplementaryDocumentEvidence,
  currentVersion?: DocumentVersionEvidence
): boolean => {
  if (!currentVersion) {
    return false;
  }
  const digest = typeof document.sha256Digest === 'string' ? document.sha256Digest.trim() : '';
  const digestAlgorithm = document.digestAlgorithm?.toUpperCase();
  const currentVersionMatches =
    (Boolean(currentVersion.versionId) && document.documentVersionId === currentVersion.versionId) ||
    (currentVersion.versionNumber !== undefined && document.documentVersionNumber === currentVersion.versionNumber);
  return (
    /^[a-fA-F0-9]{64}$/.test(digest) &&
    (!digestAlgorithm || digestAlgorithm === 'SHA-256') &&
    isPresent(document.storageKey) &&
    currentVersionMatches
  );
};

const supplementaryDocumentIsCustomsFormality = (document: SupplementaryDocumentEvidence): boolean => {
  const normalizedType = normalizedCustomsFormalitiesCode(document.documentType);
  if (customsFormalitiesDocumentTypeCodes.has(normalizedType)) {
    return true;
  }
  return customsFormalitiesDocumentTextPattern.test(
    [document.documentType, document.description, document.fileName]
      .filter((value): value is string => typeof value === 'string')
      .join(' ')
  );
};

const currentVersionCustomsFormalityDocuments = (
  manifest: EvidenceManifestInput
): SupplementaryDocumentEvidence[] =>
  (manifest.supplementaryDocuments ?? []).filter((document) =>
    supplementaryDocumentIsCustomsFormality(document) &&
    supplementaryDocumentHasCurrentVersionIntegrityLink(document, manifest.currentVersion)
  );

const sourceRecordPayloadStoredExternally = (
  evidence: NonNullable<ECMR['sourceRecordOperationalEvidence']>[number]
): boolean => Boolean(
  evidence.payloadStoredExternally === true ||
  evidence.storageBucket ||
  evidence.storageKey ||
  evidence.objectVersionId ||
  evidence.url ||
  evidence.downloadUrl
);

export const generateEvidenceManifestXml = (manifest: EvidenceManifestInput): string => {
  const version = manifest.currentVersion;
  const delivery = manifest.deliveryConfirmation;
  const deliverySignatureEvidence = delivery?.signatureEvidence;
  const deliveryStatusAtConfirmation = safeManifestLabel(delivery?.statusAtConfirmation, documentLifecycleStatuses);
  const deliverySignatureRole = safeManifestLabel(deliverySignatureEvidence?.signerRole, signatureManifestRoles);
  const deliverySignatureDigestAlgorithm = publicDigestAlgorithmLabel(deliverySignatureEvidence?.digestAlgorithm);
  const deliverySignatureMethod = safeManifestLabel(deliverySignatureEvidence?.signatureMethod, signatureManifestMethods);
  const deliverySignatureVerificationStatus = safeManifestLabel(
    deliverySignatureEvidence?.verificationStatus,
    signatureVerificationStatuses
  );
  const article9PresumptionApplies = cmrArticle9PresumptionApplies(manifest);
  const article10PackagingReservations = currentOrAmbiguousPackagingReservations(manifest);
  const article10CurrentVersionLinkedCount = article10PackagingReservations.filter((reservation) =>
    evidenceRecordHasCurrentVersionReference(reservation, manifest.currentVersion)
  ).length;
  const article11CustomsDocuments = currentVersionCustomsFormalityDocuments(manifest);
  const xmlValidationRecords = currentVersionManifestRecords(manifest.xmlValidationRecords, manifest.currentVersion);
  const cmrBusinessValidationRecords = currentVersionManifestRecords(
    manifest.cmrBusinessValidationRecords,
    manifest.currentVersion
  );
  const ecmrProtocolValidationRecords = currentVersionManifestRecords(
    manifest.ecmrProtocolValidationRecords,
    manifest.currentVersion
  );
  const disposalInstructionRecords = currentVersionManifestRecords(
    manifest.disposalInstructionRecords,
    manifest.currentVersion
  );
  const disposalExecutionNoticeRecords = currentVersionManifestRecords(
    manifest.disposalExecutionNoticeRecords,
    manifest.currentVersion
  );
  const carriageImpedimentRecords = currentVersionManifestRecords(
    manifest.carriageImpedimentRecords,
    manifest.currentVersion
  );
  const carriageAccountingRecords = currentVersionManifestRecords(
    manifest.carriageAccountingRecords,
    manifest.currentVersion
  );
  const carrierLiabilityIncidentRecords = currentVersionManifestRecords(
    manifest.carrierLiabilityIncidentRecords,
    manifest.currentVersion
  );
  const article18_2ClaimantRebuttalRecords = currentVersionManifestRecords(
    manifest.article18_2ClaimantRebuttalRecords,
    manifest.currentVersion
  );
  const deliveryTimingAssessmentRecords = currentVersionManifestRecords(
    manifest.deliveryTimingAssessmentRecords,
    manifest.currentVersion
  );
  const compensationPaymentRecords = currentVersionManifestRecords(
    manifest.compensationPaymentRecords,
    manifest.currentVersion
  );
  const recoveryDeliverySettlementRecords = currentVersionManifestRecords(
    manifest.recoveryDeliverySettlementRecords,
    manifest.currentVersion
  );
  const recoveredGoodsDispositionRecords = currentVersionManifestRecords(
    manifest.recoveredGoodsDispositionRecords,
    manifest.currentVersion
  );
  const codDeliveryOmissionRecords = currentVersionManifestRecords(
    manifest.codDeliveryOmissionRecords,
    manifest.currentVersion
  );
  const dangerousGoodsNoticeRecords = currentVersionManifestRecords(
    manifest.dangerousGoodsNoticeRecords,
    manifest.currentVersion
  );
  const compensationAssessmentRecords = currentVersionManifestRecords(
    manifest.compensationAssessmentRecords,
    manifest.currentVersion
  );
  const compensationInterestRecords = currentVersionManifestRecords(
    manifest.compensationInterestRecords,
    manifest.currentVersion
  );
  const liabilityLimitationRecords = currentVersionManifestRecords(
    manifest.liabilityLimitationRecords,
    manifest.currentVersion
  );
  const claimReservationRecords = currentVersionManifestRecords(
    manifest.claimReservationRecords,
    manifest.currentVersion
  );
  const disputeProcedureRecords = currentVersionManifestRecords(
    manifest.disputeProcedureRecords,
    manifest.currentVersion
  );
  const successiveCarrierRecords = currentVersionManifestRecords(
    manifest.successiveCarrierRecords,
    manifest.currentVersion
  );
  const multimodalCarriageRecords = currentVersionManifestRecords(
    manifest.multimodalCarriageRecords,
    manifest.currentVersion
  );
  const consignmentNoteCopyRecords = currentVersionManifestRecords(
    manifest.consignmentNoteCopyRecords,
    manifest.currentVersion
  );
  const ecmrReceiptAccessRequestRecords = currentVersionManifestRecords(
    manifest.ecmrReceiptAccessRequestRecords,
    manifest.currentVersion
  );
  const ecmrReceiptAccessRecords = currentVersionManifestRecords(
    manifest.ecmrReceiptAccessRecords,
    manifest.currentVersion
  );
  const prohibitedClauseRecords = currentVersionManifestRecords(
    manifest.prohibitedClauseRecords,
    manifest.currentVersion
  );
  const senderParticularsResponsibilityRecords = currentVersionManifestRecords(
    manifest.senderParticularsResponsibilityRecords,
    manifest.currentVersion
  );
  const consigneeDeliveryRequestRecords = currentVersionManifestRecords(
    manifest.consigneeDeliveryRequestRecords,
    manifest.currentVersion
  );
  const sourceRecordOperationalEvidence = currentVersionManifestRecords(
    manifest.sourceRecordOperationalEvidence,
    manifest.currentVersion
  );
  const article11DocumentTypes = uniqueNonEmptyStrings(
    article11CustomsDocuments
      .map((document) => safeTokenRecordType(document.documentType))
      .filter((documentType): documentType is string => Boolean(documentType))
  );

  const content = [
    oecmrElement('Title', manifest.title),
    publicEvidenceManifestSectionsXml(manifest.evidenceSections),
    version ? oecmrWrap('CurrentVersion', [
      oecmrElement('DocumentVersionReferencePresent', Boolean(version.versionId || version.versionNumber !== undefined)),
      oecmrElement('DocumentVersionNumber', version.versionNumber),
      oecmrElement('DigestAlgorithm', publicDigestAlgorithmLabel(version.digestAlgorithm)),
      oecmrElement('VersionDigestAvailable', version.digestAvailable ?? Boolean(version.digest || version.xmlContentDigest)),
      oecmrElement('CanonicalizationMethod', publicCanonicalizationMethodLabel(version.canonicalizationMethod)),
      oecmrElement('VersionStatus', safeManifestLabel(version.status, documentLifecycleStatuses)),
      oecmrElement('VersionCreatedAt', version.createdAt),
    ].join('')) : '',
    manifest.ecmrArticle4Integrity ? oecmrWrap('EcmrArticle4Integrity', [
      oecmrElement('SourceInstrument', safeManifestLabel(
        manifest.ecmrArticle4Integrity.sourceInstrument,
        ecmrArticle4SourceInstruments
      )),
      oecmrElement('SourceArticle', safeManifestLabel(
        manifest.ecmrArticle4Integrity.sourceArticle,
        ecmrArticle4SourceArticles
      )),
      oecmrElement('SameParticularsAsCmrValidationGatePresent', manifest.ecmrArticle4Integrity.sameParticularsAsCmrValidationGatePresent),
      oecmrElement('FinalFormIntegrityProcedurePresent', manifest.ecmrArticle4Integrity.finalFormIntegrityProcedurePresent),
      oecmrElement('DocumentVersionReferencePresent', manifest.ecmrArticle4Integrity.documentVersionReferencePresent),
      oecmrElement('RecordDocumentVersionNumber', manifest.ecmrArticle4Integrity.documentVersionNumber),
      oecmrElement('CurrentVersionDigestPresent', manifest.ecmrArticle4Integrity.currentVersionDigestPresent),
      oecmrElement('CurrentVersionIntegrityValid', manifest.ecmrArticle4Integrity.currentVersionIntegrityValid),
      oecmrElement('CurrentVersionIntegrityCheckPerformed', manifest.ecmrArticle4Integrity.currentVersionIntegrityCheckPerformed),
      oecmrElement('FinalXmlArchiveReferencePresent', manifest.ecmrArticle4Integrity.finalXmlArchiveReferencePresent),
      oecmrElement('FinalXmlArchiveCurrentVersionReferencePresent', manifest.ecmrArticle4Integrity.finalXmlArchiveCurrentVersionReferencePresent),
      oecmrElement('FinalXmlArchiveDigestPresent', manifest.ecmrArticle4Integrity.finalXmlArchiveDigestPresent),
      oecmrElement('FinalXmlArchiveObjectVersionReferencePresent', manifest.ecmrArticle4Integrity.finalXmlArchiveObjectVersionReferencePresent),
      oecmrElement('SupplementOrAmendmentRecordCount', manifest.ecmrArticle4Integrity.supplementOrAmendmentRecordCount),
      oecmrElement('AppliedAmendmentRecordCount', manifest.ecmrArticle4Integrity.appliedAmendmentRecordCount),
      oecmrElement('AmendmentDetectabilityEvidencePresent', manifest.ecmrArticle4Integrity.amendmentDetectabilityEvidencePresent),
      oecmrElement('OriginalParticularsPreservationEvidencePresent', manifest.ecmrArticle4Integrity.originalParticularsPreservationEvidencePresent),
      oecmrElement('TechnicalEvidenceOnly', technicalEvidenceOnly),
      oecmrElement('LegalDetermination', noLegalDetermination),
    ].join('')) : '',
    oecmrWrap('XmlValidationRecords', xmlValidationRecords?.map((record) =>
      oecmrWrap('XmlValidationRecord', [
        oecmrElement(
          'XmlValidationReferencePresent',
          record.xmlValidationReferencePresent === true || presence(record.xmlValidationId)
        ),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence(record.documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('ValidatedAt', record.validatedAt),
        oecmrElement('ValidationValid', record.valid),
        oecmrElement('ErrorCount', record.errorCount),
        oecmrElement('SchemaProfile', safeManifestLabel(record.schemaProfile, xmlValidationSchemaProfiles)),
        oecmrElement('ValidationContext', safeManifestLabel(record.validationContext, validationContextLabels)),
        oecmrElement('ActorRoleCount', publicOperationalRoleCount(record.actorRoleCount, record.actorRoles)),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CmrBusinessValidationRecords', cmrBusinessValidationRecords?.map((record) =>
      oecmrWrap('CmrBusinessValidationRecord', [
        oecmrElement(
          'CmrBusinessValidationReferencePresent',
          record.cmrBusinessValidationReferencePresent === true || presence(record.cmrBusinessValidationId)
        ),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence(record.documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('FailedAt', record.failedAt),
        oecmrElement('ValidationStatus', safeManifestLabel(record.status, cmrBusinessValidationStatuses)),
        oecmrElement('ValidationContext', safeManifestLabel(record.validationContext, validationContextLabels)),
        oecmrElement('Instrument', safeManifestLabel(record.instrument, cmrBusinessValidationInstruments)),
        oecmrElement('Article', safeManifestLabel(record.article, cmrBusinessValidationArticles)),
        oecmrElement('ErrorCount', record.errorCount),
        oecmrElement('WarningCount', record.warningCount),
        oecmrElement('FindingCount', record.findingCount),
        oecmrElement('ActorRoleCount', publicOperationalRoleCount(record.actorRoleCount, record.actorRoles)),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('EcmrProtocolValidationRecords', ecmrProtocolValidationRecords?.map((record) =>
      oecmrWrap('EcmrProtocolValidationRecord', [
        oecmrElement(
          'EcmrProtocolValidationReferencePresent',
          record.ecmrProtocolValidationReferencePresent === true || presence(record.ecmrProtocolValidationId)
        ),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence(record.documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('FailedAt', record.failedAt),
        oecmrElement('ValidationStatus', safeManifestLabel(record.status, ecmrProtocolValidationStatuses)),
        oecmrElement('ValidationContext', safeManifestLabel(record.validationContext, validationContextLabels)),
        oecmrElement('Instrument', safeManifestLabel(record.instrument, ecmrProtocolValidationInstruments)),
        oecmrElement('Article', safeManifestLabel(record.article, ecmrProtocolValidationArticles)),
        oecmrElement('ErrorCount', record.errorCount),
        oecmrElement('WarningCount', record.warningCount),
        oecmrElement('FindingCount', record.findingCount),
        oecmrElement('ActorRoleCount', publicOperationalRoleCount(record.actorRoleCount, record.actorRoles)),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('ProcedureAcceptances', manifest.procedureAcceptances?.map((acceptance) =>
      oecmrWrap('ProcedureAcceptance', [
        oecmrElement('ProcedureSetReferencePresent', acceptance.procedureSetReferencePresent ?? presence(acceptance.procedureSetId)),
        oecmrElement('ProcedureSetVersion', acceptance.procedureSetVersion),
        oecmrElement('ProcedureDigestAvailable', acceptance.procedureSetDigestAvailable ?? Boolean(acceptance.procedureSetDigest)),
        oecmrElement('AcceptedRoleCount', roleCount(acceptance.acceptedByRoles)),
        oecmrElement('Accepted', acceptance.accepted),
        oecmrElement('AcceptedAt', acceptance.acceptedAt),
        oecmrElement(
          'ContractParticipantReferencePresent',
          acceptance.contractParticipantReferencePresent === true
        ),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('Signatures', manifest.signatureEvidence?.map((signature) =>
      oecmrWrap('SignatureEvidence', [
        oecmrElement('DocumentVersionReferencePresent', presence(signature.documentVersionId)),
        oecmrElement('SignatureRole', safeManifestLabel(signature.signerRole, signatureManifestRoles)),
        oecmrElement('SignatureDigestAvailable', signature.signedDigestAvailable ?? Boolean(signature.signedDigest)),
        oecmrElement('SignatureCertificateAvailable', Boolean((signature.providerMetadata as any)?.certificate || (signature.providerMetadata as any)?.certificateId)),
        oecmrElement('ContractParticipantReferencePresent', signature.contractParticipantReferencePresent === true),
        oecmrElement('DigestAlgorithm', publicDigestAlgorithmLabel(signature.digestAlgorithm)),
        oecmrElement('SignatureMethod', safeManifestLabel(signature.signatureMethod, signatureManifestMethods)),
        oecmrElement('VerificationStatus', safeManifestLabel(signature.verificationStatus, signatureVerificationStatuses)),
        oecmrElement('Timestamp', signature.timestamp),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('SupplementaryDocuments', manifest.supplementaryDocuments?.map((attachment) =>
      oecmrWrap('SupplementaryDocument', [
        oecmrElement(
          'AttachmentEvidenceType',
          safeManifestLabel(attachment.evidenceType || 'supplementary_document_metadata', supplementaryDocumentEvidenceTypes)
        ),
        oecmrElement('MimeType', safeManifestMimeType(attachment.mimeType)),
        oecmrElement('SizeBytes', attachment.sizeBytes),
        oecmrElement(
          'AttachmentDigestAlgorithm',
          publicDigestAlgorithmLabel(attachment.digestAlgorithm, attachment.sha256Digest ? 'SHA-256' : undefined)
        ),
        oecmrElement('AttachmentDigestAvailable', attachment.digestAvailable ?? Boolean(attachment.sha256Digest)),
        oecmrElement('AttachmentPayloadStoredExternally', attachment.payloadStoredExternally ?? Boolean(attachment.storageBucket || attachment.storageKey)),
        oecmrElement('EcmrLinkReferencePresent', attachment.ecmrLinkReferencePresent === true || Boolean(safeSupplementaryDocumentLinkReference(attachment.ecmrLinkReference))),
        oecmrElement('EcmrLinkReference', safeSupplementaryDocumentLinkReference(attachment.ecmrLinkReference)),
        oecmrElement('DocumentVersionReferencePresent', attachment.documentVersionReferencePresent === true || attachment.documentVersionReferenceAvailable === true || presence(attachment.documentVersionId)),
        oecmrElement('AttachmentDocumentVersionNumber', attachment.documentVersionNumber),
        oecmrElement('AttachmentUploaderRoleCount', roleCount(attachment.uploadedByRoles)),
        oecmrElement('UploadedAt', attachment.uploadedAt),
        oecmrElement(
          'DocumentType',
          safeTokenRecordType(attachment.documentType) ??
            (typeof attachment.documentType === 'string' && attachment.documentType.trim().length > 0 ? 'other' : undefined)
        ),
        oecmrElement('AttachmentDescriptionPresent', presence(attachment.description)),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CarrierReservations', manifest.carrierReservations?.map((reservation) =>
      oecmrWrap('CarrierReservation', [
        oecmrElement('CarrierReservationCategory', safeManifestLabel(reservation.category, carrierReservationCategories)),
        oecmrElement('CarrierReservationGroundsPresent', presence(reservation.grounds)),
        oecmrElement('CarrierReservationPackageReferencePresent', presence(reservation.packageReference)),
        oecmrElement('CarrierReservationObservedValuePresent', presence(reservation.observedValue)),
        oecmrElement('CarrierReservationExpectedValuePresent', presence(reservation.expectedValue)),
        oecmrElement('CarrierReservationActorRoleCount', roleCount(reservation.createdByRoles)),
        oecmrElement('ReservationCreatedAt', reservation.createdAt),
        oecmrElement('DocumentVersionReferencePresent', reservation.documentVersionReferencePresent === true || presence((reservation as { documentVersionId?: string }).documentVersionId)),
        oecmrElement('ReservationDocumentVersionNumber', reservation.documentVersionNumber),
        oecmrElement('StatusAtReservation', safeManifestLabel(reservation.statusAtReservation, documentLifecycleStatuses)),
      ].join(''))
    ).join('') ?? ''),
    article9PresumptionApplies ? oecmrWrap('CmrArticle9Presumption', [
      oecmrElement('PresumptionApplies', true),
      oecmrElement('Basis', 'accepted_carrier_takeover_check_without_specific_reservations'),
      oecmrElement('SourceInstrument', 'CMR_1956'),
      oecmrElement('SourceArticle', '9.2'),
      oecmrElement('DocumentVersionReferencePresent', Boolean(version?.versionId || version?.versionNumber !== undefined)),
      oecmrElement('PresumptionDocumentVersionNumber', version?.versionNumber),
      oecmrElement('CarrierTakeoverCheckAccepted', true),
      oecmrElement('PackageCountAndMarksCorrespondencePresumed', true),
      oecmrElement('GoodsAndPackagingGoodConditionPresumed', true),
      oecmrElement('SpecificCarrierReservationCount', 0),
    ].join('')) : '',
    article10PackagingReservations.length > 0 ? oecmrWrap('CmrArticle10PackagingReservations', [
      oecmrElement('PackagingReservationEvidencePresent', true),
      oecmrElement('Basis', 'carrier_reservation_concerning_packaging_defect'),
      oecmrElement('SourceInstrument', 'CMR_1956'),
      oecmrElement('SourceArticle', '10'),
      oecmrElement('PackagingReservationCount', article10PackagingReservations.length),
      oecmrElement('CurrentVersionLinkedReservationCount', article10CurrentVersionLinkedCount),
      oecmrElement('AmbiguousReservationCount', article10PackagingReservations.length - article10CurrentVersionLinkedCount),
      oecmrElement('DocumentVersionReferencePresent', Boolean(version?.versionId || version?.versionNumber !== undefined)),
      oecmrElement('PackagingReservationDocumentVersionNumber', version?.versionNumber),
      oecmrElement('LiabilityDetermination', noLiabilityDetermination),
    ].join('')) : '',
    article11CustomsDocuments.length > 0 ? oecmrWrap('CmrArticle11CustomsDocuments', [
      oecmrElement('DocumentsAvailable', true),
      oecmrElement('SourceInstrument', 'CMR_1956'),
      oecmrElement('SourceArticle', '11.1'),
      oecmrElement('CustomsFormalitiesDocumentCount', article11CustomsDocuments.length),
      oecmrElement('CurrentVersionLinkedDocumentCount', article11CustomsDocuments.length),
      oecmrElement('DocumentVersionReferencePresent', Boolean(version?.versionId || version?.versionNumber !== undefined)),
      oecmrElement('CustomsDocumentsDocumentVersionNumber', version?.versionNumber),
      oecmrElement('DigestEvidenceAvailable', true),
      oecmrElement('PayloadStoredExternally', true),
      oecmrElement('DocumentTypeCount', article11DocumentTypes.length),
      oecmrWrap('DocumentTypes', article11DocumentTypes.map((documentType) =>
        oecmrElement('DocumentType', documentType)
      ).join('')),
    ].join('')) : '',
    oecmrWrap('DisposalInstructions', disposalInstructionRecords?.map((instruction) =>
      oecmrWrap('DisposalInstruction', [
        oecmrElement('InstructionReferencePresent', instruction.instructionReferencePresent === true || presence(instruction.instructionId)),
        oecmrElement('InstructionType', safeManifestLabel(instruction.instructionType, disposalInstructionTypes)),
        oecmrElement('InstructionStatus', safeManifestLabel(instruction.instructionStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', instruction.recordedAt),
        oecmrElement('RequestedExecutionAt', instruction.requestedExecutionAt),
        oecmrElement('DocumentVersionReferencePresent', instruction.documentVersionReferencePresent === true || presence((instruction as { documentVersionId?: string }).documentVersionId)),
        oecmrElement('InstructionDocumentVersionNumber', instruction.documentVersionNumber),
        oecmrElement('ConsignmentNoteCopyReferencePresent', instruction.consignmentNoteCopyReferencePresent),
        oecmrElement('StatusAtInstruction', safeManifestLabel(instruction.statusAtInstruction, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(instruction)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', '12'),
        oecmrElement('RightOfDisposalNotCeased', instruction.rightOfDisposalNotCeased),
        oecmrElement('FirstCopyInstructionProduced', instruction.firstCopyInstructionProduced),
        oecmrElement('CarrierIndemnityAccepted', instruction.carrierIndemnityAccepted),
        oecmrElement('InstructionFeasible', instruction.instructionFeasible),
        oecmrElement('DoesNotPrejudiceOtherConsignments', instruction.doesNotPrejudiceOtherConsignments),
        oecmrElement('DoesNotDivideConsignment', instruction.doesNotDivideConsignment),
        oecmrElement('SuccessiveConsigneeInstructionFinal', instruction.successiveConsigneeInstructionFinal),
        oecmrElement('ReasonPresent', instruction.reasonPresent),
        oecmrElement('NewDeliveryPlacePresent', instruction.newDeliveryPlacePresent),
        oecmrElement('NewConsigneePresent', instruction.newConsigneePresent),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('DisposalExecutionNotices', disposalExecutionNoticeRecords?.map((notice) =>
      oecmrWrap('DisposalExecutionNotice', [
        oecmrElement('NoticeReferencePresent', notice.noticeReferencePresent === true || presence(notice.noticeId)),
        oecmrElement('NoticeType', safeManifestLabel(notice.noticeType, disposalExecutionNoticeTypes)),
        oecmrElement('NoticeStatus', safeManifestLabel(notice.noticeStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', notice.recordedAt),
        oecmrElement('NotificationSentAt', notice.notificationSentAt),
        oecmrElement('DocumentVersionReferencePresent', notice.documentVersionReferencePresent === true || presence((notice as { documentVersionId?: string }).documentVersionId)),
        oecmrElement('NoticeDocumentVersionNumber', notice.documentVersionNumber),
        oecmrElement('StatusAtNotice', safeManifestLabel(notice.statusAtNotice, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(notice)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', '12.6'),
        oecmrElement('InstructionType', safeManifestLabel(notice.instructionType, disposalInstructionTypes)),
        oecmrElement('InstructionReferencePresent', notice.instructionReferencePresent),
        oecmrElement('UnableToExecute', notice.unableToExecute),
        oecmrElement('NotificationSent', notice.notificationSent),
        oecmrElement('NotificationTargetRole', safeManifestLabel(notice.notificationTargetRole, disposalExecutionNoticeTargetRoles)),
        oecmrElement('ReasonPresent', notice.reasonPresent),
        oecmrElement('NotificationDetailPresent', notice.notificationDetailPresent),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CarriageImpediments', carriageImpedimentRecords?.map((impediment) =>
      oecmrWrap('CarriageImpediment', [
        oecmrElement('ImpedimentReferencePresent', impediment.impedimentReferencePresent === true || presence(impediment.impedimentId)),
        oecmrElement('ImpedimentType', safeManifestLabel(impediment.impedimentType, carriageImpedimentTypes)),
        oecmrElement('ImpedimentStatus', safeManifestLabel(impediment.impedimentStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', impediment.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', impediment.documentVersionReferencePresent === true || presence((impediment as { documentVersionId?: string }).documentVersionId)),
        oecmrElement('ImpedimentDocumentVersionNumber', impediment.documentVersionNumber),
        oecmrElement('StatusAtImpediment', safeManifestLabel(impediment.statusAtImpediment, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(impediment)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(impediment.sourceArticle, carriageImpedimentSourceArticles)),
        oecmrElement('InstructionsRequested', impediment.instructionsRequested),
        oecmrElement('InstructionRequestTargetRole', safeManifestLabel(impediment.instructionRequestTargetRole, carriageImpedimentInstructionTargetRoles)),
        oecmrElement('InstructionRequestSentAtPresent', impediment.instructionRequestSentAtPresent),
        oecmrElement('InstructionRequestReceivedAtPresent', impediment.instructionRequestReceivedAtPresent),
        oecmrElement('InstructionRequestEvidenceAttachmentReferencePresent', impediment.instructionRequestEvidenceAttachmentReferencePresent),
        oecmrElement('InstructionRequestChronologyValid', impediment.instructionRequestChronologyValid),
        oecmrElement('InstructionsUnavailableInReasonableTime', impediment.instructionsUnavailableInReasonableTime),
        oecmrElement('BestInterestsActionTaken', impediment.bestInterestsActionTaken),
        oecmrElement('ArrivalAtDeliveryPlace', impediment.arrivalAtDeliveryPlace),
        oecmrElement('ConsigneeRefusedGoods', impediment.consigneeRefusedGoods),
        oecmrElement('Article14Or15ImpedimentBasis', impediment.article14Or15ImpedimentBasis),
        oecmrElement('GoodsUnloadedForAccount', impediment.goodsUnloadedForAccount),
        oecmrElement('GoodsHeldForEntitledPerson', impediment.goodsHeldForEntitledPerson),
        oecmrElement('ThirdPartyCustodianUsed', impediment.thirdPartyCustodianUsed),
        oecmrElement('ThirdPartyCustodianSelectedWithReasonableCare', impediment.thirdPartyCustodianSelectedWithReasonableCare),
        oecmrElement('SaleReason', safeManifestLabel(impediment.saleReason, carriageImpedimentSaleReasons)),
        oecmrElement('SaleProcedureBasis', safeManifestLabel(impediment.saleProcedureBasis, carriageImpedimentSaleProcedureBases)),
        oecmrElement('GoodsLocationCountryCodePresent', impediment.goodsLocationCountryCodePresent),
        oecmrElement('GoodsLocationDetailPresent', impediment.goodsLocationDetailPresent),
        oecmrElement('SaleProcedureReferencePresent', impediment.saleProcedureReferencePresent),
        oecmrElement('SaleProcedureAssessedAtPresent', impediment.saleProcedureAssessedAtPresent),
        oecmrElement('SaleProcedureEvidenceAttachmentReferencePresent', impediment.saleProcedureEvidenceAttachmentReferencePresent),
        oecmrElement('SaleProcedureChronologyValid', impediment.saleProcedureChronologyValid),
        oecmrElement('ProceedsPlacedAtDisposal', impediment.proceedsPlacedAtDisposal),
        oecmrElement('SaleProcedureLawOrCustomAcknowledged', impediment.saleProcedureLawOrCustomAcknowledged),
        oecmrElement('ExpensesRecoverableClaimed', impediment.expensesRecoverableClaimed),
        oecmrElement('ReasonPresent', impediment.reasonPresent),
        oecmrElement('InstructionRequestDetailPresent', impediment.instructionRequestDetailPresent),
        oecmrElement('AlternativeCarriageConditionsPresent', impediment.alternativeCarriageConditionsPresent),
        oecmrElement('StorageOrSaleDetailPresent', impediment.storageOrSaleDetailPresent),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CarriageAccountingRecords', carriageAccountingRecords?.map((record) =>
      oecmrWrap('CarriageAccountingRecord', [
        oecmrElement('AccountingType', safeManifestLabel(record.accountingType, carriageAccountingTypes)),
        oecmrElement('AccountingStatus', safeManifestLabel(record.accountingStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement('AccountingAtPresent', record.accountingAtPresent),
        oecmrElement('AccountingEvidenceAttachmentReferencePresent', record.accountingEvidenceAttachmentReferencePresent),
        oecmrElement('AccountingChronologyValid', record.accountingChronologyValid),
        oecmrElement('InstructionRequestEvidencePresent', record.instructionRequestEvidencePresent),
        oecmrElement('GrossSaleProceedsAmountPresent', record.grossSaleProceedsAmountPresent),
        oecmrElement('DeductedSaleExpensesAmountPresent', record.deductedSaleExpensesAmountPresent),
        oecmrElement('NetSaleProceedsReconciled', record.netSaleProceedsReconciled),
        oecmrElement('SaleProceedsAccountingReferencePresent', record.saleProceedsAccountingReferencePresent),
        oecmrElement('TotalChargesDueAmountPresent', record.totalChargesDueAmountPresent),
        oecmrElement('ChargeShortfallReconciled', record.chargeShortfallReconciled),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)),
        oecmrElement('AccountingDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtAccounting', safeManifestLabel(record.statusAtAccounting, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, carriageAccountingSourceArticles)),
        oecmrElement('LinkedImpedimentType', safeManifestLabel(record.linkedImpedimentType, carriageImpedimentTypes)),
        oecmrElement('ImpedimentReferencePresent', record.impedimentReferencePresent),
        oecmrElement('AmountPresent', record.amountPresent),
        oecmrElement('PositiveAmount', record.positiveAmount),
        oecmrElement('CurrencyPresent', record.currencyPresent),
        oecmrElement('RecognizedCurrency', record.recognizedCurrency),
        oecmrElement('CarrierWrongfulActOrNeglectNotAsserted', record.carrierWrongfulActOrNeglectNotAsserted),
        oecmrElement('ChargeableAgainstGoods', record.chargeableAgainstGoods),
        oecmrElement('ProceedsPlacedAtDisposal', record.proceedsPlacedAtDisposal),
        oecmrElement('ChargesExceedProceeds', record.chargesExceedProceeds),
        oecmrElement('AccountingDetailPresent', record.accountingDetailPresent),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CarrierLiabilityIncidentRecords', carrierLiabilityIncidentRecords?.map((record) =>
      oecmrWrap('CarrierLiabilityIncidentRecord', [
        oecmrElement('IncidentType', safeManifestLabel(record.incidentType, carrierLiabilityIncidentTypes)),
        oecmrElement('IncidentStatus', safeManifestLabel(record.incidentStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('IncidentDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtIncident', safeManifestLabel(record.statusAtIncident, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, carrierLiabilityIncidentSourceArticles)),
        oecmrElement('OccurredBetweenTakeoverAndDelivery', record.occurredBetweenTakeoverAndDelivery),
        oecmrElement('LossObserved', record.lossObserved),
        oecmrElement('DamageObserved', record.damageObserved),
        oecmrElement('DelayObserved', record.delayObserved),
        oecmrElement('ClaimantFaultOrNeglectAlleged', record.claimantFaultOrNeglectAlleged),
        oecmrElement('ClaimantInstructionAlleged', record.claimantInstructionAlleged),
        oecmrElement('InherentViceAlleged', record.inherentViceAlleged),
        oecmrElement('UnavoidableCircumstancesAlleged', record.unavoidableCircumstancesAlleged),
        oecmrElement('SpecialRiskClaimed', record.specialRiskClaimed),
        oecmrElement('SpecialRiskType', safeManifestLabel(record.specialRiskType, carrierLiabilityIncidentSpecialRisks)),
        oecmrElement('SpecialRiskPresumptionClaimed', record.specialRiskPresumptionClaimed),
        oecmrElement('TemperatureControlledVehicle', record.temperatureControlledVehicle),
        oecmrElement('EquipmentStepsEvidencePresent', record.equipmentStepsEvidencePresent),
        oecmrElement('LivestockCareStepsEvidencePresent', record.livestockCareStepsEvidencePresent),
        oecmrElement('DisposalInstructionReferencePresent', record.disposalInstructionReferencePresent),
        oecmrElement('InstructionNotCarriedOut', record.instructionNotCarriedOut),
        oecmrElement('InstructionCarriedOutWithoutFirstCopy', record.instructionCarriedOutWithoutFirstCopy),
        oecmrElement('ClaimantEntitlementAsserted', record.claimantEntitlementAsserted),
        oecmrElement('LossOrDamageCaused', record.lossOrDamageCaused),
        oecmrElement('TakingOverAtPresent', record.takingOverAtPresent),
        oecmrElement('IncidentAtPresent', record.incidentAtPresent),
        oecmrElement('DeliveryAtPresent', record.deliveryAtPresent),
        oecmrElement('IncidentEvidenceAttachmentReferencePresent', record.incidentEvidenceAttachmentReferencePresent),
        oecmrElement('CarrierProofRequired', record.carrierProofRequired),
        oecmrElement(
          'CarrierProofEvidenceAttachmentReferencePresent',
          record.carrierProofEvidenceAttachmentReferencePresent
        ),
        oecmrElement('IncidentChronologyValid', record.incidentChronologyValid),
        oecmrElement(
          'Article17_4aVehicleUseAssessmentRecorded',
          record.article17_4aVehicleUseAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4aOpenUnsheetedVehicleUseReported',
          record.article17_4aOpenUnsheetedVehicleUseReported
        ),
        oecmrElement(
          'Article17_4aAssessmentDetailPresent',
          record.article17_4aAssessmentDetailPresent
        ),
        oecmrElement(
          'Article17_4aEvidenceAttachmentReferencePresent',
          record.article17_4aEvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'OpenUnsheetedVehicleAgreementSpecifiedInConsignmentNote',
          record.openUnsheetedVehicleAgreementSpecifiedInConsignmentNote
        ),
        oecmrElement(
          'Article18OpenVehiclePresumptionExceptionAssessed',
          record.article18OpenVehiclePresumptionExceptionAssessed
        ),
        oecmrElement(
          'Article18OpenVehiclePresumptionExceptionAbsent',
          record.article18OpenVehiclePresumptionExceptionAbsent
        ),
        oecmrElement(
          'Article17_3NonRelievingFactorsAssessed',
          record.article17_3NonRelievingFactorsAssessed
        ),
        oecmrElement(
          'Article17_3NonRelievingFactorReported',
          record.article17_3NonRelievingFactorReported
        ),
        oecmrElement(
          'Article17_3AssessmentDetailPresent',
          record.article17_3AssessmentDetailPresent
        ),
        oecmrElement(
          'Article17_3EvidenceAttachmentReferencePresent',
          record.article17_3EvidenceAttachmentReferencePresent
        ),
        oecmrElement('Article18_1BurdenAssessmentRecorded', record.article18_1BurdenAssessmentRecorded),
        oecmrElement('Article18_1CauseProofReported', record.article18_1CauseProofReported),
        oecmrElement('Article18_1AssessmentDetailPresent', record.article18_1AssessmentDetailPresent),
        oecmrElement(
          'Article18_1EvidenceAttachmentReferencePresent',
          record.article18_1EvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article18_2CircumstancesAssessmentRecorded',
          record.article18_2CircumstancesAssessmentRecorded
        ),
        oecmrElement(
          'Article18_2CircumstancesEstablishedReported',
          record.article18_2CircumstancesEstablishedReported
        ),
        oecmrElement('Article18_2AssessmentDetailPresent', record.article18_2AssessmentDetailPresent),
        oecmrElement(
          'Article18_2EvidenceAttachmentReferencePresent',
          record.article18_2EvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article17_4bPackingConditionAssessmentRecorded',
          record.article17_4bPackingConditionAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4bPackingAbsentOrDefectiveReported',
          record.article17_4bPackingAbsentOrDefectiveReported
        ),
        oecmrElement(
          'Article17_4bGoodsLiabilityWithoutProperPackingReported',
          record.article17_4bGoodsLiabilityWithoutProperPackingReported
        ),
        oecmrElement(
          'Article17_4bAssessmentDetailPresent',
          record.article17_4bAssessmentDetailPresent
        ),
        oecmrElement(
          'Article17_4bEvidenceAttachmentReferencePresent',
          record.article17_4bEvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article17_4cOperationAssessmentRecorded',
          record.article17_4cOperationAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4cQualifyingOperationReported',
          record.article17_4cQualifyingOperationReported
        ),
        oecmrElement(
          'Article17_4cActorCapacityAssessmentRecorded',
          record.article17_4cActorCapacityAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4cQualifyingActorCapacityReported',
          record.article17_4cQualifyingActorCapacityReported
        ),
        oecmrElement(
          'Article17_4cAssessmentDetailPresent',
          record.article17_4cAssessmentDetailPresent
        ),
        oecmrElement(
          'Article17_4cEvidenceAttachmentReferencePresent',
          record.article17_4cEvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article17_4dNatureAssessmentRecorded',
          record.article17_4dNatureAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4dParticularExposureReported',
          record.article17_4dParticularExposureReported
        ),
        oecmrElement(
          'Article17_4dSusceptibilityBasisAssessmentRecorded',
          record.article17_4dSusceptibilityBasisAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4dQualifyingSusceptibilityBasisReported',
          record.article17_4dQualifyingSusceptibilityBasisReported
        ),
        oecmrElement(
          'Article17_4dAssessmentDetailPresent',
          record.article17_4dAssessmentDetailPresent
        ),
        oecmrElement(
          'Article17_4dEvidenceAttachmentReferencePresent',
          record.article17_4dEvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article17_4eMarkingAssessmentRecorded',
          record.article17_4eMarkingAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4eQualifyingMarkingConditionReported',
          record.article17_4eQualifyingMarkingConditionReported
        ),
        oecmrElement(
          'Article17_4eDeficiencyBasisAssessmentRecorded',
          record.article17_4eDeficiencyBasisAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4eQualifyingDeficiencyBasisReported',
          record.article17_4eQualifyingDeficiencyBasisReported
        ),
        oecmrElement(
          'Article17_4eAssessmentDetailPresent',
          record.article17_4eAssessmentDetailPresent
        ),
        oecmrElement(
          'Article17_4eEvidenceAttachmentReferencePresent',
          record.article17_4eEvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article17_4fLivestockAssessmentRecorded',
          record.article17_4fLivestockAssessmentRecorded
        ),
        oecmrElement(
          'Article17_4fLivestockCarriageReported',
          record.article17_4fLivestockCarriageReported
        ),
        oecmrElement(
          'Article17_4fAssessmentDetailPresent',
          record.article17_4fAssessmentDetailPresent
        ),
        oecmrElement(
          'Article17_4fEvidenceAttachmentReferencePresent',
          record.article17_4fEvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article18_4EquipmentStepsAssessmentRecorded',
          record.article18_4EquipmentStepsAssessmentRecorded
        ),
        oecmrElement(
          'Article18_4ChoiceMaintenanceUseStepsReported',
          record.article18_4ChoiceMaintenanceUseStepsReported
        ),
        oecmrElement(
          'Article18_4SpecialInstructionsAssessmentRecorded',
          record.article18_4SpecialInstructionsAssessmentRecorded
        ),
        oecmrElement(
          'Article18_4SpecialInstructionsConsistencyValid',
          record.article18_4SpecialInstructionsConsistencyValid
        ),
        oecmrElement('Article18_4AssessmentDetailPresent', record.article18_4AssessmentDetailPresent),
        oecmrElement(
          'Article18_4EvidenceAttachmentReferencePresent',
          record.article18_4EvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article18_5NormallyIncumbentStepsAssessmentRecorded',
          record.article18_5NormallyIncumbentStepsAssessmentRecorded
        ),
        oecmrElement(
          'Article18_5NormallyIncumbentStepsReported',
          record.article18_5NormallyIncumbentStepsReported
        ),
        oecmrElement(
          'Article18_5SpecialInstructionsAssessmentRecorded',
          record.article18_5SpecialInstructionsAssessmentRecorded
        ),
        oecmrElement(
          'Article18_5SpecialInstructionsConsistencyValid',
          record.article18_5SpecialInstructionsConsistencyValid
        ),
        oecmrElement('Article18_5AssessmentDetailPresent', record.article18_5AssessmentDetailPresent),
        oecmrElement(
          'Article18_5EvidenceAttachmentReferencePresent',
          record.article18_5EvidenceAttachmentReferencePresent
        ),
        oecmrElement('Article17_5MixedCausationAssessed', record.article17_5MixedCausationAssessed),
        oecmrElement('Article17_5MixedCausationReported', record.article17_5MixedCausationReported),
        oecmrElement('Article17_5ContributionSharesPresent', record.article17_5ContributionSharesPresent),
        oecmrElement(
          'Article17_5ContributionSharesReconciled',
          record.article17_5ContributionSharesReconciled
        ),
        oecmrElement('Article17_5AssessmentDetailPresent', record.article17_5AssessmentDetailPresent),
        oecmrElement(
          'Article17_5EvidenceAttachmentReferencePresent',
          record.article17_5EvidenceAttachmentReferencePresent
        ),
        oecmrElement('IncidentDetailPresent', record.incidentDetailPresent),
        oecmrElement('ClaimantStatementDetailPresent', record.claimantStatementDetailPresent),
        oecmrElement('CarrierProofDetailPresent', record.carrierProofDetailPresent),
        oecmrElement('LiabilityDetermination', noLiabilityDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('Article18_2ClaimantRebuttalRecords', article18_2ClaimantRebuttalRecords?.map((record) =>
      oecmrWrap('Article18_2ClaimantRebuttalRecord', [
        oecmrElement('RebuttalStatus', safeManifestLabel(record.rebuttalStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true
            || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('RebuttalDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRebuttal', safeManifestLabel(record.statusAtRebuttal, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement(
          'SourceArticle',
          safeManifestLabel(record.sourceArticle, article18_2ClaimantRebuttalSourceArticles)
        ),
        oecmrElement(
          'CarrierSpecialRiskPresumptionReferencePresent',
          record.carrierSpecialRiskPresumptionReferencePresent
        ),
        oecmrElement('ClaimantRoleAttributed', record.claimantRoleAttributed),
        oecmrElement(
          'LossOrDamageAttributionDisputeRecorded',
          record.lossOrDamageAttributionDisputeRecorded
        ),
        oecmrElement(
          'ClaimantCounterEvidenceSubmissionRecorded',
          record.claimantCounterEvidenceSubmissionRecorded
        ),
        oecmrElement('CounterEvidenceDetailPresent', record.counterEvidenceDetailPresent),
        oecmrElement(
          'CounterEvidenceAttachmentReferencePresent',
          record.counterEvidenceAttachmentReferencePresent
        ),
        oecmrElement('LegalDetermination', noLiabilityDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('DeliveryTimingAssessmentRecords', deliveryTimingAssessmentRecords?.map((record) =>
      oecmrWrap('DeliveryTimingAssessmentRecord', [
        oecmrElement('AssessmentType', safeManifestLabel(record.assessmentType, deliveryTimingAssessmentTypes)),
        oecmrElement('AssessmentStatus', safeManifestLabel(record.assessmentStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('AssessmentDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtAssessment', safeManifestLabel(record.statusAtAssessment, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, deliveryTimingAssessmentSourceArticles)),
        oecmrElement('GoodsNotDelivered', record.goodsNotDelivered),
        oecmrElement('AgreedTimeLimitPresent', record.agreedTimeLimitPresent),
        oecmrElement('AgreedTimeLimitExpired', record.agreedTimeLimitExpired),
        oecmrElement('AgreedTimeLimitConsignmentNoteReferencePresent', record.agreedTimeLimitConsignmentNoteReferencePresent),
        oecmrElement('Article19AgreedTimeLimitComputed', record.article19AgreedTimeLimitComputed),
        oecmrElement('Article19AgreedTimeLimitExceeded', record.article19AgreedTimeLimitExceeded),
        oecmrElement('Article19ReasonableDurationComputed', record.article19ReasonableDurationComputed),
        oecmrElement('Article19ElapsedDurationOrderValid', record.article19ElapsedDurationOrderValid),
        oecmrElement('DiligentCarrierAllowedDurationPresent', record.diligentCarrierAllowedDurationPresent),
        oecmrElement('DiligentCarrierAllowedDurationPositive', record.diligentCarrierAllowedDurationPositive),
        oecmrElement('ReasonableDurationExceeded', record.reasonableDurationExceeded),
        oecmrElement('PartialLoadAssemblyDurationPresent', record.partialLoadAssemblyDurationPresent),
        oecmrElement('PartialLoadAssemblyDurationPositive', record.partialLoadAssemblyDurationPositive),
        oecmrElement('PartialLoadAssemblyTimeConsidered', record.partialLoadAssemblyTimeConsidered),
        oecmrElement('DiligentCarrierDurationExceeded', record.diligentCarrierDurationExceeded),
        oecmrElement('ThirtyDaysAfterAgreedLimitExpired', record.thirtyDaysAfterAgreedLimitExpired),
        oecmrElement('SixtyDaysAfterTakeoverExpired', record.sixtyDaysAfterTakeoverExpired),
        oecmrElement('Article20LossPresumptionPeriodComputed', record.article20LossPresumptionPeriodComputed),
        oecmrElement('Article20LossPresumptionPeriodSatisfied', record.article20LossPresumptionPeriodSatisfied),
        oecmrElement('Article20LossPresumptionAssessmentReferencePresent', record.article20LossPresumptionAssessmentReferencePresent),
        oecmrElement('Article20CompensationAssessmentReferencePresent', record.article20CompensationAssessmentReferencePresent),
        oecmrElement('Article20CompensationPaymentReferencePresent', record.article20CompensationPaymentReferencePresent),
        oecmrElement('CompensationReceived', record.compensationReceived),
        oecmrElement('RecoveryNotificationRequested', record.recoveryNotificationRequested),
        oecmrElement('WrittenAcknowledgementIssued', record.writtenAcknowledgementIssued),
        oecmrElement('GoodsRecovered', record.goodsRecovered),
        oecmrElement('RecoveryNotificationSent', record.recoveryNotificationSent),
        oecmrElement('DeliveryRequested', record.deliveryRequested),
        oecmrElement('Article20DeliveryRequestWindowComputed', record.article20DeliveryRequestWindowComputed),
        oecmrElement('Article20DeliveryRequestChronologyValid', record.article20DeliveryRequestChronologyValid),
        oecmrElement('Article20DeliveryRequestWithinThirtyDays', record.article20DeliveryRequestWithinThirtyDays),
        oecmrElement('ChargesAndRefundConditionAcknowledged', record.chargesAndRefundConditionAcknowledged),
        oecmrElement('TakingOverAtPresent', record.takingOverAtPresent),
        oecmrElement('AgreedTimeLimitAtPresent', record.agreedTimeLimitAtPresent),
        oecmrElement('ActualDeliveryAtPresent', record.actualDeliveryAtPresent),
        oecmrElement('AssessmentAtPresent', record.assessmentAtPresent),
        oecmrElement('RecoveryNotificationAtPresent', record.recoveryNotificationAtPresent),
        oecmrElement('RecoveryNotificationReceivedAtPresent', record.recoveryNotificationReceivedAtPresent),
        oecmrElement('DeliveryRequestAtPresent', record.deliveryRequestAtPresent),
        oecmrElement('TimingDetailPresent', record.timingDetailPresent),
        oecmrElement('ClaimDetailPresent', record.claimDetailPresent),
        oecmrElement('RecoveryDetailPresent', record.recoveryDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CompensationPaymentRecords', compensationPaymentRecords?.map((record) =>
      oecmrWrap('CompensationPaymentRecord', [
        oecmrElement('PaymentType', safeManifestLabel(record.paymentType, compensationPaymentTypes)),
        oecmrElement('PaymentStatus', safeManifestLabel(record.paymentStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('PaymentDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtPayment', safeManifestLabel(record.statusAtPayment, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, compensationPaymentSourceArticles)),
        oecmrElement('PaymentEvidenceType', safeManifestLabel(record.paymentEvidenceType, compensationPaymentEvidenceTypes)),
        oecmrElement('LossPresumptionAssessmentReferencePresent', record.lossPresumptionAssessmentReferencePresent),
        oecmrElement('CompensationAssessmentReferencePresent', record.compensationAssessmentReferencePresent),
        oecmrElement('PaymentEvidenceAttachmentReferencePresent', record.paymentEvidenceAttachmentReferencePresent),
        oecmrElement('CompensationForMissingGoods', record.compensationForMissingGoods),
        oecmrElement('PaymentSettled', record.paymentSettled),
        oecmrElement('RecipientAcknowledgedReceipt', record.recipientAcknowledgedReceipt),
        oecmrElement('PaymentAmountPresent', record.paymentAmountPresent),
        oecmrElement('PaymentAmountPositive', record.paymentAmountPositive),
        oecmrElement('PaymentCurrencyPresent', record.paymentCurrencyPresent),
        oecmrElement('PaymentCurrencyRecognized', record.paymentCurrencyRecognized),
        oecmrElement('PaidAtPresent', record.paidAtPresent),
        oecmrElement('PaymentDetailPresent', record.paymentDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('RecoveryDeliverySettlementRecords', recoveryDeliverySettlementRecords?.map((record) =>
      oecmrWrap('RecoveryDeliverySettlementRecord', [
        oecmrElement('SettlementType', safeManifestLabel(record.settlementType, recoveryDeliverySettlementTypes)),
        oecmrElement('SettlementStatus', safeManifestLabel(record.settlementStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('SettlementDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtSettlement', safeManifestLabel(record.statusAtSettlement, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, recoveryDeliverySettlementSourceArticles)),
        oecmrElement('RecoveryAssessmentReferencePresent', record.recoveryAssessmentReferencePresent),
        oecmrElement('CompensationPaymentReferencePresent', record.compensationPaymentReferencePresent),
        oecmrElement('DeliveryReferencePresent', record.deliveryReferencePresent),
        oecmrElement('ChargesPaymentEvidenceAttachmentReferencePresent', record.chargesPaymentEvidenceAttachmentReferencePresent),
        oecmrElement('CompensationRefundEvidenceAttachmentReferencePresent', record.compensationRefundEvidenceAttachmentReferencePresent),
        oecmrElement('ConsignmentNoteChargesComputed', record.consignmentNoteChargesComputed),
        oecmrElement('ConsignmentNoteChargesMatched', record.consignmentNoteChargesMatched),
        oecmrElement('ChargesPaymentLineCount', record.chargesPaymentLineCount),
        oecmrElement('ChargesPaymentAmountsPositive', record.chargesPaymentAmountsPositive),
        oecmrElement('ChargesPaymentCurrenciesRecognized', record.chargesPaymentCurrenciesRecognized),
        oecmrElement('ChargesPaymentSettled', record.chargesPaymentSettled),
        oecmrElement('ChargesPaidAtPresent', record.chargesPaidAtPresent),
        oecmrElement('CompensationRefundAmountPresent', record.compensationRefundAmountPresent),
        oecmrElement('CompensationRefundAmountNonNegative', record.compensationRefundAmountNonNegative),
        oecmrElement('CompensationRefundCurrencyPresent', record.compensationRefundCurrencyPresent),
        oecmrElement('CompensationRefundCurrencyRecognized', record.compensationRefundCurrencyRecognized),
        oecmrElement('ChargesIncludedInCompensationAmountPresent', record.chargesIncludedInCompensationAmountPresent),
        oecmrElement('ChargesIncludedInCompensationAmountNonNegative', record.chargesIncludedInCompensationAmountNonNegative),
        oecmrElement('CompensationRefundFormulaComputed', record.compensationRefundFormulaComputed),
        oecmrElement('CompensationRefundFormulaMatched', record.compensationRefundFormulaMatched),
        oecmrElement('CompensationRefundSettled', record.compensationRefundSettled),
        oecmrElement('CompensationRefundedAtPresent', record.compensationRefundedAtPresent),
        oecmrElement('DeliveryChronologyComputed', record.deliveryChronologyComputed),
        oecmrElement('DeliveryChronologyValid', record.deliveryChronologyValid),
        oecmrElement('DelayClaimsRightsPreserved', record.delayClaimsRightsPreserved),
        oecmrElement('SettlementDetailPresent', record.settlementDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('RecoveredGoodsDispositionRecords', recoveredGoodsDispositionRecords?.map((record) =>
      oecmrWrap('RecoveredGoodsDispositionRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, recoveredGoodsDispositionTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, recoveredGoodsDispositionSourceArticles)),
        oecmrElement('DispositionBasis', safeManifestLabel(record.dispositionBasis, recoveredGoodsDispositionBases)),
        oecmrElement('DispositionActionType', safeManifestLabel(record.dispositionActionType, recoveredGoodsDispositionActionTypes)),
        oecmrElement('CompensationPaymentReferencePresent', record.compensationPaymentReferencePresent),
        oecmrElement('RecoveryNoticeReferenceRequired', record.recoveryNoticeReferenceRequired),
        oecmrElement('RecoveryNoticeReferencePresent', record.recoveryNoticeReferencePresent),
        oecmrElement('RecoveryEvidenceAttachmentReferencePresent', record.recoveryEvidenceAttachmentReferencePresent),
        oecmrElement('DispositionEvidenceAttachmentReferencePresent', record.dispositionEvidenceAttachmentReferencePresent),
        oecmrElement('GoodsRecovered', record.goodsRecovered),
        oecmrElement('CompensationPaidAtPresent', record.compensationPaidAtPresent),
        oecmrElement('GoodsRecoveredAtPresent', record.goodsRecoveredAtPresent),
        oecmrElement('DispositionAtPresent', record.dispositionAtPresent),
        oecmrElement('PaymentRecoveryDispositionChronologyComputed', record.paymentRecoveryDispositionChronologyComputed),
        oecmrElement('PaymentRecoveryDispositionChronologyValid', record.paymentRecoveryDispositionChronologyValid),
        oecmrElement('WrittenRecoveryNotificationRequestAbsent', record.writtenRecoveryNotificationRequestAbsent),
        oecmrElement('RecoveryNotificationSent', record.recoveryNotificationSent),
        oecmrElement('RecoveryNotificationReceivedAtPresent', record.recoveryNotificationReceivedAtPresent),
        oecmrElement('InstructionsAbsent', record.instructionsAbsent),
        oecmrElement('InstructionsAbsenceAssessedAtPresent', record.instructionsAbsenceAssessedAtPresent),
        oecmrElement('Article20ThirtyDayInstructionPeriodComputed', record.article20ThirtyDayInstructionPeriodComputed),
        oecmrElement('Article20ThirtyDayInstructionPeriodElapsed', record.article20ThirtyDayInstructionPeriodElapsed),
        oecmrElement('Article20OneYearRecoveryPeriodComputed', record.article20OneYearRecoveryPeriodComputed),
        oecmrElement('GoodsRecoveredWithinOneYearOfCompensation', record.goodsRecoveredWithinOneYearOfCompensation),
        oecmrElement('GoodsRecoveredMoreThanOneYearAfterCompensation', record.goodsRecoveredMoreThanOneYearAfterCompensation),
        oecmrElement('GoodsLocationCountryCodePresent', record.goodsLocationCountryCodePresent),
        oecmrElement('GoodsLocationDetailPresent', record.goodsLocationDetailPresent),
        oecmrElement('ApplicableLocalLawIdentified', record.applicableLocalLawIdentified),
        oecmrElement('ApplicableLawDetailPresent', record.applicableLawDetailPresent),
        oecmrElement('DispositionUnderLocalLawRecorded', record.dispositionUnderLocalLawRecorded),
        oecmrElement('DispositionDetailPresent', record.dispositionDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CodDeliveryOmissionRecords', codDeliveryOmissionRecords?.map((record) =>
      oecmrWrap('CodDeliveryOmissionRecord', [
        oecmrElement('OmissionType', safeManifestLabel(record.omissionType, codDeliveryOmissionTypes)),
        oecmrElement('OmissionStatus', safeManifestLabel(record.omissionStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('OmissionDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtOmission', safeManifestLabel(record.statusAtOmission, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, codDeliveryOmissionSourceArticles)),
        oecmrElement('GoodsDeliveredToConsignee', record.goodsDeliveredToConsignee),
        oecmrElement('CodChargeRequiredByContract', record.codChargeRequiredByContract),
        oecmrElement('CodChargeConsignmentNoteReferencePresent', record.codChargeConsignmentNoteReferencePresent),
        oecmrElement('CodChargeUncollected', record.codChargeUncollected),
        oecmrElement('CodAmountPresent', record.codAmountPresent),
        oecmrElement('CodAmountPositive', record.codAmountPositive),
        oecmrElement('CodCurrencyPresent', record.codCurrencyPresent),
        oecmrElement('CodCurrencyRecognized', record.codCurrencyRecognized),
        oecmrElement('ConsignorCompensationClaimed', record.consignorCompensationClaimed),
        oecmrElement('CarrierActionAgainstConsigneeReserved', record.carrierActionAgainstConsigneeReserved),
        oecmrElement('DeliveryAtPresent', record.deliveryAtPresent),
        oecmrElement('OmissionDetailPresent', record.omissionDetailPresent),
        oecmrElement('CompensationClaimDetailPresent', record.compensationClaimDetailPresent),
        oecmrElement('ConsigneeActionDetailPresent', record.consigneeActionDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('DangerousGoodsNoticeRecords', dangerousGoodsNoticeRecords?.map((record) =>
      oecmrWrap('DangerousGoodsNoticeRecord', [
        oecmrElement('NoticeType', safeManifestLabel(record.noticeType, dangerousGoodsNoticeTypes)),
        oecmrElement('NoticeStatus', safeManifestLabel(record.noticeStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('NoticeDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtNotice', safeManifestLabel(record.statusAtNotice, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, dangerousGoodsNoticeSourceArticles)),
        oecmrElement('DangerousGoodsHandedToCarrier', record.dangerousGoodsHandedToCarrier),
        oecmrElement('ExactNatureInformed', record.exactNatureInformed),
        oecmrElement('PrecautionsRequired', record.precautionsRequired),
        oecmrElement('PrecautionsIndicated', record.precautionsIndicated),
        oecmrElement('InformationEnteredInConsignmentNote', record.informationEnteredInConsignmentNote),
        oecmrElement('CarrierKnowledgeProvedByOtherMeans', record.carrierKnowledgeProvedByOtherMeans),
        oecmrElement(
          'DangerousGoodsConsignmentNoteReferencePresent',
          record.dangerousGoodsConsignmentNoteReferencePresent
        ),
        oecmrElement(
          'CarrierKnowledgeProofAttachmentReferencePresent',
          record.carrierKnowledgeProofAttachmentReferencePresent
        ),
        oecmrElement('CarrierDidNotKnowDangerousNature', record.carrierDidNotKnowDangerousNature),
        oecmrElement('CarrierActionType', safeManifestLabel(record.carrierActionType, dangerousGoodsCarrierActionTypes)),
        oecmrElement('CarrierActionTaken', record.carrierActionTaken),
        oecmrElement('WithoutCompensationBasisRecorded', record.withoutCompensationBasisRecorded),
        oecmrElement('SenderExpenseLiabilityClaimed', record.senderExpenseLiabilityClaimed),
        oecmrElement('NoticeAtPresent', record.noticeAtPresent),
        oecmrElement('ActionAtPresent', record.actionAtPresent),
        oecmrElement('DangerNatureDetailPresent', record.dangerNatureDetailPresent),
        oecmrElement('PrecautionsDetailPresent', record.precautionsDetailPresent),
        oecmrElement('CarrierKnowledgeProofDetailPresent', record.carrierKnowledgeProofDetailPresent),
        oecmrElement('CarrierActionDetailPresent', record.carrierActionDetailPresent),
        oecmrElement('ExpenseClaimDetailPresent', record.expenseClaimDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CompensationAssessmentRecords', compensationAssessmentRecords?.map((record) =>
      oecmrWrap('CompensationAssessmentRecord', [
        oecmrElement('AssessmentType', safeManifestLabel(record.assessmentType, compensationAssessmentTypes)),
        oecmrElement('AssessmentStatus', safeManifestLabel(record.assessmentStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('AssessmentDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtAssessment', safeManifestLabel(record.statusAtAssessment, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, compensationAssessmentSourceArticles)),
        oecmrElement('IncidentReferencePresent', record.incidentReferencePresent),
        oecmrElement(
          'TakingOverPlaceAndTimeConsignmentNoteReferencePresent',
          record.takingOverPlaceAndTimeConsignmentNoteReferencePresent
        ),
        oecmrElement(
          'ValuationEvidenceAttachmentReferencePresent',
          record.valuationEvidenceAttachmentReferencePresent
        ),
        oecmrElement(
          'Article23LossExtentBasis',
          safeManifestLabel(record.article23LossExtentBasis, compensationArticle23LossExtentBases)
        ),
        oecmrElement(
          'Article23ChargeRefundBasis',
          safeManifestLabel(record.article23ChargeRefundBasis, compensationArticle23ChargeRefundBases)
        ),
        oecmrElement(
          'Article23WeightAndRefundEvidenceReferencePresent',
          record.article23WeightAndRefundEvidenceReferencePresent
        ),
        oecmrElement('CarrierLiabilityBasisRecorded', record.carrierLiabilityBasisRecorded),
        oecmrElement('GoodsValueBasis', safeManifestLabel(record.goodsValueBasis, compensationGoodsValueBases)),
        oecmrElement('LiabilityLimitBasis', safeManifestLabel(record.liabilityLimitBasis, compensationLiabilityLimitBases)),
        oecmrElement('GoodsValueAmountPresent', record.goodsValueAmountPresent),
        oecmrElement('GoodsValueAmountPositive', record.goodsValueAmountPositive),
        oecmrElement('GoodsValueCurrencyPresent', record.goodsValueCurrencyPresent),
        oecmrElement('GoodsValueCurrencyRecognized', record.goodsValueCurrencyRecognized),
        oecmrElement('GrossWeightShortPresent', record.grossWeightShortPresent),
        oecmrElement('GrossWeightShortPositive', record.grossWeightShortPositive),
        oecmrElement('ClaimAmountPresent', record.claimAmountPresent),
        oecmrElement('ClaimAmountPositive', record.claimAmountPositive),
        oecmrElement('ClaimCurrencyPresent', record.claimCurrencyPresent),
        oecmrElement('ClaimCurrencyRecognized', record.claimCurrencyRecognized),
        oecmrElement('CarriageChargesAmountPresent', record.carriageChargesAmountPresent),
        oecmrElement('CarriageChargesAmountPositive', record.carriageChargesAmountPositive),
        oecmrElement('CarriageChargesCurrencyPresent', record.carriageChargesCurrencyPresent),
        oecmrElement('CarriageChargesCurrencyRecognized', record.carriageChargesCurrencyRecognized),
        oecmrElement('DeclaredValueAmountPresent', record.declaredValueAmountPresent),
        oecmrElement('DeclaredValueAmountPositive', record.declaredValueAmountPositive),
        oecmrElement('DeclaredValueCurrencyPresent', record.declaredValueCurrencyPresent),
        oecmrElement('DeclaredValueCurrencyRecognized', record.declaredValueCurrencyRecognized),
        oecmrElement('SpecialInterestAmountPresent', record.specialInterestAmountPresent),
        oecmrElement('SpecialInterestAmountPositive', record.specialInterestAmountPositive),
        oecmrElement('SpecialInterestCurrencyPresent', record.specialInterestCurrencyPresent),
        oecmrElement('SpecialInterestCurrencyRecognized', record.specialInterestCurrencyRecognized),
        oecmrElement('DiminishedValueAmountPresent', record.diminishedValueAmountPresent),
        oecmrElement('DiminishedValueAmountPositive', record.diminishedValueAmountPositive),
        oecmrElement('DiminishedValueCurrencyPresent', record.diminishedValueCurrencyPresent),
        oecmrElement('DiminishedValueCurrencyRecognized', record.diminishedValueCurrencyRecognized),
        oecmrElement('Article23LimitApplied', record.article23LimitApplied),
        oecmrElement('CarriageChargesRefundConsidered', record.carriageChargesRefundConsidered),
        oecmrElement('DelayDamageProved', record.delayDamageProved),
        oecmrElement('CarriageChargesLimitApplied', record.carriageChargesLimitApplied),
        oecmrElement('DamageAffectedWholeConsignment', record.damageAffectedWholeConsignment),
        oecmrElement('Article25LossLimitApplied', record.article25LossLimitApplied),
        oecmrElement('DeclaredValueEnteredInConsignmentNote', record.declaredValueEnteredInConsignmentNote),
        oecmrElement('DeclaredValueSurchargeAgreed', record.declaredValueSurchargeAgreed),
        oecmrElement('DeclaredValueExceedsArticle23Limit', record.declaredValueExceedsArticle23Limit),
        oecmrElement('DeclaredValueLimitSubstituted', record.declaredValueLimitSubstituted),
        oecmrElement('SpecialInterestEnteredInConsignmentNote', record.specialInterestEnteredInConsignmentNote),
        oecmrElement('SpecialInterestSurchargeAgreed', record.specialInterestSurchargeAgreed),
        oecmrElement('AdditionalLossOrDamageProved', record.additionalLossOrDamageProved),
        oecmrElement('SpecialInterestLimitApplied', record.specialInterestLimitApplied),
        oecmrElement('AgreedTimeLimitExceeded', record.agreedTimeLimitExceeded),
        oecmrElement('HigherCompensationBasisPresent', record.higherCompensationBasisPresent),
        oecmrElement('AssessmentAtPresent', record.assessmentAtPresent),
        oecmrElement('ValuationDetailPresent', record.valuationDetailPresent),
        oecmrElement('CalculationDetailPresent', record.calculationDetailPresent),
        oecmrElement('ClaimDetailPresent', record.claimDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('CompensationInterestRecords', compensationInterestRecords?.map((record) =>
      oecmrWrap('CompensationInterestRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, compensationInterestRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, compensationInterestSourceArticles)),
        oecmrElement('CompensationAssessmentReferencePresent', record.compensationAssessmentReferencePresent),
        oecmrElement('CompensationPayableBasisRecorded', record.compensationPayableBasisRecorded),
        oecmrElement('InterestStartBasis', safeManifestLabel(record.interestStartBasis, compensationInterestStartBases)),
        oecmrElement('ClaimSentInWriting', record.claimSentInWriting),
        oecmrElement('LegalProceedingsInstituted', record.legalProceedingsInstituted),
        oecmrElement('FivePercentPerAnnumApplied', record.fivePercentPerAnnumApplied),
        oecmrElement('CompensationAmountPresent', record.compensationAmountPresent),
        oecmrElement('CompensationAmountPositive', record.compensationAmountPositive),
        oecmrElement('CompensationCurrencyPresent', record.compensationCurrencyPresent),
        oecmrElement('CompensationCurrencyRecognized', record.compensationCurrencyRecognized),
        oecmrElement('InterestAmountPresent', record.interestAmountPresent),
        oecmrElement('InterestAmountPositive', record.interestAmountPositive),
        oecmrElement('InterestCurrencyPresent', record.interestCurrencyPresent),
        oecmrElement('InterestCurrencyRecognized', record.interestCurrencyRecognized),
        oecmrElement('AmountsNotExpressedInPaymentClaimCurrency', record.amountsNotExpressedInPaymentClaimCurrency),
        oecmrElement('PaymentClaimCurrencyCountryBasisRecorded', record.paymentClaimCurrencyCountryBasisRecorded),
        oecmrElement('PaymentPlaceBasisRecorded', record.paymentPlaceBasisRecorded),
        oecmrElement('ExchangeRateApplicableAtPaymentPlaceAndDate', record.exchangeRateApplicableAtPaymentPlaceAndDate),
        oecmrElement('SourceAmountPresent', record.sourceAmountPresent),
        oecmrElement('SourceAmountPositive', record.sourceAmountPositive),
        oecmrElement('SourceCurrencyPresent', record.sourceCurrencyPresent),
        oecmrElement('SourceCurrencyRecognized', record.sourceCurrencyRecognized),
        oecmrElement('TargetCurrencyPresent', record.targetCurrencyPresent),
        oecmrElement('TargetCurrencyRecognized', record.targetCurrencyRecognized),
        oecmrElement('ConvertedAmountPresent', record.convertedAmountPresent),
        oecmrElement('ConvertedAmountPositive', record.convertedAmountPositive),
        oecmrElement('ConvertedCurrencyPresent', record.convertedCurrencyPresent),
        oecmrElement('ConvertedCurrencyRecognized', record.convertedCurrencyRecognized),
        oecmrElement('InterestStartAtPresent', record.interestStartAtPresent),
        oecmrElement('InterestCalculatedAtPresent', record.interestCalculatedAtPresent),
        oecmrElement('InterestDateOrderComputed', record.interestDateOrderComputed),
        oecmrElement('InterestCalculatedAfterStart', record.interestCalculatedAfterStart),
        oecmrElement('PaymentAtPresent', record.paymentAtPresent),
        oecmrElement('InterestCalculationDetailPresent', record.interestCalculationDetailPresent),
        oecmrElement('ExchangeRateDetailPresent', record.exchangeRateDetailPresent),
        oecmrElement('ClaimDetailPresent', record.claimDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('LiabilityLimitationRecords', liabilityLimitationRecords?.map((record) =>
      oecmrWrap('LiabilityLimitationRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, liabilityLimitationRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, liabilityLimitationSourceArticles)),
        oecmrElement('IncidentReferencePresent', record.incidentReferencePresent),
        oecmrElement('LiablePersonRole', safeManifestLabel(record.liablePersonRole, liabilityLimitationPersonRoles)),
        oecmrElement('ClaimArisesFromCarriageUnderCmr', record.claimArisesFromCarriageUnderCmr),
        oecmrElement('LossDamageOrDelayClaim', record.lossDamageOrDelayClaim),
        oecmrElement('ExtraContractualClaimUnderApplicableLaw', record.extraContractualClaimUnderApplicableLaw),
        oecmrElement('CmrExclusionOrLimitInvoked', record.cmrExclusionOrLimitInvoked),
        oecmrElement('Article3ResponsiblePersonInvolved', record.article3ResponsiblePersonInvolved),
        oecmrElement('Article3PersonScopeRecorded', record.article3PersonScopeRecorded),
        oecmrElement('ActingWithinScopeOfEmployment', record.actingWithinScopeOfEmployment),
        oecmrElement('LimitationOrBurdenShiftProvisionInvoked', record.limitationOrBurdenShiftProvisionInvoked),
        oecmrElement('WilfulMisconductAlleged', record.wilfulMisconductAlleged),
        oecmrElement('EquivalentDefaultAlleged', record.equivalentDefaultAlleged),
        oecmrElement('CourtOrTribunalLawBasisRecorded', record.courtOrTribunalLawBasisRecorded),
        oecmrElement('LimitationDisapplicationBasisRecorded', record.limitationDisapplicationBasisRecorded),
        oecmrElement('ClaimDetailPresent', record.claimDetailPresent),
        oecmrElement('ApplicableLawDetailPresent', record.applicableLawDetailPresent),
        oecmrElement('Article3PersonDetailPresent', record.article3PersonDetailPresent),
        oecmrElement('MisconductDetailPresent', record.misconductDetailPresent),
        oecmrElement('CourtLawDetailPresent', record.courtLawDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('ClaimReservationRecords', claimReservationRecords?.map((record) =>
      oecmrWrap('ClaimReservationRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, claimReservationRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, claimReservationSourceArticles)),
        oecmrElement('DeliveryReferencePresent', record.deliveryReferencePresent),
        oecmrElement('LossOrDamageApparent', record.lossOrDamageApparent),
        oecmrElement('LossOrDamageNotApparent', record.lossOrDamageNotApparent),
        oecmrElement('DelayCompensationClaim', record.delayCompensationClaim),
        oecmrElement('ReservationSentAtDelivery', record.reservationSentAtDelivery),
        oecmrElement('ReservationSentInWriting', record.reservationSentInWriting),
        oecmrElement('ReservationWithinSevenDaysExcludingHolidaysRecorded', record.reservationWithinSevenDaysExcludingHolidaysRecorded),
        oecmrElement('ConditionCheckedWithCarrier', record.conditionCheckedWithCarrier),
        oecmrElement('ContraryEvidenceReservationSentInWriting', record.contraryEvidenceReservationSentInWriting),
        oecmrElement('DelayReservationSentInWriting', record.delayReservationSentInWriting),
        oecmrElement('ReservationWithinTwentyOneDaysRecorded', record.reservationWithinTwentyOneDaysRecorded),
        oecmrElement('RelevantStartDateExcludedFromLimitCalculation', record.relevantStartDateExcludedFromLimitCalculation),
        oecmrElement('ReasonableFacilitiesProvided', record.reasonableFacilitiesProvided),
        oecmrElement('TimeLimitComputed', record.timeLimitComputed),
        oecmrElement('WithinComputedTimeLimit', record.withinComputedTimeLimit),
        oecmrElement('TimeLimitComputationBasis', safeManifestLabel(record.timeLimitComputationBasis, claimReservationTimeLimitBases)),
        oecmrElement('PublicHolidaysExcludedFromTimeLimit', record.publicHolidaysExcludedFromTimeLimit),
        oecmrElement('PublicHolidayExclusionCount', safePublicManifestCount(record.publicHolidayExclusionCount)),
        oecmrElement('DeliveryAtPresent', record.deliveryAtPresent),
        oecmrElement('ConditionCheckedAtPresent', record.conditionCheckedAtPresent),
        oecmrElement('GoodsAvailableAtPresent', record.goodsAvailableAtPresent),
        oecmrElement('ReservationSentAtPresent', record.reservationSentAtPresent),
        oecmrElement('ReservationDetailPresent', record.reservationDetailPresent),
        oecmrElement('ConditionCheckDetailPresent', record.conditionCheckDetailPresent),
        oecmrElement('DelayClaimDetailPresent', record.delayClaimDetailPresent),
        oecmrElement('InvestigationFacilityDetailPresent', record.investigationFacilityDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('DisputeProcedureRecords', disputeProcedureRecords?.map((record) =>
      oecmrWrap('DisputeProcedureRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, disputeProcedureRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement(
          'DocumentVersionReferencePresent',
          record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)
        ),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, disputeProcedureSourceArticles)),
        oecmrElement('ClaimCategory', safeManifestLabel(record.claimCategory, disputeProcedureClaimCategories)),
        oecmrElement('LimitationPeriodBasis', safeManifestLabel(record.limitationPeriodBasis, disputeProcedureLimitationPeriodBases)),
        oecmrElement('LimitationStartBasis', safeManifestLabel(record.limitationStartBasis, disputeProcedureLimitationStartBases)),
        oecmrElement('LimitationStartComputed', record.limitationStartComputed),
        oecmrElement('LimitationStartComputationBasis', safeManifestLabel(record.limitationStartComputationBasis, disputeProcedureLimitationStartComputationBases)),
        oecmrElement('LimitationStartedAtMatchesComputedBasis', record.limitationStartedAtMatchesComputedBasis),
        oecmrElement('LimitationExpiryComputed', record.limitationExpiryComputed),
        oecmrElement('LimitationExpiryComputationBasis', safeManifestLabel(record.limitationExpiryComputationBasis, disputeProcedureLimitationExpiryComputationBases)),
        oecmrElement('LimitationExpiresAtMatchesComputedBasis', record.limitationExpiresAtMatchesComputedBasis),
        oecmrElement('SuspensionReleaseDateOrderComputed', record.suspensionReleaseDateOrderComputed),
        oecmrElement('SuspensionReleaseDatesOrdered', record.suspensionReleaseDatesOrdered),
        oecmrElement('PartyDesignatedCourtOrTribunal', record.partyDesignatedCourtOrTribunal),
        oecmrElement('DefendantResidenceOrBusinessPlaceBasis', record.defendantResidenceOrBusinessPlaceBasis),
        oecmrElement('ContractBranchOrAgencyBasis', record.contractBranchOrAgencyBasis),
        oecmrElement('TakeoverPlaceBasis', record.takeoverPlaceBasis),
        oecmrElement('DeliveryPlaceBasis', record.deliveryPlaceBasis),
        oecmrElement('ContractingCountryCourtOrTribunal', record.contractingCountryCourtOrTribunal),
        oecmrElement('SecurityForCostsNotRequiredRecorded', record.securityForCostsNotRequiredRecorded),
        oecmrElement('SamePartiesAndGroundsRecorded', record.samePartiesAndGroundsRecorded),
        oecmrElement('PendingCompetentProceedingRecorded', record.pendingCompetentProceedingRecorded),
        oecmrElement('JudgmentEnteredByCompetentCourt', record.judgmentEnteredByCompetentCourt),
        oecmrElement('FreshProceedingOnlyIfJudgmentNotEnforceableRecorded', record.freshProceedingOnlyIfJudgmentNotEnforceableRecorded),
        oecmrElement('JudgmentEnforceabilityFormalitiesOnlyRecorded', record.judgmentEnforceabilityFormalitiesOnlyRecorded),
        oecmrElement('MeritsReopeningProhibitedRecorded', record.meritsReopeningProhibitedRecorded),
        oecmrElement('WilfulMisconductOrEquivalentDefaultAlleged', record.wilfulMisconductOrEquivalentDefaultAlleged),
        oecmrElement('LimitationPeriodRecorded', record.limitationPeriodRecorded),
        oecmrElement('LimitationStartDateExcluded', record.limitationStartDateExcluded),
        oecmrElement('ClaimBarredNotAvailableByCounterclaimOrSetoffRecorded', record.claimBarredNotAvailableByCounterclaimOrSetoffRecorded),
        oecmrElement('CourtLawExtensionOrFreshAccrualBasisRecorded', record.courtLawExtensionOrFreshAccrualBasisRecorded),
        oecmrElement('WrittenClaimReceived', record.writtenClaimReceived),
        oecmrElement('CarrierRejectedClaimInWriting', record.carrierRejectedClaimInWriting),
        oecmrElement('DocumentsReturned', record.documentsReturned),
        oecmrElement('ClaimPartlyAdmitted', record.claimPartlyAdmitted),
        oecmrElement('RemainingPartStillInDispute', record.remainingPartStillInDispute),
        oecmrElement('ReceiptReplyReturnProofBurdenRecorded', record.receiptReplyReturnProofBurdenRecorded),
        oecmrElement('FurtherSameObjectClaimsDoNotSuspendRecorded', record.furtherSameObjectClaimsDoNotSuspendRecorded),
        oecmrElement('ArbitrationClausePresent', record.arbitrationClausePresent),
        oecmrElement('ArbitrationTribunalCompetenceRecorded', record.arbitrationTribunalCompetenceRecorded),
        oecmrElement('TribunalAppliesCmrConvention', record.tribunalAppliesCmrConvention),
        oecmrElement('ActionFiledAtPresent', record.actionFiledAtPresent),
        oecmrElement('JudgmentEnteredAtPresent', record.judgmentEnteredAtPresent),
        oecmrElement('JudgmentEnforceableAtPresent', record.judgmentEnforceableAtPresent),
        oecmrElement('DeliveryAtPresent', record.deliveryAtPresent),
        oecmrElement('TakeoverAtPresent', record.takeoverAtPresent),
        oecmrElement('AgreedTimeLimitExpiresAtPresent', record.agreedTimeLimitExpiresAtPresent),
        oecmrElement('ContractMadeAtPresent', record.contractMadeAtPresent),
        oecmrElement('LimitationStartedAtPresent', record.limitationStartedAtPresent),
        oecmrElement('LimitationExpiresAtPresent', record.limitationExpiresAtPresent),
        oecmrElement('WrittenClaimReceivedAtPresent', record.writtenClaimReceivedAtPresent),
        oecmrElement('ClaimRejectedAtPresent', record.claimRejectedAtPresent),
        oecmrElement('DocumentsReturnedAtPresent', record.documentsReturnedAtPresent),
        oecmrElement('JurisdictionDetailPresent', record.jurisdictionDetailPresent),
        oecmrElement('PartyAgreementDetailPresent', record.partyAgreementDetailPresent),
        oecmrElement('TakeoverDeliveryPlaceDetailPresent', record.takeoverDeliveryPlaceDetailPresent),
        oecmrElement('PendingActionDetailPresent', record.pendingActionDetailPresent),
        oecmrElement('JudgmentDetailPresent', record.judgmentDetailPresent),
        oecmrElement('LimitationCalculationDetailPresent', record.limitationCalculationDetailPresent),
        oecmrElement('WrittenClaimDetailPresent', record.writtenClaimDetailPresent),
        oecmrElement('RejectionDetailPresent', record.rejectionDetailPresent),
        oecmrElement('ArbitrationClauseDetailPresent', record.arbitrationClauseDetailPresent),
        oecmrElement('CourtOrTribunalDetailPresent', record.courtOrTribunalDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('SuccessiveCarrierRecords', successiveCarrierRecords?.map((record) =>
      oecmrWrap('SuccessiveCarrierRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, successiveCarrierRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent === true || presence((record as { documentVersionId?: string }).documentVersionId)),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, successiveCarrierSourceArticles)),
        oecmrElement('AllocationBasis', safeManifestLabel(record.allocationBasis, successiveCarrierAllocationBases)),
        oecmrElement('RecoveryLimitationStartBasis', safeManifestLabel(record.recoveryLimitationStartBasis, successiveCarrierRecoveryLimitationStartBases)),
        oecmrElement('SuccessiveReceiptDateOrderComputed', record.successiveReceiptDateOrderComputed),
        oecmrElement('SuccessiveReceiptIssuedAfterAcceptance', record.successiveReceiptIssuedAfterAcceptance),
        oecmrElement('RecoveryLimitationPeriodComputed', record.recoveryLimitationPeriodComputed),
        oecmrElement('RecoveryLimitationComputationBasis', safeManifestLabel(record.recoveryLimitationComputationBasis, successiveCarrierRecoveryLimitationComputationBases)),
        oecmrElement('RecoveryClaimFiledWithinComputedLimit', record.recoveryClaimFiledWithinComputedLimit),
        oecmrElement('ConsignmentNoteCopyReferencePresent', record.consignmentNoteCopyReferencePresent),
        oecmrElement('ReservationReferencePresent', record.reservationReferencePresent),
        oecmrElement('CompensationAssessmentReferencePresent', record.compensationAssessmentReferencePresent),
        oecmrElement('SingleContractCarriage', record.singleContractCarriage),
        oecmrElement('SuccessiveRoadCarrier', record.successiveRoadCarrier),
        oecmrElement('GoodsAccepted', record.goodsAccepted),
        oecmrElement('ConsignmentNoteAccepted', record.consignmentNoteAccepted),
        oecmrElement('BecomesPartyByAcceptance', record.becomesPartyByAcceptance),
        oecmrElement('DatedSignedReceiptIssued', record.datedSignedReceiptIssued),
        oecmrElement('CarrierNameAddressEnteredOnSecondCopy', record.carrierNameAddressEnteredOnSecondCopy),
        oecmrElement('Article8ReservationsEnteredWhereApplicable', record.article8ReservationsEnteredWhereApplicable),
        oecmrElement('Article9AppliesBetweenSuccessiveCarriers', record.article9AppliesBetweenSuccessiveCarriers),
        oecmrElement('LiabilityForWholeOperationRecorded', record.liabilityForWholeOperationRecorded),
        oecmrElement('LossDamageOrDelayProceeding', record.lossDamageOrDelayProceeding),
        oecmrElement('ActionAgainstFirstCarrier', record.actionAgainstFirstCarrier),
        oecmrElement('ActionAgainstLastCarrier', record.actionAgainstLastCarrier),
        oecmrElement('ActionAgainstEventCarrier', record.actionAgainstEventCarrier),
        oecmrElement('CounterclaimOrSetoffSameContract', record.counterclaimOrSetoffSameContract),
        oecmrElement('MultiplePermittedCarriersJoined', record.multiplePermittedCarriersJoined),
        oecmrElement('CompensationPaidUnderCmr', record.compensationPaidUnderCmr),
        oecmrElement('InterestAndCostsIncluded', record.interestAndCostsIncluded),
        oecmrElement('ResponsibleCarrierSoleLiabilityRecorded', record.responsibleCarrierSoleLiabilityRecorded),
        oecmrElement('MultipleCarrierLiabilityApportioned', record.multipleCarrierLiabilityApportioned),
        oecmrElement('LiabilityCannotBeAttributed', record.liabilityCannotBeAttributed),
        oecmrElement('PaymentShareApportionmentRecorded', record.paymentShareApportionmentRecorded),
        oecmrElement('InsolventCarrierShareUnpaid', record.insolventCarrierShareUnpaid),
        oecmrElement('InsolventShareReallocatedByPaymentShare', record.insolventShareReallocatedByPaymentShare),
        oecmrElement('JudicialAuthorityDeterminedCompensation', record.judicialAuthorityDeterminedCompensation),
        oecmrElement('CarrierGivenDueNoticeOfProceedings', record.carrierGivenDueNoticeOfProceedings),
        oecmrElement('CarrierHadOpportunityToAppear', record.carrierHadOpportunityToAppear),
        oecmrElement('PaymentValidityDisputeBarRecorded', record.paymentValidityDisputeBarRecorded),
        oecmrElement('CompetentRecoveryForumRecorded', record.competentRecoveryForumRecorded),
        oecmrElement('AllConcernedCarriersMayBeDefendants', record.allConcernedCarriersMayBeDefendants),
        oecmrElement('Article31JudgmentRulesApplied', record.article31JudgmentRulesApplied),
        oecmrElement('Article32LimitationApplied', record.article32LimitationApplied),
        oecmrElement('RecoveryLimitationStartRecorded', record.recoveryLimitationStartRecorded),
        oecmrElement('CarriersAgreedAlternativeAllocation', record.carriersAgreedAlternativeAllocation),
        oecmrElement('AgreementLimitedToArticles37And38', record.agreementLimitedToArticles37And38),
        oecmrElement('Article40AgreementRecorded', record.article40AgreementRecorded),
        oecmrElement('CarrierAcceptedAtPresent', record.carrierAcceptedAtPresent),
        oecmrElement('ReceiptIssuedAtPresent', record.receiptIssuedAtPresent),
        oecmrElement('CompensationPaidAtPresent', record.compensationPaidAtPresent),
        oecmrElement('FinalJudicialDecisionAtPresent', record.finalJudicialDecisionAtPresent),
        oecmrElement('RecoveryClaimFiledAtPresent', record.recoveryClaimFiledAtPresent),
        oecmrElement('AgreementRecordedAtPresent', record.agreementRecordedAtPresent),
        oecmrElement('ReceiptDetailPresent', record.receiptDetailPresent),
        oecmrElement('CarrierIdentityEntryDetailPresent', record.carrierIdentityEntryDetailPresent),
        oecmrElement('ReservationDetailPresent', record.reservationDetailPresent),
        oecmrElement('ActionScopeDetailPresent', record.actionScopeDetailPresent),
        oecmrElement('AllocationDetailPresent', record.allocationDetailPresent),
        oecmrElement('InsolvencyDetailPresent', record.insolvencyDetailPresent),
        oecmrElement('ProceedingDetailPresent', record.proceedingDetailPresent),
        oecmrElement('AgreementDetailPresent', record.agreementDetailPresent),
        oecmrElement('NoticeOpportunityDetailPresent', record.noticeOpportunityDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('MultimodalCarriageRecords', multimodalCarriageRecords?.map((record) =>
      oecmrWrap('MultimodalCarriageRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, multimodalCarriageRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, multimodalCarriageSourceArticles)),
        oecmrElement('OtherModeType', safeManifestLabel(record.otherModeType, multimodalCarriageOtherModes)),
        oecmrElement('VehicleCarriedByOtherMode', record.vehicleCarriedByOtherMode),
        oecmrElement('GoodsRemainedLoadedOnVehicle', record.goodsRemainedLoadedOnVehicle),
        oecmrElement('GoodsUnloadedFromVehicle', record.goodsUnloadedFromVehicle),
        oecmrElement('Article14ImpedimentBasisRecorded', record.article14ImpedimentBasisRecorded),
        oecmrElement('OtherModeCarriageEvidencePresent', record.otherModeCarriageEvidencePresent),
        oecmrElement('OtherModeCarriageDocumentReferencePresent', record.otherModeCarriageDocumentReferencePresent),
        oecmrElement('Article14ImpedimentReferencePresent', record.article14ImpedimentReferencePresent),
        oecmrElement('OtherModeCarriageDetailPresent', record.otherModeCarriageDetailPresent),
        oecmrElement('Article14ImpedimentDetailPresent', record.article14ImpedimentDetailPresent),
        oecmrElement('LiabilityDetermination', noLiabilityDetermination),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('ConsignmentNoteCopyRecords', consignmentNoteCopyRecords?.map((record) =>
      oecmrWrap('ConsignmentNoteCopyRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, consignmentNoteCopyRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, consignmentNoteCopySourceArticles)),
        oecmrElement('ElectronicCopyAccessAvailable', record.electronicCopyAccessAvailable),
        oecmrElement('EntitledPartyAccessAvailable', record.entitledPartyAccessAvailable),
        oecmrElement('SenderCarrierSignatureEvidenceAvailable', record.senderCarrierSignatureEvidenceAvailable),
        oecmrElement('ContractParticipantReferencePresent', record.contractParticipantReferencePresent === true),
        oecmrElement('SenderFirstCopyAvailable', record.senderFirstCopyAvailable),
        oecmrElement('GoodsAccompanyingSecondCopyAvailable', record.goodsAccompanyingSecondCopyAvailable),
        oecmrElement('CarrierRetainedThirdCopyAvailable', record.carrierRetainedThirdCopyAvailable),
        oecmrElement('CopyAccessDetailPresent', record.copyAccessDetailPresent),
        oecmrElement('SignatureDetailPresent', record.signatureDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('EcmrReceiptAccessRequestRecords', ecmrReceiptAccessRequestRecords?.map((record) =>
      oecmrWrap('EcmrReceiptAccessRequestRecord', [
        oecmrElement('RequestType', safeManifestLabel(record.requestType, ecmrReceiptAccessRequestTypes)),
        oecmrElement('RequestStatus', safeManifestLabel(record.requestStatus, ecmrReceiptAccessRequestStatuses)),
        oecmrElement('RequestedAt', record.requestedAt),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent),
        oecmrElement('RequestDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRequest', safeManifestLabel(record.statusAtRequest, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', safeManifestLabel(record.sourceInstrument, ecmrReceiptAccessSourceInstruments)),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, ecmrReceiptAccessSourceArticles)),
        oecmrElement('SenderRequestRecorded', record.senderRequestRecorded),
        oecmrElement('RequestDetailPresent', record.requestDetailPresent),
        oecmrElement('TechnicalEvidenceOnly', technicalEvidenceOnly),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('EcmrReceiptAccessRecords', ecmrReceiptAccessRecords?.map((record) =>
      oecmrWrap('EcmrReceiptAccessRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, ecmrReceiptAccessRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', safeManifestLabel(record.sourceInstrument, ecmrReceiptAccessSourceInstruments)),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, ecmrReceiptAccessSourceArticles)),
        oecmrElement('SenderRequestReceived', record.senderRequestReceived),
        oecmrElement('SenderRequestReferencePresent', record.senderRequestReferencePresent),
        oecmrElement('ReceiptForGoodsProvided', record.receiptForGoodsProvided),
        oecmrElement('ShipmentIdentificationInformationProvided', record.shipmentIdentificationInformationProvided),
        oecmrElement('ElectronicConsignmentNoteAccessInformationProvided', record.electronicConsignmentNoteAccessInformationProvided),
        oecmrElement('HandoverAtPresent', record.handoverAtPresent),
        oecmrElement('SenderRequestDetailPresent', record.senderRequestDetailPresent),
        oecmrElement('ReceiptDetailPresent', record.receiptDetailPresent),
        oecmrElement('ShipmentIdentificationDetailPresent', record.shipmentIdentificationDetailPresent),
        oecmrElement('AccessInformationDetailPresent', record.accessInformationDetailPresent),
        oecmrElement('TechnicalEvidenceOnly', technicalEvidenceOnly),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('ProhibitedClauseRecords', prohibitedClauseRecords?.map((record) =>
      oecmrWrap('ProhibitedClauseRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, prohibitedClauseRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, prohibitedClauseSourceArticles)),
        oecmrElement('Article40AgreementReferencePresent', record.article40AgreementReferencePresent),
        oecmrElement('ContractStipulationReviewed', record.contractStipulationReviewed),
        oecmrElement('ClauseDetected', record.clauseDetected),
        oecmrElement('DirectOrIndirectDerogationDetected', record.directOrIndirectDerogationDetected),
        oecmrElement('BenefitOfInsuranceForCarrierDetected', record.benefitOfInsuranceForCarrierDetected),
        oecmrElement('BurdenOfProofShiftDetected', record.burdenOfProofShiftDetected),
        oecmrElement('Article40ExceptionAssessed', record.article40ExceptionAssessed),
        oecmrElement('Article40ExceptionNotApplicable', record.article40ExceptionNotApplicable),
        oecmrElement('NullitySeverabilityRecorded', record.nullitySeverabilityRecorded),
        oecmrElement('ReviewOutcomeRecorded', record.reviewOutcomeRecorded),
        oecmrElement('ClauseReviewedAtPresent', record.clauseReviewedAtPresent),
        oecmrElement('ClauseDetailPresent', record.clauseDetailPresent),
        oecmrElement('ReviewDetailPresent', record.reviewDetailPresent),
        oecmrElement('InsuranceBenefitDetailPresent', record.insuranceBenefitDetailPresent),
        oecmrElement('BurdenShiftDetailPresent', record.burdenShiftDetailPresent),
        oecmrElement('Article40ExceptionDetailPresent', record.article40ExceptionDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('SenderParticularsResponsibilityRecords', senderParticularsResponsibilityRecords?.map((record) =>
      oecmrWrap('SenderParticularsResponsibilityRecord', [
        oecmrElement('RecordType', safeManifestLabel(record.recordType, senderParticularsResponsibilityRecordTypes)),
        oecmrElement('RecordStatus', safeManifestLabel(record.recordStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', record.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', record.documentVersionReferencePresent),
        oecmrElement('RecordDocumentVersionNumber', record.documentVersionNumber),
        oecmrElement('StatusAtRecord', safeManifestLabel(record.statusAtRecord, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(record)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', safeManifestLabel(record.sourceArticle, senderParticularsResponsibilitySourceArticles)),
        oecmrElement(
          'AffectedParticularCategory',
          safeManifestLabel(record.affectedParticularCategory, senderParticularsResponsibilityCategories)
        ),
        oecmrElement('InaccuracyOrInadequacyRecorded', record.inaccuracyOrInadequacyRecorded),
        oecmrElement('ExpenseLossDamageClaimed', record.expenseLossDamageClaimed),
        oecmrElement('CarrierEnteredAtSenderRequest', record.carrierEnteredAtSenderRequest),
        oecmrElement('SenderBehalfPresumptionRecorded', record.senderBehalfPresumptionRecorded),
        oecmrElement('CmrStatementOmissionRecorded', record.cmrStatementOmissionRecorded),
        oecmrElement('CarrierLiabilityBasisRecorded', record.carrierLiabilityBasisRecorded),
        oecmrElement('ReviewedAtPresent', record.reviewedAtPresent),
        oecmrElement('ParticularDetailPresent', record.particularDetailPresent),
        oecmrElement('InstructionDetailPresent', record.instructionDetailPresent),
        oecmrElement('ExpenseLossDamageDetailPresent', record.expenseLossDamageDetailPresent),
        oecmrElement('CarrierEntryDetailPresent', record.carrierEntryDetailPresent),
        oecmrElement('CmrStatementOmissionDetailPresent', record.cmrStatementOmissionDetailPresent),
        oecmrElement('LegalDetermination', noLegalDetermination),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('ConsigneeDeliveryRequests', consigneeDeliveryRequestRecords?.map((request) =>
      oecmrWrap('ConsigneeDeliveryRequest', [
        oecmrElement('DeliveryRequestReferencePresent', request.deliveryRequestReferencePresent === true || presence(request.deliveryRequestId)),
        oecmrElement('RequestType', safeManifestLabel(request.requestType, consigneeDeliveryRequestTypes)),
        oecmrElement('RequestStatus', safeManifestLabel(request.requestStatus, operationalRecordStatuses)),
        oecmrElement('RecordedAt', request.recordedAt),
        oecmrElement('DocumentVersionReferencePresent', request.documentVersionReferencePresent === true || presence((request as { documentVersionId?: string }).documentVersionId)),
        oecmrElement('RequestDocumentVersionNumber', request.documentVersionNumber),
        oecmrElement('StatusAtRequest', safeManifestLabel(request.statusAtRequest, documentLifecycleStatuses)),
        oecmrElement('ActorRoleCount', evidenceActorRoleCount(request)),
        oecmrElement('SourceInstrument', 'CMR_1956'),
        oecmrElement('SourceArticle', '13'),
        oecmrElement('ArrivalAtDeliveryPlace', request.arrivalAtDeliveryPlace),
        oecmrElement('DeliveryRequested', request.deliveryRequested),
        oecmrElement('SecondCopyRequested', request.secondCopyRequested),
        oecmrElement('ReceiptCommitment', request.receiptCommitment),
        oecmrElement('ChargesPaymentAcknowledged', request.chargesPaymentAcknowledged),
        oecmrElement('ChargesDisputed', request.chargesDisputed),
        oecmrElement('SecurityFurnished', request.securityFurnished),
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ConsignmentNoteChargesComputed', request.consignmentNoteChargesComputed)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ConsignmentNoteChargesMatched', request.consignmentNoteChargesMatched)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ChargesPaymentLineCount', request.chargesPaymentLineCount)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ChargesPaymentAmountsPositive', request.chargesPaymentAmountsPositive)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ChargesPaymentCurrenciesRecognized', request.chargesPaymentCurrenciesRecognized)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ChargesPaidAtPresent', request.chargesPaidAtPresent)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ChargesPaymentEvidenceAttachmentReferencePresent', request.chargesPaymentEvidenceAttachmentReferencePresent)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('SecurityFurnishedAtPresent', request.securityFurnishedAtPresent)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ChargesSecurityEvidenceAttachmentReferencePresent', request.chargesSecurityEvidenceAttachmentReferencePresent)
          : '',
        request.requestType === 'delivery_after_arrival'
          ? oecmrElement('ChargesEvidenceChronologyValid', request.chargesEvidenceChronologyValid)
          : '',
        oecmrElement('LossEstablished', request.lossEstablished),
        oecmrElement('LossEvidenceReferencePresent', request.lossEvidenceReferencePresent),
        oecmrElement('Article19DelayPeriodExpired', request.article19DelayPeriodExpired),
        oecmrElement('Article19DelayAssessmentReferencePresent', request.article19DelayAssessmentReferencePresent),
        oecmrElement('RightsReservedNotice', request.rightsReservedNotice),
        request.requestType === 'delivery_receipt_and_second_copy_handover'
          ? oecmrElement('DeliveryConfirmationReferencePresent', request.deliveryConfirmationReferencePresent)
          : '',
        request.requestType === 'delivery_receipt_and_second_copy_handover'
          ? oecmrElement('ConsignmentNoteCopyReferencePresent', request.consignmentNoteCopyReferencePresent)
          : '',
        request.requestType === 'delivery_receipt_and_second_copy_handover'
          ? oecmrElement('GoodsReceived', request.goodsReceived)
          : '',
        request.requestType === 'delivery_receipt_and_second_copy_handover'
          ? oecmrElement('SecondCopyReceived', request.secondCopyReceived)
          : '',
        request.requestType === 'delivery_receipt_and_second_copy_handover'
          ? oecmrElement('ReceiptIssued', request.receiptIssued)
          : '',
        oecmrElement('ReceiptDetailPresent', request.receiptDetailPresent),
        oecmrElement('ChargesSecurityDetailPresent', request.chargesSecurityDetailPresent),
        oecmrElement('EnforcementBasisDetailPresent', request.enforcementBasisDetailPresent),
        oecmrElement('ReasonPresent', request.reasonPresent),
      ].join(''))
    ).join('') ?? ''),
    delivery ? oecmrWrap('DeliveryConfirmation', [
      oecmrElement('DeliveredAt', delivery.deliveredAt),
      oecmrElement('DeliveryRecipientPresent', delivery.recipientNamePresent === true || presence(delivery.recipientName)),
      oecmrElement('DeliveryLocationPresent', delivery.deliveryLocationPresent === true || Boolean(delivery.deliveryLocation && Object.values(delivery.deliveryLocation).some(presence))),
      oecmrElement('DeliveryActorRoleCount', publicOperationalRoleCount(delivery.actorRoleCount, delivery.confirmedByRoles)),
      oecmrElement('ConfirmedAt', delivery.confirmedAt),
      oecmrElement('DocumentVersionReferencePresent', delivery.documentVersionReferencePresent === true || presence((delivery as { documentVersionId?: string }).documentVersionId)),
      oecmrElement('ConfirmationDocumentVersionNumber', delivery.documentVersionNumber),
      oecmrElement('ConfirmationStatus', deliveryStatusAtConfirmation),
      delivery.signatureEvidence ? oecmrWrap('DeliverySignature', [
        oecmrElement('DeliverySignatureAvailable', true),
        oecmrElement('DeliverySignatureRole', deliverySignatureRole),
        oecmrElement('DeliverySignatureDigestAvailable', delivery.signatureEvidence.signedDigestAvailable ?? Boolean(delivery.signatureEvidence.signedDigest)),
        oecmrElement('DigestAlgorithm', deliverySignatureDigestAlgorithm),
        oecmrElement('DeliverySignatureMethod', deliverySignatureMethod),
        oecmrElement('DeliverySignatureVerificationStatus', deliverySignatureVerificationStatus),
        oecmrElement('Timestamp', delivery.signatureEvidence.timestamp),
      ].join('')) : oecmrElement('DeliverySignatureAvailable', delivery.deliverySignatureAvailable === true),
      oecmrElement('DeliveryReservationCount', delivery.reservationCount ?? delivery.deliveryReservations?.length ?? 0),
      oecmrWrap('DeliveryReservations', delivery.deliveryReservations?.map((reservation) =>
        oecmrWrap('DeliveryReservation', [
          oecmrElement('DeliveryReservationCategory', safeManifestLabel(reservation.category, carrierReservationCategories)),
          oecmrElement('DeliveryReservationGroundsPresent', presence(reservation.grounds)),
          oecmrElement('DeliveryReservationPackageReferencePresent', presence(reservation.packageReference)),
          oecmrElement('DeliveryReservationObservedValuePresent', presence(reservation.observedValue)),
          oecmrElement('DeliveryReservationExpectedValuePresent', presence(reservation.expectedValue)),
        ].join(''))
      ).join('') ?? ''),
    ].join('')) : '',
    oecmrWrap('Amendments', manifest.amendmentRequests?.map((amendment) =>
      oecmrWrap('Amendment', [
        oecmrElement('AmendmentReasonPresent', presence(amendment.reason)),
        oecmrElement('ParentVersionReferencePresent', amendment.parentVersionReferencePresent === true),
        oecmrElement('ParentVersionNumber', amendment.parentVersionNumber),
        oecmrElement('AmendmentStatus', safeManifestLabel(amendment.status, amendmentStatuses)),
        oecmrElement('RequestedAt', amendment.requestedAt),
        oecmrElement('AppliedVersionReferencePresent', amendment.appliedVersionReferencePresent === true),
        oecmrElement(
          'DocumentVersionReferencePresent',
          amendment.documentVersionReferencePresent === true ||
            amendment.parentVersionReferencePresent === true ||
            amendment.appliedVersionReferencePresent === true
        ),
        oecmrElement('AcknowledgementCount', amendment.acknowledgementCount ?? amendment.acknowledgementList?.length ?? 0),
        oecmrElement('AcknowledgedActorRoleCount', amendment.acknowledgedActorRoleCount),
        oecmrElement(
          'ProposedChangeFieldCount',
          amendment.proposedChangeFieldCount ??
            (amendment.proposedChanges ? Object.keys(amendment.proposedChanges).length : undefined)
        ),
        oecmrElement(
          'LastAcknowledgementDecision',
          safeManifestLabel(amendment.lastAcknowledgementDecision, amendmentDecisions)
        ),
        oecmrWrap('Acknowledgements', amendment.acknowledgementList?.map((acknowledgement) =>
          oecmrWrap('Acknowledgement', [
            oecmrElement('Acknowledged', acknowledgement.acknowledged),
            oecmrElement('AcknowledgementRoleCount', publicOperationalRoleCount(
              acknowledgement.actorRoleCount,
              acknowledgement.actorRoles
            )),
            oecmrElement('AcknowledgementCommentPresent', acknowledgement.commentPresent === true || presence(acknowledgement.comment)),
            oecmrElement('AcknowledgedAt', acknowledgement.acknowledgedAt),
          ].join(''))
        ).join('') ?? ''),
      ].join(''))
    ).join('') ?? ''),
    manifest.retentionPolicy ? oecmrWrap('RetentionPolicy', [
      oecmrElement(
        'RetentionPolicyReferencePresent',
        manifest.retentionPolicy.retentionPolicyReferencePresent ?? presence(manifest.retentionPolicy.policyId)
      ),
      oecmrElement('RetentionDays', manifest.retentionPolicy.retentionDays),
      oecmrElement('Jurisdiction', safeManifestJurisdiction(manifest.retentionPolicy.jurisdiction)),
      oecmrElement('RetentionMode', safeManifestLabel(manifest.retentionPolicy.retentionMode, retentionModes)),
      oecmrElement('RetainUntilPresent', presence(manifest.retentionPolicy.retainUntil)),
      oecmrElement(
        'RetentionReasonPresent',
        manifest.retentionPolicy.reasonPresent ?? presence(manifest.retentionPolicy.reason)
      ),
      oecmrElement(
        'DocumentVersionReferencePresent',
        manifest.retentionPolicy.documentVersionReferencePresent ??
          (presence(manifest.retentionPolicy.documentVersionId) ||
            presence(manifest.retentionPolicy.documentVersionNumber))
      ),
      oecmrElement('RetentionStatus', safeManifestLabel(manifest.retentionPolicy.status, retentionPolicyStatuses)),
    ].join('')) : '',
    manifest.legalHold ? oecmrWrap('LegalHold', [
      oecmrElement('LegalHoldActive', manifest.legalHold.active),
      oecmrElement(
        'LegalHoldReasonPresent',
        manifest.legalHold.reasonPresent ?? presence(manifest.legalHold.reason)
      ),
      oecmrElement(
        'LegalHoldReferencePresent',
        manifest.legalHold.legalHoldReferencePresent ?? presence(manifest.legalHold.holdId)
      ),
      oecmrElement('LegalHoldAppliedAtPresent', presence(manifest.legalHold.appliedAt)),
      oecmrElement('LegalHoldAppliedByPresent', presence(manifest.legalHold.appliedBy)),
      oecmrElement('LegalHoldReleasedAtPresent', presence(manifest.legalHold.releasedAt)),
      oecmrElement('LegalHoldReleasedByPresent', presence(manifest.legalHold.releasedBy)),
      oecmrElement(
        'DocumentVersionReferencePresent',
        manifest.legalHold.documentVersionReferencePresent ??
          (presence(manifest.legalHold.documentVersionId) ||
            presence(manifest.legalHold.documentVersionNumber))
      ),
    ].join('')) : '',
    oecmrWrap('AuditEvents', manifest.auditEvents?.map((auditEvent) =>
      oecmrWrap('AuditEvent', [
        oecmrElement('AuditEventCategory', publicAuditReviewCategory(auditEvent.category, auditEvent.eventType)),
        oecmrElement('AuditEventCreatedAtPresent', presence(auditEvent.createdAt)),
        oecmrElement('AuditEventActorRoleCount', roleCount(auditEvent.actorRoles)),
        oecmrElement('DocumentVersionReferencePresent',
          auditEvent.documentVersionReferenceAvailable === true ||
          auditEvent.documentVersionReferencePresent === true ||
          presence(auditEvent.documentVersionId)
        ),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('DocumentTasks', manifest.documentTasks?.map((task) =>
      oecmrWrap('DocumentTask', [
        oecmrElement('TaskType', safeManifestLabel(task.taskType, taskStateTypes)),
        oecmrElement('TaskStatus', safeManifestLabel(task.status, taskStateStatuses)),
        oecmrElement('TaskAssignedRoleCount', roleCount(task.assignedRoles)),
        oecmrElement('TaskEvidenceGapPresent', (task.evidenceGaps?.length ?? 0) > 0),
        oecmrElement('DocumentVersionReferencePresent',
          task.documentVersionReferenceAvailable === true ||
          task.documentVersionReferencePresent === true ||
          presence(task.documentVersionId)
        ),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('SourceRecordOperationalEvidenceReferences', sourceRecordOperationalEvidence?.map((evidence) =>
      oecmrWrap('SourceRecordOperationalEvidence', [
        oecmrElement('SourceRecordEvidenceType', safeSourceRecordOperationalEvidenceType(evidence.evidenceType)),
        oecmrElement('SourceRecordEvidenceClass', safeSourceRecordOperationalEvidenceClass(evidence.evidenceClass)),
        oecmrElement('SourceRecordStatus', safeSourceRecordOperationalEvidenceStatus(evidence.status)),
        oecmrElement('SourceRecordCreatedAtPresent', presence(evidence.createdAt)),
        oecmrElement('DocumentVersionReferencePresent',
          evidence.documentVersionReferenceAvailable === true ||
          evidence.documentVersionReferencePresent === true ||
          presence(evidence.documentVersionId) ||
          presence(evidence.documentVersionNumber)
        ),
        oecmrElement('SourceRecordDocumentVersionNumber', evidence.documentVersionNumber),
        oecmrElement(
          'SourceRecordDigestAlgorithm',
          publicDigestAlgorithmLabel(evidence.digestAlgorithm, evidence.sha256Digest ? 'SHA-256' : undefined)
        ),
        oecmrElement('SourceRecordDigestAvailable',
          evidence.digestAvailable ?? Boolean(evidence.sha256Digest || evidence.digestAlgorithm)
        ),
        oecmrElement('SourceRecordPayloadStoredExternally', sourceRecordPayloadStoredExternally(evidence)),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('EvidenceDownloadCompletions', manifest.evidenceDownloadCompletions?.map((completion) =>
      oecmrWrap('EvidenceDownloadCompletion', [
        oecmrElement('DownloadCompletionEvidenceType', safeManifestLabel(
          completion.evidenceType,
          downloadCompletionEvidenceTypes
        )),
        oecmrElement('DownloadClientConfirmed', completion.clientConfirmed),
        oecmrElement('DownloadIndependentTransferProof', false),
        oecmrElement('DownloadConfirmedAt', completion.confirmedAt),
        oecmrElement('DownloadDocumentVersionReferencePresent',
          completion.documentVersionReferenceAvailable === true ||
          completion.documentVersionReferencePresent === true ||
          presence(completion.documentVersionId) ||
          presence(completion.documentVersionNumber)
        ),
        oecmrElement('DownloadActorRoleCount', publicOperationalRoleCount(completion.actorRoleCount, completion.actorRoles)),
        oecmrElement('DownloadEvidenceReferencePresent',
          completion.evidenceReferencePresent === true || presence(completion.evidenceId)
        ),
      ].join(''))
    ).join('') ?? ''),
    oecmrWrap('ImmutableStorageReferences', manifest.immutableStorageReferences?.map((reference) =>
      oecmrWrap('ImmutableStorageReference', [
        oecmrElement('EvidenceType', safeManifestLabel(reference.evidenceType, manifestEvidenceTypes)),
        oecmrElement('DocumentVersionReferencePresent', presence(reference.documentVersionId)),
        oecmrElement(
          'ImmutableDigestAlgorithm',
          publicDigestAlgorithmLabel(reference.digestAlgorithm, reference.sha256Digest ? 'SHA-256' : undefined)
        ),
        oecmrElement('ImmutableDigestAvailable', reference.digestAvailable ?? Boolean(reference.sha256Digest)),
        oecmrElement('ImmutablePayloadStoredExternally', Boolean(reference.storageKey)),
        oecmrElement('RetentionMode', safeManifestLabel(reference.retentionMode, retentionModes)),
        oecmrElement('RetainUntilPresent', presence(reference.retainUntil)),
      ].join(''))
    ).join('') ?? ''),
  ].join('');

  return `<?xml version="1.0" encoding="UTF-8"?><oecmr:EvidenceManifest xmlns:oecmr="https://oecmr.example/evidence-manifest/1.0" manifestReferencePresent="false" documentReferencePresent="${String(presence(manifest.documentId))}">${content}</oecmr:EvidenceManifest>`;
};

export const saveEvidenceManifestAsXml = (
  _manifest: EvidenceManifestInput,
  _filename?: string
): void => {
  safeConsoleWarn(
    'Local evidence manifest download is disabled in cloud-only mode; use the backend authoritative evidence manifest endpoint',
    'evidence_manifest.local_download_disabled'
  );
  throw new Error('Local evidence manifest export is disabled in cloud-only mode');
};

export const saveXmlContent = (xml: string, filename: string): void => {
  fileDownload(xml, filename);
};

export const saveEvidenceManifestXmlContent = saveXmlContent;

export const exportEvidenceManifestFromAPI = async (
  documentId: string
): Promise<EvidenceManifestExportResult> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/evidence-manifest`, {
      method: 'POST',
    });

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new EvidenceManifestExportError(
        response.status,
        evidenceManifestFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    const payload = data && typeof data === 'object' ? data as Record<string, unknown> : {};
    return {
      message: typeof payload.message === 'string' ? payload.message : undefined,
      evidenceManifestXml: typeof payload.evidenceManifestXml === 'string' ? payload.evidenceManifestXml : '',
      evidenceManifest: publicEvidenceManifestSummaryFromApi(payload.evidenceManifest),
      archiveReference: publicImmutableStorageReferenceFromApi(payload.archiveReference) ?? null,
    };
  } catch (error) {
    safeConsoleError('Error exporting evidence manifest from API', 'api.evidence_manifest.export');
    throw error;
  }
};

export const getFinalXmlArchiveFromAPI = async (
  documentId: string
): Promise<FinalXmlArchiveRetrievalResult> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/final-xml`);

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new FinalXmlArchiveRetrievalError(
        response.status,
        finalXmlArchiveFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    const payload = data && typeof data === 'object' ? data as Record<string, unknown> : {};
    return {
      xmlContent: typeof payload.xmlContent === 'string' ? payload.xmlContent : '',
      archiveReference: publicImmutableStorageReferenceFromApi(payload.archiveReference) ?? {},
    };
  } catch (error) {
    safeConsoleError('Error retrieving final XML archive from API', 'api.final_xml.retrieve');
    throw error;
  }
};

export const confirmEvidenceDownloadCompletionInAPI = async (
  documentId: string,
  completion: EvidenceDownloadCompletionRequest
): Promise<EvidenceDownloadCompletionResult> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/download-completions`, {
      method: 'POST',
      body: JSON.stringify({
        evidenceId: completion.evidenceId,
        evidenceType: completion.evidenceType,
      }),
    });

    if (!response.ok) {
      throw new Error('Error al confirmar la finalización de descarga de evidencia en la API');
    }

    const data = await response.json();
    return publicEvidenceDownloadCompletionResultFromApi(data.downloadCompletion);
  } catch (error) {
    safeConsoleError('Error confirming evidence download completion in API', 'api.evidence_download_completion.confirm');
    throw error;
  }
};

export interface EnhancedECMRListItem {
  id: string;
  title: string;
  date: string;
  source: 'api';
  status: DocumentLifecycleStatus;
  document_id?: string;
  participants?: EcmrParticipant[];
}

export const normalizeApiDocument = (document: Partial<ApiDocument>): ApiDocument => ({
  ...document,
  document_id: document.document_id || document.documentId || '',
  documentId: document.documentId || document.document_id,
  title: document.title || 'Sin título',
  content: document.content || document.xmlContent || '',
  xmlContent: document.xmlContent || document.content || '',
  type: document.type,
  status: normalizeDocumentLifecycleStatus(document.status),
  created_at: document.created_at || document.createdAt || '',
  createdAt: document.createdAt || document.created_at,
  updated_at: document.updated_at || document.updatedAt,
  updatedAt: document.updatedAt || document.updated_at,
  user_id: document.user_id || document.userId,
  userId: document.userId || document.user_id,
  participants: document.participants || [],
});

const publicDocumentReferenceFromApi = (
  reference: unknown,
  fallbackDocumentId?: string
): ApiDocument => {
  const source = reference && typeof reference === 'object'
    ? reference as Record<string, unknown>
    : {};
  const documentId =
    safeManifestVersionLabel(source.documentId) ??
    safeManifestVersionLabel(source.document_id) ??
    safeManifestVersionLabel(fallbackDocumentId) ??
    '';
  const status = normalizeDocumentLifecycleStatus(
    typeof source.status === 'string' ? source.status : undefined
  );
  const document: ApiDocument = {
    document_id: documentId,
    documentId,
    title: 'Sin título',
    content: '',
    xmlContent: '',
    status,
    participants: [],
  };
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (publicDocumentVersionReferencePresentFromApi(source)) {
    document.documentVersionReferencePresent = true;
  }
  if (documentVersionNumber !== undefined) {
    document.documentVersionNumber = documentVersionNumber;
  }
  return document;
};

// Get documents from API
export const getDocumentsFromAPI = async (): Promise<ApiDocument[]> => {
  try {
    const response = await authService.authenticatedRequest('/documents');

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      safeConsoleError('Error al obtener documentos de la API', 'api.documents.list.response', {
        status: response.status,
      });
      throw new DocumentListAccessError(
        response.status,
        documentListAccessFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();

    // Ensure we return an array
    if (!data.documents || !Array.isArray(data.documents)) {
      safeConsoleWarn('API returned unexpected format', 'api.documents.list.unexpected_format');
      return [];
    }

    return data.documents.map(normalizeApiDocument);
  } catch (error) {
    safeConsoleError('Error fetching documents from API', 'api.documents.list');
    throw error;
  }
};

// Save document to API
const ensureBackendXmlValidationBeforeCloudSave = async (xmlContent: string): Promise<void> => {
  const validationResult = await validateECMR(xmlContent);
  if (!validationResult?.valid) {
    throw new Error(BACKEND_XML_VALIDATION_REQUIRED_ERROR);
  }
};

export const saveDocumentToAPI = async (ecmr: ECMR, title?: string): Promise<ApiDocument> => {
  try {
    const xmlContent = await convertToXml(ecmr);
    await ensureBackendXmlValidationBeforeCloudSave(xmlContent);
    const documentTitle = title || ecmr.ExchangedDocument?.ID || `eCMR-${Date.now()}`;
    const participants = deriveParticipantsFromECMR(ecmr);

    const response = await authService.authenticatedRequest('/documents', {
      method: 'POST',
      body: JSON.stringify({
        title: documentTitle,
        xmlContent,
        content: xmlContent,
        type: 'ecmr',
        participants
      }),
    });

    if (!response.ok) {
      throw new Error('Error al guardar documento en la API');
    }

    const data = await response.json();
    return normalizeApiDocument(data.document);
  } catch (error) {
    safeConsoleError('Error saving document to API', 'api.documents.save');
    throw error;
  }
};

// Update document in API
export const updateDocumentInAPI = async (documentId: string, ecmr: ECMR, title?: string): Promise<ApiDocument> => {
  try {
    const xmlContent = await convertToXml(ecmr);
    await ensureBackendXmlValidationBeforeCloudSave(xmlContent);
    const documentTitle = title || ecmr.ExchangedDocument?.ID || `eCMR-${Date.now()}`;
    const participants = deriveParticipantsFromECMR(ecmr);

    const response = await authService.authenticatedRequest(`/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify({
        title: documentTitle,
        xmlContent,
        content: xmlContent,
        type: 'ecmr',
        participants
      }),
    });

    if (!response.ok) {
      throw new Error('Error al actualizar documento en la API');
    }

    const data = await response.json();
    return normalizeApiDocument(data.document);
  } catch (error) {
    safeConsoleError('Error updating document in API', 'api.documents.update');
    throw error;
  }
};

export const updateDocumentParticipantsInAPI = async (
  documentId: string,
  participants: EcmrParticipant[]
): Promise<ApiDocument> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify({ participants }),
    });

    if (!response.ok) {
      throw new Error('Error al actualizar participantes del documento en la API');
    }

    const data = await response.json();
    return normalizeApiDocument(data.document);
  } catch (error) {
    safeConsoleError('Error updating document participants in API', 'api.documents.participants.update');
    throw error;
  }
};

export const transitionDocumentInAPI = async (
  documentId: string,
  nextStatus: DocumentLifecycleStatus,
  reason?: string
): Promise<ApiDocument> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/transition`, {
      method: 'POST',
      body: JSON.stringify({
        nextStatus,
        ...(reason ? { reason } : {}),
      }),
    });

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new DocumentLifecycleError(
        response.status,
        documentLifecycleFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    return normalizeApiDocument(data.document);
  } catch (error) {
    safeConsoleError('Error transitioning document in API', 'api.documents.transition');
    throw error;
  }
};

export const recordAuthorityReviewInAPI = async (
  documentId: string,
  review: AuthorityReviewRequest = { outcome: 'reviewed' }
): Promise<AuthorityReviewRecord> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/authority-reviews`, {
      method: 'POST',
      body: JSON.stringify(review),
    });

    if (!response.ok) {
      throw new Error('Error al registrar la revisión de autoridad en la API');
    }

    const data = await response.json();
    return publicAuthorityReviewRecordFromApi(data.authorityReview);
  } catch (error) {
    safeConsoleError('Error recording authority review in API', 'api.authority_reviews.create');
    throw error;
  }
};

export const signDocumentVersionInAPI = async (
  documentId: string,
  documentVersionId: string,
  signatureMethod: SignatureCreationMethod = DEFAULT_SIGNATURE_CREATION_METHOD,
  providerMetadata?: Record<string, unknown>
): Promise<SignatureEvidence> => {
  try {
    if (signatureMethod !== DEFAULT_SIGNATURE_CREATION_METHOD) {
      throw new Error('Metodo de firma no soportado por la linea base actual');
    }

    const response = await authService.authenticatedRequest(`/documents/${documentId}/signatures`, {
      method: 'POST',
      body: JSON.stringify({
        documentVersionId,
        signatureMethod,
        ...(providerMetadata ? { providerMetadata } : {}),
      }),
    });

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new SignatureCreationError(
        response.status,
        signatureCreationFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    return signatureEvidenceFromApi(data.signature);
  } catch (error) {
    safeConsoleError('Error signing document version in API', 'api.signatures.create');
    throw error;
  }
};

export const recordCarrierReservationInAPI = async (
  documentId: string,
  reservation: CarrierReservationRequest
): Promise<CarrierReservationEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/reservations`, {
      method: 'POST',
      body: JSON.stringify(reservation),
    });

    if (!response.ok) {
      throw new Error('Error al registrar reserva del transportista en la API');
    }

    const data = await response.json();
    return carrierReservationEvidenceFromApi(data.reservation);
  } catch (error) {
    safeConsoleError('Error recording carrier reservation in API', 'api.carrier_reservations.create');
    throw error;
  }
};

export const acknowledgeCarrierReservationInAPI = async (
  documentId: string,
  reservationId: string,
  acknowledgement: CarrierReservationAcknowledgementRequest
): Promise<CarrierReservationAcknowledgementEvidence> => {
  try {
    const encodedReservationId = encodeURIComponent(reservationId);
    const response = await authService.authenticatedRequest(`/documents/${documentId}/reservations/${encodedReservationId}/acknowledgements`, {
      method: 'POST',
      body: JSON.stringify(acknowledgement),
    });

    if (!response.ok) {
      throw new Error('Error al registrar aceptacion de reserva del transportista en la API');
    }

    const data = await response.json();
    return reservationAcknowledgementEvidenceFromApi(data.reservationAcknowledgement);
  } catch (error) {
    safeConsoleError('Error acknowledging carrier reservation in API', 'api.carrier_reservation_acknowledgements.create');
    throw error;
  }
};

export const recordCarrierTakeoverCheckInAPI = async (
  documentId: string,
  takeoverCheck: CarrierTakeoverCheckRequest
): Promise<CarrierTakeoverCheckEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/carrier-checks`, {
      method: 'POST',
      body: JSON.stringify(takeoverCheck),
    });

    if (!response.ok) {
      throw new Error('Error al registrar comprobacion del transportista en la API');
    }

    const data = await response.json();
    return carrierTakeoverCheckEvidenceFromApi(data.carrierTakeoverCheck);
  } catch (error) {
    safeConsoleError('Error recording carrier takeover check in API', 'api.carrier_takeover_checks.create');
    throw error;
  }
};

export const requestCarrierTakeoverCheckInAPI = async (
  documentId: string,
  checkRequest: CarrierTakeoverCheckRequestRequest
): Promise<CarrierTakeoverCheckRequestEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/carrier-check-requests`, {
      method: 'POST',
      body: JSON.stringify(checkRequest),
    });

    if (!response.ok) {
      throw new Error('Error al solicitar comprobacion del transportista en la API');
    }

    const data = await response.json();
    return carrierTakeoverCheckRequestEvidenceFromApi(data.carrierTakeoverCheckRequest);
  } catch (error) {
    safeConsoleError('Error requesting carrier takeover check in API', 'api.carrier_takeover_check_requests.create');
    throw error;
  }
};

export const confirmDeliveryInAPI = async (
  documentId: string,
  deliveryConfirmation: DeliveryConfirmationRequest
): Promise<DeliveryConfirmationEvidence> => {
  try {
    const signatureMethod = deliveryConfirmation.signatureMethod ?? DEFAULT_SIGNATURE_CREATION_METHOD;
    if (signatureMethod !== DEFAULT_SIGNATURE_CREATION_METHOD) {
      throw new Error('Metodo de firma de entrega no soportado por la linea base actual');
    }

    const response = await authService.authenticatedRequest(`/documents/${documentId}/delivery`, {
      method: 'POST',
      body: JSON.stringify({
        ...deliveryConfirmation,
        signatureMethod,
      }),
    });

    if (!response.ok) {
      throw new Error('Error al confirmar la entrega del documento en la API');
    }

    const data = await response.json();
    return deliveryConfirmationEvidenceFromApi(data.deliveryConfirmation);
  } catch (error) {
    safeConsoleError('Error confirming delivery in API', 'api.delivery.confirm');
    throw error;
  }
};

export const acknowledgeDeliveryReservationInAPI = async (
  documentId: string,
  deliveryId: string,
  reservationId: string,
  acknowledgement: DeliveryReservationAcknowledgementRequest
): Promise<DeliveryReservationAcknowledgementEvidence> => {
  try {
    const encodedDeliveryId = encodeURIComponent(deliveryId);
    const encodedReservationId = encodeURIComponent(reservationId);
    const response = await authService.authenticatedRequest(`/documents/${documentId}/delivery/${encodedDeliveryId}/reservations/${encodedReservationId}/acknowledgements`, {
      method: 'POST',
      body: JSON.stringify(acknowledgement),
    });

    if (!response.ok) {
      throw new Error('Error al registrar aceptacion de reserva de entrega en la API');
    }

    const data = await response.json();
    return deliveryReservationAcknowledgementEvidenceFromApi(data.deliveryReservationAcknowledgement);
  } catch (error) {
    safeConsoleError('Error acknowledging delivery reservation in API', 'api.delivery_reservation_acknowledgements.create');
    throw error;
  }
};

export const uploadSupplementaryDocumentInAPI = async (
  documentId: string,
  attachment: SupplementaryDocumentUploadRequest
): Promise<SupplementaryDocumentEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/attachments`, {
      method: 'POST',
      body: JSON.stringify(attachment),
    });

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new SupplementaryDocumentUploadError(
        response.status,
        supplementaryDocumentUploadFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    return requirePublicSupplementaryDocumentEvidenceFromApi(data.attachment);
  } catch (error) {
    safeConsoleError('Error uploading supplementary document in API', 'api.supplementary_documents.upload');
    throw error;
  }
};

export const createSupplementaryDocumentUploadIntentInAPI = async (
  documentId: string,
  request: SupplementaryDocumentUploadIntentRequest
): Promise<SupplementaryDocumentUploadIntent> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/attachments/upload-intents`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Error al crear intención de subida directa del documento suplementario en la API');
    }

    const data = await response.json();
    return publicSupplementaryDocumentUploadIntentFromApi(data.uploadIntent);
  } catch (error) {
    safeConsoleError('Error creating supplementary document direct upload intent in API', 'api.supplementary_documents.upload_intent');
    throw error;
  }
};

export const confirmSupplementaryDocumentDirectUploadInAPI = async (
  documentId: string,
  attachmentId: string,
  confirmation: SupplementaryDocumentDirectUploadConfirmationRequest
): Promise<SupplementaryDocumentEvidence> => {
  try {
    const encodedAttachmentId = encodeURIComponent(attachmentId);
    const response = await authService.authenticatedRequest(`/documents/${documentId}/attachments/${encodedAttachmentId}/confirm`, {
      method: 'POST',
      body: JSON.stringify(confirmation),
    });

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new SupplementaryDocumentUploadError(
        response.status,
        supplementaryDocumentUploadFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    return requirePublicSupplementaryDocumentEvidenceFromApi(data.attachment);
  } catch (error) {
    safeConsoleError('Error confirming supplementary document direct upload in API', 'api.supplementary_documents.direct_upload_confirm');
    throw error;
  }
};

export const listSupplementaryDocumentsInAPI = async (
  documentId: string
): Promise<SupplementaryDocumentEvidence[]> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/attachments`);

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new SupplementaryDocumentAccessError(
        response.status,
        supplementaryDocumentAccessFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    const attachments = Array.isArray(data.attachments) ? data.attachments as unknown[] : [];
    return attachments
      .map(publicSupplementaryDocumentEvidenceFromApi)
      .filter((attachment): attachment is SupplementaryDocumentEvidence => Boolean(attachment));
  } catch (error) {
    safeConsoleError('Error listing supplementary documents in API', 'api.supplementary_documents.list');
    throw error;
  }
};

export const getSupplementaryDocumentDownloadUrlInAPI = async (
  documentId: string,
  attachmentId: string
): Promise<SupplementaryDocumentDownloadResult> => {
  try {
    const encodedAttachmentId = encodeURIComponent(attachmentId);
    const response = await authService.authenticatedRequest(`/documents/${documentId}/attachments/${encodedAttachmentId}`);

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new SupplementaryDocumentAccessError(
        response.status,
        supplementaryDocumentAccessFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    return publicSupplementaryDocumentDownloadResultFromApi(data);
  } catch (error) {
    safeConsoleError('Error getting supplementary document download URL in API', 'api.supplementary_documents.download_url');
    throw error;
  }
};

export const getActiveProcedureSetFromAPI = async (): Promise<ActiveProcedureSet> => {
  try {
    const response = await authService.authenticatedRequest('/documents/procedure-set');

    if (!response.ok) {
      throw new Error('Error al obtener el conjunto activo de procedimientos eCMR');
    }

    const data = await response.json();
    return requirePublicActiveProcedureSetFromApi(data.procedureSet);
  } catch (error) {
    safeConsoleError('Error fetching active procedure set from API', 'api.procedure_sets.active');
    throw error;
  }
};

export const getDocumentReferencedProcedureSetsFromAPI = async (
  documentId: string
): Promise<DocumentReferencedProcedureSets> => {
  try {
    const encodedDocumentId = encodeURIComponent(documentId);
    const response = await authService.authenticatedRequest(`/documents/${encodedDocumentId}/procedure-sets`);

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new DocumentProcedureAccessError(
        response.status,
        documentProcedureAccessFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    return publicDocumentReferencedProcedureSetsFromApi(data);
  } catch (error) {
    safeConsoleError('Error resolving document procedure sets from API', 'api.procedure_sets.document_references');
    throw error;
  }
};

export const acceptProcedureSetInAPI = async (
  documentId: string,
  procedureSetId: string
): Promise<ProcedureSetAcceptanceEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/procedure-acceptances`, {
      method: 'POST',
      body: JSON.stringify({
        procedureSetId,
        accepted: true,
      }),
    });

    if (!response.ok) {
      throw new Error('Error al aceptar el conjunto activo de procedimientos eCMR');
    }

    const data = await response.json();
    return procedureAcceptanceEvidenceFromApi(data.procedureAcceptance);
  } catch (error) {
    safeConsoleError('Error accepting procedure set in API', 'api.procedure_sets.accept');
    throw error;
  }
};

export const recordReplacementByOtherMeansInAPI = async (
  documentId: string,
  replacement: ReplacementByOtherMeansRequest
): Promise<ReplacementByOtherMeansEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/replacements`, {
      method: 'POST',
      body: JSON.stringify(replacement),
    });

    if (!response.ok) {
      throw new Error('Error al registrar sustitucion del eCMR en la API');
    }

    const data = await response.json();
    return replacementByOtherMeansEvidenceFromApi(data.replacement);
  } catch (error) {
    safeConsoleError('Error recording replacement-by-other-means in API', 'api.replacement.record');
    throw error;
  }
};

export const recordDisposalInstructionInAPI = async (
  documentId: string,
  instruction: DisposalInstructionRequest
): Promise<DisposalInstructionEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/disposal-instructions`, {
      method: 'POST',
      body: JSON.stringify(instruction),
    });

    if (!response.ok) {
      throw new Error('Error al registrar instruccion CMR Article 12 en la API');
    }

    const data = await response.json();
    return disposalInstructionEvidenceFromApi(data.disposalInstruction);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 12 disposal instruction in API', 'api.disposal_instruction.record');
    throw error;
  }
};

export const recordDisposalExecutionNoticeInAPI = async (
  documentId: string,
  instructionId: string,
  notice: DisposalExecutionNoticeRequest
): Promise<DisposalExecutionNoticeEvidence> => {
  try {
    const encodedInstructionId = encodeURIComponent(instructionId);
    const response = await authService.authenticatedRequest(`/documents/${documentId}/disposal-instructions/${encodedInstructionId}/execution-notices`, {
      method: 'POST',
      body: JSON.stringify(notice),
    });

    if (!response.ok) {
      throw new Error('Error al registrar notificacion CMR Article 12.6 en la API');
    }

    const data = await response.json();
    return disposalExecutionNoticeEvidenceFromApi(data.disposalExecutionNotice);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 12.6 disposal execution notice in API', 'api.disposal_execution_notice.record');
    throw error;
  }
};

export const recordCarriageImpedimentInAPI = async (
  documentId: string,
  impediment: CarriageImpedimentRequest
): Promise<CarriageImpedimentEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/carriage-impediments`, {
      method: 'POST',
      body: JSON.stringify(impediment),
    });

    if (!response.ok) {
      throw new Error('Error al registrar impedimento CMR Articles 14-16 en la API');
    }

    const data = await response.json();
    return carriageImpedimentEvidenceFromApi(data.carriageImpediment);
  } catch (error) {
    safeConsoleError('Error recording CMR Articles 14-16 carriage impediment in API', 'api.carriage_impediment.record');
    throw error;
  }
};

export const recordCarriageAccountingInAPI = async (
  documentId: string,
  impedimentId: string,
  accounting: CarriageAccountingRequest
): Promise<CarriageAccountingEvidence> => {
  try {
    const response = await authService.authenticatedRequest(
      `/documents/${documentId}/carriage-impediments/${encodeURIComponent(impedimentId)}/accounting-records`,
      {
        method: 'POST',
        body: JSON.stringify(accounting),
      }
    );

    if (!response.ok) {
      throw new Error('Error al registrar contabilidad CMR Article 16 en la API');
    }

    const data = await response.json();
    return carriageAccountingEvidenceFromApi(data.carriageAccountingRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 16 accounting in API', 'api.carriage_accounting.record');
    throw error;
  }
};

export const recordCarrierLiabilityIncidentInAPI = async (
  documentId: string,
  incident: CarrierLiabilityIncidentRequest
): Promise<CarrierLiabilityIncidentEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/carrier-liability-incidents`, {
      method: 'POST',
      body: JSON.stringify(incident),
    });

    if (!response.ok) {
      throw new Error('Error al registrar incidente de responsabilidad CMR en la API');
    }

    const data = await response.json();
    return carrierLiabilityIncidentEvidenceFromApi(data.carrierLiabilityIncidentRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR carrier liability incident in API', 'api.carrier_liability_incident.record');
    throw error;
  }
};

export const recordArticle18_2ClaimantRebuttalInAPI = async (
  documentId: string,
  incidentId: string,
  rebuttal: Article18_2ClaimantRebuttalRequest
): Promise<Article18_2ClaimantRebuttalEvidence> => {
  try {
    const response = await authService.authenticatedRequest(
      `/documents/${documentId}/carrier-liability-incidents/${encodeURIComponent(incidentId)}/article18-2-rebuttals`,
      {
        method: 'POST',
        body: JSON.stringify(rebuttal),
      }
    );

    if (!response.ok) {
      throw new Error('Error al registrar prueba en contrario CMR Article 18.2 en la API');
    }

    const data = await response.json();
    return article18_2ClaimantRebuttalEvidenceFromApi(data.article18_2ClaimantRebuttalRecord);
  } catch (error) {
    safeConsoleError(
      'Error recording CMR Article 18.2 claimant rebuttal in API',
      'api.article18_2_claimant_rebuttal.record'
    );
    throw error;
  }
};

export const recordDeliveryTimingAssessmentInAPI = async (
  documentId: string,
  assessment: DeliveryTimingAssessmentRequest
): Promise<DeliveryTimingAssessmentEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/delivery-timing-assessments`, {
      method: 'POST',
      body: JSON.stringify(assessment),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evaluacion CMR Articles 19-20 en la API');
    }

    const data = await response.json();
    return deliveryTimingAssessmentEvidenceFromApi(data.deliveryTimingAssessmentRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Articles 19-20 delivery timing assessment in API', 'api.delivery_timing_assessment.record');
    throw error;
  }
};

export const recordCompensationPaymentInAPI = async (
  documentId: string,
  payment: CompensationPaymentRequest
): Promise<CompensationPaymentEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/compensation-payment-records`, {
      method: 'POST',
      body: JSON.stringify(payment),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia de pago CMR Article 20.2 en la API');
    }

    const data = await response.json();
    return compensationPaymentEvidenceFromApi(data.compensationPaymentRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 20.2 compensation payment in API', 'api.compensation_payment.record');
    throw error;
  }
};

export const recordRecoveryDeliverySettlementInAPI = async (
  documentId: string,
  settlement: RecoveryDeliverySettlementRequest
): Promise<RecoveryDeliverySettlementEvidence> => {
  try {
    const response = await authService.authenticatedRequest(
      `/documents/${documentId}/recovery-delivery-settlement-records`,
      {
        method: 'POST',
        body: JSON.stringify(settlement),
      }
    );

    if (!response.ok) {
      throw new Error('Error al registrar liquidacion de entrega CMR Article 20.3 en la API');
    }

    const data = await response.json();
    return recoveryDeliverySettlementEvidenceFromApi(data.recoveryDeliverySettlementRecord);
  } catch (error) {
    safeConsoleError(
      'Error recording CMR Article 20.3 recovery delivery settlement in API',
      'api.recovery_delivery_settlement.record'
    );
    throw error;
  }
};

export const recordRecoveredGoodsDispositionInAPI = async (
  documentId: string,
  disposition: RecoveredGoodsDispositionRequest
): Promise<RecoveredGoodsDispositionEvidence> => {
  try {
    const response = await authService.authenticatedRequest(
      `/documents/${documentId}/recovered-goods-disposition-records`,
      {
        method: 'POST',
        body: JSON.stringify(disposition),
      }
    );
    if (!response.ok) {
      throw new Error('Error al registrar disposicion CMR Article 20.4 en la API');
    }
    const data = await response.json();
    return recoveredGoodsDispositionEvidenceFromApi(data.recoveredGoodsDispositionRecord);
  } catch (error) {
    safeConsoleError(
      'Error recording CMR Article 20.4 recovered goods disposition in API',
      'api.recovered_goods_disposition.record'
    );
    throw error;
  }
};

export const recordCodDeliveryOmissionInAPI = async (
  documentId: string,
  omission: CodDeliveryOmissionRequest
): Promise<CodDeliveryOmissionEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/cod-delivery-omissions`, {
      method: 'POST',
      body: JSON.stringify(omission),
    });

    if (!response.ok) {
      throw new Error('Error al registrar omision COD CMR Article 21 en la API');
    }

    const data = await response.json();
    return codDeliveryOmissionEvidenceFromApi(data.codDeliveryOmissionRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 21 COD delivery omission in API', 'api.cod_delivery_omission.record');
    throw error;
  }
};

export const recordDangerousGoodsNoticeInAPI = async (
  documentId: string,
  notice: DangerousGoodsNoticeRequest
): Promise<DangerousGoodsNoticeEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/dangerous-goods-notices`, {
      method: 'POST',
      body: JSON.stringify(notice),
    });

    if (!response.ok) {
      throw new Error('Error al registrar aviso de mercancias peligrosas CMR Article 22 en la API');
    }

    const data = await response.json();
    return dangerousGoodsNoticeEvidenceFromApi(data.dangerousGoodsNoticeRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 22 dangerous goods notice in API', 'api.dangerous_goods_notice.record');
    throw error;
  }
};

export const recordCompensationAssessmentInAPI = async (
  documentId: string,
  assessment: CompensationAssessmentRequest
): Promise<CompensationAssessmentEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/compensation-assessments`, {
      method: 'POST',
      body: JSON.stringify(assessment),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evaluacion de compensacion CMR Articles 23-26 en la API');
    }

    const data = await response.json();
    return compensationAssessmentEvidenceFromApi(data.compensationAssessmentRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Articles 23-26 compensation assessment in API', 'api.compensation_assessment.record');
    throw error;
  }
};

export const recordCompensationInterestInAPI = async (
  documentId: string,
  record: CompensationInterestRequest
): Promise<CompensationInterestEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/compensation-interest-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar interes/conversion de compensacion CMR Article 27 en la API');
    }

    const data = await response.json();
    return compensationInterestEvidenceFromApi(data.compensationInterestRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 27 compensation interest/conversion in API', 'api.compensation_interest.record');
    throw error;
  }
};

export const recordLiabilityLimitationInAPI = async (
  documentId: string,
  record: LiabilityLimitationRequest
): Promise<LiabilityLimitationEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/liability-limitation-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Articles 28-29 en la API');
    }

    const data = await response.json();
    return liabilityLimitationEvidenceFromApi(data.liabilityLimitationRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Articles 28-29 liability limitation in API', 'api.liability_limitation.record');
    throw error;
  }
};

export const recordClaimReservationInAPI = async (
  documentId: string,
  record: ClaimReservationRequest
): Promise<ClaimReservationEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/claim-reservation-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Article 30 en la API');
    }

    const data = await response.json();
    return claimReservationEvidenceFromApi(data.claimReservationRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 30 claim reservation in API', 'api.claim_reservation.record');
    throw error;
  }
};

export const recordDisputeProcedureInAPI = async (
  documentId: string,
  record: DisputeProcedureRequest
): Promise<DisputeProcedureEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/dispute-procedure-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Articles 31-33 en la API');
    }

    const data = await response.json();
    return disputeProcedureEvidenceFromApi(data.disputeProcedureRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Articles 31-33 dispute procedure in API', 'api.dispute_procedure.record');
    throw error;
  }
};

export const recordSuccessiveCarrierInAPI = async (
  documentId: string,
  record: SuccessiveCarrierRequest
): Promise<SuccessiveCarrierEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/successive-carrier-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Articles 34-40 en la API');
    }

    const data = await response.json();
    return successiveCarrierEvidenceFromApi(data.successiveCarrierRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Articles 34-40 successive carrier in API', 'api.successive_carrier.record');
    throw error;
  }
};

export const recordMultimodalCarriageInAPI = async (
  documentId: string,
  record: MultimodalCarriageRequest
): Promise<MultimodalCarriageEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/multimodal-carriage-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Article 2 en la API');
    }

    const data = await response.json();
    return multimodalCarriageEvidenceFromApi(data.multimodalCarriageRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 2 multimodal carriage in API', 'api.multimodal_carriage.record');
    throw error;
  }
};

export const recordConsignmentNoteCopyInAPI = async (
  documentId: string,
  record: ConsignmentNoteCopyRequest
): Promise<ConsignmentNoteCopyEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/consignment-note-copy-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Article 5 en la API');
    }

    const data = await response.json();
    return consignmentNoteCopyEvidenceFromApi(data.consignmentNoteCopyRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 5 consignment note copy access in API', 'api.consignment_note_copy.record');
    throw error;
  }
};

export const requestEcmrReceiptAccessInAPI = async (
  documentId: string,
  request: EcmrReceiptAccessSenderRequest
): Promise<EcmrReceiptAccessRequestEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/ecmr-receipt-access-requests`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Error al solicitar recibo y acceso eCMR Article 6.1 en la API');
    }

    const data = await response.json();
    return ecmrReceiptAccessRequestEvidenceFromApi(data.ecmrReceiptAccessRequest);
  } catch (error) {
    safeConsoleError('Error requesting eCMR Article 6.1 receipt/access in API', 'api.ecmr_receipt_access.request');
    throw error;
  }
};

export const recordEcmrReceiptAccessInAPI = async (
  documentId: string,
  record: EcmrReceiptAccessRequest
): Promise<EcmrReceiptAccessEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/ecmr-receipt-access-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia eCMR Article 6.1 en la API');
    }

    const data = await response.json();
    return ecmrReceiptAccessEvidenceFromApi(data.ecmrReceiptAccessRecord);
  } catch (error) {
    safeConsoleError('Error recording eCMR Article 6.1 receipt/access in API', 'api.ecmr_receipt_access.record');
    throw error;
  }
};

export const recordProhibitedClauseInAPI = async (
  documentId: string,
  record: ProhibitedClauseRequest
): Promise<ProhibitedClauseEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/prohibited-clause-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Article 41 en la API');
    }

    const data = await response.json();
    return prohibitedClauseEvidenceFromApi(data.prohibitedClauseRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 41 prohibited clause in API', 'api.prohibited_clause.record');
    throw error;
  }
};

export const recordSenderParticularsResponsibilityInAPI = async (
  documentId: string,
  record: SenderParticularsResponsibilityRequest
): Promise<SenderParticularsResponsibilityEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/sender-particulars-responsibility-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });

    if (!response.ok) {
      throw new Error('Error al registrar evidencia CMR Article 7 en la API');
    }

    const data = await response.json();
    return senderParticularsResponsibilityEvidenceFromApi(data.senderParticularsResponsibilityRecord);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 7 sender particulars responsibility in API', 'api.sender_particulars_responsibility.record');
    throw error;
  }
};

export const recordConsigneeDeliveryRequestInAPI = async (
  documentId: string,
  deliveryRequest: ConsigneeDeliveryRequestRequest
): Promise<ConsigneeDeliveryRequestEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/consignee-delivery-requests`, {
      method: 'POST',
      body: JSON.stringify(deliveryRequest),
    });

    if (!response.ok) {
      throw new Error('Error al registrar solicitud CMR Article 13 en la API');
    }

    const data = await response.json();
    return consigneeDeliveryRequestEvidenceFromApi(data.consigneeDeliveryRequest);
  } catch (error) {
    safeConsoleError('Error recording CMR Article 13 consignee delivery request in API', 'api.consignee_delivery_request.record');
    throw error;
  }
};

export const applyRetentionPolicyInAPI = async (
  documentId: string,
  retentionPolicy: RetentionPolicyRequest
): Promise<RetentionPolicyEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/retention-policy`, {
      method: 'POST',
      body: JSON.stringify(retentionPolicy),
    });

    if (!response.ok) {
      throw new Error('Error al aplicar la política de retención en la API');
    }

    const data = await response.json();
    return publicRetentionPolicyEvidenceFromApi(data.retentionPolicy);
  } catch (error) {
    safeConsoleError('Error applying retention policy in API', 'api.retention_policy.apply');
    throw error;
  }
};

export const applyLegalHoldInAPI = async (
  documentId: string,
  reason: string
): Promise<LegalHoldEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/legal-hold`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      throw new Error('Error al aplicar legal hold en la API');
    }

    const data = await response.json();
    return publicLegalHoldEvidenceFromApi(data.legalHold);
  } catch (error) {
    safeConsoleError('Error applying legal hold in API', 'api.legal_hold.apply');
    throw error;
  }
};

export const releaseLegalHoldInAPI = async (
  documentId: string,
  reason: string
): Promise<LegalHoldEvidence> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/legal-hold/release`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      throw new Error('Error al liberar legal hold en la API');
    }

    const data = await response.json();
    return publicLegalHoldEvidenceFromApi(data.legalHold);
  } catch (error) {
    safeConsoleError('Error releasing legal hold in API', 'api.legal_hold.release');
    throw error;
  }
};

export const requestAmendmentInAPI = async (
  documentId: string,
  amendmentRequest: AmendmentRequestPayload
): Promise<AmendmentEvidence> => {
  try {
    const requestPayload: AmendmentRequestPayload = {
      reason: amendmentRequest.reason,
      authorizationBasis: amendmentRequest.authorizationBasis,
      proposedChanges: { xmlContent: amendmentRequest.proposedChanges.xmlContent },
      ...(amendmentRequest.clientReferenceId
        ? { clientReferenceId: amendmentRequest.clientReferenceId }
        : {}),
    };
    const response = await authService.authenticatedRequest(`/documents/${documentId}/amendments`, {
      method: 'POST',
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      throw new Error('Error al solicitar modificación del documento en la API');
    }

    const data = await response.json();
    return requirePublicAmendmentEvidenceFromApi(data.amendment);
  } catch (error) {
    safeConsoleError('Error requesting amendment in API', 'api.amendments.request');
    throw error;
  }
};

export const acknowledgeAmendmentInAPI = async (
  documentId: string,
  amendmentId: string,
  acknowledgement: AmendmentAcknowledgementRequest
): Promise<AmendmentAcknowledgementEvidence> => {
  try {
    const encodedAmendmentId = encodeURIComponent(amendmentId);
    const response = await authService.authenticatedRequest(`/documents/${documentId}/amendments/${encodedAmendmentId}/acknowledgements`, {
      method: 'POST',
      body: JSON.stringify(acknowledgement),
    });

    if (!response.ok) {
      throw new Error('Error al registrar acuse de modificación en la API');
    }

    const data = await response.json();
    return requirePublicAmendmentAcknowledgementEvidenceFromApi(data.acknowledgement);
  } catch (error) {
    safeConsoleError('Error acknowledging amendment in API', 'api.amendments.acknowledge');
    throw error;
  }
};

export const applyAmendmentInAPI = async (
  documentId: string,
  amendmentId: string
): Promise<AmendmentApplyResult> => {
  try {
    const encodedAmendmentId = encodeURIComponent(amendmentId);
    const response = await authService.authenticatedRequest(`/documents/${documentId}/amendments/${encodedAmendmentId}/apply`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Error al aplicar modificación del documento en la API');
    }

    const data = await response.json();
    return {
      amendment: requirePublicAmendmentEvidenceFromApi(data.amendment),
      document: publicDocumentReferenceFromApi(data.documentReference ?? data.document, documentId),
    };
  } catch (error) {
    safeConsoleError('Error applying amendment in API', 'api.amendments.apply');
    throw error;
  }
};

export const verifyDocumentSignaturesInAPI = async (documentId: string): Promise<SignatureVerificationResult> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/signatures/verify`);

    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new SignatureVerificationError(
        response.status,
        signatureVerificationFailureCodeFromResponse(response.status, payload)
      );
    }

    const data = await response.json();
    return publicSignatureVerificationResultFromApi(data);
  } catch (error) {
    safeConsoleError('Error verifying document signatures in API', 'api.signatures.verify');
    throw error;
  }
};

const publicAuditEventTypeFromApi = (value: unknown): string =>
  typeof value === 'string' && publicAuditEventTypes.has(value) ? value : 'other';

const publicAuditEventFromApi = (event: unknown): DocumentAuditEvent | undefined => {
  if (!event || typeof event !== 'object') {
    return undefined;
  }
  const source = event as Record<string, unknown>;
  const result: DocumentAuditEvent = {
    eventType: publicAuditEventTypeFromApi(source.eventType),
  };
  if (typeof source.eventTimestamp === 'string') {
    result.eventTimestamp = source.eventTimestamp;
  }
  if (typeof source.timestamp === 'string') {
    result.timestamp = source.timestamp;
  }
  if (publicDocumentVersionReferencePresentFromApi(source)) {
    result.documentVersionReferencePresent = true;
  }
  const documentVersionNumber = safePublicManifestVersionNumber(source.documentVersionNumber);
  if (documentVersionNumber !== undefined) {
    result.documentVersionNumber = documentVersionNumber;
  }
  if (typeof source.actorReferencePresent === 'boolean') {
    result.actorReferencePresent = source.actorReferencePresent;
  }
  if (Array.isArray(source.actorRoles)) {
    const actorRoles = source.actorRoles.filter(
      (role): role is string => typeof role === 'string' && publicAuditActorRoles.has(role)
    );
    if (actorRoles.length > 0) {
      result.actorRoles = Array.from(new Set(actorRoles)).sort();
    }
  }
  if (typeof source.requestMetadataPresent === 'boolean') {
    result.requestMetadataPresent = source.requestMetadataPresent;
  }
  const details = source.details;
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const detailCount = safePublicManifestCount((details as Record<string, unknown>).detailCount);
    if (detailCount !== undefined) {
      result.details = { detailCount };
    }
  }
  return result;
};

export const getDocumentAuditEventsFromAPI = async (documentId: string): Promise<DocumentAuditEvent[]> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/audit`);
    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new DocumentAuditAccessError(
        response.status,
        documentAuditAccessFailureCodeFromResponse(response.status, payload)
      );
    }
    const data = await response.json();
    const rawEvents: unknown[] = Array.isArray(data.events)
      ? data.events
      : (Array.isArray(data.auditEvents) ? data.auditEvents : []);
    return rawEvents
      .map(publicAuditEventFromApi)
      .filter((event): event is DocumentAuditEvent => event !== undefined);
  } catch (error) {
    safeConsoleError('Error fetching document audit events from API', 'api.audit_events.list');
    throw error;
  }
};

export const getDocumentTaskQueueFromAPI = async (documentId: string): Promise<DocumentTaskQueue> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/tasks`);
    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new DocumentPolicyAccessError(
        response.status,
        documentPolicyAccessFailureCodeFromResponse(response.status, payload)
      );
    }
    const data = await response.json();
    return publicDocumentTaskQueueFromApi(documentId, data);
  } catch (error) {
    safeConsoleError('Error fetching document task queue from API', 'api.document_tasks.list');
    throw error;
  }
};

export const getDocumentWorkflowActionsFromAPI = async (documentId: string): Promise<DocumentWorkflowActionsResponse> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/workflow-actions`);
    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new DocumentPolicyAccessError(
        response.status,
        documentPolicyAccessFailureCodeFromResponse(response.status, payload)
      );
    }
    const data = await response.json();
    return publicDocumentWorkflowActionsResponseFromApi(documentId, data);
  } catch (error) {
    safeConsoleError('Error fetching document workflow actions from API', 'api.document_workflow_actions.list');
    throw error;
  }
};

export const updateDocumentTaskStatusInAPI = async (
  documentId: string,
  taskId: string,
  update: DocumentTaskStatusUpdateRequest
): Promise<DocumentTaskStatusUpdateResult> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/tasks/${taskId}/status`, {
      method: 'POST',
      body: JSON.stringify({
        status: update.status,
        assignedGroup: update.assignedGroup,
      }),
    });
    if (!response.ok) {
      throw new Error('Error al actualizar estado de tarea server-authored del documento en la API');
    }
    const data = await response.json();
    return publicDocumentTaskStatusUpdateResultFromApi(documentId, data);
  } catch (error) {
    safeConsoleError('Error updating document task status in API', 'api.document_tasks.update_status');
    throw error;
  }
};

// Delete document from API
export const deleteDocumentFromAPI = async (documentId: string): Promise<void> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error('Error al eliminar documento de la API');
    }
  } catch (error) {
    safeConsoleError('Error deleting document from API', 'api.documents.delete');
    throw error;
  }
};

// Get specific document from API
export const getDocumentFromAPI = async (documentId: string): Promise<ApiDocument> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}`);
    
    if (!response.ok) {
      const payload = await responseJsonObjectOrUndefined(response);
      throw new DocumentReadError(
        response.status,
        documentReadFailureCodeFromResponse(response.status, payload)
      );
    }
    
    const data = await response.json();
    return normalizeApiDocument(data.document);
  } catch (error) {
    safeConsoleError('Error fetching document from API', 'api.documents.get');
    throw error;
  }
};

// Convert API document to ECMR
export const convertApiDocumentToECMR = async (apiDocument: ApiDocument): Promise<ECMR> => {
  try {
    const normalizedDocument = normalizeApiDocument(apiDocument);
    const xmlContent = normalizedDocument.xmlContent || normalizedDocument.content;
    if (!xmlContent) {
      throw new Error('El documento de la API no contiene XML eCMR');
    }

    const ecmr = await convertFromXml(xmlContent);
    return attachApiEvidenceToECMR(ecmr, normalizedDocument);
  } catch (error) {
    safeConsoleError('Error converting API document to ECMR', 'api.documents.convert');
    throw error;
  }
};

// Legacy local-to-cloud migration is disabled in cloud-only mode.
export const syncLocalDocumentsToAPI = async (): Promise<{ synced: number, errors: number }> => {
  safeConsoleWarn('Local document migration is disabled in cloud-only mode', 'api.sync.local_migration_disabled');
  return { synced: 0, errors: 0 };
};

// Enhanced getECMRList for cloud-only documents.
export const getEnhancedECMRList = async (): Promise<EnhancedECMRListItem[]> => {
  if (!authService.isAuthenticated()) {
    return [];
  }

  try {
    const apiDocuments = await getDocumentsFromAPI();
    const apiList = apiDocuments
      .map(normalizeApiDocument)
      .filter(doc => doc.document_id) // Filter out documents without document_id
      .map(doc => ({
        id: doc.document_id || `api_${Date.now()}_${Math.random()}`,
        title: doc.title || 'Sin título',
        date: doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'Sin fecha',
        source: 'api' as const,
        status: doc.status,
        document_id: doc.document_id,
        participants: doc.participants || []
      }));

    return apiList.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (error) {
    safeConsoleError('Could not fetch API documents in cloud-only mode', 'api.documents.cloud_fetch_failed');
    throw error;
  }
};

export type CloudDocumentSource = 'api';

// Load document from the cloud system of record.
export const loadDocumentFromSource = async (
  documentId: string,
  source: CloudDocumentSource = 'api'
): Promise<ECMR | null> => {
  try {
    if (String(source) !== 'api') {
      safeConsoleWarn('Unsupported document source is disabled in cloud-only mode', 'document.source_disabled');
      return null;
    }

    const apiDoc = await getDocumentFromAPI(documentId);
    return await convertApiDocumentToECMR(apiDoc);
  } catch (error) {
    safeConsoleError('Error loading document from source', 'document.load_from_source');
    if (isSanitizedDocumentReadContractError(error)) {
      throw error;
    }
    return null;
  }
};

// Save document to the cloud system of record only.
export const saveECMRWithSync = async (
  ecmr: ECMR, 
  title?: string, 
  documentId?: string,
  source: CloudDocumentSource = 'api'
): Promise<{ apiDocument?: ApiDocument, success: boolean, validationErrorCount?: number }> => {
  if (!authService.isAuthenticated()) {
    safeConsoleWarn('Cannot save document without cloud authentication', 'document.save.cloud_auth_required');
    return { success: false };
  }

  if (String(source) !== 'api') {
    safeConsoleWarn('Unsupported document source is disabled in cloud-only mode', 'document.save.source_disabled');
    return { success: false };
  }

  const cmrValidation = validateCmrDocument(ecmr);
  const validationErrorCount = cmrValidation.errors.filter(error => error.severity === 'error').length;

  if (validationErrorCount > 0) {
    safeConsoleWarn(
      'Cannot save document before mandatory CMR/eCMR validation passes',
      'document.save.cmr_validation_required'
    );
    return { success: false, validationErrorCount };
  }

  try {
    const apiDocument = documentId
      ? await updateDocumentInAPI(documentId, ecmr, title)
      : await saveDocumentToAPI(ecmr, title);

    return { apiDocument, success: true };
  } catch (error) {
    safeConsoleError('Error saving document to cloud system of record', 'document.save.cloud_only');
    return { success: false };
  }
};

// Delete document from the cloud system of record only.
export const deleteECMRFromSource = async (
  documentId: string,
  source: CloudDocumentSource = 'api'
): Promise<boolean> => {
  try {
    if (String(source) !== 'api') {
      safeConsoleWarn('Unsupported document deletion source is disabled in cloud-only mode', 'document.delete.source_disabled');
      return false;
    }

    await deleteDocumentFromAPI(documentId);
    return true;
  } catch (error) {
    safeConsoleError('Error deleting document from source', 'document.delete_from_source');
    return false;
  }
};
